// Landing page copy — TalkPack v3 (per Ilann's brief dated April 2026).
// This file is the single source of truth for strings.
// EN is the master. All other languages currently inherit EN via spread
// and can be overridden key-by-key as translations land.

export type LandingLanguage =
  | "en" | "en-us"
  | "fr" | "es" | "de" | "it" | "pl" | "tr"
  | "ja" | "zh" | "th"
  | "hi" | "gu" | "pa" | "bn" | "ta"
  | "ar" | "ur";

export type Lang = { code: LandingLanguage; native: string; label: string; dir: "ltr" | "rtl" };

// §6.1 language dropdown order = alphabetical by English name.
export const LANGUAGES: Lang[] = [
  { code: "ar",    native: "العربية",     label: "Arabic",      dir: "rtl" },
  { code: "bn",    native: "বাংলা",       label: "Bengali",     dir: "ltr" },
  { code: "zh",    native: "中文",         label: "Chinese",     dir: "ltr" },
  { code: "en",    native: "English",     label: "English (UK)", dir: "ltr" },
  { code: "en-us", native: "English (US)", label: "English (US)", dir: "ltr" },
  { code: "fr",    native: "Français",    label: "French",      dir: "ltr" },
  { code: "de",    native: "Deutsch",     label: "German",      dir: "ltr" },
  { code: "gu",    native: "ગુજરાતી",    label: "Gujarati",    dir: "ltr" },
  { code: "hi",    native: "हिन्दी",      label: "Hindi",       dir: "ltr" },
  { code: "it",    native: "Italiano",    label: "Italian",     dir: "ltr" },
  { code: "ja",    native: "日本語",       label: "Japanese",    dir: "ltr" },
  { code: "pl",    native: "Polski",      label: "Polish",      dir: "ltr" },
  { code: "pa",    native: "ਪੰਜਾਬੀ",     label: "Punjabi",     dir: "ltr" },
  { code: "es",    native: "Español",     label: "Spanish",     dir: "ltr" },
  { code: "ta",    native: "தமிழ்",      label: "Tamil",       dir: "ltr" },
  { code: "th",    native: "ไทย",         label: "Thai",        dir: "ltr" },
  { code: "tr",    native: "Türkçe",      label: "Turkish",     dir: "ltr" },
  { code: "ur",    native: "اردو",        label: "Urdu",        dir: "rtl" },
];

export interface LandingCopy {
  // Nav (§6.1)
  navPlatform: string;
  navModules: string;
  // navUseCases removed in v3.1 — top-nav now has Platform + Modules
  // dropdowns (sourced from PLATFORM_INFO / MODULE_INFO) and a Portal link.
  // Use Cases will return when the content for that section is ready.
  navPortal: string;
  navLangButton: string;
  navRegister: string;
  navBookDemo: string;
  navLogin: string;

  // Trust strip (§6.2)
  trustLeft: string;
  trustRight: string;

  // Hero (§6.3)
  heroEyebrow: string;
  heroL1: string;
  heroL2: string;
  heroSub: string;
  heroCtaPrimary: string;
  heroCtaGhost: string;
  heroPillAllergens: string;
  heroPillHfss: string;
  heroPillIngredients: string;
  heroVerified: string;

  // Stats ticker (§6.4) — labels only; values are numeric and language-neutral
  statStores: string;
  statLanguages: string;
  statGames: string;
  statModules: string;
  statSteps: string;
  statDownloads: string;
  statGeofence: string;
  statScans: string;

  // Full Journey infographic (§6.5)
  fullJourneyEyebrow: string;
  fullJourneyHeading: string;
  fullJourneySub: string;
  fullJourneyCaption: string;

  // Pic-n-Mix grid (§6.6)
  picEyebrow: string;
  picHeading: string;
  picSub: string;
  learnDesc: string; learnF1: string; learnF2: string; learnF3: string;
  playDesc: string; playF1: string; playF2: string; playF3: string; playF4: string; playF5: string;
  sayDesc: string; sayF1: string; sayF2: string; sayF3: string;
  winDesc: string; winF1: string; winF2: string; winF3: string;
  earnDesc: string; earnF1: string; earnF2: string; earnF3: string;
  moduleSeeMore: string;

  // One engine (§6.7)
  oeEyebrow: string;
  oeHeading: string;
  oeSub: string;
  oe1Title: string; oe1Strap: string;
  oe2Title: string; oe2Strap: string;
  oe3Title: string; oe3Strap: string;
  oe4Title: string; oe4Strap: string;
  oeActiveLabel: string;

  // Use cases (§6.8) — pharma folded into Brand column
  ucHeading: string;
  ucBrandTitle: string;
  ucBrand1: string; ucBrand2: string; ucBrand3: string; ucBrand4: string; ucBrand5: string;
  ucRetailerTitle: string;
  ucRetailer1: string; ucRetailer2: string; ucRetailer3: string; ucRetailer4: string;
  ucAgencyTitle: string;
  ucAgency1: string; ucAgency2: string; ucAgency3: string; ucAgency4: string;

  // HFSS (§6.9)
  hfssEyebrow: string;
  hfssHeading: string;
  hfssSub: string;
  hfss1Title: string; hfss1Body: string;
  hfss2Title: string; hfss2Body: string;
  hfss3Title: string; hfss3Body: string;
  hfss4Title: string; hfss4Body: string;
  hfssBadge: string;

  // White-label / languages (§6.10)
  wlEyebrow: string;
  wlHeading: string;
  wlLangCaption: string;
  wlFeat1Title: string; wlFeat1Body: string;
  wlFeat2Title: string; wlFeat2Body: string;
  wlFeat3Title: string; wlFeat3Body: string;
  wlFeat4Title: string; wlFeat4Body: string;

  // Proof (§6.12) — pricing section (§6.11) omitted per Ilann
  proofEyebrow: string;
  proofHeading: string;
  proofStat1Value: string; proofStat1Label: string;
  proofStat2Value: string; proofStat2Label: string;
  proofStat3Value: string; proofStat3Label: string;
  proofQuotePlaceholder: string;

  // FAQ (§6.13)
  faqHeading: string;
  faq1Q: string; faq1A: string;
  faq2Q: string; faq2A: string;
  faq3Q: string; faq3A: string;
  faq4Q: string; faq4A: string;
  faq5Q: string; faq5A: string;
  faq6Q: string; faq6A: string;
  faq7Q: string; faq7A: string;

  // Footer (§6.14)
  // Platform / Modules link labels are now sourced from landing-info-content.ts
  // (PLATFORM_INFO + MODULE_INFO) so the footer-link text and the modal-body
  // title can never go out of sync. Company column is just About + Contact —
  // Careers / Press were removed in v3 (parent shopai.website covers
  // recruiting + press, no need to duplicate here).
  footerColPlatform: string;
  footerColModules: string;
  footerColCompany: string;
  footerColResources: string;
  footerCompany1: string; // "About"
  footerCompany2: string; // "Contact"
  footerResources3: string; footerResources4: string;
  footerNewsHeading: string;
  footerNewsPlaceholder: string;
  footerNewsSubmit: string;
  footerRegistration: string;
  footerCopyright: string;

  // ────────── Tier 2 (chrome + phone demo) keys added 2026-05-05 ──────────
  // Until per-language overrides land, every non-EN variant inherits these
  // via `...en` spread. Fill in per-language overrides as translations come
  // back from translators / linguistic QA.

  // Hero image alt
  heroAlt: string;

  // Three-step workflow (§ ThreeStepWorkflow)
  stepsTitle: string;
  stepsSub: string;
  step1Title: string; step1Desc: string; step1PillRow: string;
  step2Title: string; step2Desc: string; step2PillRow: string;
  step3Title: string; step3Desc: string; step3PillRow: string;
  // Satellite chip labels orbiting each circle. Distinct keys even where
  // the EN value happens to repeat — keeps each chip independently tunable
  // per language (e.g. CapEmail vs ChipEmail can take different wording).
  chipWebsite: string; chipSocial: string; chipEmail: string;
  chipQr: string; chipOoh: string; chipInStore: string;
  chipLearn: string; chipPlay: string; chipSay: string;
  chipWin: string; chipEarn: string;
  chipShoppers: string; chipConversation: string; chipLocation: string;
  chipEngagement: string; chipCompliance: string;
  // Aria-label prefixes for chip groups
  chipAriaEntry: string; chipAriaModule: string; chipAriaIntel: string;

  // Integrations strip
  intgTitle: string;
  intgSub: string;
  intgCap1: string; intgCap2: string; intgCap3: string; intgCap4: string;
  intgCap5: string; intgCap6: string; intgCap7: string;
  intgFootnote: string;

  // Full Journey — column headers + content
  jColEntry: string; jColExperience: string; jColDashboard: string;
  jEntryDigitalTitle: string; jEntryDigitalDesc: string;
  jEntrySocialTitle: string;  jEntrySocialDesc: string;
  jEntryPhysicalTitle: string; jEntryPhysicalDesc: string;
  jEntryPhysPill1: string; jEntryPhysPill2: string; jEntryPhysPill3: string;
  jExpVerifyLabel: string;  jExpVerifyText: string;
  jExpLearnLabel: string;   jExpLearnBody: string;
  jExpEngageLabel: string;
  jExpEngagePlayBody: string; jExpEngageEarnBody: string; jExpEngageSayBody: string;
  jExpRewardLabel: string;  jExpRewardBody: string;
  jExpCaptureLabel: string; jExpCaptureSub: string;

  // Capture grid chips
  capEmail: string; capName: string; capAddress: string; capGps: string;
  capProduct: string; capBasket: string; capSpend: string; capChat: string;

  // Dashboard cycle — 5 sections × labels + 4-5 tiles each
  dashShopperLabel: string; dashShopperShort: string;
  dashShopper1Title: string; dashShopper1Desc: string;
  dashShopper2Title: string; dashShopper2Desc: string;
  dashShopper3Title: string; dashShopper3Desc: string;
  dashShopper4Title: string; dashShopper4Desc: string;

  dashConvLabel: string; dashConvShort: string;
  dashConv1Title: string; dashConv1Desc: string;
  dashConv2Title: string; dashConv2Desc: string;
  dashConv3Title: string; dashConv3Desc: string;
  dashConv4Title: string; dashConv4Desc: string;
  dashConv5Title: string; dashConv5Desc: string;

  dashDistLabel: string; dashDistShort: string;
  dashDist1Title: string; dashDist1Desc: string;
  dashDist2Title: string; dashDist2Desc: string;
  dashDist3Title: string; dashDist3Desc: string;
  dashDist4Title: string; dashDist4Desc: string;
  dashDist5Title: string; dashDist5Desc: string;

  dashCampLabel: string; dashCampShort: string;
  dashCamp1Title: string; dashCamp1Desc: string;
  dashCamp2Title: string; dashCamp2Desc: string;
  dashCamp3Title: string; dashCamp3Desc: string;
  dashCamp4Title: string; dashCamp4Desc: string;

  dashCompLabel: string; dashCompShort: string;
  dashComp1Title: string; dashComp1Desc: string;
  dashComp2Title: string; dashComp2Desc: string;
  dashComp3Title: string; dashComp3Desc: string;
  dashComp4Title: string; dashComp4Desc: string;

  // Phone mockups (PhoneViewForm / Chat / Map / Wheel / Nutrition)
  pvFormTitle: string;  pvFormSub: string;
  pvFormName: string;   pvFormEmail: string;  pvFormPostcode: string;
  pvFormCta: string;    pvFormFootnote: string;

  pvChatTitle: string;  pvChatSub: string;
  pvChatBubble1: string; pvChatBubble2: string;
  pvChatBubble3: string; pvChatBubble4: string;
  pvChatPlaceholder: string; pvChatFootnote: string;

  pvMapTitle: string;   pvMapPin: string;     pvMapCta: string;
  pvMapFootnote: string; pvMapOos: string;

  pvWheelTitle: string; pvWheelCredits: string; pvWheelCta: string;
  pvWheelFootnote: string;
  pvWheelWedgeWin: string; pvWheelWedgeTry: string;

  pvNutTitle: string;   pvNutPer100g: string; pvNutPerServing: string;
  pvNutCta: string;     pvNutFootnote: string;

  // Book-a-demo mailto subject + body
  bookDemoSubject: string;
  bookDemoBody: string;

  // ────────── Viz mock-data labels (animated dashboard tiles) ──────────
  // Captions sit beneath each viz; labels appear inside the animated chart.

  // VizQuestionDonut
  vizCapQuestions: string;
  vizQAllergens: string; vizQIngredients: string; vizQNutrition: string;
  vizQRecipes: string;   vizQUsage: string;

  // VizSentimentBar
  vizCapSentiment: string;
  vizSentPositive: string; vizSentNeutral: string; vizSentNegative: string;

  // VizConfusionBubbles
  vizCapConfusion: string;
  vizConfusion1: string; vizConfusion2: string; vizConfusion3: string;

  // VizPurchaseFunnel
  vizCapFunnel: string;
  vizFunnelCurious: string; vizFunnelInterested: string; vizFunnelReady: string;

  // VizStoreAvailability
  vizCapAvail: string;
  vizAvailDetail: string;

  // VizOOSAlerts
  vizCapOos: string;

  // VizHeatmap
  vizCapHeat: string;

  // VizHouseholdGrid
  vizCapHousehold: string;

  // VizFasciaBars
  vizCapFascia: string;

  // VizEngagementRate
  vizCapEngrate: string;
  vizEngrateCompare: string;

  // VizGameFunnel
  vizCapGame: string;
  vizGameStart: string; vizGamePlay: string; vizGameWin: string; vizGameClaim: string;

  // VizRedemption
  vizCapRdm: string;
  vizRdmClaimed: string; vizRdmValidated: string; vizRdmExpired: string;

  // VizROIDashboard
  vizCapRoi: string;
  vizRoiSpend: string; vizRoiValueLabel: string; vizRoiReturn: string;

  // VizComplianceScore
  vizCapComp: string;

  // VizAuditLogs
  vizCapAudit: string;

  // VizViolationZero
  vizCapViolation: string;

  // VizNutritionBars + PhoneViewNutrition slices
  vizCapNut: string;
  vizNutSugar: string; vizNutFat: string; vizNutSalt: string; vizNutSatFat: string;

  // VizLangPills
  vizCapLang: string;
  vizLangOther: string;

  // VizCRMCounter
  vizCapCrm: string;

  // VizUKMap
  vizCapMap: string;

  // VizWeeklyBars (Mon–Sun + caption)
  vizCapWeek: string;
  vizDayMon: string; vizDayTue: string; vizDayWed: string;
  vizDayThu: string; vizDayFri: string; vizDaySat: string; vizDaySun: string;

  // VizBasketStack
  vizCapBasket: string;
  vizBasketDairy: string; vizBasketBakery: string; vizBasketSnacks: string; vizBasketOther: string;
}

// ────────── English (UK) — master ──────────
const en: LandingCopy = {
  // Nav
  navPlatform: "Platform",
  navModules: "Modules",
  navPortal: "Portal Login",
  navLangButton: "EN",
  navRegister: "Register",
  navBookDemo: "Book a demo",
  navLogin: "Log in",

  // Trust strip
  trustLeft: "No app · No integration · No store dependency",
  trustRight: "Since 2024 · UK · Building with The Wholesale Group · Caterers Choice · Passot (Japan) · Talysis UK · Jakavarna Group (Thailand)",

  // Hero
  heroEyebrow: "A co-pilot for commerce",
  heroL1: "Every product. A conversation.",
  heroL2: "Every scan. Real data.",
  heroSub: "TalkPack turns any product into a live conversation. Shoppers scan, learn, play, ask, win, earn. You get first-party data, HFSS compliance, and a direct relationship with shoppers in physical retail. No app. No integration. No store dependency.",
  heroCtaPrimary: "Book a demo",
  heroCtaGhost: "See how it works",
  heroPillAllergens: "Allergens",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Ingredients",
  heroVerified: "Verified",

  // Stats ticker
  statStores: "Stores · UK · expanding globally",
  statLanguages: "Languages · auto-detected",
  statGames: "From brief to live campaign",
  statModules: "Nutrition wheel built in",
  statSteps: "Your brand. Your data. Your AI.",
  statDownloads: "App downloads · integrations · friction",
  statGeofence: "Shopper data from physical retail",
  statScans: "From shelf to shopper CRM",

  // Full Journey infographic
  fullJourneyEyebrow: "The full journey",
  fullJourneyHeading: "Entry to intelligence. One platform.",
  fullJourneySub: "Every channel. Every module. Every data point. Here's how the full TalkPack architecture connects end-to-end.",
  fullJourneyCaption: "Full flow. From shopper entry to brand intelligence. Exportable via dashboard, AI chat, or SmartMail queries.",

  // Pic-n-Mix grid
  picEyebrow: "Pic-n-Mix",
  picHeading: "One platform. Five ways to engage a shopper.",
  picSub: "Brands pick the modules they need. Shoppers get a mix of experiences. Every module runs through the same scan, the same conversation, the same data layer.",
  learnDesc: "Product education, ingredient transparency, HFSS traffic lights.",
  learnF1: "Nutrition wheel", learnF2: "Allergen scan", learnF3: "Provenance data",
  playDesc: "Five game types that drive dwell time and repeat engagement.",
  playF1: "Spin wheel", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Nutrition spin",
  sayDesc: "Conversational AI that answers shopper questions in their own language.",
  sayF1: "40+ languages", sayF2: "Ingredient Q&A", sayF3: "Recipe suggestions",
  winDesc: "Prizes, directed footfall, dual-QR till validation, packing slips.",
  winF1: "Instant reward", winF2: "Geofenced prizes", winF3: "Home delivery",
  earnDesc: "Points, receipt scanning, loyalty programme, basket intelligence.",
  earnF1: "Receipt OCR", earnF2: "Cross-brand profile", earnF3: "Reward catalogue",
  moduleSeeMore: "See module",

  // One engine
  oeEyebrow: "One engine · Many dynamics",
  oeHeading: "TalkPack sits inside a bigger platform.",
  oeSub: "ShopAI gives brands, retailers, and wholesalers one data layer. Conversational business intelligence end-to-end. TalkPack is how the shopper joins the conversation.",
  oe1Title: "Pic-n-Mix",          oe1Strap: "Shopper engagement from physical retail",
  oe2Title: "Retailer Companion", oe2Strap: "AI for the convenience sector",
  oe3Title: "Shelf Compliance",   oe3Strap: "Field audit intelligence",
  oe4Title: "Loyalty & Rewards",  oe4Strap: "Points, receipts, basket data",
  oeActiveLabel: "Current",

  // Use cases — pharma folded into Brand column
  ucHeading: "Different roles. Same platform.",
  ucBrandTitle: "Brand marketer",
  ucBrand1: "National product launch across mixed retail",
  ucBrand2: "Directed footfall to participating stores",
  ucBrand3: "HFSS-compliant on-pack engagement",
  ucBrand4: "Cross-brand shopper profiling",
  ucBrand5: "Regulated categories (pharma, alcohol) with audit-logged AI responses",
  ucRetailerTitle: "Retailer / wholesaler",
  ucRetailer1: "Exclusive retailer-funded promotions",
  ucRetailer2: "Symbol group activation",
  ucRetailer3: "Shelf compliance auditing",
  ucRetailer4: "Retailer-specific loyalty",
  ucAgencyTitle: "Agency / reseller",
  ucAgency1: "White-label campaigns for client brands",
  ucAgency2: "Multi-client portal access",
  ucAgency3: "Trade marketing PDF reports",
  ucAgency4: "Social content auto-generated",

  // HFSS
  hfssEyebrow: "Built for HFSS",
  hfssHeading: "HFSS compliance, built in.",
  hfssSub: "The shopper sees the nutritional profile before they play. Fat, sugar, salt, saturated fat — traffic-lighted against daily reference intake. Mandatory. Audit-ready. Four layers of regulatory defence.",
  hfss1Title: "Mandatory nutrition wheel.",
  hfss1Body: "Every HFSS product shows traffic-light fat, sugar, salt, and saturated fat against daily reference intake before the shopper can proceed.",
  hfss2Title: "Educational-first framing.",
  hfss2Body: "The platform promotes nutritional transparency. It positions shopper interactions as informed choice, not promotional push.",
  hfss3Title: "Output validation.",
  hfss3Body: "The conversational AI is monitored for regulatory patterns. Every response is logged, filtered, and auditable.",
  hfss4Title: "Exportable audit logs.",
  hfss4Body: "ASA compliance score, every response logged and retrievable, violation alerts for flagged content.",
  hfssBadge: "ASA compliance: 98.4%",

  // White-label / languages
  wlEyebrow: "Your brand, in any language",
  wlHeading: "Your data. Your AI. Your way.",
  wlLangCaption: "40+ languages. Auto-detected or manually set per campaign.",
  wlFeat1Title: "Your brand identity",
  wlFeat1Body: "Logo, colours, fonts applied to every shopper screen and packing slip.",
  wlFeat2Title: "Your data, your AI",
  wlFeat2Body: "Data stored against your account. AI trained on your products. You own the relationship.",
  wlFeat3Title: "Your stores, your geofence",
  wlFeat3Body: "Universal mode or store-specific. 150m default geofence, configurable per campaign.",
  wlFeat4Title: "Your team, your access",
  wlFeat4Body: "Role-based permissions. Brand, agency, field, finance — each with the right view.",

  // Proof
  proofEyebrow: "In deployment",
  proofHeading: "Already live. Already working.",
  proofStat1Value: "22,000+", proofStat1Label: "UK stores in campaign database",
  proofStat2Value: "40+",     proofStat2Label: "Languages supported",
  proofStat3Value: "2024",    proofStat3Label: "Founded in the UK",
  proofQuotePlaceholder: "Customer stories coming soon.",

  // FAQ (pricing question removed to match §6.11 skip)
  faqHeading: "Questions brands ask us.",
  faq1Q: "Do shoppers need to download an app?",
  faq1A: "No. TalkPack runs in the browser. One scan opens the experience.",
  faq2Q: "Does TalkPack integrate with our EPOS or CRM?",
  faq2A: "No integration required. Data captured is first-party and exportable via PDF, CSV, or API.",
  faq3Q: "What happens to HFSS products?",
  faq3A: "Every HFSS product shows a mandatory nutrition wheel before the game. Compliance is built in, not bolted on.",
  faq4Q: "What languages do you support?",
  faq4A: "40+. The platform auto-detects or you set it per campaign.",
  faq5Q: "Can we run a campaign in specific retailers only?",
  faq5A: "Yes. Store-specific mode geofences the campaign to the stores you select. Universal mode lets any shopper play anywhere.",
  faq6Q: "How long does it take to build a campaign?",
  faq6A: "About 10 minutes for a standard promotion. Step-by-step wizard in the portal.",
  faq7Q: "Can we white-label the experience?",
  faq7A: "Yes. Your logo, your colours, your language. Shoppers never see TalkPack branding unless you want them to.",

  // Footer
  footerColPlatform: "Platform",
  footerColModules: "Modules",
  footerColCompany: "Company",
  footerColResources: "Resources",
  footerCompany1: "About",
  footerCompany2: "Contact",
  footerResources3: "Privacy",
  footerResources4: "Terms",
  footerNewsHeading: "Quarterly updates. No spam.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "Subscribe",
  footerRegistration: "ShopAI Ltd · Company No. 15877093 · Registered in England",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 master strings (EN) ──

  heroAlt: "Shopper in a supermarket aisle — every touchpoint of the TalkPack experience orbiting around her",

  // Three-step workflow
  stepsTitle: "Three steps. One platform.",
  stepsSub: "From the moment a shopper enters to the moment you learn something about them. Simple to run. Rich in data.",
  step1Title: "Enter",
  step1Desc: "Shopper taps or scans from any channel. One entry, one branded experience.",
  step1PillRow: "Website · Social · Email · QR · OOH · In-store",
  step2Title: "Engage",
  step2Desc: "Five Pic-n-Mix modules run in their language, in your brand, verified by AI.",
  step2PillRow: "Learn · Play · Say · Win · Earn",
  step3Title: "Learn",
  step3Desc: "Every interaction becomes first-party data, streaming to your dashboard in real time.",
  step3PillRow: "Shoppers · Conversation · Location · Engagement · Compliance",

  chipWebsite: "Website",
  chipSocial:  "Social",
  chipEmail:   "Email",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "In-store",
  chipLearn:   "Learn",
  chipPlay:    "Play",
  chipSay:     "Say",
  chipWin:     "Win",
  chipEarn:    "Earn",
  chipShoppers:     "Shoppers",
  chipConversation: "Conversation",
  chipLocation:     "Location",
  chipEngagement:   "Engagement",
  chipCompliance:   "Compliance",
  chipAriaEntry:  "Entry channel",
  chipAriaModule: "Pic-n-Mix module",
  chipAriaIntel:  "Intelligence category",

  // Integrations strip
  intgTitle: "Serious infrastructure. Proven technology.",
  intgSub: "TalkPack runs on the same cloud, AI, and compliance foundations trusted by the world's largest companies.",
  intgCap1: "Parent platform",
  intgCap2: "Cloud infrastructure",
  intgCap3: "Conversational AI",
  intgCap4: "Language models",
  intgCap5: "AI and mapping",
  intgCap6: "Nutrition data",
  intgCap7: "Payments",
  intgFootnote: "ISO 27001 certified providers · GDPR compliant · Multi-region data hosting · Enterprise-grade security from day one",

  // Full Journey — column headers + content
  jColEntry: "Entry",
  jColExperience: "Experience",
  jColDashboard: "Dashboard Intelligence",
  jEntryDigitalTitle: "Digital",
  jEntryDigitalDesc: "Website, app stores, search, direct links — one tap from any digital surface into the shopper experience.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Sponsored posts, influencer content, DMs — feeds flow straight into TalkPack without leaving the platform.",
  jEntryPhysicalTitle: "Physical",
  jEntryPhysicalDesc: "On-pack, on-shelf, at POS — the real-world entry points where TalkPack turns a scan into a conversation.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "In-Store POS",
  jEntryPhysPill3: "Packaging",
  jExpVerifyLabel: "Verify",
  jExpVerifyText: "AI Product Recognition — SKU verified in real time.",
  jExpLearnLabel: "Learn",
  jExpLearnBody: "Nutrition wheel, allergens, ingredients, provenance — shown in the shopper's language, verified by the brand.",
  jExpEngageLabel: "Engage",
  jExpEngagePlayBody: "Spin, gacha, puzzle, quiz — five game types for dwell time.",
  jExpEngageEarnBody: "Points, receipts, loyalty — basket-level intelligence.",
  jExpEngageSayBody: "Conversational AI answering in 40+ languages.",
  jExpRewardLabel: "Reward",
  jExpRewardBody: "Instant prizes, dual-QR till validation, GPS-verified claims — audit-ready from day one.",
  jExpCaptureLabel: "Capture",
  jExpCaptureSub: "Every interaction · captured and owned by you",

  // Capture grid chips
  capEmail:   "Email",
  capName:    "Name",
  capAddress: "Address",
  capGps:     "GPS",
  capProduct: "Product",
  capBasket:  "Basket",
  capSpend:   "Spend",
  capChat:    "Chat",

  // Dashboard cycle — Shopper Intelligence
  dashShopperLabel: "Shopper Intelligence",
  dashShopperShort: "Shopper",
  dashShopper1Title: "First-Party CRM",
  dashShopper1Desc: "Email, name, address, contact preferences.",
  dashShopper2Title: "GPS-verified locations",
  dashShopper2Desc: "Where shoppers engage by store regions.",
  dashShopper3Title: "Purchase frequency",
  dashShopper3Desc: "Repeat scan patterns, visit cadence.",
  dashShopper4Title: "Basket composition",
  dashShopper4Desc: "Item-level receipt data, co-purchased products.",

  // Conversation Intelligence
  dashConvLabel: "Conversation Intelligence",
  dashConvShort: "Conversation",
  dashConv1Title: "Question categories",
  dashConv1Desc: "Allergens, ingredients, nutrition, usage, recipes.",
  dashConv2Title: "Sentiment analysis",
  dashConv2Desc: "Positive, negative, neutral by product and region.",
  dashConv3Title: "Confusion points",
  dashConv3Desc: "What shoppers don't understand, repeated questions.",
  dashConv4Title: "Purchase intent",
  dashConv4Desc: "Progression from curiosity to retailer requests.",
  dashConv5Title: "Language preferences",
  dashConv5Desc: "Which languages shoppers choose by location.",

  // Distribution Intelligence
  dashDistLabel: "Distribution Intelligence",
  dashDistShort: "Distribution",
  dashDist1Title: "Store availability",
  dashDist1Desc: "Product on shelf, confirmed by geofenced scans.",
  dashDist2Title: "Out-of-stock alerts",
  dashDist2Desc: "'Not here' reports flagged in real time.",
  dashDist3Title: "Heatmaps",
  dashDist3Desc: "Scan density by postcode, region, fascia.",
  dashDist4Title: "Household penetration",
  dashDist4Desc: "'Scan anywhere' shows where product lives at home.",
  dashDist5Title: "Store comparison",
  dashDist5Desc: "Engagement rates by location, by fascia group.",

  // Campaign Performance
  dashCampLabel: "Campaign Performance",
  dashCampShort: "Campaign",
  dashCamp1Title: "Scan-to-engagement rate",
  dashCamp1Desc: "How many scans convert to full interactions.",
  dashCamp2Title: "Game completion rates",
  dashCamp2Desc: "Spins per session, win rates, drop-off points.",
  dashCamp3Title: "Redemption tracking",
  dashCamp3Desc: "Prizes claimed, validated, expired, by store.",
  dashCamp4Title: "ROI dashboard",
  dashCamp4Desc: "Campaign spend vs shopper value captured.",

  // Compliance & Audit
  dashCompLabel: "Compliance & Audit",
  dashCompShort: "Compliance",
  dashComp1Title: "ASA compliance score",
  dashComp1Desc: "Per campaign, per conversation.",
  dashComp2Title: "Exportable audit logs",
  dashComp2Desc: "Every AI response logged and retrievable.",
  dashComp3Title: "Violation alerts",
  dashComp3Desc: "Flagged content patterns.",
  dashComp4Title: "Nutrition engagement",
  dashComp4Desc: "Which nutrients shoppers interact with on the wheel.",

  // Phone — Form (prize claim)
  pvFormTitle: "Congratulations!",
  pvFormSub: "You've won: 20% off your next purchase",
  pvFormName: "Your name",
  pvFormEmail: "Your email",
  pvFormPostcode: "Postcode",
  pvFormCta: "CLAIM PRIZE",
  pvFormFootnote: "Privacy-first · GDPR compliant",

  // Phone — Chat
  pvChatTitle: "AI Ambassador",
  pvChatSub: "Trained on Morning O's",
  pvChatBubble1: "Is this gluten-free?",
  pvChatBubble2: "Yes — certified gluten-free and only 9g sugar per serving. Want recipe ideas?",
  pvChatBubble3: "Yes please",
  pvChatBubble4: "Try overnight oats with berries — 3 min prep.",
  pvChatPlaceholder: "Ask me anything...",
  pvChatFootnote: "40+ languages · always on",

  // Phone — Map
  pvMapTitle: "Find Morning O's near you",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "GET DIRECTIONS",
  pvMapFootnote: "GPS-verified · geofenced within 150m",
  pvMapOos: "out of stock",

  // Phone — Spin Wheel
  pvWheelTitle: "Morning O's · Spin to Win",
  pvWheelCredits: "350 credits",
  pvWheelCta: "SPIN",
  pvWheelFootnote: "Spin wheel · Nutrition spin · Gacha · Quiz · Puzzle",
  pvWheelWedgeWin: "WIN!",
  pvWheelWedgeTry: "Try",

  // Phone — Nutrition wheel
  pvNutTitle: "Morning O's · Nutrition Information",
  pvNutPer100g: "Per 100g",
  pvNutPerServing: "Per serving",
  pvNutCta: "CONTINUE",
  pvNutFootnote: "ASA compliant · mandatory before play",

  // Book-a-demo mailto
  bookDemoSubject: "TalkPack demo request",
  bookDemoBody: "Hi ShopAI team,\n\nI'd like to book a TalkPack demo. Please reach out to arrange a time.\n\nThanks,",

  // ── Viz mock-data labels (EN master) ──
  vizCapQuestions: "Top shopper questions this week",
  vizQAllergens: "Allergens", vizQIngredients: "Ingredients", vizQNutrition: "Nutrition",
  vizQRecipes: "Recipes", vizQUsage: "Usage",

  vizCapSentiment: "Last 30 days across all products",
  vizSentPositive: "Positive", vizSentNeutral: "Neutral", vizSentNegative: "Negative",

  vizCapConfusion: "Top repeated questions — content gaps",
  vizConfusion1: "Is this gluten-free?",
  vizConfusion2: "Suitable for vegans?",
  vizConfusion3: "Any nut warnings?",

  vizCapFunnel: "Conversion through conversation",
  vizFunnelCurious: "Curious", vizFunnelInterested: "Interested", vizFunnelReady: "Ready to buy",

  vizCapAvail: "Product available where you planned",
  vizAvailDetail: "1,247 of 1,326 stores confirmed on-shelf",

  vizCapOos: "3 stores flagged today · auto-alert sent",

  vizCapHeat: "London · Manchester · Birmingham leading",

  vizCapHousehold: "68% of scans happen in-home — your product lives there",

  vizCapFascia: "Engagement rate by retailer fascia",

  vizCapEngrate: "Scans that become full interactions",
  vizEngrateCompare: "+36pts vs 42% industry benchmark",

  vizCapGame: "Where shoppers drop off — tune game balance",
  vizGameStart: "Start", vizGamePlay: "Play", vizGameWin: "Win", vizGameClaim: "Claim",

  vizCapRdm: "96% validation rate · low expiry",
  vizRdmClaimed: "Claimed", vizRdmValidated: "Validated", vizRdmExpired: "Expired",

  vizCapRoi: "Campaign spend vs shopper lifetime value",
  vizRoiSpend: "Spend", vizRoiValueLabel: "Value captured", vizRoiReturn: "3.4× return",

  vizCapComp: "Per campaign · per conversation · audit-ready",

  vizCapAudit: "Every AI response logged · retrievable on demand",

  vizCapViolation: "Violations this month · AI output continuously validated",

  vizCapNut: "Nutrients shoppers tap on the wheel",
  vizNutSugar: "Sugar", vizNutFat: "Fat", vizNutSalt: "Salt", vizNutSatFat: "Sat Fat",

  vizCapLang: "Shopper language auto-detected",
  vizLangOther: "Other",

  vizCapCrm: "shoppers captured",

  vizCapMap: "247 locations this week",

  vizCapWeek: "Avg 2.3 visits per week",
  vizDayMon: "Mon", vizDayTue: "Tue", vizDayWed: "Wed",
  vizDayThu: "Thu", vizDayFri: "Fri", vizDaySat: "Sat", vizDaySun: "Sun",

  vizCapBasket: "Avg basket: £8.40 · 6.2 items",
  vizBasketDairy: "Dairy", vizBasketBakery: "Bakery", vizBasketSnacks: "Snacks", vizBasketOther: "Other",
};

// English (US) — same copy, minor US-spelling touches
const enUs: LandingCopy = {
  ...en,
  navLangButton: "EN-US",
  trustLeft: "No app · No integration · No store dependency",
  heroEyebrow: "A co-pilot for commerce",
  heroSub: en.heroSub.replace("capturing", "capturing"),
  // Keep identical content otherwise; US/UK variations can be tuned later.

  // Tier 2 — only key with a real UK→US split is the prize-form field label.
  pvFormPostcode: "ZIP code",
};

// ────────── Français ──────────
const fr: LandingCopy = {
  ...en,
  navPlatform: "Plateforme",
  navModules: "Modules",
  navPortal: "Connexion Portail",
  navLangButton: "FR",
  navRegister: "S'inscrire",
  navBookDemo: "Réserver une démo",
  navLogin: "Se connecter",

  trustLeft: "Sans app · Sans intégration · Sans dépendance magasin",
  trustRight: "Depuis 2024 · Royaume-Uni · Avec The Wholesale Group · Caterers Choice · Passot (Japon) · Talysis UK · Jakavarna Group (Thaïlande)",

  heroEyebrow: "Un copilote pour le commerce",
  heroL1: "Chaque produit. Une conversation.",
  heroL2: "Chaque scan. Des données réelles.",
  heroSub: "TalkPack transforme tout produit en conversation en direct. Les shoppers scannent, apprennent, jouent, posent des questions, gagnent, accumulent. Vous obtenez des données first-party, la conformité HFSS et une relation directe avec les shoppers en magasin physique. Sans app. Sans intégration. Sans dépendance magasin.",
  heroCtaPrimary: "Réserver une démo",
  heroCtaGhost: "Voir comment ça marche",
  heroPillAllergens: "Allergènes",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Ingrédients",
  heroVerified: "Vérifié",

  statStores: "Magasins · Royaume-Uni · expansion mondiale",
  statLanguages: "Langues · détection automatique",
  statGames: "Du brief à la campagne en ligne",
  statModules: "Roue nutritionnelle intégrée",
  statSteps: "Votre marque. Vos données. Votre IA.",
  statDownloads: "Téléchargements d'app · intégrations · friction",
  statGeofence: "Données shopper depuis le magasin physique",
  statScans: "Du linéaire au CRM shopper",

  fullJourneyEyebrow: "Le parcours complet",
  fullJourneyHeading: "De l'entrée à l'intelligence. Une plateforme.",
  fullJourneySub: "Chaque canal. Chaque module. Chaque donnée. Voici comment l'architecture TalkPack se connecte de bout en bout.",
  fullJourneyCaption: "Flux complet. De l'entrée du shopper à l'intelligence marque. Exportable via dashboard, chat IA ou requêtes SmartMail.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Une plateforme. Cinq façons d'engager un shopper.",
  picSub: "Les marques choisissent les modules dont elles ont besoin. Les shoppers vivent un mix d'expériences. Chaque module passe par le même scan, la même conversation, la même couche de données.",
  learnDesc: "Éducation produit, transparence des ingrédients, feux tricolores HFSS.",
  learnF1: "Roue nutritionnelle", learnF2: "Scan d'allergènes", learnF3: "Données de provenance",
  playDesc: "Cinq types de jeux qui favorisent le temps passé et le réengagement.",
  playF1: "Roue de la fortune", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Roue nutritionnelle",
  sayDesc: "IA conversationnelle qui répond aux questions des shoppers dans leur propre langue.",
  sayF1: "40+ langues", sayF2: "Q&R sur les ingrédients", sayF3: "Suggestions de recettes",
  winDesc: "Lots, drive-to-store, validation dual-QR en caisse, bons de livraison.",
  winF1: "Récompense instantanée", winF2: "Lots géolocalisés", winF3: "Livraison à domicile",
  earnDesc: "Points, scan de tickets, programme de fidélité, intelligence panier.",
  earnF1: "OCR ticket", earnF2: "Profil multi-marques", earnF3: "Catalogue de récompenses",
  moduleSeeMore: "Voir le module",

  oeEyebrow: "Un moteur · Plusieurs dynamiques",
  oeHeading: "TalkPack s'intègre dans une plateforme plus large.",
  oeSub: "ShopAI offre aux marques, retailers et grossistes une couche de données unique. Une intelligence d'affaires conversationnelle de bout en bout. TalkPack est la façon dont le shopper rejoint la conversation.",
  oe1Strap: "Engagement shopper en magasin physique",
  oe2Strap: "IA pour le secteur de la proximité",
  oe3Strap: "Intelligence d'audit terrain",
  oe4Strap: "Points, tickets, données panier",
  oeActiveLabel: "Actuel",

  ucHeading: "Différents rôles. Même plateforme.",
  ucBrandTitle: "Marketing marque",
  ucBrand1: "Lancement national de produit dans la distribution mixte",
  ucBrand2: "Drive-to-store vers les magasins participants",
  ucBrand3: "Engagement on-pack conforme HFSS",
  ucBrand4: "Profilage shopper multi-marques",
  ucBrand5: "Catégories réglementées (pharma, alcool) avec réponses IA auditées",
  ucRetailerTitle: "Retailer / Grossiste",
  ucRetailer1: "Promotions exclusives financées par le retailer",
  ucRetailer2: "Activation symbol group",
  ucRetailer3: "Audit de conformité linéaire",
  ucRetailer4: "Fidélité spécifique au retailer",
  ucAgencyTitle: "Agence / Revendeur",
  ucAgency1: "Campagnes en marque blanche pour vos clients",
  ucAgency2: "Accès portail multi-clients",
  ucAgency3: "Rapports trade marketing en PDF",
  ucAgency4: "Contenu social généré automatiquement",

  hfssEyebrow: "Conçu pour HFSS",
  hfssHeading: "Conformité HFSS, intégrée.",
  hfssSub: "Le shopper voit le profil nutritionnel avant de jouer. Lipides, sucre, sel, acides gras saturés — feux tricolores selon les apports journaliers de référence. Obligatoire. Auditable. Quatre niveaux de défense réglementaire.",
  hfss1Title: "Roue nutritionnelle obligatoire.",
  hfss1Body: "Chaque produit HFSS affiche les feux tricolores lipides, sucre, sel et acides gras saturés selon les apports journaliers avant que le shopper puisse continuer.",
  hfss2Title: "Cadrage d'abord éducatif.",
  hfss2Body: "La plateforme promeut la transparence nutritionnelle. Elle positionne les interactions shopper comme un choix éclairé, pas une promotion forcée.",
  hfss3Title: "Validation des sorties.",
  hfss3Body: "L'IA conversationnelle est surveillée selon des schémas réglementaires. Chaque réponse est journalisée, filtrée et auditable.",
  hfss4Title: "Journaux d'audit exportables.",
  hfss4Body: "Score de conformité ASA, chaque réponse journalisée et récupérable, alertes en cas de contenu signalé.",
  hfssBadge: "Conformité ASA : 98,4 %",

  wlEyebrow: "Votre marque, dans n'importe quelle langue",
  wlHeading: "Vos données. Votre IA. À votre façon.",
  wlLangCaption: "40+ langues. Détection automatique ou paramétrage manuel par campagne.",
  wlFeat1Title: "Votre identité de marque",
  wlFeat1Body: "Logo, couleurs, polices appliqués à chaque écran shopper et bon de livraison.",
  wlFeat2Title: "Vos données, votre IA",
  wlFeat2Body: "Données stockées sur votre compte. IA entraînée sur vos produits. Vous possédez la relation.",
  wlFeat3Title: "Vos magasins, votre géofence",
  wlFeat3Body: "Mode universel ou par magasin. Géofence par défaut de 150 m, configurable par campagne.",
  wlFeat4Title: "Votre équipe, vos accès",
  wlFeat4Body: "Permissions par rôle. Marque, agence, terrain, finance — chacun avec la bonne vue.",

  proofEyebrow: "En déploiement",
  proofHeading: "Déjà en ligne. Déjà opérationnel.",
  proofStat1Label: "magasins UK dans la base campagne",
  proofStat2Label: "langues prises en charge",
  proofStat3Label: "Fondée au Royaume-Uni",
  proofQuotePlaceholder: "Témoignages clients à venir.",

  faqHeading: "Les questions que les marques nous posent.",
  faq1Q: "Les shoppers doivent-ils télécharger une app ?",
  faq1A: "Non. TalkPack tourne dans le navigateur. Un scan ouvre l'expérience.",
  faq2Q: "TalkPack s'intègre-t-il à notre EPOS ou CRM ?",
  faq2A: "Aucune intégration nécessaire. Les données capturées sont first-party et exportables en PDF, CSV ou via API.",
  faq3Q: "Que se passe-t-il pour les produits HFSS ?",
  faq3A: "Chaque produit HFSS affiche une roue nutritionnelle obligatoire avant le jeu. La conformité est intégrée, pas ajoutée.",
  faq4Q: "Quelles langues prenez-vous en charge ?",
  faq4A: "40+. La plateforme détecte automatiquement ou vous paramétrez par campagne.",
  faq5Q: "Peut-on lancer une campagne dans certains retailers uniquement ?",
  faq5A: "Oui. Le mode magasin-spécifique géofence la campagne aux magasins choisis. Le mode universel laisse n'importe quel shopper jouer n'importe où.",
  faq6Q: "Combien de temps pour construire une campagne ?",
  faq6A: "Environ 10 minutes pour une promotion standard. Assistant pas-à-pas dans le portail.",
  faq7Q: "Peut-on personnaliser l'expérience en marque blanche ?",
  faq7A: "Oui. Votre logo, vos couleurs, votre langue. Les shoppers ne voient jamais la marque TalkPack sauf si vous le souhaitez.",

  footerColPlatform: "Plateforme",
  footerColModules: "Modules",
  footerColCompany: "Société",
  footerColResources: "Ressources",
  footerCompany1: "À propos",
  footerCompany2: "Contact",
  footerResources3: "Confidentialité",
  footerResources4: "Conditions",
  footerNewsHeading: "Mises à jour trimestrielles. Pas de spam.",
  footerNewsPlaceholder: "votre@email.com",
  footerNewsSubmit: "S'abonner",
  footerRegistration: "ShopAI Ltd · No. de société 15877093 · Enregistrée en Angleterre",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (FR) ──
  heroAlt: "Acheteuse dans une allée de supermarché — chaque point de contact de l'expérience TalkPack tournant autour d'elle",

  stepsTitle: "Trois étapes. Une plateforme.",
  stepsSub: "Du moment où le shopper entre, jusqu'au moment où vous apprenez quelque chose de lui. Simple à exécuter. Riche en données.",
  step1Title: "Entrer",
  step1Desc: "Le shopper tape ou scanne depuis n'importe quel canal. Une entrée, une expérience à votre marque.",
  step1PillRow: "Site · Social · Email · QR · OOH · En magasin",
  step2Title: "Engager",
  step2Desc: "Cinq modules Pic-n-Mix dans leur langue, à votre marque, vérifiés par l'IA.",
  step2PillRow: "Apprendre · Jouer · Dire · Gagner · Cumuler",
  step3Title: "Apprendre",
  step3Desc: "Chaque interaction devient une donnée first-party, en flux temps réel sur votre dashboard.",
  step3PillRow: "Shoppers · Conversation · Lieu · Engagement · Conformité",

  chipWebsite: "Site",
  chipSocial:  "Social",
  chipEmail:   "Email",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "En magasin",
  chipLearn:   "Apprendre",
  chipPlay:    "Jouer",
  chipSay:     "Dire",
  chipWin:     "Gagner",
  chipEarn:    "Cumuler",
  chipShoppers:     "Shoppers",
  chipConversation: "Conversation",
  chipLocation:     "Lieu",
  chipEngagement:   "Engagement",
  chipCompliance:   "Conformité",
  chipAriaEntry:  "Canal d'entrée",
  chipAriaModule: "Module Pic-n-Mix",
  chipAriaIntel:  "Catégorie d'intelligence",

  intgTitle: "Une infrastructure sérieuse. Une technologie éprouvée.",
  intgSub: "TalkPack repose sur les mêmes fondations cloud, IA et conformité que les plus grandes entreprises du monde.",
  intgCap1: "Plateforme parente",
  intgCap2: "Infrastructure cloud",
  intgCap3: "IA conversationnelle",
  intgCap4: "Modèles de langage",
  intgCap5: "IA et cartographie",
  intgCap6: "Données nutritionnelles",
  intgCap7: "Paiements",
  intgFootnote: "Fournisseurs certifiés ISO 27001 · Conformes RGPD · Hébergement multi-région · Sécurité de niveau entreprise dès le premier jour",

  jColEntry: "Entrée",
  jColExperience: "Expérience",
  jColDashboard: "Intelligence Dashboard",
  jEntryDigitalTitle: "Digital",
  jEntryDigitalDesc: "Site web, app stores, recherche, liens directs — un tap depuis n'importe quelle surface digitale vers l'expérience shopper.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Posts sponsorisés, contenus d'influenceurs, DMs — les flux convergent vers TalkPack sans quitter la plateforme.",
  jEntryPhysicalTitle: "Physique",
  jEntryPhysicalDesc: "Sur emballage, en linéaire, en caisse — les points d'entrée du monde réel où TalkPack transforme un scan en conversation.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "PLV magasin",
  jEntryPhysPill3: "Packaging",
  jExpVerifyLabel: "Vérifier",
  jExpVerifyText: "Reconnaissance produit IA — SKU vérifié en temps réel.",
  jExpLearnLabel: "Apprendre",
  jExpLearnBody: "Roue nutritionnelle, allergènes, ingrédients, provenance — affichés dans la langue du shopper, validés par la marque.",
  jExpEngageLabel: "Engager",
  jExpEngagePlayBody: "Roue, gacha, puzzle, quiz — cinq types de jeux pour le temps de présence.",
  jExpEngageEarnBody: "Points, tickets, fidélité — intelligence panier.",
  jExpEngageSayBody: "IA conversationnelle qui répond en 40+ langues.",
  jExpRewardLabel: "Récompenser",
  jExpRewardBody: "Lots instantanés, validation dual-QR en caisse, réclamations vérifiées GPS — auditables dès le premier jour.",
  jExpCaptureLabel: "Capturer",
  jExpCaptureSub: "Chaque interaction · capturée et possédée par vous",

  capEmail:   "Email",
  capName:    "Nom",
  capAddress: "Adresse",
  capGps:     "GPS",
  capProduct: "Produit",
  capBasket:  "Panier",
  capSpend:   "Dépense",
  capChat:    "Chat",

  dashShopperLabel: "Intelligence shopper",
  dashShopperShort: "Shopper",
  dashShopper1Title: "CRM first-party",
  dashShopper1Desc: "Email, nom, adresse, préférences de contact.",
  dashShopper2Title: "Localisations vérifiées GPS",
  dashShopper2Desc: "Où les shoppers s'engagent par région magasin.",
  dashShopper3Title: "Fréquence d'achat",
  dashShopper3Desc: "Patterns de scan répétés, cadence des visites.",
  dashShopper4Title: "Composition du panier",
  dashShopper4Desc: "Données ticket à l'item près, produits co-achetés.",

  dashConvLabel: "Intelligence conversation",
  dashConvShort: "Conversation",
  dashConv1Title: "Catégories de questions",
  dashConv1Desc: "Allergènes, ingrédients, nutrition, usage, recettes.",
  dashConv2Title: "Analyse de sentiment",
  dashConv2Desc: "Positif, négatif, neutre par produit et par région.",
  dashConv3Title: "Points de confusion",
  dashConv3Desc: "Ce que les shoppers ne comprennent pas, questions répétées.",
  dashConv4Title: "Intention d'achat",
  dashConv4Desc: "Progression de la curiosité aux demandes retailer.",
  dashConv5Title: "Préférences linguistiques",
  dashConv5Desc: "Quelles langues les shoppers choisissent par lieu.",

  dashDistLabel: "Intelligence distribution",
  dashDistShort: "Distribution",
  dashDist1Title: "Disponibilité magasin",
  dashDist1Desc: "Produit en linéaire, confirmé par scans géofencés.",
  dashDist2Title: "Alertes rupture de stock",
  dashDist2Desc: "Signalements 'Pas ici' remontés en temps réel.",
  dashDist3Title: "Heatmaps",
  dashDist3Desc: "Densité de scans par code postal, région, fascia.",
  dashDist4Title: "Pénétration foyer",
  dashDist4Desc: "'Scan partout' montre où vit le produit chez les shoppers.",
  dashDist5Title: "Comparaison magasin",
  dashDist5Desc: "Taux d'engagement par lieu, par groupe fascia.",

  dashCampLabel: "Performance campagne",
  dashCampShort: "Campagne",
  dashCamp1Title: "Taux scan-vers-engagement",
  dashCamp1Desc: "Combien de scans se convertissent en interactions complètes.",
  dashCamp2Title: "Taux de complétion des jeux",
  dashCamp2Desc: "Spins par session, taux de gain, points d'abandon.",
  dashCamp3Title: "Suivi des récompenses",
  dashCamp3Desc: "Lots réclamés, validés, expirés, par magasin.",
  dashCamp4Title: "Dashboard ROI",
  dashCamp4Desc: "Dépense de campagne vs valeur shopper capturée.",

  dashCompLabel: "Conformité & audit",
  dashCompShort: "Conformité",
  dashComp1Title: "Score de conformité ASA",
  dashComp1Desc: "Par campagne, par conversation.",
  dashComp2Title: "Logs d'audit exportables",
  dashComp2Desc: "Chaque réponse IA loggée et récupérable.",
  dashComp3Title: "Alertes de violation",
  dashComp3Desc: "Patterns de contenu signalés.",
  dashComp4Title: "Engagement nutritionnel",
  dashComp4Desc: "Avec quels nutriments les shoppers interagissent sur la roue.",

  pvFormTitle: "Félicitations !",
  pvFormSub: "Vous avez gagné : -20 % sur votre prochain achat",
  pvFormName: "Votre nom",
  pvFormEmail: "Votre email",
  pvFormPostcode: "Code postal",
  pvFormCta: "RÉCLAMER",
  pvFormFootnote: "Privacy-first · Conforme RGPD",

  pvChatTitle: "Ambassadeur IA",
  pvChatSub: "Entraîné sur Morning O's",
  pvChatBubble1: "Est-ce sans gluten ?",
  pvChatBubble2: "Oui — certifié sans gluten et seulement 9g de sucre par portion. Des idées de recettes ?",
  pvChatBubble3: "Oui s'il vous plaît",
  pvChatBubble4: "Essayez l'overnight oats aux fruits rouges — 3 min de prep.",
  pvChatPlaceholder: "Posez-moi une question…",
  pvChatFootnote: "40+ langues · toujours actif",

  pvMapTitle: "Trouvez Morning O's près de chez vous",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "ITINÉRAIRE",
  pvMapFootnote: "Vérifié GPS · géofence 150m",
  pvMapOos: "rupture de stock",

  pvWheelTitle: "Morning O's · Roue de la chance",
  pvWheelCredits: "350 crédits",
  pvWheelCta: "TOURNER",
  pvWheelFootnote: "Roue · Roue nutritionnelle · Gacha · Quiz · Puzzle",
  pvWheelWedgeWin: "GAGNÉ !",
  pvWheelWedgeTry: "Encore",

  pvNutTitle: "Morning O's · Informations nutritionnelles",
  pvNutPer100g: "Pour 100g",
  pvNutPerServing: "Par portion",
  pvNutCta: "CONTINUER",
  pvNutFootnote: "Conforme ASA · obligatoire avant de jouer",

  bookDemoSubject: "Demande de démo TalkPack",
  bookDemoBody: "Bonjour à l'équipe ShopAI,\n\nJe souhaiterais réserver une démo de TalkPack. Pouvez-vous me contacter pour fixer un créneau ?\n\nMerci,",

  // ── Viz mock-data labels (FR) ──
  vizCapQuestions: "Top des questions shopper cette semaine",
  vizQAllergens: "Allergènes", vizQIngredients: "Ingrédients", vizQNutrition: "Nutrition",
  vizQRecipes: "Recettes", vizQUsage: "Usage",
  vizCapSentiment: "30 derniers jours, tous produits confondus",
  vizSentPositive: "Positif", vizSentNeutral: "Neutre", vizSentNegative: "Négatif",
  vizCapConfusion: "Questions répétées — manques de contenu",
  vizConfusion1: "C'est sans gluten ?",
  vizConfusion2: "Convient aux véganes ?",
  vizConfusion3: "Avertissement aux noix ?",
  vizCapFunnel: "Conversion par la conversation",
  vizFunnelCurious: "Curieux", vizFunnelInterested: "Intéressé", vizFunnelReady: "Prêt à acheter",
  vizCapAvail: "Produit disponible là où vous l'avez prévu",
  vizAvailDetail: "1 247 sur 1 326 magasins confirmés en linéaire",
  vizCapOos: "3 magasins signalés aujourd'hui · alerte automatique envoyée",
  vizCapHeat: "Londres · Manchester · Birmingham en tête",
  vizCapHousehold: "68 % des scans se font à domicile — votre produit y vit",
  vizCapFascia: "Taux d'engagement par enseigne",
  vizCapEngrate: "Scans qui deviennent des interactions complètes",
  vizEngrateCompare: "+36 pts vs 42 % du benchmark sectoriel",
  vizCapGame: "Où les shoppers décrochent — calibrez le jeu",
  vizGameStart: "Début", vizGamePlay: "Jeu", vizGameWin: "Gain", vizGameClaim: "Réclamé",
  vizCapRdm: "96 % de taux de validation · peu d'expirations",
  vizRdmClaimed: "Réclamé", vizRdmValidated: "Validé", vizRdmExpired: "Expiré",
  vizCapRoi: "Dépense de campagne vs valeur shopper sur le long terme",
  vizRoiSpend: "Dépense", vizRoiValueLabel: "Valeur captée", vizRoiReturn: "3,4× retour",
  vizCapComp: "Par campagne · par conversation · prêt pour audit",
  vizCapAudit: "Chaque réponse IA loggée · disponible à la demande",
  vizCapViolation: "Violations ce mois · sortie IA validée en continu",
  vizCapNut: "Nutriments avec lesquels les shoppers interagissent sur la roue",
  vizNutSugar: "Sucre", vizNutFat: "Graisses", vizNutSalt: "Sel", vizNutSatFat: "Saturés",
  vizCapLang: "Langue du shopper détectée automatiquement",
  vizLangOther: "Autre",
  vizCapCrm: "shoppers captés",
  vizCapMap: "247 lieux cette semaine",
  vizCapWeek: "Moyenne 2,3 visites par semaine",
  vizDayMon: "Lun", vizDayTue: "Mar", vizDayWed: "Mer",
  vizDayThu: "Jeu", vizDayFri: "Ven", vizDaySat: "Sam", vizDaySun: "Dim",
  vizCapBasket: "Panier moyen : 8,40 £ · 6,2 articles",
  vizBasketDairy: "Crémerie", vizBasketBakery: "Boulangerie", vizBasketSnacks: "Snacks", vizBasketOther: "Autre",
};

// ────────── Español ──────────
const es: LandingCopy = {
  ...en,
  navPlatform: "Plataforma",
  navModules: "Módulos",
  navPortal: "Acceso al Portal",
  navLangButton: "ES",
  navRegister: "Registrarse",
  navBookDemo: "Reservar una demo",
  navLogin: "Iniciar sesión",

  trustLeft: "Sin app · Sin integración · Sin dependencia de tienda",
  trustRight: "Desde 2024 · Reino Unido · Construyendo con The Wholesale Group · Caterers Choice · Passot (Japón) · Talysis UK · Jakavarna Group (Tailandia)",

  heroEyebrow: "Un copiloto para el comercio",
  heroL1: "Cada producto. Una conversación.",
  heroL2: "Cada escaneo. Datos reales.",
  heroSub: "TalkPack convierte cualquier producto en una conversación en directo. Los compradores escanean, aprenden, juegan, preguntan, ganan, acumulan. Tú obtienes datos first-party, conformidad HFSS y una relación directa con los compradores en tienda física. Sin app. Sin integración. Sin dependencia de tienda.",
  heroCtaPrimary: "Reservar una demo",
  heroCtaGhost: "Ver cómo funciona",
  heroPillAllergens: "Alérgenos",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Ingredientes",
  heroVerified: "Verificado",

  statStores: "Tiendas · Reino Unido · expansión global",
  statLanguages: "Idiomas · detección automática",
  statGames: "Del brief a la campaña en marcha",
  statModules: "Rueda nutricional integrada",
  statSteps: "Tu marca. Tus datos. Tu IA.",
  statDownloads: "Descargas de app · integraciones · fricción",
  statGeofence: "Datos del comprador desde la tienda física",
  statScans: "Del lineal al CRM del comprador",

  fullJourneyEyebrow: "El recorrido completo",
  fullJourneyHeading: "De la entrada a la inteligencia. Una plataforma.",
  fullJourneySub: "Cada canal. Cada módulo. Cada dato. Así se conecta toda la arquitectura TalkPack de extremo a extremo.",
  fullJourneyCaption: "Flujo completo. De la entrada del comprador a la inteligencia de marca. Exportable vía dashboard, chat IA o consultas SmartMail.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Una plataforma. Cinco formas de implicar al comprador.",
  picSub: "Las marcas eligen los módulos que necesitan. Los compradores viven una mezcla de experiencias. Cada módulo pasa por el mismo escaneo, la misma conversación, la misma capa de datos.",
  learnDesc: "Educación de producto, transparencia de ingredientes, semáforos HFSS.",
  learnF1: "Rueda nutricional", learnF2: "Escaneo de alérgenos", learnF3: "Datos de procedencia",
  playDesc: "Cinco tipos de juego que impulsan el tiempo de permanencia y el reengagement.",
  playF1: "Ruleta", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Ruleta nutricional",
  sayDesc: "IA conversacional que responde a las preguntas del comprador en su propio idioma.",
  sayF1: "40+ idiomas", sayF2: "P&R sobre ingredientes", sayF3: "Sugerencias de recetas",
  winDesc: "Premios, drive-to-store, validación dual-QR en caja, albaranes.",
  winF1: "Recompensa instantánea", winF2: "Premios geolocalizados", winF3: "Entrega a domicilio",
  earnDesc: "Puntos, escaneo de tickets, programa de fidelización, inteligencia de cesta.",
  earnF1: "OCR de ticket", earnF2: "Perfil multimarca", earnF3: "Catálogo de recompensas",
  moduleSeeMore: "Ver módulo",

  oeEyebrow: "Un motor · Múltiples dinámicas",
  oeHeading: "TalkPack se integra en una plataforma más amplia.",
  oeSub: "ShopAI ofrece a marcas, retailers y mayoristas una única capa de datos. Inteligencia de negocio conversacional de extremo a extremo. TalkPack es cómo el comprador se une a la conversación.",
  oe1Strap: "Engagement del comprador desde la tienda física",
  oe2Strap: "IA para el sector de proximidad",
  oe3Strap: "Inteligencia de auditoría en campo",
  oe4Strap: "Puntos, tickets, datos de cesta",
  oeActiveLabel: "Actual",

  ucHeading: "Distintos roles. Misma plataforma.",
  ucBrandTitle: "Marketing de marca",
  ucBrand1: "Lanzamiento nacional de producto en distribución mixta",
  ucBrand2: "Drive-to-store hacia tiendas participantes",
  ucBrand3: "Engagement on-pack conforme HFSS",
  ucBrand4: "Perfilado del comprador entre marcas",
  ucBrand5: "Categorías reguladas (farma, alcohol) con respuestas de IA auditadas",
  ucRetailerTitle: "Retailer / Mayorista",
  ucRetailer1: "Promociones exclusivas financiadas por el retailer",
  ucRetailer2: "Activación de symbol group",
  ucRetailer3: "Auditoría de conformidad de lineal",
  ucRetailer4: "Fidelización específica del retailer",
  ucAgencyTitle: "Agencia / Reseller",
  ucAgency1: "Campañas de marca blanca para tus clientes",
  ucAgency2: "Acceso al portal multi-cliente",
  ucAgency3: "Informes de trade marketing en PDF",
  ucAgency4: "Contenido social autogenerado",

  hfssEyebrow: "Diseñado para HFSS",
  hfssHeading: "Conformidad HFSS, integrada.",
  hfssSub: "El comprador ve el perfil nutricional antes de jugar. Grasas, azúcar, sal, grasas saturadas — semáforos según las ingestas diarias de referencia. Obligatorio. Auditable. Cuatro capas de defensa regulatoria.",
  hfss1Title: "Rueda nutricional obligatoria.",
  hfss1Body: "Cada producto HFSS muestra los semáforos de grasas, azúcar, sal y grasas saturadas según las ingestas diarias antes de que el comprador pueda continuar.",
  hfss2Title: "Enfoque educativo primero.",
  hfss2Body: "La plataforma promueve la transparencia nutricional. Sitúa las interacciones del comprador como elección informada, no como empuje promocional.",
  hfss3Title: "Validación de salidas.",
  hfss3Body: "La IA conversacional se monitoriza con patrones regulatorios. Cada respuesta queda registrada, filtrada y auditable.",
  hfss4Title: "Registros de auditoría exportables.",
  hfss4Body: "Puntuación de conformidad ASA, cada respuesta registrada y recuperable, alertas ante contenido marcado.",
  hfssBadge: "Conformidad ASA: 98,4 %",

  wlEyebrow: "Tu marca, en cualquier idioma",
  wlHeading: "Tus datos. Tu IA. A tu manera.",
  wlLangCaption: "40+ idiomas. Detección automática o configuración manual por campaña.",
  wlFeat1Title: "Tu identidad de marca",
  wlFeat1Body: "Logo, colores y tipografías aplicados a cada pantalla del comprador y a cada albarán.",
  wlFeat2Title: "Tus datos, tu IA",
  wlFeat2Body: "Datos almacenados en tu cuenta. IA entrenada con tus productos. La relación es tuya.",
  wlFeat3Title: "Tus tiendas, tu geocerca",
  wlFeat3Body: "Modo universal o por tienda. Geocerca por defecto de 150 m, configurable por campaña.",
  wlFeat4Title: "Tu equipo, tus accesos",
  wlFeat4Body: "Permisos por rol. Marca, agencia, campo, finanzas — cada uno con la vista adecuada.",

  proofEyebrow: "En despliegue",
  proofHeading: "Ya en marcha. Ya funcionando.",
  proofStat1Label: "tiendas UK en la base de campañas",
  proofStat2Label: "idiomas soportados",
  proofStat3Label: "Fundada en el Reino Unido",
  proofQuotePlaceholder: "Casos de cliente próximamente.",

  faqHeading: "Preguntas que las marcas nos hacen.",
  faq1Q: "¿Necesitan los compradores descargar una app?",
  faq1A: "No. TalkPack funciona en el navegador. Un escaneo abre la experiencia.",
  faq2Q: "¿TalkPack se integra con nuestro EPOS o CRM?",
  faq2A: "No requiere integración. Los datos capturados son first-party y exportables en PDF, CSV o vía API.",
  faq3Q: "¿Qué ocurre con los productos HFSS?",
  faq3A: "Cada producto HFSS muestra una rueda nutricional obligatoria antes del juego. La conformidad está integrada, no añadida.",
  faq4Q: "¿Qué idiomas soportáis?",
  faq4A: "40+. La plataforma detecta automáticamente o lo configuras por campaña.",
  faq5Q: "¿Podemos lanzar una campaña solo en retailers concretos?",
  faq5A: "Sí. El modo por tienda geocerca la campaña a las tiendas elegidas. El modo universal permite que cualquier comprador juegue en cualquier sitio.",
  faq6Q: "¿Cuánto se tarda en construir una campaña?",
  faq6A: "Unos 10 minutos para una promoción estándar. Asistente paso a paso en el portal.",
  faq7Q: "¿Podemos personalizar la experiencia en marca blanca?",
  faq7A: "Sí. Tu logo, tus colores, tu idioma. Los compradores no ven la marca TalkPack salvo que tú quieras.",

  footerColPlatform: "Plataforma",
  footerColModules: "Módulos",
  footerColCompany: "Empresa",
  footerColResources: "Recursos",
  footerCompany1: "Acerca de",
  footerCompany2: "Contacto",
  footerResources3: "Privacidad",
  footerResources4: "Términos",
  footerNewsHeading: "Actualizaciones trimestrales. Sin spam.",
  footerNewsPlaceholder: "tu@email.com",
  footerNewsSubmit: "Suscribirse",
  footerRegistration: "ShopAI Ltd · N.º de empresa 15877093 · Registrada en Inglaterra",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (ES) ──
  heroAlt: "Compradora en un pasillo de supermercado — cada punto de contacto de la experiencia TalkPack orbitando a su alrededor",

  stepsTitle: "Tres pasos. Una plataforma.",
  stepsSub: "Desde el momento en que el comprador entra hasta el momento en que aprendes algo sobre él. Sencillo de ejecutar. Rico en datos.",
  step1Title: "Entrar",
  step1Desc: "El comprador toca o escanea desde cualquier canal. Una entrada, una experiencia con tu marca.",
  step1PillRow: "Web · Social · Email · QR · OOH · En tienda",
  step2Title: "Implicar",
  step2Desc: "Cinco módulos Pic-n-Mix en su idioma, con tu marca, verificados por IA.",
  step2PillRow: "Aprender · Jugar · Hablar · Ganar · Acumular",
  step3Title: "Aprender",
  step3Desc: "Cada interacción se convierte en datos first-party, en tiempo real en tu dashboard.",
  step3PillRow: "Compradores · Conversación · Lugar · Engagement · Conformidad",

  chipWebsite: "Web",
  chipSocial:  "Social",
  chipEmail:   "Email",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "En tienda",
  chipLearn:   "Aprender",
  chipPlay:    "Jugar",
  chipSay:     "Hablar",
  chipWin:     "Ganar",
  chipEarn:    "Acumular",
  chipShoppers:     "Compradores",
  chipConversation: "Conversación",
  chipLocation:     "Lugar",
  chipEngagement:   "Engagement",
  chipCompliance:   "Conformidad",
  chipAriaEntry:  "Canal de entrada",
  chipAriaModule: "Módulo Pic-n-Mix",
  chipAriaIntel:  "Categoría de inteligencia",

  intgTitle: "Infraestructura seria. Tecnología probada.",
  intgSub: "TalkPack se apoya sobre las mismas bases de cloud, IA y conformidad en las que confían las mayores empresas del mundo.",
  intgCap1: "Plataforma matriz",
  intgCap2: "Infraestructura cloud",
  intgCap3: "IA conversacional",
  intgCap4: "Modelos de lenguaje",
  intgCap5: "IA y mapas",
  intgCap6: "Datos nutricionales",
  intgCap7: "Pagos",
  intgFootnote: "Proveedores certificados ISO 27001 · Conformes con RGPD · Hosting multi-región · Seguridad nivel empresa desde el primer día",

  jColEntry: "Entrada",
  jColExperience: "Experiencia",
  jColDashboard: "Inteligencia Dashboard",
  jEntryDigitalTitle: "Digital",
  jEntryDigitalDesc: "Web, app stores, búsqueda, enlaces directos — un toque desde cualquier superficie digital hacia la experiencia del comprador.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Posts patrocinados, contenido de influencers, DMs — los flujos llegan a TalkPack sin salir de la plataforma.",
  jEntryPhysicalTitle: "Físico",
  jEntryPhysicalDesc: "En el envase, en el lineal, en caja — los puntos de entrada del mundo real donde TalkPack convierte un escaneo en una conversación.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "PLV en tienda",
  jEntryPhysPill3: "Packaging",
  jExpVerifyLabel: "Verificar",
  jExpVerifyText: "Reconocimiento de producto por IA — SKU verificado en tiempo real.",
  jExpLearnLabel: "Aprender",
  jExpLearnBody: "Rueda nutricional, alérgenos, ingredientes, procedencia — mostrados en el idioma del comprador, validados por la marca.",
  jExpEngageLabel: "Implicar",
  jExpEngagePlayBody: "Ruleta, gacha, puzle, quiz — cinco tipos de juego para aumentar la permanencia.",
  jExpEngageEarnBody: "Puntos, tickets, fidelización — inteligencia de cesta.",
  jExpEngageSayBody: "IA conversacional respondiendo en 40+ idiomas.",
  jExpRewardLabel: "Recompensar",
  jExpRewardBody: "Premios instantáneos, validación dual-QR en caja, reclamaciones verificadas por GPS — listo para auditoría desde el primer día.",
  jExpCaptureLabel: "Capturar",
  jExpCaptureSub: "Cada interacción · capturada y de tu propiedad",

  capEmail:   "Email",
  capName:    "Nombre",
  capAddress: "Dirección",
  capGps:     "GPS",
  capProduct: "Producto",
  capBasket:  "Cesta",
  capSpend:   "Gasto",
  capChat:    "Chat",

  dashShopperLabel: "Inteligencia comprador",
  dashShopperShort: "Comprador",
  dashShopper1Title: "CRM first-party",
  dashShopper1Desc: "Email, nombre, dirección, preferencias de contacto.",
  dashShopper2Title: "Ubicaciones verificadas por GPS",
  dashShopper2Desc: "Dónde se implican los compradores por región de tienda.",
  dashShopper3Title: "Frecuencia de compra",
  dashShopper3Desc: "Patrones de escaneos repetidos, cadencia de visita.",
  dashShopper4Title: "Composición de la cesta",
  dashShopper4Desc: "Datos de ticket a nivel de ítem, productos co-comprados.",

  dashConvLabel: "Inteligencia conversación",
  dashConvShort: "Conversación",
  dashConv1Title: "Categorías de preguntas",
  dashConv1Desc: "Alérgenos, ingredientes, nutrición, uso, recetas.",
  dashConv2Title: "Análisis de sentimiento",
  dashConv2Desc: "Positivo, negativo, neutro por producto y por región.",
  dashConv3Title: "Puntos de confusión",
  dashConv3Desc: "Lo que los compradores no entienden, preguntas repetidas.",
  dashConv4Title: "Intención de compra",
  dashConv4Desc: "Progresión desde la curiosidad hasta peticiones al retailer.",
  dashConv5Title: "Preferencias de idioma",
  dashConv5Desc: "Qué idiomas eligen los compradores por ubicación.",

  dashDistLabel: "Inteligencia distribución",
  dashDistShort: "Distribución",
  dashDist1Title: "Disponibilidad en tienda",
  dashDist1Desc: "Producto en lineal, confirmado por escaneos geolocalizados.",
  dashDist2Title: "Alertas de rotura de stock",
  dashDist2Desc: "Reportes 'No está aquí' marcados en tiempo real.",
  dashDist3Title: "Mapas de calor",
  dashDist3Desc: "Densidad de escaneos por código postal, región, fascia.",
  dashDist4Title: "Penetración hogar",
  dashDist4Desc: "'Escanea en cualquier sitio' muestra dónde vive el producto en casa.",
  dashDist5Title: "Comparación de tiendas",
  dashDist5Desc: "Tasas de engagement por ubicación, por grupo de fascia.",

  dashCampLabel: "Rendimiento de campaña",
  dashCampShort: "Campaña",
  dashCamp1Title: "Tasa escaneo-a-engagement",
  dashCamp1Desc: "Cuántos escaneos se convierten en interacciones completas.",
  dashCamp2Title: "Tasas de finalización de juego",
  dashCamp2Desc: "Tiradas por sesión, tasas de victoria, puntos de abandono.",
  dashCamp3Title: "Seguimiento de canjes",
  dashCamp3Desc: "Premios reclamados, validados, expirados, por tienda.",
  dashCamp4Title: "Dashboard de ROI",
  dashCamp4Desc: "Gasto de campaña vs valor de comprador capturado.",

  dashCompLabel: "Conformidad y auditoría",
  dashCompShort: "Conformidad",
  dashComp1Title: "Puntuación de conformidad ASA",
  dashComp1Desc: "Por campaña, por conversación.",
  dashComp2Title: "Logs de auditoría exportables",
  dashComp2Desc: "Cada respuesta de la IA registrada y recuperable.",
  dashComp3Title: "Alertas de violación",
  dashComp3Desc: "Patrones de contenido marcados.",
  dashComp4Title: "Engagement nutricional",
  dashComp4Desc: "Con qué nutrientes interactúan los compradores en la rueda.",

  pvFormTitle: "¡Felicidades!",
  pvFormSub: "Has ganado: 20% de descuento en tu próxima compra",
  pvFormName: "Tu nombre",
  pvFormEmail: "Tu email",
  pvFormPostcode: "Código postal",
  pvFormCta: "RECLAMAR",
  pvFormFootnote: "Privacy-first · Conforme RGPD",

  pvChatTitle: "Embajador IA",
  pvChatSub: "Entrenado con Morning O's",
  pvChatBubble1: "¿Es sin gluten?",
  pvChatBubble2: "Sí — certificado sin gluten y solo 9g de azúcar por ración. ¿Quieres ideas de recetas?",
  pvChatBubble3: "Sí, por favor",
  pvChatBubble4: "Prueba avena en remojo con frutos rojos — 3 min de prep.",
  pvChatPlaceholder: "Pregúntame lo que quieras…",
  pvChatFootnote: "40+ idiomas · siempre activo",

  pvMapTitle: "Encuentra Morning O's cerca de ti",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "CÓMO LLEGAR",
  pvMapFootnote: "Verificado por GPS · geocerca de 150m",
  pvMapOos: "agotado",

  pvWheelTitle: "Morning O's · Gira y gana",
  pvWheelCredits: "350 créditos",
  pvWheelCta: "GIRAR",
  pvWheelFootnote: "Ruleta · Ruleta nutricional · Gacha · Quiz · Puzle",
  pvWheelWedgeWin: "¡GANA!",
  pvWheelWedgeTry: "Otra",

  pvNutTitle: "Morning O's · Información nutricional",
  pvNutPer100g: "Por 100g",
  pvNutPerServing: "Por ración",
  pvNutCta: "CONTINUAR",
  pvNutFootnote: "Conforme ASA · obligatorio antes de jugar",

  bookDemoSubject: "Solicitud de demo de TalkPack",
  bookDemoBody: "Hola equipo de ShopAI,\n\nMe gustaría reservar una demo de TalkPack. ¿Podríais contactar conmigo para acordar una fecha?\n\nGracias,",

  // ── Viz mock-data labels (ES) ──
  vizCapQuestions: "Principales preguntas del comprador esta semana",
  vizQAllergens: "Alérgenos", vizQIngredients: "Ingredientes", vizQNutrition: "Nutrición",
  vizQRecipes: "Recetas", vizQUsage: "Uso",
  vizCapSentiment: "Últimos 30 días en todos los productos",
  vizSentPositive: "Positivo", vizSentNeutral: "Neutro", vizSentNegative: "Negativo",
  vizCapConfusion: "Preguntas repetidas — vacíos de contenido",
  vizConfusion1: "¿Es sin gluten?",
  vizConfusion2: "¿Apto para veganos?",
  vizConfusion3: "¿Avisos sobre frutos secos?",
  vizCapFunnel: "Conversión a través de la conversación",
  vizFunnelCurious: "Curioso", vizFunnelInterested: "Interesado", vizFunnelReady: "Listo para comprar",
  vizCapAvail: "Producto disponible donde lo planeaste",
  vizAvailDetail: "1.247 de 1.326 tiendas confirmadas en lineal",
  vizCapOos: "3 tiendas marcadas hoy · alerta automática enviada",
  vizCapHeat: "Londres · Manchester · Birmingham a la cabeza",
  vizCapHousehold: "68 % de los escaneos ocurren en casa — tu producto vive ahí",
  vizCapFascia: "Tasa de engagement por enseña",
  vizCapEngrate: "Escaneos que se convierten en interacciones completas",
  vizEngrateCompare: "+36 pts vs 42 % del benchmark del sector",
  vizCapGame: "Dónde abandonan los compradores — ajusta el juego",
  vizGameStart: "Inicio", vizGamePlay: "Juego", vizGameWin: "Premio", vizGameClaim: "Reclamado",
  vizCapRdm: "96 % de tasa de validación · pocas caducidades",
  vizRdmClaimed: "Reclamado", vizRdmValidated: "Validado", vizRdmExpired: "Caducado",
  vizCapRoi: "Gasto de campaña vs valor de vida del comprador",
  vizRoiSpend: "Gasto", vizRoiValueLabel: "Valor capturado", vizRoiReturn: "3,4× retorno",
  vizCapComp: "Por campaña · por conversación · listo para auditoría",
  vizCapAudit: "Cada respuesta de la IA registrada · recuperable a demanda",
  vizCapViolation: "Violaciones este mes · salida de la IA validada continuamente",
  vizCapNut: "Nutrientes con los que los compradores interactúan en la rueda",
  vizNutSugar: "Azúcar", vizNutFat: "Grasa", vizNutSalt: "Sal", vizNutSatFat: "Saturadas",
  vizCapLang: "Idioma del comprador detectado automáticamente",
  vizLangOther: "Otro",
  vizCapCrm: "compradores capturados",
  vizCapMap: "247 ubicaciones esta semana",
  vizCapWeek: "Media 2,3 visitas por semana",
  vizDayMon: "Lun", vizDayTue: "Mar", vizDayWed: "Mié",
  vizDayThu: "Jue", vizDayFri: "Vie", vizDaySat: "Sáb", vizDaySun: "Dom",
  vizCapBasket: "Cesta media: 8,40 £ · 6,2 artículos",
  vizBasketDairy: "Lácteos", vizBasketBakery: "Panadería", vizBasketSnacks: "Aperitivos", vizBasketOther: "Otros",
};

// ────────── Deutsch ──────────
const de: LandingCopy = {
  ...en,
  navPlatform: "Plattform",
  navModules: "Module",
  navPortal: "Portal-Login",
  navLangButton: "DE",
  navRegister: "Registrieren",
  navBookDemo: "Demo buchen",
  navLogin: "Anmelden",

  trustLeft: "Keine App · Keine Integration · Keine Abhängigkeit vom Geschäft",
  trustRight: "Seit 2024 · UK · Mit The Wholesale Group · Caterers Choice · Passot (Japan) · Talysis UK · Jakavarna Group (Thailand)",

  heroEyebrow: "Ein Co-Pilot für den Handel",
  heroL1: "Jedes Produkt. Ein Gespräch.",
  heroL2: "Jeder Scan. Echte Daten.",
  heroSub: "TalkPack macht aus jedem Produkt ein Live-Gespräch. Shopper scannen, lernen, spielen, fragen, gewinnen, sammeln. Sie erhalten First-Party-Daten, HFSS-Konformität und eine direkte Beziehung zu Shoppern im stationären Handel. Keine App. Keine Integration. Keine Abhängigkeit vom Geschäft.",
  heroCtaPrimary: "Demo buchen",
  heroCtaGhost: "So funktioniert es",
  heroPillAllergens: "Allergene",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Inhaltsstoffe",
  heroVerified: "Verifiziert",

  statStores: "Filialen · UK · weltweite Expansion",
  statLanguages: "Sprachen · automatisch erkannt",
  statGames: "Vom Briefing zur Live-Kampagne",
  statModules: "Nährwert-Rad eingebaut",
  statSteps: "Ihre Marke. Ihre Daten. Ihre KI.",
  statDownloads: "App-Downloads · Integrationen · Reibung",
  statGeofence: "Shopper-Daten aus dem stationären Handel",
  statScans: "Vom Regal zum Shopper-CRM",

  fullJourneyEyebrow: "Die ganze Reise",
  fullJourneyHeading: "Vom Eintritt zur Intelligenz. Eine Plattform.",
  fullJourneySub: "Jeder Kanal. Jedes Modul. Jeder Datenpunkt. So verbindet sich die gesamte TalkPack-Architektur durchgängig.",
  fullJourneyCaption: "Vollständiger Fluss. Vom Shopper-Eintritt zur Markenintelligenz. Exportierbar via Dashboard, KI-Chat oder SmartMail-Abfragen.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Eine Plattform. Fünf Wege, einen Shopper zu erreichen.",
  picSub: "Marken wählen die Module, die sie brauchen. Shopper erleben einen Mix von Erlebnissen. Jedes Modul läuft über denselben Scan, dasselbe Gespräch, dieselbe Datenebene.",
  learnDesc: "Produktaufklärung, Inhaltsstoff-Transparenz, HFSS-Ampel.",
  learnF1: "Nährwert-Rad", learnF2: "Allergen-Scan", learnF3: "Herkunftsdaten",
  playDesc: "Fünf Spielarten, die Verweildauer und Wiederengagement steigern.",
  playF1: "Glücksrad", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Nährwert-Rad",
  sayDesc: "Konversations-KI, die Shopper-Fragen in der eigenen Sprache beantwortet.",
  sayF1: "40+ Sprachen", sayF2: "Inhaltsstoff-FAQ", sayF3: "Rezeptvorschläge",
  winDesc: "Preise, Drive-to-Store, Dual-QR-Validierung an der Kasse, Lieferscheine.",
  winF1: "Sofortige Belohnung", winF2: "Geofencete Preise", winF3: "Lieferung nach Hause",
  earnDesc: "Punkte, Beleg-Scan, Treueprogramm, Warenkorb-Intelligenz.",
  earnF1: "Beleg-OCR", earnF2: "Markenübergreifendes Profil", earnF3: "Belohnungskatalog",
  moduleSeeMore: "Modul ansehen",

  oeEyebrow: "Eine Engine · Viele Dynamiken",
  oeHeading: "TalkPack ist Teil einer größeren Plattform.",
  oeSub: "ShopAI bietet Marken, Händlern und Großhändlern eine einzige Datenebene. Konversationelle Business Intelligence durchgängig. TalkPack ist der Weg, wie der Shopper am Gespräch teilnimmt.",
  oe1Strap: "Shopper-Engagement aus dem stationären Handel",
  oe2Strap: "KI für den Convenience-Sektor",
  oe3Strap: "Feldaudit-Intelligenz",
  oe4Strap: "Punkte, Belege, Warenkorbdaten",
  oeActiveLabel: "Aktiv",

  ucHeading: "Verschiedene Rollen. Dieselbe Plattform.",
  ucBrandTitle: "Marken-Marketing",
  ucBrand1: "Nationaler Produktstart über gemischten Handel",
  ucBrand2: "Drive-to-Store zu teilnehmenden Filialen",
  ucBrand3: "HFSS-konformes On-Pack-Engagement",
  ucBrand4: "Markenübergreifendes Shopper-Profiling",
  ucBrand5: "Regulierte Kategorien (Pharma, Alkohol) mit auditierten KI-Antworten",
  ucRetailerTitle: "Händler / Großhändler",
  ucRetailer1: "Exklusive händlerfinanzierte Aktionen",
  ucRetailer2: "Symbol-Group-Aktivierung",
  ucRetailer3: "Regal-Compliance-Audit",
  ucRetailer4: "Händlerspezifische Treueprogramme",
  ucAgencyTitle: "Agentur / Reseller",
  ucAgency1: "White-Label-Kampagnen für Kundenmarken",
  ucAgency2: "Multi-Mandanten-Portalzugriff",
  ucAgency3: "Trade-Marketing-PDF-Berichte",
  ucAgency4: "Automatisch erzeugter Social-Content",

  hfssEyebrow: "Für HFSS gebaut",
  hfssHeading: "HFSS-Konformität, eingebaut.",
  hfssSub: "Der Shopper sieht das Nährwertprofil, bevor er spielt. Fett, Zucker, Salz, gesättigte Fettsäuren — Ampelsystem gemäß täglicher Referenzaufnahme. Pflicht. Audit-bereit. Vier Schichten regulatorischer Verteidigung.",
  hfss1Title: "Verbindliches Nährwert-Rad.",
  hfss1Body: "Jedes HFSS-Produkt zeigt die Ampel für Fett, Zucker, Salz und gesättigte Fettsäuren gemäß täglicher Referenzaufnahme, bevor der Shopper fortfahren kann.",
  hfss2Title: "Bildung an erster Stelle.",
  hfss2Body: "Die Plattform fördert Nährwert-Transparenz. Sie positioniert Shopper-Interaktionen als informierte Entscheidung, nicht als Werbedruck.",
  hfss3Title: "Output-Validierung.",
  hfss3Body: "Die Konversations-KI wird auf regulatorische Muster überwacht. Jede Antwort wird protokolliert, gefiltert und ist auditierbar.",
  hfss4Title: "Exportierbare Audit-Logs.",
  hfss4Body: "ASA-Compliance-Score, jede Antwort protokolliert und abrufbar, Verstoß-Alarm bei markierten Inhalten.",
  hfssBadge: "ASA-Konformität: 98,4 %",

  wlEyebrow: "Ihre Marke, in jeder Sprache",
  wlHeading: "Ihre Daten. Ihre KI. Ihr Weg.",
  wlLangCaption: "40+ Sprachen. Automatisch erkannt oder pro Kampagne manuell gesetzt.",
  wlFeat1Title: "Ihre Markenidentität",
  wlFeat1Body: "Logo, Farben, Schriften auf jedem Shopper-Bildschirm und Lieferschein.",
  wlFeat2Title: "Ihre Daten, Ihre KI",
  wlFeat2Body: "Daten in Ihrem Konto gespeichert. KI auf Ihren Produkten trainiert. Die Beziehung gehört Ihnen.",
  wlFeat3Title: "Ihre Filialen, Ihr Geofence",
  wlFeat3Body: "Universal-Modus oder filialspezifisch. 150 m Standard-Geofence, pro Kampagne konfigurierbar.",
  wlFeat4Title: "Ihr Team, Ihr Zugang",
  wlFeat4Body: "Rollenbasierte Berechtigungen. Marke, Agentur, Außendienst, Finanzen — jeweils mit der richtigen Sicht.",

  proofEyebrow: "Im Einsatz",
  proofHeading: "Schon live. Schon funktionsfähig.",
  proofStat1Label: "UK-Filialen in der Kampagnen-Datenbank",
  proofStat2Label: "unterstützte Sprachen",
  proofStat3Label: "Im UK gegründet",
  proofQuotePlaceholder: "Kundengeschichten folgen demnächst.",

  faqHeading: "Fragen, die Marken uns stellen.",
  faq1Q: "Müssen Shopper eine App herunterladen?",
  faq1A: "Nein. TalkPack läuft im Browser. Ein Scan öffnet das Erlebnis.",
  faq2Q: "Lässt sich TalkPack in unser Kassensystem oder CRM integrieren?",
  faq2A: "Keine Integration nötig. Erfasste Daten sind First-Party und exportierbar via PDF, CSV oder API.",
  faq3Q: "Was passiert mit HFSS-Produkten?",
  faq3A: "Jedes HFSS-Produkt zeigt vor dem Spiel ein verbindliches Nährwert-Rad. Konformität ist eingebaut, nicht aufgesetzt.",
  faq4Q: "Welche Sprachen unterstützen Sie?",
  faq4A: "40+. Die Plattform erkennt automatisch oder Sie setzen sie pro Kampagne.",
  faq5Q: "Können wir eine Kampagne nur bei bestimmten Händlern fahren?",
  faq5A: "Ja. Der Filialspezifische Modus geofencet die Kampagne auf die gewählten Filialen. Im Universal-Modus kann jeder Shopper überall spielen.",
  faq6Q: "Wie lange dauert der Aufbau einer Kampagne?",
  faq6A: "Etwa 10 Minuten für eine Standard-Promotion. Schritt-für-Schritt-Assistent im Portal.",
  faq7Q: "Können wir das Erlebnis als White-Label gestalten?",
  faq7A: "Ja. Ihr Logo, Ihre Farben, Ihre Sprache. Shopper sehen die TalkPack-Marke nur, wenn Sie es wünschen.",

  footerColPlatform: "Plattform",
  footerColModules: "Module",
  footerColCompany: "Unternehmen",
  footerColResources: "Ressourcen",
  footerCompany1: "Über uns",
  footerCompany2: "Kontakt",
  footerResources3: "Datenschutz",
  footerResources4: "AGB",
  footerNewsHeading: "Quartalsweise Updates. Kein Spam.",
  footerNewsPlaceholder: "ihre@email.com",
  footerNewsSubmit: "Abonnieren",
  footerRegistration: "ShopAI Ltd · Firmen-Nr. 15877093 · Registriert in England",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (DE) ──
  heroAlt: "Käuferin in einem Supermarktgang — jeder Berührungspunkt der TalkPack-Erfahrung kreist um sie",

  stepsTitle: "Drei Schritte. Eine Plattform.",
  stepsSub: "Vom Moment, in dem ein Shopper einsteigt, bis zu dem Moment, in dem Sie etwas über ihn lernen. Einfach im Betrieb. Reich an Daten.",
  step1Title: "Einstieg",
  step1Desc: "Der Shopper tippt oder scannt aus jedem Kanal. Ein Einstieg, ein Markenerlebnis.",
  step1PillRow: "Website · Social · E-Mail · QR · OOH · Im Laden",
  step2Title: "Engagement",
  step2Desc: "Fünf Pic-n-Mix-Module in seiner Sprache, in Ihrer Marke, KI-verifiziert.",
  step2PillRow: "Lernen · Spielen · Fragen · Gewinnen · Sammeln",
  step3Title: "Lernen",
  step3Desc: "Jede Interaktion wird zu First-Party-Daten und fließt in Echtzeit in Ihr Dashboard.",
  step3PillRow: "Shopper · Konversation · Standort · Engagement · Compliance",

  chipWebsite: "Website",
  chipSocial:  "Social",
  chipEmail:   "E-Mail",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "Im Laden",
  chipLearn:   "Lernen",
  chipPlay:    "Spielen",
  chipSay:     "Fragen",
  chipWin:     "Gewinnen",
  chipEarn:    "Sammeln",
  chipShoppers:     "Shopper",
  chipConversation: "Konversation",
  chipLocation:     "Standort",
  chipEngagement:   "Engagement",
  chipCompliance:   "Compliance",
  chipAriaEntry:  "Einstiegskanal",
  chipAriaModule: "Pic-n-Mix-Modul",
  chipAriaIntel:  "Intelligenz-Kategorie",

  intgTitle: "Solide Infrastruktur. Bewährte Technologie.",
  intgSub: "TalkPack läuft auf denselben Cloud-, KI- und Compliance-Fundamenten, denen die größten Unternehmen der Welt vertrauen.",
  intgCap1: "Mutterplattform",
  intgCap2: "Cloud-Infrastruktur",
  intgCap3: "Konversations-KI",
  intgCap4: "Sprachmodelle",
  intgCap5: "KI und Karten",
  intgCap6: "Nährwertdaten",
  intgCap7: "Zahlungen",
  intgFootnote: "ISO-27001-zertifizierte Anbieter · DSGVO-konform · Multi-Region-Hosting · Enterprise-Sicherheit ab Tag eins",

  jColEntry: "Einstieg",
  jColExperience: "Erlebnis",
  jColDashboard: "Dashboard-Intelligenz",
  jEntryDigitalTitle: "Digital",
  jEntryDigitalDesc: "Website, App-Stores, Suche, direkte Links — ein Tap von jeder digitalen Oberfläche ins Shopper-Erlebnis.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Sponsored Posts, Influencer-Inhalte, DMs — Feeds fließen direkt in TalkPack, ohne die Plattform zu verlassen.",
  jEntryPhysicalTitle: "Physisch",
  jEntryPhysicalDesc: "Auf der Verpackung, im Regal, an der Kasse — die realen Einstiegspunkte, an denen TalkPack einen Scan in eine Konversation verwandelt.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "POS im Laden",
  jEntryPhysPill3: "Verpackung",
  jExpVerifyLabel: "Verifizieren",
  jExpVerifyText: "KI-Produkterkennung — SKU in Echtzeit verifiziert.",
  jExpLearnLabel: "Lernen",
  jExpLearnBody: "Nährwertrad, Allergene, Zutaten, Herkunft — in der Sprache des Shoppers, von der Marke geprüft.",
  jExpEngageLabel: "Engagement",
  jExpEngagePlayBody: "Glücksrad, Gacha, Puzzle, Quiz — fünf Spielarten für Verweildauer.",
  jExpEngageEarnBody: "Punkte, Belege, Loyalty — Warenkorb-Intelligenz.",
  jExpEngageSayBody: "Konversations-KI, die in 40+ Sprachen antwortet.",
  jExpRewardLabel: "Belohnen",
  jExpRewardBody: "Sofortgewinne, Dual-QR-Validierung an der Kasse, GPS-verifizierte Einlösungen — auditbereit ab Tag eins.",
  jExpCaptureLabel: "Erfassen",
  jExpCaptureSub: "Jede Interaktion · von Ihnen erfasst und besessen",

  capEmail:   "E-Mail",
  capName:    "Name",
  capAddress: "Adresse",
  capGps:     "GPS",
  capProduct: "Produkt",
  capBasket:  "Warenkorb",
  capSpend:   "Ausgabe",
  capChat:    "Chat",

  dashShopperLabel: "Shopper-Intelligenz",
  dashShopperShort: "Shopper",
  dashShopper1Title: "First-Party-CRM",
  dashShopper1Desc: "E-Mail, Name, Adresse, Kontaktpräferenzen.",
  dashShopper2Title: "GPS-verifizierte Standorte",
  dashShopper2Desc: "Wo Shopper sich engagieren, nach Filialregion.",
  dashShopper3Title: "Kauffrequenz",
  dashShopper3Desc: "Wiederkehrende Scan-Muster, Besuchsrhythmus.",
  dashShopper4Title: "Warenkorbzusammensetzung",
  dashShopper4Desc: "Beleg-Daten auf Artikelebene, gemeinsam gekaufte Produkte.",

  dashConvLabel: "Konversations-Intelligenz",
  dashConvShort: "Konversation",
  dashConv1Title: "Fragenkategorien",
  dashConv1Desc: "Allergene, Zutaten, Nährwerte, Verwendung, Rezepte.",
  dashConv2Title: "Sentiment-Analyse",
  dashConv2Desc: "Positiv, negativ, neutral nach Produkt und Region.",
  dashConv3Title: "Verwirrungspunkte",
  dashConv3Desc: "Was Shopper nicht verstehen, wiederholte Fragen.",
  dashConv4Title: "Kaufabsicht",
  dashConv4Desc: "Vom Interesse bis zur Händleranfrage.",
  dashConv5Title: "Sprachpräferenzen",
  dashConv5Desc: "Welche Sprachen Shopper je nach Standort wählen.",

  dashDistLabel: "Distributions-Intelligenz",
  dashDistShort: "Distribution",
  dashDist1Title: "Verfügbarkeit im Laden",
  dashDist1Desc: "Produkt im Regal, durch geofenced Scans bestätigt.",
  dashDist2Title: "Out-of-Stock-Alerts",
  dashDist2Desc: "'Nicht hier'-Meldungen in Echtzeit markiert.",
  dashDist3Title: "Heatmaps",
  dashDist3Desc: "Scan-Dichte nach Postleitzahl, Region, Fascia.",
  dashDist4Title: "Haushaltsdurchdringung",
  dashDist4Desc: "'Überall scannen' zeigt, wo das Produkt zu Hause lebt.",
  dashDist5Title: "Filialvergleich",
  dashDist5Desc: "Engagement-Raten nach Standort, nach Fascia-Gruppe.",

  dashCampLabel: "Kampagnen-Performance",
  dashCampShort: "Kampagne",
  dashCamp1Title: "Scan-zu-Engagement-Rate",
  dashCamp1Desc: "Wie viele Scans zu vollständigen Interaktionen werden.",
  dashCamp2Title: "Spielabschlussraten",
  dashCamp2Desc: "Spins pro Session, Gewinnraten, Abbruchpunkte.",
  dashCamp3Title: "Einlösungs-Tracking",
  dashCamp3Desc: "Eingelöste, validierte, abgelaufene Preise nach Filiale.",
  dashCamp4Title: "ROI-Dashboard",
  dashCamp4Desc: "Kampagnenausgaben vs. erfasster Shopper-Wert.",

  dashCompLabel: "Compliance & Audit",
  dashCompShort: "Compliance",
  dashComp1Title: "ASA-Compliance-Score",
  dashComp1Desc: "Pro Kampagne, pro Konversation.",
  dashComp2Title: "Exportierbare Audit-Logs",
  dashComp2Desc: "Jede KI-Antwort protokolliert und abrufbar.",
  dashComp3Title: "Verstoß-Alerts",
  dashComp3Desc: "Markierte Inhaltsmuster.",
  dashComp4Title: "Nährwert-Engagement",
  dashComp4Desc: "Mit welchen Nährstoffen Shopper am Rad interagieren.",

  pvFormTitle: "Glückwunsch!",
  pvFormSub: "Sie haben gewonnen: 20 % Rabatt auf Ihren nächsten Einkauf",
  pvFormName: "Ihr Name",
  pvFormEmail: "Ihre E-Mail",
  pvFormPostcode: "Postleitzahl",
  pvFormCta: "PREIS EINLÖSEN",
  pvFormFootnote: "Privacy-first · DSGVO-konform",

  pvChatTitle: "KI-Botschafter",
  pvChatSub: "Trainiert auf Morning O's",
  pvChatBubble1: "Ist das glutenfrei?",
  pvChatBubble2: "Ja — zertifiziert glutenfrei und nur 9 g Zucker pro Portion. Möchten Sie Rezeptideen?",
  pvChatBubble3: "Ja, gerne",
  pvChatBubble4: "Probieren Sie Overnight Oats mit Beeren — 3 Min. Vorbereitung.",
  pvChatPlaceholder: "Fragen Sie mich alles…",
  pvChatFootnote: "40+ Sprachen · immer aktiv",

  pvMapTitle: "Finden Sie Morning O's in Ihrer Nähe",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "ROUTE BERECHNEN",
  pvMapFootnote: "GPS-verifiziert · Geofence im 150-m-Radius",
  pvMapOos: "ausverkauft",

  pvWheelTitle: "Morning O's · Glücksrad",
  pvWheelCredits: "350 Credits",
  pvWheelCta: "DREHEN",
  pvWheelFootnote: "Glücksrad · Nährwert-Rad · Gacha · Quiz · Puzzle",
  pvWheelWedgeWin: "GEWINN!",
  pvWheelWedgeTry: "Nochmal",

  pvNutTitle: "Morning O's · Nährwertangaben",
  pvNutPer100g: "Pro 100 g",
  pvNutPerServing: "Pro Portion",
  pvNutCta: "WEITER",
  pvNutFootnote: "ASA-konform · vor dem Spiel verpflichtend",

  bookDemoSubject: "TalkPack-Demo-Anfrage",
  bookDemoBody: "Hallo ShopAI-Team,\n\nich möchte gerne eine TalkPack-Demo buchen. Bitte melden Sie sich, um einen Termin zu vereinbaren.\n\nVielen Dank,",

  // ── Viz mock-data labels (DE) ──
  vizCapQuestions: "Top-Fragen der Shopper diese Woche",
  vizQAllergens: "Allergene", vizQIngredients: "Zutaten", vizQNutrition: "Nährwerte",
  vizQRecipes: "Rezepte", vizQUsage: "Verwendung",
  vizCapSentiment: "Letzte 30 Tage über alle Produkte",
  vizSentPositive: "Positiv", vizSentNeutral: "Neutral", vizSentNegative: "Negativ",
  vizCapConfusion: "Wiederholte Fragen — Inhaltslücken",
  vizConfusion1: "Ist das glutenfrei?",
  vizConfusion2: "Vegan geeignet?",
  vizConfusion3: "Nusswarnungen?",
  vizCapFunnel: "Konversion durch Konversation",
  vizFunnelCurious: "Neugierig", vizFunnelInterested: "Interessiert", vizFunnelReady: "Kaufbereit",
  vizCapAvail: "Produkt verfügbar, wo geplant",
  vizAvailDetail: "1.247 von 1.326 Filialen am Regal bestätigt",
  vizCapOos: "3 Filialen heute markiert · automatische Warnung gesendet",
  vizCapHeat: "London · Manchester · Birmingham führen",
  vizCapHousehold: "68 % der Scans passieren zu Hause — dort lebt Ihr Produkt",
  vizCapFascia: "Engagement-Rate nach Filialformat",
  vizCapEngrate: "Scans, die zu vollständigen Interaktionen werden",
  vizEngrateCompare: "+36 Pkt vs. 42 % Branchendurchschnitt",
  vizCapGame: "Wo Shopper abspringen — Spielbalance feinjustieren",
  vizGameStart: "Start", vizGamePlay: "Spiel", vizGameWin: "Gewinn", vizGameClaim: "Eingelöst",
  vizCapRdm: "96 % Validierungsrate · wenige Verfälle",
  vizRdmClaimed: "Eingelöst", vizRdmValidated: "Validiert", vizRdmExpired: "Verfallen",
  vizCapRoi: "Kampagnenausgaben vs. Shopper-Lifetime-Value",
  vizRoiSpend: "Ausgabe", vizRoiValueLabel: "Erfasster Wert", vizRoiReturn: "3,4× Return",
  vizCapComp: "Pro Kampagne · pro Konversation · auditbereit",
  vizCapAudit: "Jede KI-Antwort protokolliert · jederzeit abrufbar",
  vizCapViolation: "Verstöße diesen Monat · KI-Ausgabe kontinuierlich validiert",
  vizCapNut: "Nährstoffe, mit denen Shopper am Rad interagieren",
  vizNutSugar: "Zucker", vizNutFat: "Fett", vizNutSalt: "Salz", vizNutSatFat: "Ges. Fett",
  vizCapLang: "Shopper-Sprache automatisch erkannt",
  vizLangOther: "Andere",
  vizCapCrm: "Shopper erfasst",
  vizCapMap: "247 Standorte diese Woche",
  vizCapWeek: "Ø 2,3 Besuche pro Woche",
  vizDayMon: "Mo", vizDayTue: "Di", vizDayWed: "Mi",
  vizDayThu: "Do", vizDayFri: "Fr", vizDaySat: "Sa", vizDaySun: "So",
  vizCapBasket: "Ø Warenkorb: 8,40 £ · 6,2 Artikel",
  vizBasketDairy: "Molkerei", vizBasketBakery: "Bäckerei", vizBasketSnacks: "Snacks", vizBasketOther: "Sonstiges",
};

// ────────── Italiano ──────────
const it: LandingCopy = {
  ...en,
  navPlatform: "Piattaforma",
  navModules: "Moduli",
  navPortal: "Accesso al Portale",
  navLangButton: "IT",
  navRegister: "Registrati",
  navBookDemo: "Prenota una demo",
  navLogin: "Accedi",

  trustLeft: "Senza app · Senza integrazione · Senza dipendenza dal punto vendita",
  trustRight: "Dal 2024 · Regno Unito · Insieme a The Wholesale Group · Caterers Choice · Passot (Giappone) · Talysis UK · Jakavarna Group (Thailandia)",

  heroEyebrow: "Un copilota per il commercio",
  heroL1: "Ogni prodotto. Una conversazione.",
  heroL2: "Ogni scansione. Dati reali.",
  heroSub: "TalkPack trasforma qualsiasi prodotto in una conversazione dal vivo. Gli shopper scansionano, imparano, giocano, chiedono, vincono, accumulano. Tu ottieni dati first-party, conformità HFSS e una relazione diretta con gli shopper nei punti vendita fisici. Nessuna app. Nessuna integrazione. Nessuna dipendenza dal punto vendita.",
  heroCtaPrimary: "Prenota una demo",
  heroCtaGhost: "Scopri come funziona",
  heroPillAllergens: "Allergeni",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Ingredienti",
  heroVerified: "Verificato",

  statStores: "Punti vendita · Regno Unito · espansione globale",
  statLanguages: "Lingue · rilevamento automatico",
  statGames: "Dal brief alla campagna live",
  statModules: "Ruota nutrizionale integrata",
  statSteps: "Il tuo brand. I tuoi dati. La tua AI.",
  statDownloads: "Download di app · integrazioni · attriti",
  statGeofence: "Dati shopper dal punto vendita fisico",
  statScans: "Dallo scaffale al CRM dello shopper",

  fullJourneyEyebrow: "Il percorso completo",
  fullJourneyHeading: "Dall'ingresso all'intelligenza. Una sola piattaforma.",
  fullJourneySub: "Ogni canale. Ogni modulo. Ogni dato. Ecco come l'architettura TalkPack si collega end-to-end.",
  fullJourneyCaption: "Flusso completo. Dall'ingresso dello shopper all'intelligenza di marca. Esportabile via dashboard, chat AI o query SmartMail.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Una piattaforma. Cinque modi per coinvolgere lo shopper.",
  picSub: "I brand scelgono i moduli che servono. Gli shopper vivono un mix di esperienze. Ogni modulo passa dalla stessa scansione, dalla stessa conversazione, dallo stesso strato dati.",
  learnDesc: "Educazione al prodotto, trasparenza degli ingredienti, semafori HFSS.",
  learnF1: "Ruota nutrizionale", learnF2: "Scansione allergeni", learnF3: "Dati di provenienza",
  playDesc: "Cinque tipi di gioco che aumentano il tempo di permanenza e il riengagement.",
  playF1: "Ruota della fortuna", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Ruota nutrizionale",
  sayDesc: "AI conversazionale che risponde alle domande dello shopper nella sua lingua.",
  sayF1: "40+ lingue", sayF2: "Q&A sugli ingredienti", sayF3: "Suggerimenti di ricette",
  winDesc: "Premi, drive-to-store, validazione dual-QR alla cassa, bolle di consegna.",
  winF1: "Ricompensa istantanea", winF2: "Premi geolocalizzati", winF3: "Consegna a domicilio",
  earnDesc: "Punti, scansione scontrini, programma fedeltà, intelligenza sul carrello.",
  earnF1: "OCR scontrino", earnF2: "Profilo cross-brand", earnF3: "Catalogo premi",
  moduleSeeMore: "Vedi modulo",

  oeEyebrow: "Un motore · Molte dinamiche",
  oeHeading: "TalkPack è parte di una piattaforma più ampia.",
  oeSub: "ShopAI offre a brand, retailer e grossisti un unico strato dati. Business intelligence conversazionale end-to-end. TalkPack è il modo in cui lo shopper entra nella conversazione.",
  oe1Strap: "Engagement shopper dal punto vendita fisico",
  oe2Strap: "AI per il settore della prossimità",
  oe3Strap: "Intelligenza per audit sul campo",
  oe4Strap: "Punti, scontrini, dati del carrello",
  oeActiveLabel: "Attuale",

  ucHeading: "Ruoli diversi. Stessa piattaforma.",
  ucBrandTitle: "Marketing di marca",
  ucBrand1: "Lancio nazionale di prodotto su distribuzione mista",
  ucBrand2: "Drive-to-store verso i punti vendita aderenti",
  ucBrand3: "Engagement on-pack conforme HFSS",
  ucBrand4: "Profilazione shopper cross-brand",
  ucBrand5: "Categorie regolamentate (farma, alcol) con risposte AI auditate",
  ucRetailerTitle: "Retailer / Grossista",
  ucRetailer1: "Promozioni esclusive finanziate dal retailer",
  ucRetailer2: "Attivazione symbol group",
  ucRetailer3: "Audit di conformità a scaffale",
  ucRetailer4: "Fidelizzazione specifica del retailer",
  ucAgencyTitle: "Agenzia / Reseller",
  ucAgency1: "Campagne white-label per i tuoi clienti",
  ucAgency2: "Accesso al portale multi-cliente",
  ucAgency3: "Report trade marketing in PDF",
  ucAgency4: "Contenuti social generati automaticamente",

  hfssEyebrow: "Costruito per HFSS",
  hfssHeading: "Conformità HFSS, integrata.",
  hfssSub: "Lo shopper vede il profilo nutrizionale prima di giocare. Grassi, zucchero, sale, grassi saturi — semafori in base alle assunzioni giornaliere di riferimento. Obbligatorio. Auditabile. Quattro livelli di difesa regolatoria.",
  hfss1Title: "Ruota nutrizionale obbligatoria.",
  hfss1Body: "Ogni prodotto HFSS mostra i semafori di grassi, zucchero, sale e grassi saturi rispetto alle assunzioni giornaliere prima che lo shopper possa procedere.",
  hfss2Title: "Approccio educativo prima di tutto.",
  hfss2Body: "La piattaforma promuove la trasparenza nutrizionale. Posiziona le interazioni shopper come scelta informata, non come spinta promozionale.",
  hfss3Title: "Validazione degli output.",
  hfss3Body: "L'AI conversazionale è monitorata su pattern regolatori. Ogni risposta è registrata, filtrata e auditabile.",
  hfss4Title: "Log di audit esportabili.",
  hfss4Body: "Punteggio di conformità ASA, ogni risposta registrata e recuperabile, alert per contenuti segnalati.",
  hfssBadge: "Conformità ASA: 98,4 %",

  wlEyebrow: "Il tuo brand, in qualunque lingua",
  wlHeading: "I tuoi dati. La tua AI. A modo tuo.",
  wlLangCaption: "40+ lingue. Rilevamento automatico o impostazione manuale per campagna.",
  wlFeat1Title: "La tua identità di marca",
  wlFeat1Body: "Logo, colori, font applicati a ogni schermata shopper e a ogni bolla di consegna.",
  wlFeat2Title: "I tuoi dati, la tua AI",
  wlFeat2Body: "Dati archiviati sul tuo account. AI addestrata sui tuoi prodotti. La relazione è tua.",
  wlFeat3Title: "I tuoi punti vendita, il tuo geofence",
  wlFeat3Body: "Modalità universale o per punto vendita. Geofence di default a 150 m, configurabile per campagna.",
  wlFeat4Title: "Il tuo team, i tuoi accessi",
  wlFeat4Body: "Permessi per ruolo. Brand, agenzia, campo, finanza — ognuno con la vista giusta.",

  proofEyebrow: "In esercizio",
  proofHeading: "Già live. Già operativo.",
  proofStat1Label: "punti vendita UK nel database campagne",
  proofStat2Label: "lingue supportate",
  proofStat3Label: "Fondata nel Regno Unito",
  proofQuotePlaceholder: "Casi cliente in arrivo.",

  faqHeading: "Le domande che ci fanno i brand.",
  faq1Q: "Gli shopper devono scaricare un'app?",
  faq1A: "No. TalkPack gira nel browser. Una scansione apre l'esperienza.",
  faq2Q: "TalkPack si integra con il nostro EPOS o CRM?",
  faq2A: "Nessuna integrazione richiesta. I dati raccolti sono first-party ed esportabili via PDF, CSV o API.",
  faq3Q: "Cosa succede ai prodotti HFSS?",
  faq3A: "Ogni prodotto HFSS mostra una ruota nutrizionale obbligatoria prima del gioco. La conformità è integrata, non aggiunta.",
  faq4Q: "Quali lingue supportate?",
  faq4A: "40+. La piattaforma rileva automaticamente o la imposti tu per campagna.",
  faq5Q: "Possiamo lanciare una campagna solo in alcuni retailer?",
  faq5A: "Sì. La modalità per punto vendita geofencia la campagna sui punti scelti. La modalità universale lascia giocare qualunque shopper ovunque.",
  faq6Q: "Quanto ci vuole a costruire una campagna?",
  faq6A: "Circa 10 minuti per una promozione standard. Wizard passo-passo nel portale.",
  faq7Q: "Possiamo personalizzare l'esperienza in white-label?",
  faq7A: "Sì. Il tuo logo, i tuoi colori, la tua lingua. Lo shopper non vede il marchio TalkPack a meno che tu non lo voglia.",

  footerColPlatform: "Piattaforma",
  footerColModules: "Moduli",
  footerColCompany: "Azienda",
  footerColResources: "Risorse",
  footerCompany1: "Chi siamo",
  footerCompany2: "Contatti",
  footerResources3: "Privacy",
  footerResources4: "Termini",
  footerNewsHeading: "Aggiornamenti trimestrali. Niente spam.",
  footerNewsPlaceholder: "tua@email.com",
  footerNewsSubmit: "Iscriviti",
  footerRegistration: "ShopAI Ltd · Numero di società 15877093 · Registrata in Inghilterra",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (IT) ──
  heroAlt: "Acquirente in una corsia di supermercato — ogni punto di contatto dell'esperienza TalkPack che le ruota attorno",

  stepsTitle: "Tre passi. Una piattaforma.",
  stepsSub: "Dal momento in cui lo shopper entra al momento in cui imparate qualcosa su di lui. Semplice da gestire. Ricco di dati.",
  step1Title: "Entrare",
  step1Desc: "Lo shopper tocca o scansiona da qualunque canale. Un ingresso, un'esperienza brandizzata.",
  step1PillRow: "Sito · Social · Email · QR · OOH · In negozio",
  step2Title: "Coinvolgere",
  step2Desc: "Cinque moduli Pic-n-Mix nella sua lingua, con il vostro brand, verificati dall'IA.",
  step2PillRow: "Imparare · Giocare · Chiedere · Vincere · Accumulare",
  step3Title: "Imparare",
  step3Desc: "Ogni interazione diventa dato first-party, in tempo reale sulla vostra dashboard.",
  step3PillRow: "Shopper · Conversazione · Luogo · Engagement · Conformità",

  chipWebsite: "Sito",
  chipSocial:  "Social",
  chipEmail:   "Email",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "In negozio",
  chipLearn:   "Imparare",
  chipPlay:    "Giocare",
  chipSay:     "Chiedere",
  chipWin:     "Vincere",
  chipEarn:    "Accumulare",
  chipShoppers:     "Shopper",
  chipConversation: "Conversazione",
  chipLocation:     "Luogo",
  chipEngagement:   "Engagement",
  chipCompliance:   "Conformità",
  chipAriaEntry:  "Canale di ingresso",
  chipAriaModule: "Modulo Pic-n-Mix",
  chipAriaIntel:  "Categoria di intelligence",

  intgTitle: "Infrastruttura seria. Tecnologia comprovata.",
  intgSub: "TalkPack si appoggia sulle stesse fondamenta cloud, IA e conformità di cui si fidano le aziende più grandi del mondo.",
  intgCap1: "Piattaforma madre",
  intgCap2: "Infrastruttura cloud",
  intgCap3: "IA conversazionale",
  intgCap4: "Modelli linguistici",
  intgCap5: "IA e mappe",
  intgCap6: "Dati nutrizionali",
  intgCap7: "Pagamenti",
  intgFootnote: "Provider certificati ISO 27001 · Conformi GDPR · Hosting multi-regione · Sicurezza enterprise dal primo giorno",

  jColEntry: "Ingresso",
  jColExperience: "Esperienza",
  jColDashboard: "Intelligence Dashboard",
  jEntryDigitalTitle: "Digitale",
  jEntryDigitalDesc: "Sito, app store, ricerca, link diretti — un tap da qualsiasi superficie digitale all'esperienza shopper.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Post sponsorizzati, contenuti di influencer, DM — i flussi confluiscono in TalkPack senza lasciare la piattaforma.",
  jEntryPhysicalTitle: "Fisico",
  jEntryPhysicalDesc: "Sulla confezione, a scaffale, in cassa — i punti di ingresso reali in cui TalkPack trasforma una scansione in conversazione.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "POS in negozio",
  jEntryPhysPill3: "Packaging",
  jExpVerifyLabel: "Verificare",
  jExpVerifyText: "Riconoscimento prodotto IA — SKU verificato in tempo reale.",
  jExpLearnLabel: "Imparare",
  jExpLearnBody: "Ruota nutrizionale, allergeni, ingredienti, provenienza — mostrati nella lingua dello shopper, validati dal brand.",
  jExpEngageLabel: "Coinvolgere",
  jExpEngagePlayBody: "Ruota, gacha, puzzle, quiz — cinque tipi di gioco per il dwell time.",
  jExpEngageEarnBody: "Punti, scontrini, fedeltà — intelligence sul carrello.",
  jExpEngageSayBody: "IA conversazionale che risponde in 40+ lingue.",
  jExpRewardLabel: "Premiare",
  jExpRewardBody: "Premi istantanei, validazione dual-QR in cassa, reclami verificati via GPS — pronti per audit dal primo giorno.",
  jExpCaptureLabel: "Catturare",
  jExpCaptureSub: "Ogni interazione · catturata e di vostra proprietà",

  capEmail:   "Email",
  capName:    "Nome",
  capAddress: "Indirizzo",
  capGps:     "GPS",
  capProduct: "Prodotto",
  capBasket:  "Carrello",
  capSpend:   "Spesa",
  capChat:    "Chat",

  dashShopperLabel: "Intelligence shopper",
  dashShopperShort: "Shopper",
  dashShopper1Title: "CRM first-party",
  dashShopper1Desc: "Email, nome, indirizzo, preferenze di contatto.",
  dashShopper2Title: "Località verificate via GPS",
  dashShopper2Desc: "Dove gli shopper si attivano per regione punto vendita.",
  dashShopper3Title: "Frequenza d'acquisto",
  dashShopper3Desc: "Pattern di scansione ripetuti, cadenza delle visite.",
  dashShopper4Title: "Composizione del carrello",
  dashShopper4Desc: "Dati scontrino a livello articolo, prodotti co-acquistati.",

  dashConvLabel: "Intelligence conversazione",
  dashConvShort: "Conversazione",
  dashConv1Title: "Categorie di domande",
  dashConv1Desc: "Allergeni, ingredienti, nutrizione, uso, ricette.",
  dashConv2Title: "Analisi del sentiment",
  dashConv2Desc: "Positivo, negativo, neutro per prodotto e regione.",
  dashConv3Title: "Punti di confusione",
  dashConv3Desc: "Cosa gli shopper non capiscono, domande ripetute.",
  dashConv4Title: "Intenzione d'acquisto",
  dashConv4Desc: "Dalla curiosità alle richieste al retailer.",
  dashConv5Title: "Preferenze linguistiche",
  dashConv5Desc: "Quali lingue gli shopper scelgono in base alla località.",

  dashDistLabel: "Intelligence distribuzione",
  dashDistShort: "Distribuzione",
  dashDist1Title: "Disponibilità in negozio",
  dashDist1Desc: "Prodotto a scaffale, confermato da scansioni geofenced.",
  dashDist2Title: "Avvisi di rottura di stock",
  dashDist2Desc: "Segnalazioni 'Non c'è' marcate in tempo reale.",
  dashDist3Title: "Mappe di calore",
  dashDist3Desc: "Densità di scansione per CAP, regione, fascia.",
  dashDist4Title: "Penetrazione domestica",
  dashDist4Desc: "'Scansiona ovunque' mostra dove vive il prodotto a casa.",
  dashDist5Title: "Confronto punti vendita",
  dashDist5Desc: "Tassi di engagement per località, per gruppo fascia.",

  dashCampLabel: "Performance campagna",
  dashCampShort: "Campagna",
  dashCamp1Title: "Tasso scansione-a-engagement",
  dashCamp1Desc: "Quante scansioni si convertono in interazioni complete.",
  dashCamp2Title: "Tassi di completamento gioco",
  dashCamp2Desc: "Spin per sessione, tassi di vincita, punti di abbandono.",
  dashCamp3Title: "Tracciamento riscatti",
  dashCamp3Desc: "Premi rivendicati, validati, scaduti, per punto vendita.",
  dashCamp4Title: "Dashboard ROI",
  dashCamp4Desc: "Spesa di campagna vs valore shopper acquisito.",

  dashCompLabel: "Conformità & audit",
  dashCompShort: "Conformità",
  dashComp1Title: "Punteggio conformità ASA",
  dashComp1Desc: "Per campagna, per conversazione.",
  dashComp2Title: "Log di audit esportabili",
  dashComp2Desc: "Ogni risposta IA registrata e recuperabile.",
  dashComp3Title: "Avvisi di violazione",
  dashComp3Desc: "Pattern di contenuto segnalati.",
  dashComp4Title: "Engagement nutrizionale",
  dashComp4Desc: "Con quali nutrienti gli shopper interagiscono sulla ruota.",

  pvFormTitle: "Congratulazioni!",
  pvFormSub: "Hai vinto: 20% di sconto sul tuo prossimo acquisto",
  pvFormName: "Il tuo nome",
  pvFormEmail: "La tua email",
  pvFormPostcode: "CAP",
  pvFormCta: "RISCATTA",
  pvFormFootnote: "Privacy-first · Conforme GDPR",

  pvChatTitle: "Ambasciatore IA",
  pvChatSub: "Addestrato su Morning O's",
  pvChatBubble1: "È senza glutine?",
  pvChatBubble2: "Sì — certificato senza glutine e solo 9g di zucchero per porzione. Vuoi qualche idea ricetta?",
  pvChatBubble3: "Sì grazie",
  pvChatBubble4: "Prova l'overnight oats con frutti di bosco — 3 min di prep.",
  pvChatPlaceholder: "Chiedimi qualsiasi cosa…",
  pvChatFootnote: "40+ lingue · sempre attivo",

  pvMapTitle: "Trova Morning O's vicino a te",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "INDICAZIONI",
  pvMapFootnote: "Verificato GPS · geofence entro 150m",
  pvMapOos: "esaurito",

  pvWheelTitle: "Morning O's · Gira e vinci",
  pvWheelCredits: "350 crediti",
  pvWheelCta: "GIRA",
  pvWheelFootnote: "Ruota · Ruota nutrizionale · Gacha · Quiz · Puzzle",
  pvWheelWedgeWin: "VINCI!",
  pvWheelWedgeTry: "Riprova",

  pvNutTitle: "Morning O's · Informazioni nutrizionali",
  pvNutPer100g: "Per 100 g",
  pvNutPerServing: "Per porzione",
  pvNutCta: "CONTINUA",
  pvNutFootnote: "Conforme ASA · obbligatorio prima di giocare",

  bookDemoSubject: "Richiesta demo TalkPack",
  bookDemoBody: "Salve team ShopAI,\n\nvorrei prenotare una demo di TalkPack. Potete contattarmi per fissare un appuntamento?\n\nGrazie,",

  // ── Viz mock-data labels (IT) ──
  vizCapQuestions: "Domande principali degli shopper questa settimana",
  vizQAllergens: "Allergeni", vizQIngredients: "Ingredienti", vizQNutrition: "Nutrizione",
  vizQRecipes: "Ricette", vizQUsage: "Uso",
  vizCapSentiment: "Ultimi 30 giorni su tutti i prodotti",
  vizSentPositive: "Positivo", vizSentNeutral: "Neutro", vizSentNegative: "Negativo",
  vizCapConfusion: "Domande ricorrenti — lacune di contenuto",
  vizConfusion1: "È senza glutine?",
  vizConfusion2: "Adatto ai vegani?",
  vizConfusion3: "Avvertenze sulla frutta a guscio?",
  vizCapFunnel: "Conversione tramite conversazione",
  vizFunnelCurious: "Curioso", vizFunnelInterested: "Interessato", vizFunnelReady: "Pronto all'acquisto",
  vizCapAvail: "Prodotto disponibile dove l'avete pianificato",
  vizAvailDetail: "1.247 su 1.326 punti vendita confermati a scaffale",
  vizCapOos: "3 punti vendita segnalati oggi · alert automatico inviato",
  vizCapHeat: "Londra · Manchester · Birmingham in testa",
  vizCapHousehold: "Il 68 % delle scansioni avviene a casa — il prodotto vive lì",
  vizCapFascia: "Tasso di engagement per insegna",
  vizCapEngrate: "Scansioni che diventano interazioni complete",
  vizEngrateCompare: "+36 pt vs 42 % del benchmark di settore",
  vizCapGame: "Dove gli shopper abbandonano — calibrate il gioco",
  vizGameStart: "Inizio", vizGamePlay: "Gioca", vizGameWin: "Vinci", vizGameClaim: "Riscossa",
  vizCapRdm: "96 % di tasso di convalida · poche scadenze",
  vizRdmClaimed: "Richiesti", vizRdmValidated: "Validati", vizRdmExpired: "Scaduti",
  vizCapRoi: "Spesa di campagna vs valore di vita dello shopper",
  vizRoiSpend: "Spesa", vizRoiValueLabel: "Valore catturato", vizRoiReturn: "3,4× ritorno",
  vizCapComp: "Per campagna · per conversazione · pronto per audit",
  vizCapAudit: "Ogni risposta IA registrata · recuperabile su richiesta",
  vizCapViolation: "Violazioni questo mese · output IA validato in continuo",
  vizCapNut: "Nutrienti con cui gli shopper interagiscono sulla ruota",
  vizNutSugar: "Zuccheri", vizNutFat: "Grassi", vizNutSalt: "Sale", vizNutSatFat: "Saturi",
  vizCapLang: "Lingua dello shopper rilevata automaticamente",
  vizLangOther: "Altro",
  vizCapCrm: "shopper acquisiti",
  vizCapMap: "247 località questa settimana",
  vizCapWeek: "Media 2,3 visite a settimana",
  vizDayMon: "Lun", vizDayTue: "Mar", vizDayWed: "Mer",
  vizDayThu: "Gio", vizDayFri: "Ven", vizDaySat: "Sab", vizDaySun: "Dom",
  vizCapBasket: "Carrello medio: 8,40 £ · 6,2 articoli",
  vizBasketDairy: "Latticini", vizBasketBakery: "Panetteria", vizBasketSnacks: "Snack", vizBasketOther: "Altro",
};

// ────────── Polski ──────────
const pl: LandingCopy = {
  ...en,
  navPlatform: "Platforma",
  navModules: "Moduły",
  navPortal: "Logowanie do Portalu",
  navLangButton: "PL",
  navRegister: "Zarejestruj się",
  navBookDemo: "Zarezerwuj demo",
  navLogin: "Zaloguj się",

  trustLeft: "Bez aplikacji · Bez integracji · Bez zależności od sklepu",
  trustRight: "Od 2024 · Wielka Brytania · Wspólnie z The Wholesale Group · Caterers Choice · Passot (Japonia) · Talysis UK · Jakavarna Group (Tajlandia)",

  heroEyebrow: "Drugi pilot dla handlu",
  heroL1: "Każdy produkt. Rozmowa.",
  heroL2: "Każdy skan. Realne dane.",
  heroSub: "TalkPack zamienia każdy produkt w żywą rozmowę. Klienci skanują, uczą się, grają, pytają, wygrywają, zbierają. Ty otrzymujesz dane first-party, zgodność z HFSS i bezpośrednią relację z klientami w sklepach stacjonarnych. Bez aplikacji. Bez integracji. Bez zależności od sklepu.",
  heroCtaPrimary: "Zarezerwuj demo",
  heroCtaGhost: "Zobacz, jak to działa",
  heroPillAllergens: "Alergeny",
  heroPillHfss: "HFSS",
  heroPillIngredients: "Składniki",
  heroVerified: "Zweryfikowane",

  statStores: "Sklepy · Wielka Brytania · ekspansja globalna",
  statLanguages: "Języki · automatyczne wykrywanie",
  statGames: "Od briefu do działającej kampanii",
  statModules: "Wbudowane koło wartości odżywczych",
  statSteps: "Twoja marka. Twoje dane. Twoja AI.",
  statDownloads: "Pobrania aplikacji · integracje · tarcia",
  statGeofence: "Dane shopperów ze sklepów stacjonarnych",
  statScans: "Od półki do CRM klienta",

  fullJourneyEyebrow: "Cała ścieżka",
  fullJourneyHeading: "Od wejścia do inteligencji. Jedna platforma.",
  fullJourneySub: "Każdy kanał. Każdy moduł. Każdy punkt danych. Tak architektura TalkPack łączy się od początku do końca.",
  fullJourneyCaption: "Pełny przepływ. Od wejścia klienta do inteligencji marki. Eksport przez dashboard, czat AI lub zapytania SmartMail.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Jedna platforma. Pięć sposobów na zaangażowanie klienta.",
  picSub: "Marki wybierają moduły, których potrzebują. Klienci dostają mix doświadczeń. Każdy moduł działa w oparciu o ten sam skan, tę samą rozmowę, tę samą warstwę danych.",
  learnDesc: "Edukacja produktowa, transparentność składników, sygnalizacja świetlna HFSS.",
  learnF1: "Koło wartości odżywczych", learnF2: "Skan alergenów", learnF3: "Dane o pochodzeniu",
  playDesc: "Pięć typów gier zwiększających czas spędzony i powracalność.",
  playF1: "Koło fortuny", playF2: "Gacha", playF3: "Puzzle", playF4: "Quiz", playF5: "Koło odżywcze",
  sayDesc: "Konwersacyjna AI, która odpowiada na pytania klientów w ich języku.",
  sayF1: "40+ języków", sayF2: "Q&A o składnikach", sayF3: "Sugestie przepisów",
  winDesc: "Nagrody, drive-to-store, walidacja dual-QR przy kasie, listy przewozowe.",
  winF1: "Natychmiastowa nagroda", winF2: "Nagrody geofencowane", winF3: "Dostawa do domu",
  earnDesc: "Punkty, skan paragonu, program lojalnościowy, inteligencja koszyka.",
  earnF1: "OCR paragonu", earnF2: "Profil cross-brand", earnF3: "Katalog nagród",
  moduleSeeMore: "Zobacz moduł",

  oeEyebrow: "Jeden silnik · Wiele dynamik",
  oeHeading: "TalkPack to część szerszej platformy.",
  oeSub: "ShopAI daje markom, sieciom i hurtowniom jedną warstwę danych. Konwersacyjna business intelligence end-to-end. TalkPack to sposób, w jaki klient dołącza do rozmowy.",
  oe1Strap: "Zaangażowanie klienta ze sklepu stacjonarnego",
  oe2Strap: "AI dla sektora convenience",
  oe3Strap: "Inteligencja audytów terenowych",
  oe4Strap: "Punkty, paragony, dane koszyka",
  oeActiveLabel: "Aktualne",

  ucHeading: "Różne role. Ta sama platforma.",
  ucBrandTitle: "Marketing marki",
  ucBrand1: "Krajowe wprowadzenie produktu w handlu mieszanym",
  ucBrand2: "Drive-to-store do sklepów uczestniczących",
  ucBrand3: "Engagement on-pack zgodny z HFSS",
  ucBrand4: "Profilowanie klienta cross-brand",
  ucBrand5: "Kategorie regulowane (farma, alkohol) z audytowanymi odpowiedziami AI",
  ucRetailerTitle: "Sieć / Hurtownik",
  ucRetailer1: "Ekskluzywne promocje finansowane przez sieć",
  ucRetailer2: "Aktywacja symbol group",
  ucRetailer3: "Audyt zgodności półek",
  ucRetailer4: "Lojalność dedykowana sieci",
  ucAgencyTitle: "Agencja / Reseller",
  ucAgency1: "Kampanie white-label dla klientów",
  ucAgency2: "Wieloklientowy dostęp do portalu",
  ucAgency3: "Raporty trade marketing w PDF",
  ucAgency4: "Treści social generowane automatycznie",

  hfssEyebrow: "Stworzone pod HFSS",
  hfssHeading: "Zgodność HFSS, wbudowana.",
  hfssSub: "Klient widzi profil odżywczy przed grą. Tłuszcz, cukier, sól, tłuszcze nasycone — sygnalizacja świetlna względem dziennego spożycia referencyjnego. Obowiązkowe. Audytowalne. Cztery warstwy obrony regulacyjnej.",
  hfss1Title: "Obowiązkowe koło odżywcze.",
  hfss1Body: "Każdy produkt HFSS pokazuje sygnalizację świetlną tłuszczu, cukru, soli i tłuszczów nasyconych względem dziennego spożycia, zanim klient może przejść dalej.",
  hfss2Title: "Najpierw edukacja.",
  hfss2Body: "Platforma promuje transparentność odżywczą. Pozycjonuje interakcje klienta jako świadomy wybór, nie nacisk promocyjny.",
  hfss3Title: "Walidacja wyników.",
  hfss3Body: "Konwersacyjna AI jest monitorowana pod kątem wzorców regulacyjnych. Każda odpowiedź jest logowana, filtrowana i audytowalna.",
  hfss4Title: "Eksportowalne logi audytu.",
  hfss4Body: "Wynik zgodności ASA, każda odpowiedź zalogowana i odzyskiwalna, alerty przy oznaczonej treści.",
  hfssBadge: "Zgodność ASA: 98,4 %",

  wlEyebrow: "Twoja marka, w dowolnym języku",
  wlHeading: "Twoje dane. Twoja AI. Po twojemu.",
  wlLangCaption: "40+ języków. Wykrywanie automatyczne lub ustawienie ręczne na kampanię.",
  wlFeat1Title: "Twoja tożsamość marki",
  wlFeat1Body: "Logo, kolory, czcionki na każdym ekranie klienta i każdym liście przewozowym.",
  wlFeat2Title: "Twoje dane, twoja AI",
  wlFeat2Body: "Dane przechowywane na twoim koncie. AI trenowana na twoich produktach. Relacja należy do ciebie.",
  wlFeat3Title: "Twoje sklepy, twój geofence",
  wlFeat3Body: "Tryb uniwersalny lub na sklep. Domyślny geofence 150 m, konfigurowalny na kampanię.",
  wlFeat4Title: "Twój zespół, twój dostęp",
  wlFeat4Body: "Uprawnienia oparte na rolach. Marka, agencja, teren, finanse — każdy ze swoim widokiem.",

  proofEyebrow: "Wdrożone",
  proofHeading: "Już działa. Już skuteczne.",
  proofStat1Label: "sklepów UK w bazie kampanii",
  proofStat2Label: "obsługiwanych języków",
  proofStat3Label: "Założona w Wielkiej Brytanii",
  proofQuotePlaceholder: "Historie klientów wkrótce.",

  faqHeading: "Pytania, które zadają nam marki.",
  faq1Q: "Czy klienci muszą pobierać aplikację?",
  faq1A: "Nie. TalkPack działa w przeglądarce. Jeden skan otwiera doświadczenie.",
  faq2Q: "Czy TalkPack integruje się z naszym EPOS lub CRM?",
  faq2A: "Bez integracji. Zebrane dane są first-party i eksportowalne do PDF, CSV lub przez API.",
  faq3Q: "Co dzieje się z produktami HFSS?",
  faq3A: "Każdy produkt HFSS pokazuje obowiązkowe koło odżywcze przed grą. Zgodność jest wbudowana, nie doklejona.",
  faq4Q: "Jakie języki obsługujecie?",
  faq4A: "40+. Platforma wykrywa automatycznie lub ustawiasz na kampanię.",
  faq5Q: "Czy możemy uruchomić kampanię tylko w wybranych sieciach?",
  faq5A: "Tak. Tryb na sklep geofence'uje kampanię do wybranych punktów. Tryb uniwersalny pozwala każdemu klientowi grać wszędzie.",
  faq6Q: "Ile zajmuje zbudowanie kampanii?",
  faq6A: "Około 10 minut dla standardowej promocji. Krok po kroku w kreatorze portalu.",
  faq7Q: "Czy możemy mieć doświadczenie white-label?",
  faq7A: "Tak. Twoje logo, kolory, język. Klient nie widzi marki TalkPack, chyba że tego chcesz.",

  footerColPlatform: "Platforma",
  footerColModules: "Moduły",
  footerColCompany: "Firma",
  footerColResources: "Zasoby",
  footerCompany1: "O nas",
  footerCompany2: "Kontakt",
  footerResources3: "Prywatność",
  footerResources4: "Regulamin",
  footerNewsHeading: "Kwartalne aktualizacje. Bez spamu.",
  footerNewsPlaceholder: "twoj@email.com",
  footerNewsSubmit: "Subskrybuj",
  footerRegistration: "ShopAI Ltd · Nr spółki 15877093 · Zarejestrowana w Anglii",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (PL) ──
  heroAlt: "Klientka w alejce supermarketu — każdy punkt styku z doświadczeniem TalkPack krąży wokół niej",

  stepsTitle: "Trzy kroki. Jedna platforma.",
  stepsSub: "Od chwili, gdy klient wchodzi, do chwili, gdy poznajesz coś o nim. Proste w obsłudze. Bogate w dane.",
  step1Title: "Wejdź",
  step1Desc: "Klient stuka lub skanuje z dowolnego kanału. Jedno wejście, jedno markowe doświadczenie.",
  step1PillRow: "Strona · Social · Email · QR · OOH · W sklepie",
  step2Title: "Zaangażuj",
  step2Desc: "Pięć modułów Pic-n-Mix w jego języku, z Twoją marką, weryfikowane przez AI.",
  step2PillRow: "Ucz · Graj · Pytaj · Wygrywaj · Zbieraj",
  step3Title: "Ucz się",
  step3Desc: "Każda interakcja staje się danymi first-party, na żywo na Twoim dashboardzie.",
  step3PillRow: "Klienci · Konwersacja · Lokalizacja · Zaangażowanie · Zgodność",

  chipWebsite: "Strona",
  chipSocial:  "Social",
  chipEmail:   "Email",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "W sklepie",
  chipLearn:   "Ucz",
  chipPlay:    "Graj",
  chipSay:     "Pytaj",
  chipWin:     "Wygrywaj",
  chipEarn:    "Zbieraj",
  chipShoppers:     "Klienci",
  chipConversation: "Konwersacja",
  chipLocation:     "Lokalizacja",
  chipEngagement:   "Zaangażowanie",
  chipCompliance:   "Zgodność",
  chipAriaEntry:  "Kanał wejścia",
  chipAriaModule: "Moduł Pic-n-Mix",
  chipAriaIntel:  "Kategoria inteligencji",

  intgTitle: "Solidna infrastruktura. Sprawdzona technologia.",
  intgSub: "TalkPack opiera się na tych samych fundamentach chmurowych, AI i zgodności, którym ufają największe firmy świata.",
  intgCap1: "Platforma macierzysta",
  intgCap2: "Infrastruktura chmurowa",
  intgCap3: "AI konwersacyjne",
  intgCap4: "Modele językowe",
  intgCap5: "AI i mapy",
  intgCap6: "Dane żywieniowe",
  intgCap7: "Płatności",
  intgFootnote: "Dostawcy z certyfikatem ISO 27001 · Zgodni z RODO · Hosting wieloregionalny · Bezpieczeństwo enterprise od pierwszego dnia",

  jColEntry: "Wejście",
  jColExperience: "Doświadczenie",
  jColDashboard: "Inteligencja Dashboard",
  jEntryDigitalTitle: "Cyfrowo",
  jEntryDigitalDesc: "Strona, sklepy z aplikacjami, wyszukiwarka, linki bezpośrednie — jeden tap z dowolnej cyfrowej powierzchni do doświadczenia klienta.",
  jEntrySocialTitle: "Social",
  jEntrySocialDesc: "Posty sponsorowane, treści influencerów, DM-y — strumienie trafiają wprost do TalkPack bez opuszczania platformy.",
  jEntryPhysicalTitle: "Fizycznie",
  jEntryPhysicalDesc: "Na opakowaniu, na półce, przy kasie — punkty wejścia w realnym świecie, w których TalkPack zamienia skan w rozmowę.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "POS w sklepie",
  jEntryPhysPill3: "Opakowanie",
  jExpVerifyLabel: "Weryfikuj",
  jExpVerifyText: "Rozpoznawanie produktu AI — SKU weryfikowane w czasie rzeczywistym.",
  jExpLearnLabel: "Ucz",
  jExpLearnBody: "Koło żywieniowe, alergeny, składniki, pochodzenie — pokazane w języku klienta, zatwierdzone przez markę.",
  jExpEngageLabel: "Zaangażuj",
  jExpEngagePlayBody: "Koło, gacha, układanka, quiz — pięć typów gier dla czasu spędzonego.",
  jExpEngageEarnBody: "Punkty, paragony, lojalność — inteligencja koszyka.",
  jExpEngageSayBody: "Konwersacyjne AI odpowiada w 40+ językach.",
  jExpRewardLabel: "Nagradzaj",
  jExpRewardBody: "Natychmiastowe nagrody, walidacja dual-QR przy kasie, roszczenia weryfikowane GPS — gotowe do audytu od pierwszego dnia.",
  jExpCaptureLabel: "Zbieraj",
  jExpCaptureSub: "Każda interakcja · zebrana i należąca do Ciebie",

  capEmail:   "Email",
  capName:    "Imię",
  capAddress: "Adres",
  capGps:     "GPS",
  capProduct: "Produkt",
  capBasket:  "Koszyk",
  capSpend:   "Wydatek",
  capChat:    "Czat",

  dashShopperLabel: "Inteligencja klienta",
  dashShopperShort: "Klient",
  dashShopper1Title: "CRM first-party",
  dashShopper1Desc: "Email, imię, adres, preferencje kontaktu.",
  dashShopper2Title: "Lokalizacje weryfikowane GPS",
  dashShopper2Desc: "Gdzie klienci się angażują w podziale na regiony sklepów.",
  dashShopper3Title: "Częstotliwość zakupów",
  dashShopper3Desc: "Powtarzające się wzorce skanów, kadencja wizyt.",
  dashShopper4Title: "Skład koszyka",
  dashShopper4Desc: "Dane paragonu na poziomie pozycji, produkty kupowane razem.",

  dashConvLabel: "Inteligencja konwersacji",
  dashConvShort: "Konwersacja",
  dashConv1Title: "Kategorie pytań",
  dashConv1Desc: "Alergeny, składniki, wartości odżywcze, użycie, przepisy.",
  dashConv2Title: "Analiza nastrojów",
  dashConv2Desc: "Pozytywne, negatywne, neutralne wg produktu i regionu.",
  dashConv3Title: "Punkty zagubienia",
  dashConv3Desc: "Czego klienci nie rozumieją, powtarzające się pytania.",
  dashConv4Title: "Intencja zakupu",
  dashConv4Desc: "Od ciekawości do próśb o sprzedaż detaliczną.",
  dashConv5Title: "Preferencje językowe",
  dashConv5Desc: "Jakie języki klienci wybierają wg lokalizacji.",

  dashDistLabel: "Inteligencja dystrybucji",
  dashDistShort: "Dystrybucja",
  dashDist1Title: "Dostępność w sklepie",
  dashDist1Desc: "Produkt na półce, potwierdzony skanami z geofencingiem.",
  dashDist2Title: "Alerty braku stanu",
  dashDist2Desc: "Zgłoszenia 'Nie ma tu' oznaczane w czasie rzeczywistym.",
  dashDist3Title: "Mapy ciepła",
  dashDist3Desc: "Gęstość skanów wg kodu pocztowego, regionu, fascia.",
  dashDist4Title: "Penetracja gospodarstw",
  dashDist4Desc: "'Skanuj wszędzie' pokazuje, gdzie produkt mieszka w domu.",
  dashDist5Title: "Porównanie sklepów",
  dashDist5Desc: "Wskaźniki zaangażowania wg lokalizacji, wg grupy fascia.",

  dashCampLabel: "Wydajność kampanii",
  dashCampShort: "Kampania",
  dashCamp1Title: "Wskaźnik skan-do-zaangażowania",
  dashCamp1Desc: "Ile skanów przekłada się na pełne interakcje.",
  dashCamp2Title: "Wskaźniki ukończenia gry",
  dashCamp2Desc: "Spiny na sesję, wskaźniki wygranych, punkty rezygnacji.",
  dashCamp3Title: "Śledzenie odbiorów",
  dashCamp3Desc: "Nagrody odebrane, zwalidowane, wygasłe wg sklepu.",
  dashCamp4Title: "Dashboard ROI",
  dashCamp4Desc: "Wydatki kampanii vs uchwycona wartość klienta.",

  dashCompLabel: "Zgodność i audyt",
  dashCompShort: "Zgodność",
  dashComp1Title: "Wynik zgodności ASA",
  dashComp1Desc: "Na kampanię, na rozmowę.",
  dashComp2Title: "Eksportowalne logi audytu",
  dashComp2Desc: "Każda odpowiedź AI zapisana i odzyskiwalna.",
  dashComp3Title: "Alerty naruszeń",
  dashComp3Desc: "Oznaczone wzorce treści.",
  dashComp4Title: "Zaangażowanie żywieniowe",
  dashComp4Desc: "Z którymi składnikami klienci wchodzą w interakcję na kole.",

  pvFormTitle: "Gratulacje!",
  pvFormSub: "Wygrałeś: 20% zniżki na kolejny zakup",
  pvFormName: "Twoje imię",
  pvFormEmail: "Twój email",
  pvFormPostcode: "Kod pocztowy",
  pvFormCta: "ODBIERZ NAGRODĘ",
  pvFormFootnote: "Privacy-first · Zgodne z RODO",

  pvChatTitle: "Ambasador AI",
  pvChatSub: "Wytrenowany na Morning O's",
  pvChatBubble1: "Czy to bezglutenowe?",
  pvChatBubble2: "Tak — certyfikowane bezglutenowe, tylko 9 g cukru na porcję. Chcesz pomysłów na przepisy?",
  pvChatBubble3: "Tak proszę",
  pvChatBubble4: "Spróbuj owsianki na noc z owocami leśnymi — 3 min przygotowania.",
  pvChatPlaceholder: "Zapytaj mnie o cokolwiek…",
  pvChatFootnote: "40+ języków · zawsze dostępne",

  pvMapTitle: "Znajdź Morning O's w pobliżu",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "WYZNACZ TRASĘ",
  pvMapFootnote: "Weryfikacja GPS · geofencing 150 m",
  pvMapOos: "brak na stanie",

  pvWheelTitle: "Morning O's · Zakręć i wygraj",
  pvWheelCredits: "350 kredytów",
  pvWheelCta: "ZAKRĘĆ",
  pvWheelFootnote: "Koło · Koło żywieniowe · Gacha · Quiz · Układanka",
  pvWheelWedgeWin: "WYGRANA!",
  pvWheelWedgeTry: "Jeszcze",

  pvNutTitle: "Morning O's · Wartości odżywcze",
  pvNutPer100g: "Na 100 g",
  pvNutPerServing: "Na porcję",
  pvNutCta: "DALEJ",
  pvNutFootnote: "Zgodne z ASA · obowiązkowe przed grą",

  bookDemoSubject: "Prośba o demo TalkPack",
  bookDemoBody: "Witam zespół ShopAI,\n\nchciał(a)bym zarezerwować demo TalkPack. Czy mogliby Państwo skontaktować się ze mną, aby ustalić termin?\n\nPozdrawiam,",

  // ── Viz mock-data labels (PL) ──
  vizCapQuestions: "Najczęstsze pytania klientów w tym tygodniu",
  vizQAllergens: "Alergeny", vizQIngredients: "Składniki", vizQNutrition: "Wartości",
  vizQRecipes: "Przepisy", vizQUsage: "Użycie",
  vizCapSentiment: "Ostatnie 30 dni dla wszystkich produktów",
  vizSentPositive: "Pozytywne", vizSentNeutral: "Neutralne", vizSentNegative: "Negatywne",
  vizCapConfusion: "Powtarzające się pytania — luki w treści",
  vizConfusion1: "Czy to bezglutenowe?",
  vizConfusion2: "Czy odpowiednie dla wegan?",
  vizConfusion3: "Ostrzeżenia o orzechach?",
  vizCapFunnel: "Konwersja przez rozmowę",
  vizFunnelCurious: "Ciekawy", vizFunnelInterested: "Zainteresowany", vizFunnelReady: "Gotowy do zakupu",
  vizCapAvail: "Produkt dostępny tam, gdzie planowałeś",
  vizAvailDetail: "1247 z 1326 sklepów potwierdzonych na półce",
  vizCapOos: "3 sklepy oznaczone dziś · auto-alert wysłany",
  vizCapHeat: "Londyn · Manchester · Birmingham na czele",
  vizCapHousehold: "68% skanów ma miejsce w domu — tam mieszka twój produkt",
  vizCapFascia: "Wskaźnik zaangażowania według sieci handlowej",
  vizCapEngrate: "Skany, które przeradzają się w pełne interakcje",
  vizEngrateCompare: "+36 pkt vs 42% benchmarku branżowego",
  vizCapGame: "Gdzie klienci rezygnują — dostrój balans gry",
  vizGameStart: "Start", vizGamePlay: "Gra", vizGameWin: "Wygrana", vizGameClaim: "Odebrane",
  vizCapRdm: "96% skuteczność walidacji · niewiele wygaśnięć",
  vizRdmClaimed: "Odebrane", vizRdmValidated: "Zwalidowane", vizRdmExpired: "Wygasłe",
  vizCapRoi: "Wydatki kampanii vs wartość życiowa klienta",
  vizRoiSpend: "Wydatek", vizRoiValueLabel: "Uchwycona wartość", vizRoiReturn: "3,4× zwrot",
  vizCapComp: "Na kampanię · na rozmowę · gotowe do audytu",
  vizCapAudit: "Każda odpowiedź AI zalogowana · dostępna na żądanie",
  vizCapViolation: "Naruszenia w tym miesiącu · wynik AI walidowany w czasie ciągłym",
  vizCapNut: "Składniki, z którymi klienci wchodzą w interakcję na kole",
  vizNutSugar: "Cukier", vizNutFat: "Tłuszcz", vizNutSalt: "Sól", vizNutSatFat: "Nasycone",
  vizCapLang: "Język klienta wykrywany automatycznie",
  vizLangOther: "Inne",
  vizCapCrm: "pozyskanych klientów",
  vizCapMap: "247 lokalizacji w tym tygodniu",
  vizCapWeek: "Śr. 2,3 wizyty tygodniowo",
  vizDayMon: "Pon", vizDayTue: "Wt", vizDayWed: "Śr",
  vizDayThu: "Czw", vizDayFri: "Pt", vizDaySat: "Sob", vizDaySun: "Nd",
  vizCapBasket: "Śr. koszyk: 8,40 £ · 6,2 produktu",
  vizBasketDairy: "Nabiał", vizBasketBakery: "Pieczywo", vizBasketSnacks: "Przekąski", vizBasketOther: "Inne",
};

// ────────── Türkçe ──────────
const tr: LandingCopy = {
  ...en,
  navPlatform: "Platform",
  navModules: "Modüller",
  navPortal: "Portal Girişi",
  navLangButton: "TR",
  navRegister: "Kaydol",
  navBookDemo: "Demo rezerve et",
  navLogin: "Giriş yap",

  trustLeft: "Uygulama yok · Entegrasyon yok · Mağaza bağımlılığı yok",
  trustRight: "2024'ten beri · Birleşik Krallık · The Wholesale Group · Caterers Choice · Passot (Japonya) · Talysis UK · Jakavarna Group (Tayland) ile inşa ediliyor",

  heroEyebrow: "Ticaret için bir yardımcı pilot",
  heroL1: "Her ürün. Bir sohbet.",
  heroL2: "Her tarama. Gerçek veri.",
  heroSub: "TalkPack her ürünü canlı bir sohbete dönüştürür. Alışverişçiler tarar, öğrenir, oynar, sorar, kazanır, biriktirir. Siz first-party veri, HFSS uyumu ve fiziksel perakendede alışverişçilerle doğrudan ilişki elde edersiniz. Uygulama yok. Entegrasyon yok. Mağaza bağımlılığı yok.",
  heroCtaPrimary: "Demo rezerve et",
  heroCtaGhost: "Nasıl çalıştığını gör",
  heroPillAllergens: "Alerjenler",
  heroPillHfss: "HFSS",
  heroPillIngredients: "İçindekiler",
  heroVerified: "Doğrulandı",

  statStores: "Mağaza · Birleşik Krallık · küresel olarak büyüyor",
  statLanguages: "Dil · otomatik algılanır",
  statGames: "Brief'ten canlı kampanyaya",
  statModules: "Beslenme çarkı dahili",
  statSteps: "Markanız. Verileriniz. Yapay zekânız.",
  statDownloads: "Uygulama indirme · entegrasyon · sürtünme",
  statGeofence: "Fiziksel perakendeden alışverişçi verisi",
  statScans: "Raftan alışverişçi CRM'ine",

  fullJourneyEyebrow: "Tam yolculuk",
  fullJourneyHeading: "Girişten zekâya. Tek bir platform.",
  fullJourneySub: "Her kanal. Her modül. Her veri noktası. TalkPack mimarisi uçtan uca böyle bağlanır.",
  fullJourneyCaption: "Tam akış. Alışverişçi girişinden marka zekâsına. Dashboard, AI sohbet veya SmartMail sorguları üzerinden dışa aktarılabilir.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "Tek platform. Alışverişçiyi etkilemenin beş yolu.",
  picSub: "Markalar ihtiyaç duydukları modülleri seçer. Alışverişçiler bir deneyim karması yaşar. Her modül aynı tarama, aynı sohbet, aynı veri katmanı üzerinden çalışır.",
  learnDesc: "Ürün eğitimi, içerik şeffaflığı, HFSS trafik ışıkları.",
  learnF1: "Beslenme çarkı", learnF2: "Alerjen taraması", learnF3: "Menşe verisi",
  playDesc: "Kalış süresini ve tekrar etkileşimi artıran beş oyun türü.",
  playF1: "Çarkıfelek", playF2: "Gacha", playF3: "Yapboz", playF4: "Quiz", playF5: "Beslenme çarkı",
  sayDesc: "Alışverişçinin sorularını kendi dilinde yanıtlayan konuşan yapay zekâ.",
  sayF1: "40+ dil", sayF2: "İçerik Q&A", sayF3: "Tarif önerileri",
  winDesc: "Ödüller, mağazaya yönlendirme, kasada ikili QR doğrulama, sevk irsaliyeleri.",
  winF1: "Anında ödül", winF2: "Geofence'lı ödüller", winF3: "Eve teslim",
  earnDesc: "Puan, fiş tarama, sadakat programı, sepet zekâsı.",
  earnF1: "Fiş OCR", earnF2: "Markalar arası profil", earnF3: "Ödül kataloğu",
  moduleSeeMore: "Modülü gör",

  oeEyebrow: "Tek motor · Çoklu dinamik",
  oeHeading: "TalkPack daha geniş bir platformun parçasıdır.",
  oeSub: "ShopAI markalara, perakendecilere ve toptancılara tek bir veri katmanı sunar. Uçtan uca konuşan iş zekâsı. TalkPack alışverişçinin sohbete dahil oluş biçimidir.",
  oe1Strap: "Fiziksel perakendeden alışverişçi etkileşimi",
  oe2Strap: "Convenience sektörü için yapay zekâ",
  oe3Strap: "Saha denetimi zekâsı",
  oe4Strap: "Puan, fiş, sepet verisi",
  oeActiveLabel: "Şu an",

  ucHeading: "Farklı roller. Aynı platform.",
  ucBrandTitle: "Marka pazarlamacısı",
  ucBrand1: "Karma perakendede ulusal ürün lansmanı",
  ucBrand2: "Katılan mağazalara yönlendirilmiş trafik",
  ucBrand3: "HFSS uyumlu on-pack etkileşimi",
  ucBrand4: "Markalar arası alışverişçi profillemesi",
  ucBrand5: "Düzenlenen kategoriler (ilaç, alkol) için denetlenmiş AI yanıtları",
  ucRetailerTitle: "Perakendeci / Toptancı",
  ucRetailer1: "Perakendeci tarafından finanse edilen özel promosyonlar",
  ucRetailer2: "Symbol group aktivasyonu",
  ucRetailer3: "Raf uyum denetimi",
  ucRetailer4: "Perakendeciye özel sadakat",
  ucAgencyTitle: "Ajans / Bayi",
  ucAgency1: "Müşteri markaları için white-label kampanyalar",
  ucAgency2: "Çoklu müşteri portalı erişimi",
  ucAgency3: "Trade marketing PDF raporları",
  ucAgency4: "Otomatik üretilen sosyal içerik",

  hfssEyebrow: "HFSS için inşa edildi",
  hfssHeading: "HFSS uyumu, dahili.",
  hfssSub: "Alışverişçi oynamadan önce besin profilini görür. Yağ, şeker, tuz, doymuş yağ — günlük referans alıma göre trafik ışığı. Zorunlu. Denetlenebilir. Dört katmanlı düzenleyici savunma.",
  hfss1Title: "Zorunlu beslenme çarkı.",
  hfss1Body: "Her HFSS ürünü, alışverişçi devam etmeden önce günlük alıma göre yağ, şeker, tuz ve doymuş yağ trafik ışıklarını gösterir.",
  hfss2Title: "Önce eğitim odaklı çerçeve.",
  hfss2Body: "Platform beslenme şeffaflığını öne çıkarır. Alışverişçi etkileşimlerini bilinçli seçim olarak konumlandırır, promosyon baskısı olarak değil.",
  hfss3Title: "Çıktı doğrulaması.",
  hfss3Body: "Konuşan yapay zekâ düzenleyici örüntülere göre izlenir. Her yanıt loglanır, filtrelenir ve denetlenebilir.",
  hfss4Title: "Dışa aktarılabilir denetim kayıtları.",
  hfss4Body: "ASA uyum puanı, her yanıt loglanmış ve geri çağrılabilir, işaretlenen içerik için ihlal uyarıları.",
  hfssBadge: "ASA uyumu: %98,4",

  wlEyebrow: "Markanız, herhangi bir dilde",
  wlHeading: "Verileriniz. Yapay zekânız. Sizin yolunuz.",
  wlLangCaption: "40+ dil. Otomatik algılanır veya kampanya bazında elle ayarlanır.",
  wlFeat1Title: "Markanızın kimliği",
  wlFeat1Body: "Logo, renkler, fontlar her alışverişçi ekranında ve sevk irsaliyesinde uygulanır.",
  wlFeat2Title: "Verileriniz, yapay zekânız",
  wlFeat2Body: "Veriler hesabınızda saklanır. AI ürünlerinizle eğitilir. İlişki size aittir.",
  wlFeat3Title: "Mağazalarınız, geofence'iniz",
  wlFeat3Body: "Evrensel mod veya mağazaya özel. Varsayılan 150 m geofence, kampanya bazında yapılandırılabilir.",
  wlFeat4Title: "Ekibiniz, erişiminiz",
  wlFeat4Body: "Rol bazlı izinler. Marka, ajans, saha, finans — her biri doğru görünümle.",

  proofEyebrow: "Sahada",
  proofHeading: "Zaten canlı. Zaten çalışıyor.",
  proofStat1Label: "kampanya veritabanındaki UK mağaza",
  proofStat2Label: "desteklenen dil",
  proofStat3Label: "Birleşik Krallık'ta kuruldu",
  proofQuotePlaceholder: "Müşteri hikâyeleri yakında.",

  faqHeading: "Markaların bize sorduğu sorular.",
  faq1Q: "Alışverişçilerin uygulama indirmesi gerekir mi?",
  faq1A: "Hayır. TalkPack tarayıcıda çalışır. Tek tarama deneyimi açar.",
  faq2Q: "TalkPack EPOS veya CRM'imizle entegre olur mu?",
  faq2A: "Entegrasyon gerektirmez. Toplanan veriler first-party'dir; PDF, CSV veya API ile dışa aktarılır.",
  faq3Q: "HFSS ürünlerine ne olur?",
  faq3A: "Her HFSS ürünü oyun öncesi zorunlu bir beslenme çarkı gösterir. Uyum dahili, sonradan eklenmemiş.",
  faq4Q: "Hangi dilleri destekliyorsunuz?",
  faq4A: "40+. Platform otomatik algılar veya kampanya bazında siz ayarlarsınız.",
  faq5Q: "Yalnızca belirli perakendecilerde kampanya yapabilir miyiz?",
  faq5A: "Evet. Mağazaya özel mod kampanyayı seçtiğiniz mağazalara geofence'lar. Evrensel mod alışverişçinin her yerde oynamasına izin verir.",
  faq6Q: "Bir kampanya ne kadar sürede kurulur?",
  faq6A: "Standart bir promosyon için yaklaşık 10 dakika. Portalda adım adım sihirbaz.",
  faq7Q: "Deneyimi white-label yapabilir miyiz?",
  faq7A: "Evet. Logonuz, renkleriniz, diliniz. Siz istemedikçe alışverişçi TalkPack markasını görmez.",

  footerColPlatform: "Platform",
  footerColModules: "Modüller",
  footerColCompany: "Şirket",
  footerColResources: "Kaynaklar",
  footerCompany1: "Hakkında",
  footerCompany2: "İletişim",
  footerResources3: "Gizlilik",
  footerResources4: "Şartlar",
  footerNewsHeading: "Üç ayda bir güncelleme. Spam yok.",
  footerNewsPlaceholder: "siz@email.com",
  footerNewsSubmit: "Abone ol",
  footerRegistration: "ShopAI Ltd · Şirket No. 15877093 · İngiltere'de tescilli",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (TR) ──
  heroAlt: "Süpermarket reyonunda alışveriş yapan kadın — TalkPack deneyiminin her temas noktası onun etrafında dönüyor",

  stepsTitle: "Üç adım. Tek platform.",
  stepsSub: "Alışverişçi içeri girdiği andan, onun hakkında bir şey öğrendiğiniz ana kadar. Çalıştırması basit. Veriler zengin.",
  step1Title: "Gir",
  step1Desc: "Alışverişçi her kanaldan dokunur veya tarar. Tek giriş, tek markalı deneyim.",
  step1PillRow: "Web · Sosyal · E-posta · QR · OOH · Mağazada",
  step2Title: "Etkileşim",
  step2Desc: "Beş Pic-n-Mix modülü kendi dilinde, sizin markanızla, AI tarafından doğrulanmış olarak çalışır.",
  step2PillRow: "Öğren · Oyna · Sor · Kazan · Topla",
  step3Title: "Öğren",
  step3Desc: "Her etkileşim first-party veriye dönüşür ve gerçek zamanlı olarak panonuza akar.",
  step3PillRow: "Alışverişçi · Konuşma · Konum · Etkileşim · Uyumluluk",

  chipWebsite: "Web",
  chipSocial:  "Sosyal",
  chipEmail:   "E-posta",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "Mağazada",
  chipLearn:   "Öğren",
  chipPlay:    "Oyna",
  chipSay:     "Sor",
  chipWin:     "Kazan",
  chipEarn:    "Topla",
  chipShoppers:     "Alışverişçi",
  chipConversation: "Konuşma",
  chipLocation:     "Konum",
  chipEngagement:   "Etkileşim",
  chipCompliance:   "Uyumluluk",
  chipAriaEntry:  "Giriş kanalı",
  chipAriaModule: "Pic-n-Mix modülü",
  chipAriaIntel:  "Zeka kategorisi",

  intgTitle: "Ciddi altyapı. Kanıtlanmış teknoloji.",
  intgSub: "TalkPack, dünyanın en büyük şirketlerinin güvendiği aynı bulut, AI ve uyumluluk temelleri üzerinde çalışır.",
  intgCap1: "Ana platform",
  intgCap2: "Bulut altyapısı",
  intgCap3: "Konuşma AI",
  intgCap4: "Dil modelleri",
  intgCap5: "AI ve haritalama",
  intgCap6: "Beslenme verisi",
  intgCap7: "Ödemeler",
  intgFootnote: "ISO 27001 sertifikalı sağlayıcılar · KVKK/GDPR uyumlu · Çoklu bölge barındırma · İlk günden kurumsal güvenlik",

  jColEntry: "Giriş",
  jColExperience: "Deneyim",
  jColDashboard: "Pano Zekâsı",
  jEntryDigitalTitle: "Dijital",
  jEntryDigitalDesc: "Web sitesi, app store'lar, arama, doğrudan bağlantılar — herhangi bir dijital yüzeyden alışverişçi deneyimine tek dokunuş.",
  jEntrySocialTitle: "Sosyal",
  jEntrySocialDesc: "Sponsorlu gönderiler, etkileyici içerikleri, DM'ler — akışlar platformdan ayrılmadan TalkPack'e akar.",
  jEntryPhysicalTitle: "Fiziksel",
  jEntryPhysicalDesc: "Ambalajda, rafta, kasada — TalkPack'in bir taramayı sohbete dönüştürdüğü gerçek dünya giriş noktaları.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "Mağaza içi POS",
  jEntryPhysPill3: "Ambalaj",
  jExpVerifyLabel: "Doğrula",
  jExpVerifyText: "AI Ürün Tanıma — SKU gerçek zamanlı doğrulanır.",
  jExpLearnLabel: "Öğren",
  jExpLearnBody: "Beslenme çarkı, alerjenler, içindekiler, menşei — alışverişçinin dilinde, marka tarafından doğrulanmış.",
  jExpEngageLabel: "Etkileşim",
  jExpEngagePlayBody: "Çark, gacha, yapboz, quiz — bekleme süresi için beş oyun türü.",
  jExpEngageEarnBody: "Puanlar, fişler, sadakat — sepet düzeyinde zekâ.",
  jExpEngageSayBody: "40+ dilde yanıt veren konuşma AI'ı.",
  jExpRewardLabel: "Ödüllendir",
  jExpRewardBody: "Anında ödüller, kasada çift QR doğrulaması, GPS doğrulamalı talepler — ilk günden denetime hazır.",
  jExpCaptureLabel: "Yakala",
  jExpCaptureSub: "Her etkileşim · sizin tarafınızdan yakalanır ve sahiplenilir",

  capEmail:   "E-posta",
  capName:    "İsim",
  capAddress: "Adres",
  capGps:     "GPS",
  capProduct: "Ürün",
  capBasket:  "Sepet",
  capSpend:   "Harcama",
  capChat:    "Sohbet",

  dashShopperLabel: "Alışverişçi Zekâsı",
  dashShopperShort: "Alışverişçi",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "E-posta, isim, adres, iletişim tercihleri.",
  dashShopper2Title: "GPS doğrulamalı konumlar",
  dashShopper2Desc: "Alışverişçilerin nerede etkileşim kurduğu, mağaza bölgelerine göre.",
  dashShopper3Title: "Satın alma sıklığı",
  dashShopper3Desc: "Tekrar eden tarama desenleri, ziyaret kadansı.",
  dashShopper4Title: "Sepet kompozisyonu",
  dashShopper4Desc: "Ürün düzeyinde fiş verisi, birlikte alınan ürünler.",

  dashConvLabel: "Konuşma Zekâsı",
  dashConvShort: "Konuşma",
  dashConv1Title: "Soru kategorileri",
  dashConv1Desc: "Alerjenler, içindekiler, beslenme, kullanım, tarifler.",
  dashConv2Title: "Duygu analizi",
  dashConv2Desc: "Ürün ve bölgeye göre olumlu, olumsuz, nötr.",
  dashConv3Title: "Kafa karışıklığı noktaları",
  dashConv3Desc: "Alışverişçilerin anlamadığı, tekrar eden sorular.",
  dashConv4Title: "Satın alma niyeti",
  dashConv4Desc: "Meraktan perakendeci taleplerine ilerleme.",
  dashConv5Title: "Dil tercihleri",
  dashConv5Desc: "Alışverişçilerin konuma göre hangi dilleri seçtiği.",

  dashDistLabel: "Dağıtım Zekâsı",
  dashDistShort: "Dağıtım",
  dashDist1Title: "Mağaza bulunabilirliği",
  dashDist1Desc: "Rafta ürün, geofence taramalarıyla doğrulanmış.",
  dashDist2Title: "Stokta yok uyarıları",
  dashDist2Desc: "'Burada yok' raporları gerçek zamanlı işaretlenir.",
  dashDist3Title: "Isı haritaları",
  dashDist3Desc: "Posta kodu, bölge, fascia bazında tarama yoğunluğu.",
  dashDist4Title: "Hane penetrasyonu",
  dashDist4Desc: "'Her yerde tara' ürünün evde nerede yaşadığını gösterir.",
  dashDist5Title: "Mağaza karşılaştırması",
  dashDist5Desc: "Konuma, fascia grubuna göre etkileşim oranları.",

  dashCampLabel: "Kampanya Performansı",
  dashCampShort: "Kampanya",
  dashCamp1Title: "Tarama-etkileşim oranı",
  dashCamp1Desc: "Kaç taramanın tam etkileşime dönüştüğü.",
  dashCamp2Title: "Oyun tamamlanma oranları",
  dashCamp2Desc: "Oturum başına spin, kazanma oranları, vazgeçme noktaları.",
  dashCamp3Title: "Ödül takibi",
  dashCamp3Desc: "Mağaza bazında talep edilen, doğrulanan, süresi dolan ödüller.",
  dashCamp4Title: "ROI panosu",
  dashCamp4Desc: "Kampanya harcaması vs. yakalanan alışverişçi değeri.",

  dashCompLabel: "Uyumluluk ve Denetim",
  dashCompShort: "Uyumluluk",
  dashComp1Title: "ASA uyumluluk skoru",
  dashComp1Desc: "Kampanya başına, konuşma başına.",
  dashComp2Title: "Dışa aktarılabilir denetim günlükleri",
  dashComp2Desc: "Her AI yanıtı kayıtlı ve geri alınabilir.",
  dashComp3Title: "İhlal uyarıları",
  dashComp3Desc: "İşaretlenmiş içerik desenleri.",
  dashComp4Title: "Beslenme etkileşimi",
  dashComp4Desc: "Alışverişçilerin çark üzerinde hangi besinlerle etkileşime girdiği.",

  pvFormTitle: "Tebrikler!",
  pvFormSub: "Kazandınız: bir sonraki alışverişinizde %20 indirim",
  pvFormName: "Adınız",
  pvFormEmail: "E-postanız",
  pvFormPostcode: "Posta kodu",
  pvFormCta: "ÖDÜLÜ AL",
  pvFormFootnote: "Privacy-first · KVKK/GDPR uyumlu",

  pvChatTitle: "AI Elçisi",
  pvChatSub: "Morning O's üzerinde eğitilmiş",
  pvChatBubble1: "Bu glutensiz mi?",
  pvChatBubble2: "Evet — sertifikalı glutensiz ve porsiyon başına yalnızca 9 g şeker. Tarif fikirleri ister misiniz?",
  pvChatBubble3: "Evet lütfen",
  pvChatBubble4: "Çileklerle gece yulafını deneyin — 3 dk hazırlık.",
  pvChatPlaceholder: "Bana her şeyi sorun…",
  pvChatFootnote: "40+ dil · her zaman açık",

  pvMapTitle: "Yakınınızda Morning O's bulun",
  pvMapPin: "Nisa · SE1 · 0,3 mi",
  pvMapCta: "YOL TARİFİ",
  pvMapFootnote: "GPS doğrulamalı · 150 m geofence",
  pvMapOos: "stokta yok",

  pvWheelTitle: "Morning O's · Çevir ve Kazan",
  pvWheelCredits: "350 kredi",
  pvWheelCta: "ÇEVİR",
  pvWheelFootnote: "Çark · Beslenme çarkı · Gacha · Quiz · Yapboz",
  pvWheelWedgeWin: "KAZAN!",
  pvWheelWedgeTry: "Tekrar",

  pvNutTitle: "Morning O's · Beslenme Bilgileri",
  pvNutPer100g: "100 g başına",
  pvNutPerServing: "Porsiyon başına",
  pvNutCta: "DEVAM",
  pvNutFootnote: "ASA uyumlu · oyundan önce zorunlu",

  bookDemoSubject: "TalkPack demo talebi",
  bookDemoBody: "Merhaba ShopAI ekibi,\n\nbir TalkPack demosu rezerve etmek istiyorum. Bir randevu ayarlamak için lütfen benimle iletişime geçer misiniz?\n\nTeşekkürler,",

  // ── Viz mock-data labels (TR) ──
  vizCapQuestions: "Bu hafta alışverişçilerin başlıca soruları",
  vizQAllergens: "Alerjenler", vizQIngredients: "İçindekiler", vizQNutrition: "Beslenme",
  vizQRecipes: "Tarifler", vizQUsage: "Kullanım",
  vizCapSentiment: "Tüm ürünlerde son 30 gün",
  vizSentPositive: "Olumlu", vizSentNeutral: "Nötr", vizSentNegative: "Olumsuz",
  vizCapConfusion: "Tekrar eden sorular — içerik boşlukları",
  vizConfusion1: "Bu glutensiz mi?",
  vizConfusion2: "Veganlar için uygun mu?",
  vizConfusion3: "Kuruyemiş uyarısı var mı?",
  vizCapFunnel: "Konuşma yoluyla dönüşüm",
  vizFunnelCurious: "Meraklı", vizFunnelInterested: "İlgili", vizFunnelReady: "Almaya hazır",
  vizCapAvail: "Planladığınız yerde ürün mevcut",
  vizAvailDetail: "1.326 mağazanın 1.247'sinde rafta onaylandı",
  vizCapOos: "Bugün 3 mağaza işaretlendi · otomatik uyarı gönderildi",
  vizCapHeat: "Londra · Manchester · Birmingham önde",
  vizCapHousehold: "Taramaların %68'i evde gerçekleşiyor — ürün orada yaşıyor",
  vizCapFascia: "Perakendeci formatına göre etkileşim oranı",
  vizCapEngrate: "Tam etkileşime dönüşen taramalar",
  vizEngrateCompare: "+36 puan, %42 sektör ortalamasına karşı",
  vizCapGame: "Alışverişçilerin nerede ayrıldığı — oyun dengesini ayarlayın",
  vizGameStart: "Başla", vizGamePlay: "Oyna", vizGameWin: "Kazan", vizGameClaim: "Talep",
  vizCapRdm: "%96 doğrulama oranı · düşük süre dolumu",
  vizRdmClaimed: "Talep edilen", vizRdmValidated: "Doğrulanan", vizRdmExpired: "Süresi dolan",
  vizCapRoi: "Kampanya harcaması vs alışverişçi yaşam boyu değeri",
  vizRoiSpend: "Harcama", vizRoiValueLabel: "Yakalanan değer", vizRoiReturn: "3,4× geri dönüş",
  vizCapComp: "Kampanya başına · konuşma başına · denetime hazır",
  vizCapAudit: "Her AI yanıtı kayıtlı · talep üzerine alınabilir",
  vizCapViolation: "Bu ay ihlaller · AI çıktısı sürekli doğrulanıyor",
  vizCapNut: "Çarkta alışverişçilerin etkileşime girdiği besinler",
  vizNutSugar: "Şeker", vizNutFat: "Yağ", vizNutSalt: "Tuz", vizNutSatFat: "Doymuş",
  vizCapLang: "Alışverişçi dili otomatik algılanır",
  vizLangOther: "Diğer",
  vizCapCrm: "alışverişçi yakalandı",
  vizCapMap: "Bu hafta 247 konum",
  vizCapWeek: "Haftada ort. 2,3 ziyaret",
  vizDayMon: "Pzt", vizDayTue: "Sal", vizDayWed: "Çar",
  vizDayThu: "Per", vizDayFri: "Cum", vizDaySat: "Cmt", vizDaySun: "Paz",
  vizCapBasket: "Ort. sepet: 8,40 £ · 6,2 ürün",
  vizBasketDairy: "Süt ürünleri", vizBasketBakery: "Fırın", vizBasketSnacks: "Atıştırmalık", vizBasketOther: "Diğer",
};

// ────────── 日本語 ──────────
const ja: LandingCopy = {
  ...en,
  navPlatform: "プラットフォーム",
  navModules: "モジュール",
  navPortal: "ポータルログイン",
  navLangButton: "JA",
  navRegister: "登録",
  navBookDemo: "デモを予約",
  navLogin: "ログイン",

  trustLeft: "アプリ不要 · 連携不要 · 店舗依存なし",
  trustRight: "2024年から · 英国 · The Wholesale Group · Caterers Choice · パッソ(日本) · Talysis UK · Jakavarna Group(タイ) と共に構築",

  heroEyebrow: "コマースのコパイロット",
  heroL1: "すべての商品を、会話に。",
  heroL2: "すべてのスキャンが、リアルなデータに。",
  heroSub: "TalkPackは、あらゆる商品をライブの会話に変えます。ショッパーはスキャンし、学び、遊び、質問し、勝ち取り、貯めます。ブランドはファーストパーティデータ、HFSS対応、そしてリアル店舗のショッパーとの直接的な関係を手に入れます。アプリ不要。連携不要。店舗依存なし。",
  heroCtaPrimary: "デモを予約",
  heroCtaGhost: "仕組みを見る",
  heroPillAllergens: "アレルゲン",
  heroPillHfss: "HFSS",
  heroPillIngredients: "原材料",
  heroVerified: "検証済み",

  statStores: "店舗 · 英国 · 世界展開中",
  statLanguages: "言語 · 自動検出",
  statGames: "ブリーフからライブ施策まで",
  statModules: "栄養ホイール標準搭載",
  statSteps: "あなたのブランド。あなたのデータ。あなたのAI。",
  statDownloads: "アプリDL · 連携 · 摩擦",
  statGeofence: "リアル店舗からのショッパーデータ",
  statScans: "棚からショッパーCRMへ",

  fullJourneyEyebrow: "全行程",
  fullJourneyHeading: "入口からインテリジェンスまで。1つのプラットフォーム。",
  fullJourneySub: "あらゆるチャネル。あらゆるモジュール。あらゆるデータ。TalkPackアーキテクチャがエンドツーエンドで繋がる仕組み。",
  fullJourneyCaption: "完全なフロー。ショッパー入口からブランドインテリジェンスまで。ダッシュボード、AIチャット、SmartMailクエリで書き出し可能。",

  picEyebrow: "Pic-n-Mix",
  picHeading: "1つのプラットフォーム。ショッパーと関わる5つの方法。",
  picSub: "ブランドは必要なモジュールを選びます。ショッパーは体験のミックスを楽しみます。すべてのモジュールが同じスキャン、同じ会話、同じデータレイヤーを共有します。",
  learnDesc: "商品教育、原材料の透明性、HFSSの信号機表示。",
  learnF1: "栄養ホイール", learnF2: "アレルゲンスキャン", learnF3: "産地データ",
  playDesc: "滞在時間と再エンゲージメントを高める5種類のゲーム。",
  playF1: "スピンホイール", playF2: "ガチャ", playF3: "パズル", playF4: "クイズ", playF5: "栄養スピン",
  sayDesc: "ショッパーの質問に、その人の言語で答える対話型AI。",
  sayF1: "40以上の言語", sayF2: "原材料Q&A", sayF3: "レシピ提案",
  winDesc: "賞品、来店誘導、レジでのデュアルQR検証、配送伝票。",
  winF1: "即時報酬", winF2: "ジオフェンス賞品", winF3: "自宅配送",
  earnDesc: "ポイント、レシートスキャン、ロイヤリティ、バスケットインテリジェンス。",
  earnF1: "レシートOCR", earnF2: "クロスブランドプロファイル", earnF3: "リワードカタログ",
  moduleSeeMore: "モジュールを見る",

  oeEyebrow: "1つのエンジン · 複数のダイナミクス",
  oeHeading: "TalkPackは、より大きなプラットフォームの一部です。",
  oeSub: "ShopAIはブランド、リテーラー、ホールセラーに単一のデータレイヤーを提供します。エンドツーエンドの対話型ビジネスインテリジェンス。TalkPackはショッパーが会話に参加する手段です。",
  oe1Strap: "リアル店舗からのショッパーエンゲージメント",
  oe2Strap: "コンビニエンスセクター向けAI",
  oe3Strap: "現場監査インテリジェンス",
  oe4Strap: "ポイント、レシート、バスケットデータ",
  oeActiveLabel: "現在",

  ucHeading: "異なる役割。同じプラットフォーム。",
  ucBrandTitle: "ブランドマーケター",
  ucBrand1: "混合流通における全国ローンチ",
  ucBrand2: "参加店舗への来店誘導",
  ucBrand3: "HFSS準拠のオンパックエンゲージメント",
  ucBrand4: "クロスブランドのショッパープロファイリング",
  ucBrand5: "規制カテゴリ(医薬品、酒類)に監査ログ付きのAI応答",
  ucRetailerTitle: "リテーラー / ホールセラー",
  ucRetailer1: "リテーラー出資の限定プロモ",
  ucRetailer2: "シンボルグループのアクティベーション",
  ucRetailer3: "棚コンプライアンス監査",
  ucRetailer4: "リテーラー専用ロイヤリティ",
  ucAgencyTitle: "代理店 / リセラー",
  ucAgency1: "クライアントブランド向けホワイトラベル施策",
  ucAgency2: "マルチクライアントポータルアクセス",
  ucAgency3: "トレードマーケティング向けPDFレポート",
  ucAgency4: "自動生成されるソーシャルコンテンツ",

  hfssEyebrow: "HFSSのために設計",
  hfssHeading: "HFSSコンプライアンス、標準搭載。",
  hfssSub: "ショッパーはプレイ前に栄養プロファイルを確認します。脂質、糖、塩、飽和脂肪酸 — 1日の参考摂取量に対する信号機表示。必須。監査対応。4層の規制対策。",
  hfss1Title: "栄養ホイールの必須表示。",
  hfss1Body: "すべてのHFSS商品は、ショッパーが進む前に脂質、糖、塩、飽和脂肪酸の信号機を1日の参考摂取量に対して表示します。",
  hfss2Title: "教育を起点にしたフレーミング。",
  hfss2Body: "プラットフォームは栄養の透明性を促進します。ショッパーとの関わりを「販促の押し付け」ではなく「情報に基づく選択」として位置付けます。",
  hfss3Title: "出力の検証。",
  hfss3Body: "対話型AIは規制パターンに沿って監視されます。すべての応答はログされ、フィルタされ、監査可能です。",
  hfss4Title: "書き出し可能な監査ログ。",
  hfss4Body: "ASAコンプライアンススコア、すべての応答をログ・取り出し可能、フラグされた内容には違反アラート。",
  hfssBadge: "ASAコンプライアンス: 98.4%",

  wlEyebrow: "あなたのブランドを、どんな言語でも",
  wlHeading: "あなたのデータ。あなたのAI。あなたのやり方で。",
  wlLangCaption: "40以上の言語。自動検出または施策ごとに手動設定。",
  wlFeat1Title: "あなたのブランドアイデンティティ",
  wlFeat1Body: "ロゴ、カラー、フォントを、すべてのショッパー画面と配送伝票に反映。",
  wlFeat2Title: "あなたのデータ、あなたのAI",
  wlFeat2Body: "データはあなたのアカウントに保管。AIはあなたの商品で学習。関係性はあなたのもの。",
  wlFeat3Title: "あなたの店舗、あなたのジオフェンス",
  wlFeat3Body: "ユニバーサルモードまたは店舗指定。デフォルト150mのジオフェンス、施策ごとに設定可能。",
  wlFeat4Title: "あなたのチーム、あなたのアクセス",
  wlFeat4Body: "ロールベースの権限。ブランド、代理店、現場、財務 — それぞれに適したビューを。",

  proofEyebrow: "稼働中",
  proofHeading: "すでにライブ。すでに機能中。",
  proofStat1Label: "施策データベース内の英国店舗数",
  proofStat2Label: "対応言語数",
  proofStat3Label: "英国にて創業",
  proofQuotePlaceholder: "顧客事例は近日公開。",

  faqHeading: "ブランドからよく寄せられる質問。",
  faq1Q: "ショッパーはアプリのDLが必要ですか?",
  faq1A: "不要です。TalkPackはブラウザで動作します。1スキャンで体験が始まります。",
  faq2Q: "TalkPackは当社のEPOSやCRMと連携しますか?",
  faq2A: "連携は不要です。取得データはファーストパーティで、PDF・CSV・APIで書き出せます。",
  faq3Q: "HFSS商品ではどうなりますか?",
  faq3A: "すべてのHFSS商品でゲーム前に栄養ホイールが必須表示されます。コンプライアンスは標準搭載です。",
  faq4Q: "対応言語は何ですか?",
  faq4A: "40以上。自動検出または施策ごとに設定。",
  faq5Q: "特定のリテーラーだけで施策を実施できますか?",
  faq5A: "はい。店舗指定モードで選んだ店舗にジオフェンスをかけられます。ユニバーサルモードはどこでもプレイ可能です。",
  faq6Q: "施策の構築にはどれくらいかかりますか?",
  faq6A: "標準的なプロモなら約10分。ポータルのステップバイステップ・ウィザードで作成。",
  faq7Q: "体験をホワイトラベルにできますか?",
  faq7A: "はい。あなたのロゴ、カラー、言語で。希望しない限りショッパーがTalkPackブランドを目にすることはありません。",

  footerColPlatform: "プラットフォーム",
  footerColModules: "モジュール",
  footerColCompany: "会社",
  footerColResources: "リソース",
  footerCompany1: "About",
  footerCompany2: "お問い合わせ",
  footerResources3: "プライバシー",
  footerResources4: "利用規約",
  footerNewsHeading: "四半期ごとのアップデート。スパムなし。",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "登録",
  footerRegistration: "ShopAI Ltd · 会社番号 15877093 · イングランド登記",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (JA) ──
  heroAlt: "スーパーマーケットの通路にいる買い物客 — 彼女を中心にTalkPack体験のあらゆるタッチポイントが回っている",

  stepsTitle: "3ステップ。1つのプラットフォーム。",
  stepsSub: "買い物客が入った瞬間から、あなたが彼らについて何かを学ぶ瞬間まで。運用はシンプル、データは豊富。",
  step1Title: "入る",
  step1Desc: "買い物客はあらゆるチャネルからタップまたはスキャン。1つの入口、1つのブランド体験。",
  step1PillRow: "Web · SNS · メール · QR · OOH · 店頭",
  step2Title: "つながる",
  step2Desc: "5つのPic-n-Mixモジュールが、彼らの言語で、あなたのブランドで、AIによって検証されて動作します。",
  step2PillRow: "学ぶ · 遊ぶ · 話す · 当たる · 貯める",
  step3Title: "学ぶ",
  step3Desc: "あらゆるインタラクションがファーストパーティデータとなり、リアルタイムでダッシュボードに流れ込みます。",
  step3PillRow: "買い物客 · 会話 · 場所 · エンゲージメント · コンプライアンス",

  chipWebsite: "Web",
  chipSocial:  "SNS",
  chipEmail:   "メール",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "店頭",
  chipLearn:   "学ぶ",
  chipPlay:    "遊ぶ",
  chipSay:     "話す",
  chipWin:     "当たる",
  chipEarn:    "貯める",
  chipShoppers:     "買い物客",
  chipConversation: "会話",
  chipLocation:     "場所",
  chipEngagement:   "エンゲージメント",
  chipCompliance:   "コンプライアンス",
  chipAriaEntry:  "入口チャネル",
  chipAriaModule: "Pic-n-Mixモジュール",
  chipAriaIntel:  "インテリジェンスカテゴリ",

  intgTitle: "本格的なインフラ。実証済みのテクノロジー。",
  intgSub: "TalkPackは、世界最大級の企業が信頼するのと同じクラウド、AI、コンプライアンスの基盤の上で動作しています。",
  intgCap1: "親プラットフォーム",
  intgCap2: "クラウドインフラ",
  intgCap3: "会話AI",
  intgCap4: "言語モデル",
  intgCap5: "AIとマップ",
  intgCap6: "栄養データ",
  intgCap7: "決済",
  intgFootnote: "ISO 27001認証プロバイダー · GDPR準拠 · マルチリージョンホスティング · 初日からエンタープライズ級セキュリティ",

  jColEntry: "入口",
  jColExperience: "体験",
  jColDashboard: "ダッシュボードインテリジェンス",
  jEntryDigitalTitle: "デジタル",
  jEntryDigitalDesc: "Webサイト、アプリストア、検索、ダイレクトリンク — あらゆるデジタル面から買い物客体験へワンタップ。",
  jEntrySocialTitle: "ソーシャル",
  jEntrySocialDesc: "スポンサー投稿、インフルエンサーコンテンツ、DM — フィードはプラットフォームを離れることなくTalkPackに流れ込みます。",
  jEntryPhysicalTitle: "リアル",
  jEntryPhysicalDesc: "パッケージ、棚、レジで — TalkPackがスキャンを会話に変えるリアル世界の入口。",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "店頭POS",
  jEntryPhysPill3: "パッケージ",
  jExpVerifyLabel: "検証",
  jExpVerifyText: "AI商品認識 — SKUをリアルタイム検証。",
  jExpLearnLabel: "学ぶ",
  jExpLearnBody: "栄養ホイール、アレルゲン、原材料、原産地 — 買い物客の言語で表示、ブランドが検証。",
  jExpEngageLabel: "つながる",
  jExpEngagePlayBody: "ルーレット、ガチャ、パズル、クイズ — 滞在時間を伸ばす5つのゲーム。",
  jExpEngageEarnBody: "ポイント、レシート、ロイヤルティ — バスケットレベルのインテリジェンス。",
  jExpEngageSayBody: "40以上の言語で応答する会話AI。",
  jExpRewardLabel: "報酬",
  jExpRewardBody: "即時の景品、レジでのデュアルQR検証、GPSで確認された請求 — 初日から監査対応。",
  jExpCaptureLabel: "取得",
  jExpCaptureSub: "あらゆるインタラクション · あなたが取得し、保有",

  capEmail:   "メール",
  capName:    "名前",
  capAddress: "住所",
  capGps:     "GPS",
  capProduct: "商品",
  capBasket:  "バスケット",
  capSpend:   "金額",
  capChat:    "チャット",

  dashShopperLabel: "買い物客インテリジェンス",
  dashShopperShort: "買い物客",
  dashShopper1Title: "ファーストパーティCRM",
  dashShopper1Desc: "メール、名前、住所、連絡設定。",
  dashShopper2Title: "GPS確認済みロケーション",
  dashShopper2Desc: "店舗エリア別に買い物客がエンゲージする場所。",
  dashShopper3Title: "購買頻度",
  dashShopper3Desc: "繰り返しのスキャンパターン、来店ペース。",
  dashShopper4Title: "バスケット構成",
  dashShopper4Desc: "アイテム単位のレシートデータ、併買商品。",

  dashConvLabel: "会話インテリジェンス",
  dashConvShort: "会話",
  dashConv1Title: "質問カテゴリ",
  dashConv1Desc: "アレルゲン、原材料、栄養、使い方、レシピ。",
  dashConv2Title: "感情分析",
  dashConv2Desc: "商品・地域別のポジティブ、ネガティブ、ニュートラル。",
  dashConv3Title: "混乱ポイント",
  dashConv3Desc: "買い物客が理解できないこと、繰り返される質問。",
  dashConv4Title: "購買意図",
  dashConv4Desc: "好奇心から小売業者への問い合わせまでの進行。",
  dashConv5Title: "言語の好み",
  dashConv5Desc: "ロケーションごとに買い物客が選ぶ言語。",

  dashDistLabel: "流通インテリジェンス",
  dashDistShort: "流通",
  dashDist1Title: "店頭在庫",
  dashDist1Desc: "棚にある商品、ジオフェンススキャンで確認。",
  dashDist2Title: "在庫切れアラート",
  dashDist2Desc: "「ここにない」報告をリアルタイムでフラグ。",
  dashDist3Title: "ヒートマップ",
  dashDist3Desc: "郵便番号、地域、屋号別のスキャン密度。",
  dashDist4Title: "世帯浸透率",
  dashDist4Desc: "「どこでもスキャン」が家庭での商品の在り方を可視化。",
  dashDist5Title: "店舗比較",
  dashDist5Desc: "ロケーション・屋号グループ別のエンゲージメント率。",

  dashCampLabel: "キャンペーン成果",
  dashCampShort: "キャンペーン",
  dashCamp1Title: "スキャン→エンゲージメント率",
  dashCamp1Desc: "スキャンのうち完全なインタラクションになる割合。",
  dashCamp2Title: "ゲーム完遂率",
  dashCamp2Desc: "セッションあたりのスピン、勝率、離脱ポイント。",
  dashCamp3Title: "引換トラッキング",
  dashCamp3Desc: "店舗別の請求済み・検証済み・期限切れの景品。",
  dashCamp4Title: "ROIダッシュボード",
  dashCamp4Desc: "キャンペーン費用 vs 取得した買い物客価値。",

  dashCompLabel: "コンプライアンス & 監査",
  dashCompShort: "コンプライアンス",
  dashComp1Title: "ASAコンプライアンススコア",
  dashComp1Desc: "キャンペーン別、会話別。",
  dashComp2Title: "エクスポート可能な監査ログ",
  dashComp2Desc: "AIの応答はすべて記録・検索可能。",
  dashComp3Title: "違反アラート",
  dashComp3Desc: "フラグされたコンテンツパターン。",
  dashComp4Title: "栄養エンゲージメント",
  dashComp4Desc: "ホイールで買い物客がどの栄養素と関わるか。",

  pvFormTitle: "おめでとうございます！",
  pvFormSub: "獲得：次回購入20%オフ",
  pvFormName: "お名前",
  pvFormEmail: "メールアドレス",
  pvFormPostcode: "郵便番号",
  pvFormCta: "賞品を受け取る",
  pvFormFootnote: "プライバシー優先 · GDPR準拠",

  pvChatTitle: "AIアンバサダー",
  pvChatSub: "Morning O'sで学習済み",
  pvChatBubble1: "これはグルテンフリーですか？",
  pvChatBubble2: "はい — 認証グルテンフリーで、1食あたり糖質はわずか9gです。レシピのアイデアはいかがですか？",
  pvChatBubble3: "お願いします",
  pvChatBubble4: "ベリー入りオーバーナイトオーツがおすすめです — 準備3分。",
  pvChatPlaceholder: "何でも聞いてください…",
  pvChatFootnote: "40以上の言語 · 常時対応",

  pvMapTitle: "近くのMorning O'sを探す",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "経路を表示",
  pvMapFootnote: "GPS確認済み · 半径150mのジオフェンス",
  pvMapOos: "在庫切れ",

  pvWheelTitle: "Morning O's · スピン＆ウィン",
  pvWheelCredits: "350クレジット",
  pvWheelCta: "スピン",
  pvWheelFootnote: "ルーレット · 栄養ルーレット · ガチャ · クイズ · パズル",
  pvWheelWedgeWin: "WIN!",
  pvWheelWedgeTry: "再挑戦",

  pvNutTitle: "Morning O's · 栄養成分情報",
  pvNutPer100g: "100gあたり",
  pvNutPerServing: "1食分",
  pvNutCta: "次へ",
  pvNutFootnote: "ASA準拠 · ゲーム前必須",

  bookDemoSubject: "TalkPackデモのご依頼",
  bookDemoBody: "ShopAIチームの皆様\n\nTalkPackのデモをご予約させていただきたく存じます。日程調整のためご連絡いただけますと幸いです。\n\nよろしくお願いいたします、",

  // ── Viz mock-data labels (JA) ──
  vizCapQuestions: "今週のショッパー上位質問",
  vizQAllergens: "アレルゲン", vizQIngredients: "原材料", vizQNutrition: "栄養",
  vizQRecipes: "レシピ", vizQUsage: "使い方",
  vizCapSentiment: "全商品の過去30日間",
  vizSentPositive: "ポジティブ", vizSentNeutral: "ニュートラル", vizSentNegative: "ネガティブ",
  vizCapConfusion: "繰り返される質問 — コンテンツの抜け",
  vizConfusion1: "これはグルテンフリーですか?",
  vizConfusion2: "ヴィーガンに適していますか?",
  vizConfusion3: "ナッツの注意書きは?",
  vizCapFunnel: "会話によるコンバージョン",
  vizFunnelCurious: "興味あり", vizFunnelInterested: "関心あり", vizFunnelReady: "購入準備完了",
  vizCapAvail: "計画通りの場所で商品が利用可能",
  vizAvailDetail: "1,326店舗中1,247店舗で棚に在庫確認",
  vizCapOos: "本日3店舗をフラグ · 自動アラート送信済み",
  vizCapHeat: "ロンドン · マンチェスター · バーミンガムが先行",
  vizCapHousehold: "スキャンの68%は家庭内で発生 — 商品はそこで生きている",
  vizCapFascia: "店舗フォーマット別エンゲージメント率",
  vizCapEngrate: "完全なインタラクションになるスキャン",
  vizEngrateCompare: "業界ベンチマーク42%に対して+36ポイント",
  vizCapGame: "ショッパーの離脱地点 — ゲームバランスを調整",
  vizGameStart: "開始", vizGamePlay: "プレイ", vizGameWin: "当選", vizGameClaim: "請求",
  vizCapRdm: "検証率96% · 失効少なめ",
  vizRdmClaimed: "請求済み", vizRdmValidated: "検証済み", vizRdmExpired: "失効",
  vizCapRoi: "キャンペーン費用 vs ショッパー生涯価値",
  vizRoiSpend: "費用", vizRoiValueLabel: "獲得価値", vizRoiReturn: "3.4倍リターン",
  vizCapComp: "キャンペーン別 · 会話別 · 監査対応",
  vizCapAudit: "AIの応答はすべてログ · オンデマンドで取得可能",
  vizCapViolation: "今月の違反件数 · AI出力を継続的に検証",
  vizCapNut: "ホイール上でショッパーが触れる栄養素",
  vizNutSugar: "糖質", vizNutFat: "脂質", vizNutSalt: "塩分", vizNutSatFat: "飽和脂肪",
  vizCapLang: "ショッパーの言語を自動検出",
  vizLangOther: "その他",
  vizCapCrm: "獲得したショッパー",
  vizCapMap: "今週247か所",
  vizCapWeek: "週平均2.3回の来店",
  vizDayMon: "月", vizDayTue: "火", vizDayWed: "水",
  vizDayThu: "木", vizDayFri: "金", vizDaySat: "土", vizDaySun: "日",
  vizCapBasket: "平均買い物カゴ:£8.40 · 6.2点",
  vizBasketDairy: "乳製品", vizBasketBakery: "ベーカリー", vizBasketSnacks: "スナック", vizBasketOther: "その他",
};

// ────────── 中文(简体) ──────────
const zh: LandingCopy = {
  ...en,
  navPlatform: "平台",
  navModules: "模块",
  navPortal: "门户登录",
  navLangButton: "ZH",
  navRegister: "注册",
  navBookDemo: "预约演示",
  navLogin: "登录",

  trustLeft: "无需 App · 无需对接 · 不依赖门店",
  trustRight: "成立于 2024 · 英国 · 与 The Wholesale Group · Caterers Choice · Passot(日本) · Talysis UK · Jakavarna Group(泰国)共同打造",

  heroEyebrow: "商业的副驾驶",
  heroL1: "每件商品。一段对话。",
  heroL2: "每次扫描。真实数据。",
  heroSub: "TalkPack 让任何商品变成实时对话。购物者扫一扫,即可学习、游戏、提问、赢取奖励、累积积分。品牌方获得第一方数据、HFSS 合规以及与线下零售购物者的直接关系。无需 App。无需对接。不依赖门店。",
  heroCtaPrimary: "预约演示",
  heroCtaGhost: "了解工作方式",
  heroPillAllergens: "过敏原",
  heroPillHfss: "HFSS",
  heroPillIngredients: "成分",
  heroVerified: "已验证",

  statStores: "门店 · 英国 · 全球扩展中",
  statLanguages: "语言 · 自动识别",
  statGames: "从需求到上线营销",
  statModules: "内置营养转盘",
  statSteps: "您的品牌。您的数据。您的 AI。",
  statDownloads: "App 下载 · 集成 · 摩擦",
  statGeofence: "来自实体零售的购物者数据",
  statScans: "从货架到购物者 CRM",

  fullJourneyEyebrow: "完整旅程",
  fullJourneyHeading: "从入口到智能。一个平台。",
  fullJourneySub: "每个渠道。每个模块。每个数据点。TalkPack 架构端到端的连接方式。",
  fullJourneyCaption: "完整流程。从购物者入口到品牌智能。可通过仪表板、AI 聊天或 SmartMail 查询导出。",

  picEyebrow: "Pic-n-Mix",
  picHeading: "一个平台。五种与购物者互动的方式。",
  picSub: "品牌按需选择模块。购物者获得多元体验。每个模块共享同一次扫描、同一段对话、同一层数据。",
  learnDesc: "产品教育、成分透明度、HFSS 红绿灯标识。",
  learnF1: "营养转盘", learnF2: "过敏原扫描", learnF3: "产地数据",
  playDesc: "五种游戏类型,提升停留时间和复访率。",
  playF1: "幸运转盘", playF2: "扭蛋", playF3: "拼图", playF4: "问答", playF5: "营养转盘",
  sayDesc: "用购物者母语回答其疑问的对话式 AI。",
  sayF1: "40+ 种语言", sayF2: "成分问答", sayF3: "食谱建议",
  winDesc: "奖品、引流到店、收银台双 QR 验证、配送单。",
  winF1: "即时奖励", winF2: "围栏化奖品", winF3: "送货上门",
  earnDesc: "积分、小票扫描、忠诚度计划、购物篮智能。",
  earnF1: "小票 OCR", earnF2: "跨品牌画像", earnF3: "奖励目录",
  moduleSeeMore: "查看模块",

  oeEyebrow: "一引擎 · 多动态",
  oeHeading: "TalkPack 是更大平台的一部分。",
  oeSub: "ShopAI 为品牌、零售商和批发商提供一个统一的数据层。端到端的对话式商业智能。TalkPack 是购物者加入对话的方式。",
  oe1Strap: "来自实体零售的购物者互动",
  oe2Strap: "面向便利店行业的 AI",
  oe3Strap: "现场审计智能",
  oe4Strap: "积分、小票、购物篮数据",
  oeActiveLabel: "当前",

  ucHeading: "不同角色。同一平台。",
  ucBrandTitle: "品牌营销",
  ucBrand1: "在混合零售中开展全国级新品上市",
  ucBrand2: "向参与门店引流",
  ucBrand3: "符合 HFSS 的包装互动",
  ucBrand4: "跨品牌购物者画像",
  ucBrand5: "受监管类目(医药、酒精)中带审计日志的 AI 应答",
  ucRetailerTitle: "零售商 / 批发商",
  ucRetailer1: "零售商出资的独家促销",
  ucRetailer2: "Symbol Group 激活",
  ucRetailer3: "货架合规审计",
  ucRetailer4: "面向特定零售商的忠诚度计划",
  ucAgencyTitle: "代理商 / 经销商",
  ucAgency1: "为客户品牌定制的白标活动",
  ucAgency2: "多客户门户访问",
  ucAgency3: "贸易营销 PDF 报告",
  ucAgency4: "自动生成的社交内容",

  hfssEyebrow: "为 HFSS 而生",
  hfssHeading: "内置 HFSS 合规。",
  hfssSub: "购物者在游戏前先看到营养概况。脂肪、糖、盐、饱和脂肪 — 按每日参考摄入量以红绿灯呈现。强制。可审计。四层监管防线。",
  hfss1Title: "强制营养转盘。",
  hfss1Body: "每件 HFSS 商品在购物者继续之前,都会按每日参考摄入量显示脂肪、糖、盐和饱和脂肪的红绿灯。",
  hfss2Title: "以教育为先的呈现。",
  hfss2Body: "平台推动营养透明度。把购物者互动定位为知情选择,而非促销推送。",
  hfss3Title: "输出验证。",
  hfss3Body: "对话式 AI 按监管模式监控。每条回应都会被记录、过滤,并可审计。",
  hfss4Title: "可导出审计日志。",
  hfss4Body: "ASA 合规分,每条回应可记录与回溯,被标记内容触发违规告警。",
  hfssBadge: "ASA 合规度:98.4%",

  wlEyebrow: "您的品牌,任何语言",
  wlHeading: "您的数据。您的 AI。按您的方式。",
  wlLangCaption: "40+ 种语言。自动识别或按活动手动设定。",
  wlFeat1Title: "您的品牌识别",
  wlFeat1Body: "Logo、色彩、字体应用于每个购物者界面与配送单。",
  wlFeat2Title: "您的数据,您的 AI",
  wlFeat2Body: "数据存于您的账户。AI 基于您的产品训练。关系归您所有。",
  wlFeat3Title: "您的门店,您的电子围栏",
  wlFeat3Body: "通用模式或按门店模式。默认 150 m 围栏,按活动可配置。",
  wlFeat4Title: "您的团队,您的访问",
  wlFeat4Body: "基于角色的权限。品牌、代理、现场、财务 — 各取所需。",

  proofEyebrow: "已部署",
  proofHeading: "已上线。已运行。",
  proofStat1Label: "活动数据库中的英国门店",
  proofStat2Label: "支持语言",
  proofStat3Label: "于英国创立",
  proofQuotePlaceholder: "客户案例即将推出。",

  faqHeading: "品牌常向我们提出的问题。",
  faq1Q: "购物者需要下载 App 吗?",
  faq1A: "不需要。TalkPack 在浏览器中运行,一次扫描即可开启体验。",
  faq2Q: "TalkPack 需要与我们的 EPOS 或 CRM 集成吗?",
  faq2A: "无需集成。所采集的数据为第一方,可通过 PDF、CSV 或 API 导出。",
  faq3Q: "对于 HFSS 商品会怎样?",
  faq3A: "每件 HFSS 商品在游戏前显示强制营养转盘。合规是内置的,而非附加。",
  faq4Q: "你们支持哪些语言?",
  faq4A: "40+ 种。平台自动识别,或按活动手动设定。",
  faq5Q: "我们能仅在特定零售商处投放吗?",
  faq5A: "可以。门店专属模式会将活动围栏限制在所选门店;通用模式则允许任何购物者随时随地参与。",
  faq6Q: "搭建一个活动需要多久?",
  faq6A: "标准促销大约 10 分钟。门户中提供分步向导。",
  faq7Q: "可以白标体验吗?",
  faq7A: "可以。您的 Logo、您的色彩、您的语言。除非您愿意,购物者不会看到 TalkPack 的品牌。",

  footerColPlatform: "平台",
  footerColModules: "模块",
  footerColCompany: "公司",
  footerColResources: "资源",
  footerCompany1: "关于",
  footerCompany2: "联系",
  footerResources3: "隐私",
  footerResources4: "条款",
  footerNewsHeading: "季度更新。无垃圾邮件。",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "订阅",
  footerRegistration: "ShopAI Ltd · 公司编号 15877093 · 在英格兰注册",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (ZH) ──
  heroAlt: "在超市过道里购物的女士 — TalkPack 体验的每个触点都围绕着她",

  stepsTitle: "三步。一个平台。",
  stepsSub: "从购物者进入的那一刻起,到您了解他们的那一刻。运行简单。数据丰富。",
  step1Title: "进入",
  step1Desc: "购物者从任何渠道点击或扫描。一个入口,一个品牌化体验。",
  step1PillRow: "网站 · 社交 · 邮件 · QR · OOH · 店内",
  step2Title: "互动",
  step2Desc: "五个 Pic-n-Mix 模块用他们的语言、您的品牌运行,经 AI 验证。",
  step2PillRow: "学 · 玩 · 问 · 赢 · 赚",
  step3Title: "学习",
  step3Desc: "每一次互动都成为第一方数据,实时流入您的仪表板。",
  step3PillRow: "购物者 · 对话 · 位置 · 互动 · 合规",

  chipWebsite: "网站",
  chipSocial:  "社交",
  chipEmail:   "邮件",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "店内",
  chipLearn:   "学",
  chipPlay:    "玩",
  chipSay:     "问",
  chipWin:     "赢",
  chipEarn:    "赚",
  chipShoppers:     "购物者",
  chipConversation: "对话",
  chipLocation:     "位置",
  chipEngagement:   "互动",
  chipCompliance:   "合规",
  chipAriaEntry:  "入口渠道",
  chipAriaModule: "Pic-n-Mix 模块",
  chipAriaIntel:  "智能类别",

  intgTitle: "扎实的基础设施。经过验证的技术。",
  intgSub: "TalkPack 运行在与全球最大企业所信赖的相同的云、AI 与合规基础之上。",
  intgCap1: "母平台",
  intgCap2: "云基础设施",
  intgCap3: "对话式 AI",
  intgCap4: "语言模型",
  intgCap5: "AI 与地图",
  intgCap6: "营养数据",
  intgCap7: "支付",
  intgFootnote: "通过 ISO 27001 认证的供应商 · 符合 GDPR · 多区域托管 · 第一天起即享企业级安全",

  jColEntry: "入口",
  jColExperience: "体验",
  jColDashboard: "仪表板智能",
  jEntryDigitalTitle: "数字",
  jEntryDigitalDesc: "网站、应用商店、搜索、直接链接 — 从任何数字界面一键进入购物者体验。",
  jEntrySocialTitle: "社交",
  jEntrySocialDesc: "赞助贴文、KOL 内容、私信 — 信息流直接进入 TalkPack,无需离开平台。",
  jEntryPhysicalTitle: "实体",
  jEntryPhysicalDesc: "在包装上、货架上、收银台旁 — TalkPack 把扫描变成对话的真实世界入口。",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "店内 POS",
  jEntryPhysPill3: "包装",
  jExpVerifyLabel: "验证",
  jExpVerifyText: "AI 商品识别 — SKU 实时验证。",
  jExpLearnLabel: "学习",
  jExpLearnBody: "营养转盘、过敏原、配料、产地 — 以购物者的语言显示,由品牌验证。",
  jExpEngageLabel: "互动",
  jExpEngagePlayBody: "转盘、扭蛋、拼图、问答 — 五种游戏类型,延长停留时间。",
  jExpEngageEarnBody: "积分、小票、忠诚度 — 篮级智能。",
  jExpEngageSayBody: "对话式 AI,以 40+ 种语言回答。",
  jExpRewardLabel: "奖励",
  jExpRewardBody: "即时奖品、收银台双 QR 验证、GPS 校验领取 — 自第一天起即可审计。",
  jExpCaptureLabel: "采集",
  jExpCaptureSub: "每一次互动 · 由您采集并拥有",

  capEmail:   "邮件",
  capName:    "姓名",
  capAddress: "地址",
  capGps:     "GPS",
  capProduct: "商品",
  capBasket:  "购物篮",
  capSpend:   "消费",
  capChat:    "聊天",

  dashShopperLabel: "购物者智能",
  dashShopperShort: "购物者",
  dashShopper1Title: "第一方 CRM",
  dashShopper1Desc: "邮件、姓名、地址、联系偏好。",
  dashShopper2Title: "GPS 验证位置",
  dashShopper2Desc: "购物者按门店区域互动的地点。",
  dashShopper3Title: "购买频次",
  dashShopper3Desc: "重复扫描模式、到访节奏。",
  dashShopper4Title: "购物篮构成",
  dashShopper4Desc: "单品级小票数据、共同购买的商品。",

  dashConvLabel: "对话智能",
  dashConvShort: "对话",
  dashConv1Title: "问题类别",
  dashConv1Desc: "过敏原、配料、营养、用法、食谱。",
  dashConv2Title: "情感分析",
  dashConv2Desc: "按商品和地区划分的正面、负面、中性。",
  dashConv3Title: "困惑点",
  dashConv3Desc: "购物者不理解的内容、重复出现的问题。",
  dashConv4Title: "购买意向",
  dashConv4Desc: "从好奇到对零售商的请求的进展。",
  dashConv5Title: "语言偏好",
  dashConv5Desc: "购物者按地点选择的语言。",

  dashDistLabel: "分销智能",
  dashDistShort: "分销",
  dashDist1Title: "门店在售情况",
  dashDist1Desc: "货架上的商品,经地理围栏扫描确认。",
  dashDist2Title: "缺货警报",
  dashDist2Desc: "实时标记的「这里没有」上报。",
  dashDist3Title: "热力图",
  dashDist3Desc: "按邮编、地区、招牌划分的扫描密度。",
  dashDist4Title: "家庭渗透",
  dashDist4Desc: "「随处扫描」展示商品在家中的存在。",
  dashDist5Title: "门店对比",
  dashDist5Desc: "按地点、按招牌组的互动率。",

  dashCampLabel: "活动表现",
  dashCampShort: "活动",
  dashCamp1Title: "扫描转互动率",
  dashCamp1Desc: "多少次扫描转化为完整互动。",
  dashCamp2Title: "游戏完成率",
  dashCamp2Desc: "每次会话的转盘数、获胜率、流失点。",
  dashCamp3Title: "兑换跟踪",
  dashCamp3Desc: "按门店统计的已领取、已验证、过期奖品。",
  dashCamp4Title: "ROI 仪表板",
  dashCamp4Desc: "活动支出对比已捕获的购物者价值。",

  dashCompLabel: "合规与审计",
  dashCompShort: "合规",
  dashComp1Title: "ASA 合规分数",
  dashComp1Desc: "按活动、按对话。",
  dashComp2Title: "可导出审计日志",
  dashComp2Desc: "每条 AI 回复均记录可检索。",
  dashComp3Title: "违规警报",
  dashComp3Desc: "标记的内容模式。",
  dashComp4Title: "营养互动",
  dashComp4Desc: "购物者在转盘上与哪些营养素互动。",

  pvFormTitle: "恭喜!",
  pvFormSub: "您赢得了:下次消费 8 折",
  pvFormName: "您的姓名",
  pvFormEmail: "您的邮箱",
  pvFormPostcode: "邮编",
  pvFormCta: "领取奖品",
  pvFormFootnote: "隐私优先 · 符合 GDPR",

  pvChatTitle: "AI 大使",
  pvChatSub: "在 Morning O's 上训练",
  pvChatBubble1: "这是无麸质的吗?",
  pvChatBubble2: "是 — 认证无麸质,每份仅含 9g 糖。需要食谱建议吗?",
  pvChatBubble3: "好的,谢谢",
  pvChatBubble4: "试试浆果隔夜燕麦 — 准备 3 分钟。",
  pvChatPlaceholder: "随便问我吧…",
  pvChatFootnote: "40+ 种语言 · 全天候",

  pvMapTitle: "查找您附近的 Morning O's",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "导航",
  pvMapFootnote: "GPS 验证 · 150 米地理围栏",
  pvMapOos: "缺货",

  pvWheelTitle: "Morning O's · 转盘赢奖",
  pvWheelCredits: "350 积分",
  pvWheelCta: "转动",
  pvWheelFootnote: "转盘 · 营养转盘 · 扭蛋 · 问答 · 拼图",
  pvWheelWedgeWin: "中奖!",
  pvWheelWedgeTry: "再来",

  pvNutTitle: "Morning O's · 营养成分信息",
  pvNutPer100g: "每 100g",
  pvNutPerServing: "每份",
  pvNutCta: "继续",
  pvNutFootnote: "符合 ASA · 游玩前必读",

  bookDemoSubject: "TalkPack 演示申请",
  bookDemoBody: "您好 ShopAI 团队,\n\n我希望预约一次 TalkPack 演示。请与我联系以安排时间。\n\n谢谢,",

  // ── Viz mock-data labels (ZH) ──
  vizCapQuestions: "本周购物者最常问的问题",
  vizQAllergens: "过敏原", vizQIngredients: "成分", vizQNutrition: "营养",
  vizQRecipes: "食谱", vizQUsage: "用法",
  vizCapSentiment: "所有商品最近30天",
  vizSentPositive: "积极", vizSentNeutral: "中性", vizSentNegative: "消极",
  vizCapConfusion: "重复的问题 — 内容缺口",
  vizConfusion1: "这是无麸质的吗?",
  vizConfusion2: "适合素食者吗?",
  vizConfusion3: "有坚果警告吗?",
  vizCapFunnel: "通过对话实现转化",
  vizFunnelCurious: "好奇", vizFunnelInterested: "感兴趣", vizFunnelReady: "准备购买",
  vizCapAvail: "在您计划的地方有货",
  vizAvailDetail: "1,326家门店中1,247家在架确认",
  vizCapOos: "今日标记3家门店 · 自动警报已发送",
  vizCapHeat: "伦敦 · 曼彻斯特 · 伯明翰领先",
  vizCapHousehold: "68%的扫描发生在家中 — 您的商品在那里生活",
  vizCapFascia: "按零售商招牌划分的互动率",
  vizCapEngrate: "转化为完整互动的扫描",
  vizEngrateCompare: "对比42%行业基准+36分",
  vizCapGame: "购物者在哪里流失 — 调整游戏平衡",
  vizGameStart: "开始", vizGamePlay: "游戏", vizGameWin: "中奖", vizGameClaim: "领取",
  vizCapRdm: "96%验证率 · 过期少",
  vizRdmClaimed: "已领取", vizRdmValidated: "已验证", vizRdmExpired: "已过期",
  vizCapRoi: "活动支出对比购物者生命周期价值",
  vizRoiSpend: "支出", vizRoiValueLabel: "捕获价值", vizRoiReturn: "3.4倍回报",
  vizCapComp: "按活动 · 按对话 · 审计就绪",
  vizCapAudit: "每条AI回复均记录 · 按需检索",
  vizCapViolation: "本月违规次数 · AI输出持续验证",
  vizCapNut: "购物者在转盘上互动的营养素",
  vizNutSugar: "糖", vizNutFat: "脂肪", vizNutSalt: "盐", vizNutSatFat: "饱和脂肪",
  vizCapLang: "自动检测购物者语言",
  vizLangOther: "其他",
  vizCapCrm: "已获取的购物者",
  vizCapMap: "本周247个地点",
  vizCapWeek: "平均每周2.3次访问",
  vizDayMon: "周一", vizDayTue: "周二", vizDayWed: "周三",
  vizDayThu: "周四", vizDayFri: "周五", vizDaySat: "周六", vizDaySun: "周日",
  vizCapBasket: "平均购物篮:£8.40 · 6.2件",
  vizBasketDairy: "乳制品", vizBasketBakery: "烘焙", vizBasketSnacks: "零食", vizBasketOther: "其他",
};

// ────────── ภาษาไทย ──────────
const th: LandingCopy = {
  ...en,
  navPlatform: "แพลตฟอร์ม",
  navModules: "โมดูล",
  navPortal: "เข้าสู่พอร์ทัล",
  navLangButton: "TH",
  navRegister: "ลงทะเบียน",
  navBookDemo: "จองเดโม",
  navLogin: "เข้าสู่ระบบ",

  trustLeft: "ไม่ต้องใช้แอป · ไม่ต้องเชื่อมต่อระบบ · ไม่ต้องพึ่งร้านค้า",
  trustRight: "ตั้งแต่ปี 2024 · สหราชอาณาจักร · พัฒนาร่วมกับ The Wholesale Group · Caterers Choice · Passot (ญี่ปุ่น) · Talysis UK · Jakavarna Group (ไทย)",

  heroEyebrow: "ผู้ช่วยนักบินสำหรับธุรกิจค้าปลีก",
  heroL1: "ทุกสินค้า. หนึ่งบทสนทนา.",
  heroL2: "ทุกการสแกน. ข้อมูลจริง.",
  heroSub: "TalkPack เปลี่ยนสินค้าทุกชิ้นให้กลายเป็นบทสนทนาสด ผู้ซื้อสแกน เรียนรู้ เล่น ถาม รับรางวัล สะสมแต้ม คุณได้ข้อมูล first-party การปฏิบัติตาม HFSS และความสัมพันธ์โดยตรงกับผู้ซื้อในร้านค้าจริง ไม่ต้องใช้แอป ไม่ต้องเชื่อมต่อระบบ ไม่ต้องพึ่งร้านค้า",
  heroCtaPrimary: "จองเดโม",
  heroCtaGhost: "ดูวิธีการทำงาน",
  heroPillAllergens: "สารก่อภูมิแพ้",
  heroPillHfss: "HFSS",
  heroPillIngredients: "ส่วนประกอบ",
  heroVerified: "ยืนยันแล้ว",

  statStores: "ร้านค้า · สหราชอาณาจักร · ขยายทั่วโลก",
  statLanguages: "ภาษา · ตรวจจับอัตโนมัติ",
  statGames: "จากบรีฟสู่แคมเปญที่เปิดใช้งานจริง",
  statModules: "วงล้อโภชนาการในตัว",
  statSteps: "แบรนด์ของคุณ ข้อมูลของคุณ AI ของคุณ",
  statDownloads: "การดาวน์โหลดแอป · การเชื่อมต่อ · ความยุ่งยาก",
  statGeofence: "ข้อมูลผู้ซื้อจากร้านค้าจริง",
  statScans: "จากชั้นวางสู่ CRM ของผู้ซื้อ",

  fullJourneyEyebrow: "เส้นทางครบวงจร",
  fullJourneyHeading: "จากจุดเริ่มต้นสู่ข้อมูลเชิงลึก หนึ่งแพลตฟอร์ม",
  fullJourneySub: "ทุกช่องทาง ทุกโมดูล ทุกจุดข้อมูล นี่คือวิธีที่สถาปัตยกรรม TalkPack เชื่อมต่อกันแบบครบวงจร",
  fullJourneyCaption: "การไหลแบบครบวงจร จากจุดเริ่มต้นของผู้ซื้อสู่ข้อมูลเชิงลึกของแบรนด์ ส่งออกได้ผ่านแดชบอร์ด แชต AI หรือคำสั่ง SmartMail",

  picEyebrow: "Pic-n-Mix",
  picHeading: "หนึ่งแพลตฟอร์ม ห้าวิธีในการมีส่วนร่วมกับผู้ซื้อ",
  picSub: "แบรนด์เลือกโมดูลที่ต้องการ ผู้ซื้อได้รับประสบการณ์แบบผสมผสาน ทุกโมดูลทำงานบนการสแกนเดียวกัน บทสนทนาเดียวกัน และชั้นข้อมูลเดียวกัน",
  learnDesc: "ให้ความรู้เกี่ยวกับสินค้า ความโปร่งใสด้านส่วนประกอบ ไฟจราจร HFSS",
  learnF1: "วงล้อโภชนาการ", learnF2: "การสแกนสารก่อภูมิแพ้", learnF3: "ข้อมูลแหล่งผลิต",
  playDesc: "เกมห้าประเภทที่เพิ่มเวลาบนหน้าและการกลับมาเล่นซ้ำ",
  playF1: "วงล้อหมุนโชค", playF2: "Gacha", playF3: "ปริศนา", playF4: "ตอบคำถาม", playF5: "วงล้อโภชนาการ",
  sayDesc: "AI สนทนาที่ตอบคำถามผู้ซื้อในภาษาของพวกเขาเอง",
  sayF1: "40+ ภาษา", sayF2: "ถาม-ตอบส่วนประกอบ", sayF3: "แนะนำสูตรอาหาร",
  winDesc: "รางวัล ดึงคนเข้าร้าน ตรวจสอบ QR สองชั้นที่จุดชำระ ใบส่งสินค้า",
  winF1: "รางวัลทันที", winF2: "รางวัลตามพื้นที่", winF3: "ส่งถึงบ้าน",
  earnDesc: "คะแนน สแกนใบเสร็จ โปรแกรมสะสมแต้ม ข้อมูลตะกร้าสินค้า",
  earnF1: "OCR ใบเสร็จ", earnF2: "โปรไฟล์ข้ามแบรนด์", earnF3: "แค็ตตาล็อกของรางวัล",
  moduleSeeMore: "ดูโมดูล",

  oeEyebrow: "หนึ่งเครื่องยนต์ · หลายพลวัต",
  oeHeading: "TalkPack เป็นส่วนหนึ่งของแพลตฟอร์มที่ใหญ่กว่า",
  oeSub: "ShopAI มอบชั้นข้อมูลเดียวให้กับแบรนด์ ผู้ค้าปลีก และผู้ค้าส่ง ระบบ business intelligence แบบสนทนาตั้งแต่ต้นจนจบ TalkPack คือวิธีที่ผู้ซื้อเข้าร่วมบทสนทนา",
  oe1Strap: "การมีส่วนร่วมของผู้ซื้อจากร้านค้าจริง",
  oe2Strap: "AI สำหรับร้านสะดวกซื้อ",
  oe3Strap: "ข้อมูลเชิงลึกการตรวจสอบภาคสนาม",
  oe4Strap: "คะแนน ใบเสร็จ ข้อมูลตะกร้า",
  oeActiveLabel: "ปัจจุบัน",

  ucHeading: "บทบาทต่างกัน แพลตฟอร์มเดียวกัน",
  ucBrandTitle: "ฝ่ายการตลาดของแบรนด์",
  ucBrand1: "เปิดตัวสินค้าระดับชาติในการค้าปลีกแบบผสม",
  ucBrand2: "ดึงคนเข้าร้านที่เข้าร่วม",
  ucBrand3: "การมีส่วนร่วมบนบรรจุภัณฑ์ที่สอดคล้อง HFSS",
  ucBrand4: "การทำโปรไฟล์ผู้ซื้อข้ามแบรนด์",
  ucBrand5: "หมวดที่ถูกกำกับ (เภสัช แอลกอฮอล์) พร้อมคำตอบ AI ที่ตรวจสอบได้",
  ucRetailerTitle: "ผู้ค้าปลีก / ผู้ค้าส่ง",
  ucRetailer1: "โปรโมชันเฉพาะที่ผู้ค้าปลีกสนับสนุน",
  ucRetailer2: "การเปิดใช้กลุ่ม symbol",
  ucRetailer3: "การตรวจสอบการปฏิบัติตามชั้นวาง",
  ucRetailer4: "โปรแกรมสะสมแต้มเฉพาะของผู้ค้าปลีก",
  ucAgencyTitle: "เอเจนซี / ตัวแทน",
  ucAgency1: "แคมเปญแบบ white-label สำหรับลูกค้าแบรนด์",
  ucAgency2: "การเข้าใช้พอร์ทัลหลายลูกค้า",
  ucAgency3: "รายงาน trade marketing แบบ PDF",
  ucAgency4: "เนื้อหาโซเชียลที่สร้างอัตโนมัติ",

  hfssEyebrow: "ออกแบบเพื่อ HFSS",
  hfssHeading: "การปฏิบัติตาม HFSS ที่ติดมาในตัว",
  hfssSub: "ผู้ซื้อเห็นโปรไฟล์โภชนาการก่อนเล่น ไขมัน น้ำตาล เกลือ ไขมันอิ่มตัว — แสดงเป็นไฟจราจรเทียบกับค่าอ้างอิงรายวัน บังคับ ตรวจสอบได้ การป้องกันด้านกฎระเบียบ 4 ชั้น",
  hfss1Title: "วงล้อโภชนาการบังคับ",
  hfss1Body: "สินค้า HFSS ทุกชิ้นต้องแสดงไฟจราจรของไขมัน น้ำตาล เกลือ และไขมันอิ่มตัวเทียบกับค่าอ้างอิงรายวันก่อนที่ผู้ซื้อจะดำเนินการต่อ",
  hfss2Title: "เน้นการศึกษาเป็นอันดับแรก",
  hfss2Body: "แพลตฟอร์มส่งเสริมความโปร่งใสด้านโภชนาการ จัดวางการมีส่วนร่วมเป็นทางเลือกที่มีข้อมูล ไม่ใช่การกดดันโปรโมชัน",
  hfss3Title: "ตรวจสอบผลลัพธ์",
  hfss3Body: "AI สนทนาถูกตรวจสอบตามรูปแบบที่กำกับ ทุกคำตอบถูกบันทึก กรอง และตรวจสอบได้",
  hfss4Title: "ส่งออกบันทึกการตรวจสอบได้",
  hfss4Body: "คะแนนการปฏิบัติตาม ASA ทุกคำตอบถูกบันทึกและเรียกคืนได้ มีการแจ้งเตือนการละเมิดสำหรับเนื้อหาที่ถูกตั้งธง",
  hfssBadge: "การปฏิบัติตาม ASA: 98.4%",

  wlEyebrow: "แบรนด์ของคุณ ในทุกภาษา",
  wlHeading: "ข้อมูลของคุณ AI ของคุณ ในแบบของคุณ",
  wlLangCaption: "40+ ภาษา ตรวจจับอัตโนมัติหรือกำหนดด้วยตนเองต่อแคมเปญ",
  wlFeat1Title: "เอกลักษณ์ของแบรนด์คุณ",
  wlFeat1Body: "โลโก้ สี ฟอนต์ ใช้กับทุกหน้าจอผู้ซื้อและใบส่งสินค้า",
  wlFeat2Title: "ข้อมูลของคุณ AI ของคุณ",
  wlFeat2Body: "ข้อมูลถูกเก็บในบัญชีของคุณ AI ฝึกบนสินค้าของคุณ คุณเป็นเจ้าของความสัมพันธ์",
  wlFeat3Title: "ร้านค้าของคุณ จีโอเฟนซ์ของคุณ",
  wlFeat3Body: "โหมดสากลหรือเฉพาะร้าน จีโอเฟนซ์เริ่มต้น 150 ม. ปรับได้ต่อแคมเปญ",
  wlFeat4Title: "ทีมของคุณ การเข้าถึงของคุณ",
  wlFeat4Body: "สิทธิ์ตามบทบาท แบรนด์ เอเจนซี ภาคสนาม การเงิน — แต่ละคนเห็นมุมมองที่เหมาะสม",

  proofEyebrow: "อยู่ในการใช้งานจริง",
  proofHeading: "เปิดใช้งานแล้ว ใช้งานได้แล้ว",
  proofStat1Label: "ร้านค้าในสหราชอาณาจักรในฐานข้อมูลแคมเปญ",
  proofStat2Label: "ภาษาที่รองรับ",
  proofStat3Label: "ก่อตั้งในสหราชอาณาจักร",
  proofQuotePlaceholder: "เรื่องราวลูกค้ากำลังจะมา",

  faqHeading: "คำถามที่แบรนด์ถามเรา",
  faq1Q: "ผู้ซื้อต้องดาวน์โหลดแอปหรือไม่?",
  faq1A: "ไม่ต้อง TalkPack ทำงานในเบราว์เซอร์ สแกนครั้งเดียวเปิดประสบการณ์ได้ทันที",
  faq2Q: "TalkPack เชื่อมต่อกับ EPOS หรือ CRM ของเราไหม?",
  faq2A: "ไม่จำเป็นต้องเชื่อมต่อ ข้อมูลที่เก็บคือ first-party และส่งออกได้ในรูปแบบ PDF, CSV หรือผ่าน API",
  faq3Q: "สินค้า HFSS จะเป็นอย่างไร?",
  faq3A: "สินค้า HFSS ทุกชิ้นจะแสดงวงล้อโภชนาการบังคับก่อนเล่นเกม การปฏิบัติตามเป็นแบบในตัว ไม่ใช่ติดเพิ่ม",
  faq4Q: "รองรับภาษาใดบ้าง?",
  faq4A: "40+ ภาษา ระบบตรวจจับอัตโนมัติหรือคุณกำหนดต่อแคมเปญ",
  faq5Q: "เราสามารถจัดแคมเปญเฉพาะผู้ค้าปลีกบางรายได้ไหม?",
  faq5A: "ได้ โหมดเฉพาะร้านจะใช้จีโอเฟนซ์ในร้านที่เลือก โหมดสากลให้ผู้ซื้อใดก็เล่นได้ทุกที่",
  faq6Q: "การสร้างแคมเปญใช้เวลานานแค่ไหน?",
  faq6A: "ประมาณ 10 นาทีสำหรับโปรโมชันมาตรฐาน วิซาร์ดทีละขั้นในพอร์ทัล",
  faq7Q: "เราทำ white-label ประสบการณ์ได้หรือไม่?",
  faq7A: "ได้ โลโก้ของคุณ สีของคุณ ภาษาของคุณ ผู้ซื้อจะไม่เห็นแบรนด์ TalkPack เว้นแต่คุณต้องการ",

  footerColPlatform: "แพลตฟอร์ม",
  footerColModules: "โมดูล",
  footerColCompany: "บริษัท",
  footerColResources: "ทรัพยากร",
  footerCompany1: "เกี่ยวกับ",
  footerCompany2: "ติดต่อ",
  footerResources3: "ความเป็นส่วนตัว",
  footerResources4: "เงื่อนไข",
  footerNewsHeading: "อัปเดตทุกไตรมาส ไม่มีสแปม",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "สมัคร",
  footerRegistration: "ShopAI Ltd · เลขทะเบียนบริษัท 15877093 · จดทะเบียนในอังกฤษ",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (TH) ──
  heroAlt: "ผู้ซื้อในทางเดินซูเปอร์มาร์เก็ต — ทุกจุดสัมผัสของประสบการณ์ TalkPack หมุนรอบเธอ",

  stepsTitle: "สามขั้นตอน หนึ่งแพลตฟอร์ม",
  stepsSub: "ตั้งแต่วินาทีที่ผู้ซื้อเข้ามา จนถึงวินาทีที่คุณเรียนรู้บางอย่างเกี่ยวกับเขา ใช้งานง่าย ข้อมูลครบถ้วน",
  step1Title: "เข้าสู่",
  step1Desc: "ผู้ซื้อแตะหรือสแกนจากช่องทางใดก็ได้ หนึ่งทางเข้า หนึ่งประสบการณ์ภายใต้แบรนด์ของคุณ",
  step1PillRow: "เว็บ · โซเชียล · อีเมล · QR · OOH · ในร้าน",
  step2Title: "มีส่วนร่วม",
  step2Desc: "ห้าโมดูล Pic-n-Mix ทำงานในภาษาของพวกเขา ในแบรนด์ของคุณ ตรวจสอบโดย AI",
  step2PillRow: "เรียน · เล่น · ถาม · ชนะ · สะสม",
  step3Title: "เรียนรู้",
  step3Desc: "ทุกการโต้ตอบกลายเป็นข้อมูล first-party ส่งเข้าแดชบอร์ดของคุณแบบเรียลไทม์",
  step3PillRow: "ผู้ซื้อ · บทสนทนา · ตำแหน่ง · การมีส่วนร่วม · การปฏิบัติตามข้อกำหนด",

  chipWebsite: "เว็บ",
  chipSocial:  "โซเชียล",
  chipEmail:   "อีเมล",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "ในร้าน",
  chipLearn:   "เรียน",
  chipPlay:    "เล่น",
  chipSay:     "ถาม",
  chipWin:     "ชนะ",
  chipEarn:    "สะสม",
  chipShoppers:     "ผู้ซื้อ",
  chipConversation: "บทสนทนา",
  chipLocation:     "ตำแหน่ง",
  chipEngagement:   "การมีส่วนร่วม",
  chipCompliance:   "การปฏิบัติตามข้อกำหนด",
  chipAriaEntry:  "ช่องทางเข้า",
  chipAriaModule: "โมดูล Pic-n-Mix",
  chipAriaIntel:  "หมวดหมู่ของอินเทลลิเจนซ์",

  intgTitle: "โครงสร้างพื้นฐานที่จริงจัง เทคโนโลยีที่ผ่านการพิสูจน์",
  intgSub: "TalkPack ทำงานบนรากฐานคลาวด์ AI และการปฏิบัติตามข้อกำหนดเดียวกับที่บริษัทขนาดใหญ่ที่สุดในโลกไว้วางใจ",
  intgCap1: "แพลตฟอร์มหลัก",
  intgCap2: "โครงสร้างพื้นฐานคลาวด์",
  intgCap3: "AI สนทนา",
  intgCap4: "โมเดลภาษา",
  intgCap5: "AI และแผนที่",
  intgCap6: "ข้อมูลโภชนาการ",
  intgCap7: "การชำระเงิน",
  intgFootnote: "ผู้ให้บริการที่ผ่านการรับรอง ISO 27001 · เป็นไปตาม GDPR · โฮสติ้งหลายภูมิภาค · ความปลอดภัยระดับองค์กรตั้งแต่วันแรก",

  jColEntry: "ทางเข้า",
  jColExperience: "ประสบการณ์",
  jColDashboard: "อินเทลลิเจนซ์ของแดชบอร์ด",
  jEntryDigitalTitle: "ดิจิทัล",
  jEntryDigitalDesc: "เว็บไซต์ App Store การค้นหา ลิงก์ตรง — แตะเดียวจากพื้นผิวดิจิทัลใดๆ เพื่อเข้าสู่ประสบการณ์ผู้ซื้อ",
  jEntrySocialTitle: "โซเชียล",
  jEntrySocialDesc: "โพสต์โฆษณา คอนเทนต์ของอินฟลูเอนเซอร์ DM — ฟีดทั้งหมดไหลเข้าสู่ TalkPack โดยไม่ออกจากแพลตฟอร์ม",
  jEntryPhysicalTitle: "ทางกายภาพ",
  jEntryPhysicalDesc: "บนแพ็กเกจ บนชั้นวาง ที่จุดขาย — จุดเข้าจริงในโลกที่ TalkPack เปลี่ยนการสแกนให้เป็นบทสนทนา",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "POS ในร้าน",
  jEntryPhysPill3: "บรรจุภัณฑ์",
  jExpVerifyLabel: "ยืนยัน",
  jExpVerifyText: "AI จดจำสินค้า — ยืนยัน SKU แบบเรียลไทม์",
  jExpLearnLabel: "เรียน",
  jExpLearnBody: "วงล้อโภชนาการ สารก่อภูมิแพ้ ส่วนประกอบ แหล่งที่มา — แสดงในภาษาของผู้ซื้อ ตรวจสอบโดยแบรนด์",
  jExpEngageLabel: "มีส่วนร่วม",
  jExpEngagePlayBody: "วงล้อ กาชา ปริศนา แบบทดสอบ — เกมห้าประเภทเพื่อเพิ่มเวลาที่ใช้",
  jExpEngageEarnBody: "คะแนน ใบเสร็จ ลอยัลตี้ — อินเทลลิเจนซ์ระดับตะกร้า",
  jExpEngageSayBody: "AI สนทนาตอบในกว่า 40 ภาษา",
  jExpRewardLabel: "รางวัล",
  jExpRewardBody: "รางวัลทันที การยืนยันด้วย QR คู่ที่จุดชำระ การรับรางวัลที่ตรวจสอบด้วย GPS — พร้อมตรวจสอบตั้งแต่วันแรก",
  jExpCaptureLabel: "เก็บข้อมูล",
  jExpCaptureSub: "ทุกการโต้ตอบ · เก็บและเป็นเจ้าของโดยคุณ",

  capEmail:   "อีเมล",
  capName:    "ชื่อ",
  capAddress: "ที่อยู่",
  capGps:     "GPS",
  capProduct: "สินค้า",
  capBasket:  "ตะกร้า",
  capSpend:   "ใช้จ่าย",
  capChat:    "แชต",

  dashShopperLabel: "อินเทลลิเจนซ์ผู้ซื้อ",
  dashShopperShort: "ผู้ซื้อ",
  dashShopper1Title: "CRM แบบ first-party",
  dashShopper1Desc: "อีเมล ชื่อ ที่อยู่ การตั้งค่าการติดต่อ",
  dashShopper2Title: "ตำแหน่งที่ตรวจสอบด้วย GPS",
  dashShopper2Desc: "ผู้ซื้อมีส่วนร่วมที่ใดตามภูมิภาคของร้าน",
  dashShopper3Title: "ความถี่ในการซื้อ",
  dashShopper3Desc: "รูปแบบการสแกนซ้ำ จังหวะการเยี่ยมชม",
  dashShopper4Title: "องค์ประกอบของตะกร้า",
  dashShopper4Desc: "ข้อมูลใบเสร็จระดับชิ้น สินค้าที่ซื้อร่วมกัน",

  dashConvLabel: "อินเทลลิเจนซ์บทสนทนา",
  dashConvShort: "บทสนทนา",
  dashConv1Title: "หมวดหมู่คำถาม",
  dashConv1Desc: "สารก่อภูมิแพ้ ส่วนประกอบ โภชนาการ การใช้งาน สูตรอาหาร",
  dashConv2Title: "วิเคราะห์ความรู้สึก",
  dashConv2Desc: "บวก ลบ กลาง ตามสินค้าและภูมิภาค",
  dashConv3Title: "จุดที่สับสน",
  dashConv3Desc: "สิ่งที่ผู้ซื้อไม่เข้าใจ คำถามที่ถามซ้ำ",
  dashConv4Title: "เจตนาในการซื้อ",
  dashConv4Desc: "การเปลี่ยนจากความสนใจเป็นคำขอที่ส่งถึงผู้ค้าปลีก",
  dashConv5Title: "การตั้งค่าภาษา",
  dashConv5Desc: "ผู้ซื้อเลือกภาษาใดตามตำแหน่ง",

  dashDistLabel: "อินเทลลิเจนซ์การกระจายสินค้า",
  dashDistShort: "การกระจายสินค้า",
  dashDist1Title: "ความพร้อมในร้าน",
  dashDist1Desc: "สินค้าบนชั้น ยืนยันด้วยการสแกนแบบ geofence",
  dashDist2Title: "การแจ้งเตือนสินค้าหมด",
  dashDist2Desc: "รายงาน 'ไม่มีที่นี่' ถูกแจ้งแบบเรียลไทม์",
  dashDist3Title: "ฮีตแมป",
  dashDist3Desc: "ความหนาแน่นการสแกนตามรหัสไปรษณีย์ ภูมิภาค ป้ายร้าน",
  dashDist4Title: "การกระจายในครัวเรือน",
  dashDist4Desc: "'สแกนทุกที่' แสดงว่าผลิตภัณฑ์อาศัยที่ใดในบ้าน",
  dashDist5Title: "เปรียบเทียบร้านค้า",
  dashDist5Desc: "อัตราการมีส่วนร่วมตามตำแหน่ง ตามกลุ่มป้ายร้าน",

  dashCampLabel: "ประสิทธิภาพแคมเปญ",
  dashCampShort: "แคมเปญ",
  dashCamp1Title: "อัตราสแกนต่อการมีส่วนร่วม",
  dashCamp1Desc: "การสแกนกี่ครั้งที่กลายเป็นการโต้ตอบเต็มรูปแบบ",
  dashCamp2Title: "อัตราการเล่นเกมจบ",
  dashCamp2Desc: "การหมุนต่อเซสชัน อัตราการชนะ จุดที่ผู้เล่นถอนตัว",
  dashCamp3Title: "ติดตามการแลกรับ",
  dashCamp3Desc: "รางวัลที่อ้างสิทธิ์ ยืนยัน หมดอายุ ตามร้าน",
  dashCamp4Title: "แดชบอร์ด ROI",
  dashCamp4Desc: "ค่าใช้จ่ายแคมเปญเทียบกับมูลค่าผู้ซื้อที่จับได้",

  dashCompLabel: "การปฏิบัติตามข้อกำหนดและการตรวจสอบ",
  dashCompShort: "การปฏิบัติตามข้อกำหนด",
  dashComp1Title: "คะแนนการปฏิบัติตามข้อกำหนด ASA",
  dashComp1Desc: "ตามแคมเปญ ตามบทสนทนา",
  dashComp2Title: "บันทึกการตรวจสอบที่ส่งออกได้",
  dashComp2Desc: "การตอบของ AI ทุกครั้งบันทึกและเรียกคืนได้",
  dashComp3Title: "การแจ้งเตือนการละเมิด",
  dashComp3Desc: "รูปแบบเนื้อหาที่ถูกตั้งสถานะ",
  dashComp4Title: "การมีส่วนร่วมด้านโภชนาการ",
  dashComp4Desc: "ผู้ซื้อมีปฏิสัมพันธ์กับสารอาหารใดบนวงล้อ",

  pvFormTitle: "ยินดีด้วย!",
  pvFormSub: "คุณชนะ: ส่วนลด 20% ในการซื้อครั้งถัดไป",
  pvFormName: "ชื่อของคุณ",
  pvFormEmail: "อีเมลของคุณ",
  pvFormPostcode: "รหัสไปรษณีย์",
  pvFormCta: "รับรางวัล",
  pvFormFootnote: "Privacy-first · เป็นไปตาม GDPR",

  pvChatTitle: "ทูต AI",
  pvChatSub: "ฝึกบน Morning O's",
  pvChatBubble1: "ปลอดกลูเตนหรือไม่?",
  pvChatBubble2: "ใช่ — รับรองว่าปลอดกลูเตน และมีน้ำตาลเพียง 9 กรัมต่อหนึ่งหน่วยบริโภค ต้องการแนะนำสูตรไหม?",
  pvChatBubble3: "ค่ะ ขอบคุณ",
  pvChatBubble4: "ลองโอ๊ตค้างคืนกับเบอร์รี่ — เตรียม 3 นาที",
  pvChatPlaceholder: "ถามอะไรก็ได้…",
  pvChatFootnote: "40+ ภาษา · ใช้งานได้ตลอด",

  pvMapTitle: "ค้นหา Morning O's ใกล้คุณ",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "นำทาง",
  pvMapFootnote: "ตรวจสอบด้วย GPS · ขอบเขต 150 เมตร",
  pvMapOos: "สินค้าหมด",

  pvWheelTitle: "Morning O's · หมุนเพื่อชนะ",
  pvWheelCredits: "350 เครดิต",
  pvWheelCta: "หมุน",
  pvWheelFootnote: "วงล้อ · วงล้อโภชนาการ · กาชา · แบบทดสอบ · ปริศนา",
  pvWheelWedgeWin: "ชนะ!",
  pvWheelWedgeTry: "อีกครั้ง",

  pvNutTitle: "Morning O's · ข้อมูลโภชนาการ",
  pvNutPer100g: "ต่อ 100 ก.",
  pvNutPerServing: "ต่อหนึ่งหน่วยบริโภค",
  pvNutCta: "ดำเนินการต่อ",
  pvNutFootnote: "เป็นไปตาม ASA · จำเป็นก่อนเล่น",

  bookDemoSubject: "ขอเดโม TalkPack",
  bookDemoBody: "เรียน ทีม ShopAI\n\nฉันต้องการจองเดโม TalkPack กรุณาติดต่อกลับเพื่อนัดเวลาด้วย\n\nขอบคุณ,",

  // ── Viz mock-data labels (TH) ──
  vizCapQuestions: "คำถามยอดฮิตของผู้ซื้อในสัปดาห์นี้",
  vizQAllergens: "สารก่อภูมิแพ้", vizQIngredients: "ส่วนประกอบ", vizQNutrition: "โภชนาการ",
  vizQRecipes: "สูตรอาหาร", vizQUsage: "การใช้งาน",
  vizCapSentiment: "30 วันที่ผ่านมาในทุกผลิตภัณฑ์",
  vizSentPositive: "บวก", vizSentNeutral: "เป็นกลาง", vizSentNegative: "ลบ",
  vizCapConfusion: "คำถามที่ถามซ้ำ — ช่องโหว่ของเนื้อหา",
  vizConfusion1: "ปลอดกลูเตนหรือไม่?",
  vizConfusion2: "เหมาะกับวีแกนหรือไม่?",
  vizConfusion3: "มีคำเตือนเรื่องถั่วหรือไม่?",
  vizCapFunnel: "การแปลงผ่านบทสนทนา",
  vizFunnelCurious: "อยากรู้", vizFunnelInterested: "สนใจ", vizFunnelReady: "พร้อมซื้อ",
  vizCapAvail: "สินค้ามีให้บริการตามที่คุณวางแผนไว้",
  vizAvailDetail: "1,247 จาก 1,326 ร้านค้ายืนยันมีบนชั้น",
  vizCapOos: "วันนี้ 3 ร้านถูกแจ้ง · ส่งแจ้งเตือนอัตโนมัติแล้ว",
  vizCapHeat: "ลอนดอน · แมนเชสเตอร์ · เบอร์มิงแฮมนำหน้า",
  vizCapHousehold: "68% ของการสแกนเกิดที่บ้าน — สินค้าของคุณอาศัยอยู่ที่นั่น",
  vizCapFascia: "อัตราการมีส่วนร่วมตามแบรนด์ร้านค้า",
  vizCapEngrate: "การสแกนที่กลายเป็นการโต้ตอบสมบูรณ์",
  vizEngrateCompare: "+36 คะแนนเทียบกับ 42% เกณฑ์อุตสาหกรรม",
  vizCapGame: "ผู้ซื้อหายไปที่จุดใด — ปรับสมดุลเกม",
  vizGameStart: "เริ่ม", vizGamePlay: "เล่น", vizGameWin: "ชนะ", vizGameClaim: "รับ",
  vizCapRdm: "อัตราการตรวจสอบ 96% · หมดอายุน้อย",
  vizRdmClaimed: "อ้างสิทธิ์แล้ว", vizRdmValidated: "ตรวจสอบแล้ว", vizRdmExpired: "หมดอายุ",
  vizCapRoi: "ค่าใช้จ่ายแคมเปญเทียบกับมูลค่าตลอดชีพของผู้ซื้อ",
  vizRoiSpend: "ค่าใช้จ่าย", vizRoiValueLabel: "มูลค่าที่ได้", vizRoiReturn: "ผลตอบแทน 3.4 เท่า",
  vizCapComp: "ต่อแคมเปญ · ต่อบทสนทนา · พร้อมตรวจสอบ",
  vizCapAudit: "บันทึกทุกการตอบของ AI · เรียกดูได้ตามต้องการ",
  vizCapViolation: "การละเมิดในเดือนนี้ · ผลลัพธ์ AI ตรวจสอบอย่างต่อเนื่อง",
  vizCapNut: "สารอาหารที่ผู้ซื้อสัมผัสบนวงล้อ",
  vizNutSugar: "น้ำตาล", vizNutFat: "ไขมัน", vizNutSalt: "เกลือ", vizNutSatFat: "ไขมันอิ่มตัว",
  vizCapLang: "ภาษาของผู้ซื้อตรวจจับอัตโนมัติ",
  vizLangOther: "อื่น ๆ",
  vizCapCrm: "ผู้ซื้อที่จับได้",
  vizCapMap: "สัปดาห์นี้ 247 สถานที่",
  vizCapWeek: "เฉลี่ย 2.3 ครั้งต่อสัปดาห์",
  vizDayMon: "จ.", vizDayTue: "อ.", vizDayWed: "พ.",
  vizDayThu: "พฤ.", vizDayFri: "ศ.", vizDaySat: "ส.", vizDaySun: "อา.",
  vizCapBasket: "ตะกร้าเฉลี่ย: £8.40 · 6.2 รายการ",
  vizBasketDairy: "ผลิตภัณฑ์นม", vizBasketBakery: "เบเกอรี่", vizBasketSnacks: "ขนม", vizBasketOther: "อื่น ๆ",
};

// ────────── हिन्दी ──────────
const hi: LandingCopy = {
  ...en,
  navPlatform: "प्लेटफ़ॉर्म",
  navModules: "मॉड्यूल",
  navPortal: "पोर्टल लॉगिन",
  navLangButton: "HI",
  navRegister: "रजिस्टर करें",
  navBookDemo: "डेमो बुक करें",
  navLogin: "लॉग इन करें",

  trustLeft: "कोई ऐप नहीं · कोई इंटीग्रेशन नहीं · स्टोर पर निर्भरता नहीं",
  trustRight: "2024 से · यूके · The Wholesale Group · Caterers Choice · Passot (जापान) · Talysis UK · Jakavarna Group (थाईलैंड) के साथ निर्माणाधीन",

  heroEyebrow: "कॉमर्स के लिए सह-पायलट",
  heroL1: "हर उत्पाद. एक बातचीत.",
  heroL2: "हर स्कैन. वास्तविक डेटा.",
  heroSub: "TalkPack किसी भी उत्पाद को लाइव बातचीत में बदल देता है। शॉपर्स स्कैन करते हैं, सीखते हैं, खेलते हैं, सवाल पूछते हैं, जीतते हैं और पॉइंट कमाते हैं। आपको first-party डेटा, HFSS अनुपालन और भौतिक रिटेल में शॉपर्स के साथ सीधा रिश्ता मिलता है। कोई ऐप नहीं। कोई इंटीग्रेशन नहीं। स्टोर पर निर्भरता नहीं।",
  heroCtaPrimary: "डेमो बुक करें",
  heroCtaGhost: "देखें कैसे काम करता है",
  heroPillAllergens: "एलर्जेन",
  heroPillHfss: "HFSS",
  heroPillIngredients: "सामग्री",
  heroVerified: "सत्यापित",

  statStores: "स्टोर्स · यूके · वैश्विक विस्तार",
  statLanguages: "भाषाएं · ऑटो-डिटेक्ट",
  statGames: "ब्रीफ़ से लाइव कैंपेन तक",
  statModules: "न्यूट्रिशन व्हील बिल्ट-इन",
  statSteps: "आपका ब्रांड. आपका डेटा. आपका AI.",
  statDownloads: "ऐप डाउनलोड · इंटीग्रेशन · घर्षण",
  statGeofence: "भौतिक रिटेल से शॉपर डेटा",
  statScans: "शेल्फ़ से शॉपर CRM तक",

  fullJourneyEyebrow: "पूरी यात्रा",
  fullJourneyHeading: "एंट्री से इंटेलिजेंस तक. एक प्लेटफ़ॉर्म.",
  fullJourneySub: "हर चैनल. हर मॉड्यूल. हर डेटा पॉइंट. इस तरह TalkPack आर्किटेक्चर एंड-टू-एंड जुड़ता है।",
  fullJourneyCaption: "पूर्ण फ़्लो. शॉपर एंट्री से ब्रांड इंटेलिजेंस तक. डैशबोर्ड, AI चैट या SmartMail क्वेरीज़ के माध्यम से एक्सपोर्ट योग्य।",

  picEyebrow: "Pic-n-Mix",
  picHeading: "एक प्लेटफ़ॉर्म. शॉपर से जुड़ने के पाँच तरीके.",
  picSub: "ब्रांड्स ज़रूरी मॉड्यूल चुनते हैं। शॉपर्स को अनुभवों का मिश्रण मिलता है। हर मॉड्यूल एक ही स्कैन, एक ही बातचीत, एक ही डेटा लेयर पर चलता है।",
  learnDesc: "उत्पाद शिक्षा, सामग्री में पारदर्शिता, HFSS ट्रैफ़िक लाइट्स।",
  learnF1: "न्यूट्रिशन व्हील", learnF2: "एलर्जेन स्कैन", learnF3: "उद्गम डेटा",
  playDesc: "पाँच गेम प्रकार जो ठहराव और दोहराव बढ़ाते हैं।",
  playF1: "स्पिन व्हील", playF2: "गाचा", playF3: "पज़ल", playF4: "क्विज़", playF5: "न्यूट्रिशन स्पिन",
  sayDesc: "बातचीत आधारित AI जो शॉपर्स के सवाल उनकी अपनी भाषा में उत्तर देता है।",
  sayF1: "40+ भाषाएं", sayF2: "सामग्री Q&A", sayF3: "रेसिपी सुझाव",
  winDesc: "इनाम, डायरेक्टेड फ़ुटफ़ॉल, टिल पर डुअल-QR सत्यापन, पैकिंग स्लिप।",
  winF1: "तत्काल इनाम", winF2: "जियोफ़ेंस्ड पुरस्कार", winF3: "होम डिलीवरी",
  earnDesc: "पॉइंट्स, रसीद स्कैन, लॉयल्टी प्रोग्राम, बास्केट इंटेलिजेंस।",
  earnF1: "रसीद OCR", earnF2: "क्रॉस-ब्रांड प्रोफ़ाइल", earnF3: "रिवॉर्ड कैटलॉग",
  moduleSeeMore: "मॉड्यूल देखें",

  oeEyebrow: "एक इंजन · कई गतिशीलताएँ",
  oeHeading: "TalkPack एक बड़े प्लेटफ़ॉर्म का हिस्सा है।",
  oeSub: "ShopAI ब्रांड्स, रिटेलर्स और होलसेलर्स को एक डेटा लेयर देता है। एंड-टू-एंड संवाद-आधारित बिज़नेस इंटेलिजेंस। TalkPack वह तरीका है जिससे शॉपर बातचीत में शामिल होता है।",
  oe1Strap: "भौतिक रिटेल से शॉपर एंगेजमेंट",
  oe2Strap: "कन्वीनियंस सेक्टर के लिए AI",
  oe3Strap: "फ़ील्ड ऑडिट इंटेलिजेंस",
  oe4Strap: "पॉइंट्स, रसीदें, बास्केट डेटा",
  oeActiveLabel: "वर्तमान",

  ucHeading: "अलग-अलग भूमिकाएँ. एक ही प्लेटफ़ॉर्म.",
  ucBrandTitle: "ब्रांड मार्केटर",
  ucBrand1: "मिश्रित रिटेल में राष्ट्रीय उत्पाद लॉन्च",
  ucBrand2: "भागीदार स्टोर्स की ओर डायरेक्टेड फ़ुटफ़ॉल",
  ucBrand3: "HFSS-अनुपालक ऑन-पैक एंगेजमेंट",
  ucBrand4: "क्रॉस-ब्रांड शॉपर प्रोफ़ाइलिंग",
  ucBrand5: "नियामित श्रेणियाँ (फ़ार्मा, अल्कोहल) ऑडिट-लॉग AI उत्तरों के साथ",
  ucRetailerTitle: "रिटेलर / होलसेलर",
  ucRetailer1: "रिटेलर-वित्तपोषित विशिष्ट प्रमोशन",
  ucRetailer2: "सिंबल ग्रुप एक्टिवेशन",
  ucRetailer3: "शेल्फ़ अनुपालन ऑडिट",
  ucRetailer4: "रिटेलर-विशिष्ट लॉयल्टी",
  ucAgencyTitle: "एजेंसी / रीसेलर",
  ucAgency1: "क्लाइंट ब्रांड्स के लिए व्हाइट-लेबल कैंपेन",
  ucAgency2: "मल्टी-क्लाइंट पोर्टल एक्सेस",
  ucAgency3: "ट्रेड मार्केटिंग PDF रिपोर्ट",
  ucAgency4: "ऑटो-जनरेटेड सोशल कंटेंट",

  hfssEyebrow: "HFSS के लिए बना",
  hfssHeading: "HFSS अनुपालन, बिल्ट-इन।",
  hfssSub: "शॉपर खेलने से पहले पोषण प्रोफ़ाइल देखता है। फ़ैट, चीनी, नमक, संतृप्त वसा — दैनिक संदर्भ सेवन के विरुद्ध ट्रैफ़िक लाइट। अनिवार्य. ऑडिट-तैयार. नियामक रक्षा की चार परतें।",
  hfss1Title: "अनिवार्य न्यूट्रिशन व्हील.",
  hfss1Body: "हर HFSS उत्पाद आगे बढ़ने से पहले फ़ैट, चीनी, नमक और संतृप्त वसा के ट्रैफ़िक लाइट दैनिक संदर्भ सेवन के विरुद्ध दिखाता है।",
  hfss2Title: "शिक्षा-प्रथम फ़्रेमिंग.",
  hfss2Body: "प्लेटफ़ॉर्म पोषण पारदर्शिता को बढ़ावा देता है। शॉपर इंटरैक्शन को सूचित विकल्प के रूप में स्थापित करता है, प्रोमोशन के दबाव के रूप में नहीं।",
  hfss3Title: "आउटपुट सत्यापन.",
  hfss3Body: "संवाद-आधारित AI नियामक पैटर्नों के लिए मॉनिटर किया जाता है। हर उत्तर लॉग किया, फ़िल्टर किया और ऑडिट किया जा सकता है।",
  hfss4Title: "एक्सपोर्ट योग्य ऑडिट लॉग्स.",
  hfss4Body: "ASA अनुपालन स्कोर, हर उत्तर लॉग और पुनः प्राप्ति योग्य, फ़्लैग की गई सामग्री के लिए उल्लंघन अलर्ट।",
  hfssBadge: "ASA अनुपालन: 98.4%",

  wlEyebrow: "आपका ब्रांड, किसी भी भाषा में",
  wlHeading: "आपका डेटा. आपका AI. आपकी तरह.",
  wlLangCaption: "40+ भाषाएं. ऑटो-डिटेक्ट या प्रति कैंपेन मैन्युअल सेट।",
  wlFeat1Title: "आपकी ब्रांड पहचान",
  wlFeat1Body: "लोगो, रंग, फ़ॉन्ट हर शॉपर स्क्रीन और पैकिंग स्लिप पर लागू।",
  wlFeat2Title: "आपका डेटा, आपका AI",
  wlFeat2Body: "डेटा आपके अकाउंट में। AI आपके उत्पादों पर प्रशिक्षित। संबंध आपका है।",
  wlFeat3Title: "आपके स्टोर्स, आपका जियोफ़ेंस",
  wlFeat3Body: "यूनिवर्सल मोड या स्टोर-विशिष्ट। डिफ़ॉल्ट 150 मीटर जियोफ़ेंस, प्रति कैंपेन कॉन्फ़िगर योग्य।",
  wlFeat4Title: "आपकी टीम, आपकी पहुँच",
  wlFeat4Body: "रोल-आधारित अनुमतियाँ। ब्रांड, एजेंसी, फ़ील्ड, फ़ाइनेंस — हर एक के लिए सही दृश्य।",

  proofEyebrow: "तैनाती में",
  proofHeading: "पहले से लाइव. पहले से चालू.",
  proofStat1Label: "कैंपेन डेटाबेस में यूके स्टोर्स",
  proofStat2Label: "समर्थित भाषाएं",
  proofStat3Label: "यूके में स्थापित",
  proofQuotePlaceholder: "ग्राहक कहानियाँ जल्द ही।",

  faqHeading: "ब्रांड्स द्वारा पूछे जाने वाले सवाल।",
  faq1Q: "क्या शॉपर्स को ऐप डाउनलोड करना होगा?",
  faq1A: "नहीं. TalkPack ब्राउज़र में चलता है। एक स्कैन अनुभव खोलता है।",
  faq2Q: "क्या TalkPack हमारे EPOS या CRM के साथ इंटीग्रेट होता है?",
  faq2A: "कोई इंटीग्रेशन ज़रूरी नहीं। एकत्रित डेटा first-party है और PDF, CSV या API के माध्यम से एक्सपोर्ट योग्य।",
  faq3Q: "HFSS उत्पादों का क्या होता है?",
  faq3A: "हर HFSS उत्पाद गेम से पहले अनिवार्य न्यूट्रिशन व्हील दिखाता है। अनुपालन बिल्ट-इन है, बाद में जोड़ा नहीं।",
  faq4Q: "आप कौन सी भाषाएं समर्थित करते हैं?",
  faq4A: "40+. प्लेटफ़ॉर्म ऑटो-डिटेक्ट करता है या आप प्रति कैंपेन सेट करते हैं।",
  faq5Q: "क्या हम कैंपेन केवल विशिष्ट रिटेलर्स में चला सकते हैं?",
  faq5A: "हाँ. स्टोर-विशिष्ट मोड कैंपेन को चयनित स्टोर्स पर जियोफ़ेंस करता है। यूनिवर्सल मोड किसी भी शॉपर को कहीं भी खेलने देता है।",
  faq6Q: "कैंपेन बनाने में कितना समय लगता है?",
  faq6A: "मानक प्रमोशन के लिए लगभग 10 मिनट। पोर्टल में स्टेप-बाय-स्टेप विज़ार्ड।",
  faq7Q: "क्या हम अनुभव को व्हाइट-लेबल कर सकते हैं?",
  faq7A: "हाँ. आपका लोगो, आपके रंग, आपकी भाषा। शॉपर्स TalkPack ब्रांडिंग नहीं देखेंगे जब तक आप न चाहें।",

  footerColPlatform: "प्लेटफ़ॉर्म",
  footerColModules: "मॉड्यूल",
  footerColCompany: "कंपनी",
  footerColResources: "संसाधन",
  footerCompany1: "हमारे बारे में",
  footerCompany2: "संपर्क",
  footerResources3: "गोपनीयता",
  footerResources4: "नियम",
  footerNewsHeading: "तिमाही अपडेट. कोई स्पैम नहीं.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "सब्सक्राइब",
  footerRegistration: "ShopAI Ltd · कंपनी नं. 15877093 · इंग्लैंड में पंजीकृत",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (HI) ──
  heroAlt: "सुपरमार्केट के गलियारे में खरीदार — TalkPack अनुभव का हर टचपॉइंट उसके चारों ओर घूम रहा है",

  stepsTitle: "तीन चरण. एक प्लेटफ़ॉर्म.",
  stepsSub: "जिस क्षण शॉपर प्रवेश करता है, उस क्षण से जब आप उसके बारे में कुछ सीखते हैं. चलाने में सरल. डेटा से समृद्ध.",
  step1Title: "प्रवेश",
  step1Desc: "शॉपर किसी भी चैनल से टैप या स्कैन करता है. एक प्रवेश, एक ब्रांडेड अनुभव.",
  step1PillRow: "वेबसाइट · सोशल · ईमेल · QR · OOH · स्टोर में",
  step2Title: "जुड़ाव",
  step2Desc: "पाँच Pic-n-Mix मॉड्यूल उनकी भाषा में, आपके ब्रांड में, AI द्वारा सत्यापित चलते हैं.",
  step2PillRow: "सीखें · खेलें · पूछें · जीतें · कमाएँ",
  step3Title: "सीखें",
  step3Desc: "हर इंटरैक्शन first-party डेटा बन जाता है, रीयल टाइम में आपके डैशबोर्ड पर.",
  step3PillRow: "शॉपर्स · वार्तालाप · स्थान · जुड़ाव · अनुपालन",

  chipWebsite: "वेबसाइट",
  chipSocial:  "सोशल",
  chipEmail:   "ईमेल",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "स्टोर में",
  chipLearn:   "सीखें",
  chipPlay:    "खेलें",
  chipSay:     "पूछें",
  chipWin:     "जीतें",
  chipEarn:    "कमाएँ",
  chipShoppers:     "शॉपर्स",
  chipConversation: "वार्तालाप",
  chipLocation:     "स्थान",
  chipEngagement:   "जुड़ाव",
  chipCompliance:   "अनुपालन",
  chipAriaEntry:  "प्रवेश चैनल",
  chipAriaModule: "Pic-n-Mix मॉड्यूल",
  chipAriaIntel:  "इंटेलिजेंस श्रेणी",

  intgTitle: "गंभीर इन्फ़्रास्ट्रक्चर. सिद्ध तकनीक.",
  intgSub: "TalkPack उन्हीं क्लाउड, AI और अनुपालन आधारों पर चलता है जिन पर दुनिया की सबसे बड़ी कंपनियाँ भरोसा करती हैं.",
  intgCap1: "मूल प्लेटफ़ॉर्म",
  intgCap2: "क्लाउड इन्फ़्रास्ट्रक्चर",
  intgCap3: "वार्तालाप AI",
  intgCap4: "भाषा मॉडल",
  intgCap5: "AI और मानचित्रण",
  intgCap6: "पोषण डेटा",
  intgCap7: "भुगतान",
  intgFootnote: "ISO 27001 प्रमाणित प्रदाता · GDPR-अनुपालक · बहु-क्षेत्र होस्टिंग · पहले दिन से एंटरप्राइज़-स्तरीय सुरक्षा",

  jColEntry: "प्रवेश",
  jColExperience: "अनुभव",
  jColDashboard: "डैशबोर्ड इंटेलिजेंस",
  jEntryDigitalTitle: "डिजिटल",
  jEntryDigitalDesc: "वेबसाइट, ऐप स्टोर, खोज, सीधे लिंक — किसी भी डिजिटल सतह से शॉपर अनुभव में एक टैप.",
  jEntrySocialTitle: "सोशल",
  jEntrySocialDesc: "स्पॉन्सर्ड पोस्ट, इन्फ़्लुएंसर कंटेंट, DMs — फ़ीड्स प्लेटफ़ॉर्म छोड़े बिना सीधे TalkPack में बहती हैं.",
  jEntryPhysicalTitle: "भौतिक",
  jEntryPhysicalDesc: "पैक पर, शेल्फ़ पर, POS पर — असली दुनिया के प्रवेश बिंदु जहाँ TalkPack एक स्कैन को वार्तालाप में बदल देता है.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "स्टोर POS",
  jEntryPhysPill3: "पैकेजिंग",
  jExpVerifyLabel: "सत्यापन",
  jExpVerifyText: "AI उत्पाद पहचान — SKU रीयल टाइम में सत्यापित.",
  jExpLearnLabel: "सीखें",
  jExpLearnBody: "पोषण व्हील, एलर्जेंस, सामग्री, उद्गम — शॉपर की भाषा में दिखाए जाते हैं, ब्रांड द्वारा सत्यापित.",
  jExpEngageLabel: "जुड़ाव",
  jExpEngagePlayBody: "स्पिन, गाचा, पज़ल, क्विज़ — रुकने के समय के लिए पाँच गेम प्रकार.",
  jExpEngageEarnBody: "अंक, रसीदें, लॉयल्टी — बास्केट-स्तर इंटेलिजेंस.",
  jExpEngageSayBody: "वार्तालाप AI 40+ भाषाओं में जवाब देता है.",
  jExpRewardLabel: "पुरस्कार",
  jExpRewardBody: "तत्काल पुरस्कार, टिल पर डुअल-QR सत्यापन, GPS-सत्यापित दावे — पहले दिन से ऑडिट के लिए तैयार.",
  jExpCaptureLabel: "कैप्चर",
  jExpCaptureSub: "हर इंटरैक्शन · आपके द्वारा कैप्चर और स्वामित्व",

  capEmail:   "ईमेल",
  capName:    "नाम",
  capAddress: "पता",
  capGps:     "GPS",
  capProduct: "उत्पाद",
  capBasket:  "बास्केट",
  capSpend:   "खर्च",
  capChat:    "चैट",

  dashShopperLabel: "शॉपर इंटेलिजेंस",
  dashShopperShort: "शॉपर",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "ईमेल, नाम, पता, संपर्क प्राथमिकताएँ.",
  dashShopper2Title: "GPS-सत्यापित स्थान",
  dashShopper2Desc: "स्टोर क्षेत्रों के अनुसार शॉपर्स कहाँ जुड़ते हैं.",
  dashShopper3Title: "खरीद आवृत्ति",
  dashShopper3Desc: "दोहराए जाने वाले स्कैन पैटर्न, यात्रा की लय.",
  dashShopper4Title: "बास्केट संरचना",
  dashShopper4Desc: "आइटम-स्तरीय रसीद डेटा, साथ-खरीदे गए उत्पाद.",

  dashConvLabel: "वार्तालाप इंटेलिजेंस",
  dashConvShort: "वार्तालाप",
  dashConv1Title: "प्रश्न श्रेणियाँ",
  dashConv1Desc: "एलर्जेंस, सामग्री, पोषण, उपयोग, रेसिपीज़.",
  dashConv2Title: "भावना विश्लेषण",
  dashConv2Desc: "उत्पाद और क्षेत्र के अनुसार सकारात्मक, नकारात्मक, तटस्थ.",
  dashConv3Title: "उलझन के बिंदु",
  dashConv3Desc: "शॉपर्स क्या नहीं समझते, बार-बार पूछे गए प्रश्न.",
  dashConv4Title: "खरीद इरादा",
  dashConv4Desc: "जिज्ञासा से रिटेलर अनुरोधों तक की प्रगति.",
  dashConv5Title: "भाषा प्राथमिकताएँ",
  dashConv5Desc: "स्थान के अनुसार शॉपर्स कौन-सी भाषाएँ चुनते हैं.",

  dashDistLabel: "वितरण इंटेलिजेंस",
  dashDistShort: "वितरण",
  dashDist1Title: "स्टोर उपलब्धता",
  dashDist1Desc: "शेल्फ़ पर उत्पाद, geofence स्कैन से पुष्टि.",
  dashDist2Title: "स्टॉक-आउट अलर्ट",
  dashDist2Desc: "'यहाँ नहीं' रिपोर्ट रीयल टाइम में फ़्लैग की जाती है.",
  dashDist3Title: "हीटमैप",
  dashDist3Desc: "पिनकोड, क्षेत्र, फ़ासिया के अनुसार स्कैन घनत्व.",
  dashDist4Title: "घरेलू पैठ",
  dashDist4Desc: "'कहीं भी स्कैन' दिखाता है कि उत्पाद घर में कहाँ रहता है.",
  dashDist5Title: "स्टोर तुलना",
  dashDist5Desc: "स्थान, फ़ासिया समूह के अनुसार जुड़ाव दर.",

  dashCampLabel: "कैंपेन प्रदर्शन",
  dashCampShort: "कैंपेन",
  dashCamp1Title: "स्कैन-से-जुड़ाव दर",
  dashCamp1Desc: "कितने स्कैन पूर्ण इंटरैक्शन में बदलते हैं.",
  dashCamp2Title: "गेम पूर्णता दर",
  dashCamp2Desc: "प्रति सत्र स्पिन, जीत दर, ड्रॉप-ऑफ़ बिंदु.",
  dashCamp3Title: "रिडेम्पशन ट्रैकिंग",
  dashCamp3Desc: "स्टोर के अनुसार दावा किए, सत्यापित, समाप्त पुरस्कार.",
  dashCamp4Title: "ROI डैशबोर्ड",
  dashCamp4Desc: "कैंपेन व्यय बनाम कैप्चर किया गया शॉपर मूल्य.",

  dashCompLabel: "अनुपालन और ऑडिट",
  dashCompShort: "अनुपालन",
  dashComp1Title: "ASA अनुपालन स्कोर",
  dashComp1Desc: "प्रति कैंपेन, प्रति वार्तालाप.",
  dashComp2Title: "निर्यात योग्य ऑडिट लॉग",
  dashComp2Desc: "हर AI प्रतिक्रिया लॉग और पुनर्प्राप्त करने योग्य.",
  dashComp3Title: "उल्लंघन अलर्ट",
  dashComp3Desc: "फ़्लैग किए गए सामग्री पैटर्न.",
  dashComp4Title: "पोषण जुड़ाव",
  dashComp4Desc: "व्हील पर शॉपर्स किन पोषक तत्वों के साथ इंटरैक्ट करते हैं.",

  pvFormTitle: "बधाई हो!",
  pvFormSub: "आपने जीता: अगली खरीद पर 20% की छूट",
  pvFormName: "आपका नाम",
  pvFormEmail: "आपका ईमेल",
  pvFormPostcode: "पिन कोड",
  pvFormCta: "पुरस्कार लें",
  pvFormFootnote: "Privacy-first · GDPR-अनुपालक",

  pvChatTitle: "AI एंबेसडर",
  pvChatSub: "Morning O's पर प्रशिक्षित",
  pvChatBubble1: "क्या यह ग्लूटेन-मुक्त है?",
  pvChatBubble2: "हाँ — प्रमाणित ग्लूटेन-मुक्त और प्रति सर्विंग केवल 9g चीनी. क्या आप रेसिपी आइडियाज़ चाहेंगे?",
  pvChatBubble3: "हाँ कृपया",
  pvChatBubble4: "बेरीज़ के साथ ओवरनाइट ओट्स आज़माएँ — 3 मिनट तैयारी.",
  pvChatPlaceholder: "मुझसे कुछ भी पूछें…",
  pvChatFootnote: "40+ भाषाएँ · हमेशा उपलब्ध",

  pvMapTitle: "अपने नज़दीक Morning O's खोजें",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "दिशा-निर्देश",
  pvMapFootnote: "GPS-सत्यापित · 150m तक geofenced",
  pvMapOos: "स्टॉक में नहीं",

  pvWheelTitle: "Morning O's · घुमाएँ और जीतें",
  pvWheelCredits: "350 क्रेडिट",
  pvWheelCta: "घुमाएँ",
  pvWheelFootnote: "स्पिन व्हील · पोषण व्हील · गाचा · क्विज़ · पज़ल",
  pvWheelWedgeWin: "जीत!",
  pvWheelWedgeTry: "फिर से",

  pvNutTitle: "Morning O's · पोषण जानकारी",
  pvNutPer100g: "प्रति 100g",
  pvNutPerServing: "प्रति सर्विंग",
  pvNutCta: "जारी रखें",
  pvNutFootnote: "ASA-अनुपालक · खेलने से पहले अनिवार्य",

  bookDemoSubject: "TalkPack डेमो अनुरोध",
  bookDemoBody: "नमस्ते ShopAI टीम,\n\nमैं TalkPack का एक डेमो बुक करना चाहूँगा/चाहूँगी. कृपया समय निर्धारित करने के लिए संपर्क करें.\n\nधन्यवाद,",

  // ── Viz mock-data labels (HI) ──
  vizCapQuestions: "इस हफ्ते शॉपर्स के सबसे अहम सवाल",
  vizQAllergens: "एलर्जेंस", vizQIngredients: "सामग्री", vizQNutrition: "पोषण",
  vizQRecipes: "रेसिपीज़", vizQUsage: "उपयोग",
  vizCapSentiment: "सभी उत्पादों के पिछले 30 दिन",
  vizSentPositive: "सकारात्मक", vizSentNeutral: "तटस्थ", vizSentNegative: "नकारात्मक",
  vizCapConfusion: "बार-बार पूछे गए सवाल — सामग्री में कमी",
  vizConfusion1: "क्या यह ग्लूटेन-मुक्त है?",
  vizConfusion2: "क्या यह शाकाहारियों के लिए उपयुक्त है?",
  vizConfusion3: "क्या नट्स की चेतावनी है?",
  vizCapFunnel: "बातचीत के माध्यम से कन्वर्ज़न",
  vizFunnelCurious: "जिज्ञासु", vizFunnelInterested: "रुचि रखने वाला", vizFunnelReady: "खरीदने को तैयार",
  vizCapAvail: "जहाँ आपने योजना बनाई वहाँ उत्पाद उपलब्ध",
  vizAvailDetail: "1,326 में से 1,247 स्टोर्स पर शेल्फ़ पर पुष्टि",
  vizCapOos: "आज 3 स्टोर्स फ़्लैग किए · ऑटो-अलर्ट भेजा गया",
  vizCapHeat: "लंदन · मैनचेस्टर · बर्मिंघम आगे",
  vizCapHousehold: "68% स्कैन घर पर होते हैं — आपका उत्पाद वहाँ रहता है",
  vizCapFascia: "रिटेलर फ़ासिया के अनुसार जुड़ाव दर",
  vizCapEngrate: "स्कैन जो पूर्ण इंटरैक्शन में बदलते हैं",
  vizEngrateCompare: "उद्योग बेंचमार्क 42% की तुलना में +36 अंक",
  vizCapGame: "शॉपर्स कहाँ छोड़ देते हैं — गेम संतुलन ट्यून करें",
  vizGameStart: "शुरू", vizGamePlay: "खेलें", vizGameWin: "जीत", vizGameClaim: "लें",
  vizCapRdm: "96% सत्यापन दर · कम समाप्ति",
  vizRdmClaimed: "लिए गए", vizRdmValidated: "सत्यापित", vizRdmExpired: "समाप्त",
  vizCapRoi: "कैंपेन खर्च बनाम शॉपर लाइफ़टाइम वैल्यू",
  vizRoiSpend: "खर्च", vizRoiValueLabel: "हासिल मूल्य", vizRoiReturn: "3.4× रिटर्न",
  vizCapComp: "प्रति कैंपेन · प्रति बातचीत · ऑडिट के लिए तैयार",
  vizCapAudit: "हर AI जवाब लॉग किया · माँग पर उपलब्ध",
  vizCapViolation: "इस महीने उल्लंघन · AI आउटपुट लगातार सत्यापित",
  vizCapNut: "व्हील पर शॉपर्स जिन पोषक तत्वों से इंटरैक्ट करते हैं",
  vizNutSugar: "चीनी", vizNutFat: "वसा", vizNutSalt: "नमक", vizNutSatFat: "संतृप्त",
  vizCapLang: "शॉपर की भाषा स्वतः पहचानी जाती है",
  vizLangOther: "अन्य",
  vizCapCrm: "हासिल किए शॉपर्स",
  vizCapMap: "इस हफ़्ते 247 स्थान",
  vizCapWeek: "औसत 2.3 विज़िट प्रति सप्ताह",
  vizDayMon: "सोम", vizDayTue: "मंगल", vizDayWed: "बुध",
  vizDayThu: "गुरु", vizDayFri: "शुक्र", vizDaySat: "शनि", vizDaySun: "रवि",
  vizCapBasket: "औसत बास्केट: £8.40 · 6.2 आइटम",
  vizBasketDairy: "डेयरी", vizBasketBakery: "बेकरी", vizBasketSnacks: "स्नैक्स", vizBasketOther: "अन्य",
};

// ────────── ગુજરાતી ──────────
const gu: LandingCopy = {
  ...en,
  navPlatform: "પ્લેટફોર્મ",
  navModules: "મોડ્યુલ્સ",
  navPortal: "પોર્ટલ લોગિન",
  navLangButton: "GU",
  navRegister: "નોંધણી કરો",
  navBookDemo: "ડેમો બુક કરો",
  navLogin: "લોગ ઇન કરો",

  trustLeft: "કોઈ એપ નહીં · કોઈ ઇન્ટિગ્રેશન નહીં · સ્ટોર પર નિર્ભરતા નહીં",
  trustRight: "2024 થી · યુકે · The Wholesale Group · Caterers Choice · Passot (જાપાન) · Talysis UK · Jakavarna Group (થાઇલેન્ડ) સાથે બાંધકામ ચાલુ",

  heroEyebrow: "વ્યવસાય માટે કો-પાઇલટ",
  heroL1: "દરેક ઉત્પાદન. એક વાતચીત.",
  heroL2: "દરેક સ્કેન. વાસ્તવિક ડેટા.",
  heroSub: "TalkPack કોઈપણ ઉત્પાદનને જીવંત વાતચીતમાં ફેરવે છે. શોપર્સ સ્કેન કરે, શીખે, રમે, પૂછે, જીતે, કમાય. તમને ફર્સ્ટ-પાર્ટી ડેટા, HFSS અનુપાલન અને ભૌતિક રિટેલમાં શોપર્સ સાથે સીધો સંબંધ મળે છે. કોઈ એપ નહીં. કોઈ ઇન્ટિગ્રેશન નહીં. સ્ટોર પર નિર્ભરતા નહીં.",
  heroCtaPrimary: "ડેમો બુક કરો",
  heroCtaGhost: "જુઓ કેવી રીતે કામ કરે છે",
  heroPillAllergens: "એલર્જન",
  heroPillHfss: "HFSS",
  heroPillIngredients: "ઘટકો",
  heroVerified: "ચકાસાયેલ",

  statStores: "સ્ટોર્સ · યુકે · વૈશ્વિક વિસ્તરણ",
  statLanguages: "ભાષાઓ · ઑટો-ડિટેક્ટ",
  statGames: "બ્રીફથી લાઇવ ઝુંબેશ સુધી",
  statModules: "ન્યુટ્રિશન વ્હીલ બિલ્ટ-ઇન",
  statSteps: "તમારો બ્રાન્ડ. તમારો ડેટા. તમારો AI.",
  statDownloads: "એપ ડાઉનલોડ · ઇન્ટિગ્રેશન · ઘર્ષણ",
  statGeofence: "ભૌતિક રિટેલમાંથી શોપર ડેટા",
  statScans: "છાજલીથી શોપર CRM સુધી",

  fullJourneyEyebrow: "સંપૂર્ણ યાત્રા",
  fullJourneyHeading: "પ્રવેશથી ઇન્ટેલિજન્સ સુધી. એક પ્લેટફોર્મ.",
  fullJourneySub: "દરેક ચેનલ. દરેક મોડ્યુલ. દરેક ડેટા પોઇન્ટ. આ રીતે TalkPack આર્કિટેક્ચર એન્ડ-ટુ-એન્ડ જોડાય છે.",
  fullJourneyCaption: "પૂર્ણ પ્રવાહ. શોપર પ્રવેશથી બ્રાન્ડ ઇન્ટેલિજન્સ સુધી. ડેશબોર્ડ, AI ચેટ અથવા SmartMail ક્વેરી દ્વારા એક્સપોર્ટ કરી શકાય.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "એક પ્લેટફોર્મ. શોપર સાથે જોડાવાના પાંચ રસ્તા.",
  picSub: "બ્રાન્ડ્સ જરૂરી મોડ્યુલ્સ પસંદ કરે છે. શોપર્સને અનુભવોનું મિશ્રણ મળે છે. દરેક મોડ્યુલ એક જ સ્કેન, એક જ વાતચીત, એક જ ડેટા લેયર પર ચાલે છે.",
  learnDesc: "ઉત્પાદન શિક્ષણ, ઘટકમાં પારદર્શિતા, HFSS ટ્રાફિક લાઇટ્સ.",
  learnF1: "ન્યુટ્રિશન વ્હીલ", learnF2: "એલર્જન સ્કેન", learnF3: "ઉત્પત્તિ ડેટા",
  playDesc: "પાંચ રમતના પ્રકારો જે રોકાઈ રહેવાનો સમય અને પુનરાવર્તન વધારે છે.",
  playF1: "સ્પિન વ્હીલ", playF2: "ગાચા", playF3: "પઝલ", playF4: "ક્વિઝ", playF5: "ન્યુટ્રિશન સ્પિન",
  sayDesc: "વાતચીત આધારિત AI જે શોપરના સવાલો તેમની ભાષામાં જવાબ આપે છે.",
  sayF1: "40+ ભાષાઓ", sayF2: "ઘટકો વિશે Q&A", sayF3: "વાનગીઓના સૂચનો",
  winDesc: "પુરસ્કારો, સ્ટોર પર લાવવાનું, કેશિયર પર ડ્યુઅલ-QR ચકાસણી, પેકિંગ સ્લિપ.",
  winF1: "તાત્કાલિક પુરસ્કાર", winF2: "જિયોફેન્સ્ડ પુરસ્કારો", winF3: "ઘરે ડિલિવરી",
  earnDesc: "પોઇન્ટ્સ, રસીદ સ્કેન, લોયલ્ટી પ્રોગ્રામ, બાસ્કેટ ઇન્ટેલિજન્સ.",
  earnF1: "રસીદ OCR", earnF2: "ક્રોસ-બ્રાન્ડ પ્રોફાઇલ", earnF3: "પુરસ્કાર કેટલોગ",
  moduleSeeMore: "મોડ્યુલ જુઓ",

  oeEyebrow: "એક એન્જિન · અનેક ગતિશીલતા",
  oeHeading: "TalkPack મોટા પ્લેટફોર્મનો ભાગ છે.",
  oeSub: "ShopAI બ્રાન્ડ્સ, રિટેલર્સ અને હોલસેલર્સને એક ડેટા લેયર પૂરો પાડે છે. એન્ડ-ટુ-એન્ડ વાતચીત આધારિત બિઝનેસ ઇન્ટેલિજન્સ. TalkPack એ રીતે છે જેમાં શોપર વાતચીતમાં જોડાય છે.",
  oe1Strap: "ભૌતિક રિટેલમાંથી શોપર એંગેજમેન્ટ",
  oe2Strap: "કન્વીનિયન્સ ક્ષેત્ર માટે AI",
  oe3Strap: "ફિલ્ડ ઓડિટ ઇન્ટેલિજન્સ",
  oe4Strap: "પોઇન્ટ્સ, રસીદો, બાસ્કેટ ડેટા",
  oeActiveLabel: "વર્તમાન",

  ucHeading: "જુદી ભૂમિકાઓ. એક જ પ્લેટફોર્મ.",
  ucBrandTitle: "બ્રાન્ડ માર્કેટર",
  ucBrand1: "મિશ્રિત રિટેલમાં રાષ્ટ્રીય ઉત્પાદન લોન્ચ",
  ucBrand2: "ભાગીદાર સ્ટોર્સ તરફ ડાયરેક્ટેડ ફુટફોલ",
  ucBrand3: "HFSS-અનુપાલક ઓન-પેક એંગેજમેન્ટ",
  ucBrand4: "ક્રોસ-બ્રાન્ડ શોપર પ્રોફાઇલિંગ",
  ucBrand5: "નિયમિત શ્રેણીઓ (ફાર્મા, આલ્કોહોલ) ઓડિટ-લોગ AI જવાબો સાથે",
  ucRetailerTitle: "રિટેલર / હોલસેલર",
  ucRetailer1: "રિટેલર-ભંડોળિત વિશિષ્ટ પ્રમોશન",
  ucRetailer2: "સિમ્બોલ ગ્રુપ એક્ટિવેશન",
  ucRetailer3: "છાજલી અનુપાલન ઓડિટ",
  ucRetailer4: "રિટેલર-વિશિષ્ટ લોયલ્ટી",
  ucAgencyTitle: "એજન્સી / રીસેલર",
  ucAgency1: "ક્લાયન્ટ બ્રાન્ડ્સ માટે વ્હાઇટ-લેબલ ઝુંબેશો",
  ucAgency2: "મલ્ટિ-ક્લાયન્ટ પોર્ટલ એક્સેસ",
  ucAgency3: "ટ્રેડ માર્કેટિંગ PDF રિપોર્ટ્સ",
  ucAgency4: "ઓટો-જનરેટેડ સોશિયલ કન્ટેન્ટ",

  hfssEyebrow: "HFSS માટે બનેલું",
  hfssHeading: "HFSS અનુપાલન, બિલ્ટ-ઇન.",
  hfssSub: "શોપર રમતા પહેલા પોષણ પ્રોફાઇલ જુએ છે. ચરબી, ખાંડ, મીઠું, સંતૃપ્ત ચરબી — દૈનિક સંદર્ભ સેવન સામે ટ્રાફિક લાઇટ. ફરજિયાત. ઓડિટ-તૈયાર. નિયામક રક્ષાની ચાર સ્તરો.",
  hfss1Title: "ફરજિયાત ન્યુટ્રિશન વ્હીલ.",
  hfss1Body: "દરેક HFSS ઉત્પાદન શોપર આગળ વધે તે પહેલા ચરબી, ખાંડ, મીઠું અને સંતૃપ્ત ચરબીની ટ્રાફિક લાઇટ દૈનિક સંદર્ભ સેવન સામે દર્શાવે છે.",
  hfss2Title: "શિક્ષણ-પ્રથમ ફ્રેમિંગ.",
  hfss2Body: "પ્લેટફોર્મ પોષણ પારદર્શિતાને પ્રોત્સાહન આપે છે. શોપર ક્રિયાપ્રતિક્રિયાઓને માહિતી-આધારિત પસંદગી તરીકે મૂકે છે, પ્રોમોશનલ દબાણ તરીકે નહીં.",
  hfss3Title: "આઉટપુટ ચકાસણી.",
  hfss3Body: "વાતચીત આધારિત AI નિયામક પેટર્ન માટે મોનિટર થાય છે. દરેક પ્રતિભાવ લોગ, ફિલ્ટર અને ઓડિટ થઈ શકે છે.",
  hfss4Title: "એક્સપોર્ટ યોગ્ય ઓડિટ લોગ્સ.",
  hfss4Body: "ASA અનુપાલન સ્કોર, દરેક પ્રતિભાવ લોગ અને પુનઃપ્રાપ્ય, ફ્લેગ થયેલા સામગ્રી માટે ઉલ્લંઘન અલર્ટ.",
  hfssBadge: "ASA અનુપાલન: 98.4%",

  wlEyebrow: "તમારો બ્રાન્ડ, કોઈપણ ભાષામાં",
  wlHeading: "તમારો ડેટા. તમારો AI. તમારી રીતે.",
  wlLangCaption: "40+ ભાષાઓ. ઓટો-ડિટેક્ટ અથવા પ્રતિ ઝુંબેશ મેન્યુઅલી સેટ.",
  wlFeat1Title: "તમારી બ્રાન્ડ ઓળખ",
  wlFeat1Body: "લોગો, રંગો, ફોન્ટ્સ દરેક શોપર સ્ક્રીન અને પેકિંગ સ્લિપ પર લાગુ.",
  wlFeat2Title: "તમારો ડેટા, તમારો AI",
  wlFeat2Body: "ડેટા તમારા એકાઉન્ટ સામે સંગ્રહિત. AI તમારા ઉત્પાદનો પર પ્રશિક્ષિત. સંબંધ તમારો છે.",
  wlFeat3Title: "તમારા સ્ટોર્સ, તમારું જિયોફેન્સ",
  wlFeat3Body: "યુનિવર્સલ મોડ અથવા સ્ટોર-વિશિષ્ટ. ડિફોલ્ટ 150 મીટર જિયોફેન્સ, પ્રતિ ઝુંબેશ કોન્ફિગર કરી શકાય.",
  wlFeat4Title: "તમારી ટીમ, તમારો એક્સેસ",
  wlFeat4Body: "ભૂમિકા આધારિત પરવાનગીઓ. બ્રાન્ડ, એજન્સી, ફિલ્ડ, ફાઇનાન્સ — દરેકને યોગ્ય દૃશ્ય.",

  proofEyebrow: "ગોઠવણીમાં",
  proofHeading: "પહેલેથી લાઇવ. પહેલેથી કાર્યરત.",
  proofStat1Label: "ઝુંબેશ ડેટાબેઝમાં યુકે સ્ટોર્સ",
  proofStat2Label: "સમર્થિત ભાષાઓ",
  proofStat3Label: "યુકેમાં સ્થાપિત",
  proofQuotePlaceholder: "ગ્રાહકોની વાર્તાઓ ટૂંક સમયમાં.",

  faqHeading: "બ્રાન્ડ્સ અમને પૂછે છે તેવા સવાલો.",
  faq1Q: "શું શોપર્સને એપ ડાઉનલોડ કરવી પડે?",
  faq1A: "ના. TalkPack બ્રાઉઝરમાં ચાલે છે. એક સ્કેન અનુભવ ખોલે છે.",
  faq2Q: "શું TalkPack અમારા EPOS અથવા CRM સાથે ઇન્ટિગ્રેટ થાય?",
  faq2A: "કોઈ ઇન્ટિગ્રેશન જરૂરી નથી. કેપ્ચર થયેલ ડેટા ફર્સ્ટ-પાર્ટી છે અને PDF, CSV અથવા API દ્વારા એક્સપોર્ટ યોગ્ય છે.",
  faq3Q: "HFSS ઉત્પાદનો માટે શું થાય?",
  faq3A: "દરેક HFSS ઉત્પાદન રમત પહેલા ફરજિયાત ન્યુટ્રિશન વ્હીલ દર્શાવે છે. અનુપાલન બિલ્ટ-ઇન છે, બાદમાં ઉમેરાયેલું નથી.",
  faq4Q: "તમે કઈ ભાષાઓ સમર્થન કરો છો?",
  faq4A: "40+. પ્લેટફોર્મ ઓટો-ડિટેક્ટ કરે છે અથવા તમે પ્રતિ ઝુંબેશ સેટ કરો છો.",
  faq5Q: "શું અમે ફક્ત ચોક્કસ રિટેલર્સમાં ઝુંબેશ ચલાવી શકીએ?",
  faq5A: "હા. સ્ટોર-વિશિષ્ટ મોડ પસંદ કરેલા સ્ટોર્સમાં ઝુંબેશને જિયોફેન્સ કરે છે. યુનિવર્સલ મોડ કોઈપણ શોપરને ગમે ત્યાં રમવા દે છે.",
  faq6Q: "ઝુંબેશ બાંધવામાં કેટલો સમય લાગે?",
  faq6A: "પ્રમાણભૂત પ્રમોશન માટે લગભગ 10 મિનિટ. પોર્ટલમાં સ્ટેપ-બાય-સ્ટેપ વિઝાર્ડ.",
  faq7Q: "શું અમે અનુભવને વ્હાઇટ-લેબલ કરી શકીએ?",
  faq7A: "હા. તમારો લોગો, તમારા રંગો, તમારી ભાષા. શોપર્સ TalkPack બ્રાન્ડિંગ ક્યારેય જોતા નથી જ્યાં સુધી તમે ઇચ્છો નહીં.",

  footerColPlatform: "પ્લેટફોર્મ",
  footerColModules: "મોડ્યુલ્સ",
  footerColCompany: "કંપની",
  footerColResources: "સંસાધનો",
  footerCompany1: "વિશે",
  footerCompany2: "સંપર્ક",
  footerResources3: "ગોપનીયતા",
  footerResources4: "નિયમો",
  footerNewsHeading: "ત્રિમાસિક અપડેટ્સ. કોઈ સ્પામ નહીં.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "સબ્સ્ક્રાઇબ",
  footerRegistration: "ShopAI Ltd · કંપની નં. 15877093 · ઇંગ્લેન્ડમાં નોંધાયેલ",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (GU) ──
  heroAlt: "સુપરમાર્કેટના પાંથમાં ખરીદનાર — TalkPack અનુભવનો દરેક ટચપોઈન્ટ તેની આસપાસ ફરી રહ્યો છે",

  stepsTitle: "ત્રણ પગલાં. એક પ્લેટફોર્મ.",
  stepsSub: "જ્યારે શોપર પ્રવેશે છે ત્યારથી લઈને જ્યારે તમે તેના વિશે કંઇક શીખો છો ત્યાં સુધી. ચલાવવામાં સરળ. ડેટાથી સમૃદ્ધ.",
  step1Title: "પ્રવેશ",
  step1Desc: "શોપર કોઈપણ ચેનલમાંથી ટેપ અથવા સ્કેન કરે છે. એક પ્રવેશ, એક બ્રાન્ડેડ અનુભવ.",
  step1PillRow: "વેબસાઇટ · સોશિયલ · ઈમેલ · QR · OOH · સ્ટોરમાં",
  step2Title: "જોડાણ",
  step2Desc: "પાંચ Pic-n-Mix મોડ્યુલ્સ તેમની ભાષામાં, તમારી બ્રાન્ડમાં, AI દ્વારા ચકાસાયેલ ચાલે છે.",
  step2PillRow: "શીખો · રમો · પૂછો · જીતો · કમાઓ",
  step3Title: "શીખો",
  step3Desc: "દરેક ઇન્ટરેક્શન first-party ડેટા બને છે, રીઅલ-ટાઇમમાં તમારા ડેશબોર્ડ પર.",
  step3PillRow: "શોપર્સ · વાર્તાલાપ · સ્થાન · જોડાણ · અનુપાલન",

  chipWebsite: "વેબસાઇટ",
  chipSocial:  "સોશિયલ",
  chipEmail:   "ઈમેલ",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "સ્ટોરમાં",
  chipLearn:   "શીખો",
  chipPlay:    "રમો",
  chipSay:     "પૂછો",
  chipWin:     "જીતો",
  chipEarn:    "કમાઓ",
  chipShoppers:     "શોપર્સ",
  chipConversation: "વાર્તાલાપ",
  chipLocation:     "સ્થાન",
  chipEngagement:   "જોડાણ",
  chipCompliance:   "અનુપાલન",
  chipAriaEntry:  "પ્રવેશ ચેનલ",
  chipAriaModule: "Pic-n-Mix મોડ્યુલ",
  chipAriaIntel:  "ઇન્ટેલિજન્સ શ્રેણી",

  intgTitle: "ગંભીર ઇન્ફ્રાસ્ટ્રક્ચર. સાબિત ટેકનોલોજી.",
  intgSub: "TalkPack એ જ ક્લાઉડ, AI અને અનુપાલન પાયાઓ પર ચાલે છે જેના પર વિશ્વની સૌથી મોટી કંપનીઓ વિશ્વાસ કરે છે.",
  intgCap1: "મુખ્ય પ્લેટફોર્મ",
  intgCap2: "ક્લાઉડ ઇન્ફ્રાસ્ટ્રક્ચર",
  intgCap3: "વાર્તાલાપ AI",
  intgCap4: "ભાષા મોડેલ",
  intgCap5: "AI અને મેપિંગ",
  intgCap6: "પોષણ ડેટા",
  intgCap7: "ચુકવણીઓ",
  intgFootnote: "ISO 27001 પ્રમાણિત પ્રદાતાઓ · GDPR-અનુપાલક · મલ્ટિ-રિજન હોસ્ટિંગ · પહેલા દિવસથી એન્ટરપ્રાઇઝ-ગ્રેડ સુરક્ષા",

  jColEntry: "પ્રવેશ",
  jColExperience: "અનુભવ",
  jColDashboard: "ડેશબોર્ડ ઇન્ટેલિજન્સ",
  jEntryDigitalTitle: "ડિજિટલ",
  jEntryDigitalDesc: "વેબસાઇટ, એપ સ્ટોર્સ, શોધ, સીધી લિંક્સ — કોઈપણ ડિજિટલ સપાટીથી શોપર અનુભવમાં એક ટેપ.",
  jEntrySocialTitle: "સોશિયલ",
  jEntrySocialDesc: "સ્પોન્સર્ડ પોસ્ટ્સ, ઇન્ફ્લુએન્સર કન્ટેન્ટ, DMs — ફીડ્સ પ્લેટફોર્મ છોડ્યા વિના સીધા TalkPack માં વહે છે.",
  jEntryPhysicalTitle: "ભૌતિક",
  jEntryPhysicalDesc: "પેક પર, શેલ્ફ પર, POS પર — વાસ્તવિક દુનિયાના પ્રવેશ બિંદુઓ જ્યાં TalkPack સ્કેનને વાર્તાલાપમાં ફેરવે છે.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "સ્ટોર POS",
  jEntryPhysPill3: "પેકેજિંગ",
  jExpVerifyLabel: "ચકાસો",
  jExpVerifyText: "AI પ્રોડક્ટ ઓળખ — SKU રીઅલ-ટાઇમમાં ચકાસાયેલ.",
  jExpLearnLabel: "શીખો",
  jExpLearnBody: "પોષણ વ્હીલ, એલર્જન, ઘટકો, ઉત્પત્તિ — શોપરની ભાષામાં દર્શાવાય છે, બ્રાન્ડ દ્વારા ચકાસાયેલ.",
  jExpEngageLabel: "જોડાણ",
  jExpEngagePlayBody: "સ્પિન, ગાચા, પઝલ, ક્વિઝ — રોકાવા માટે પાંચ રમત પ્રકારો.",
  jExpEngageEarnBody: "પોઈન્ટ્સ, રસીદો, લોયલ્ટી — બાસ્કેટ-સ્તરની ઇન્ટેલિજન્સ.",
  jExpEngageSayBody: "વાર્તાલાપ AI 40+ ભાષાઓમાં જવાબ આપે છે.",
  jExpRewardLabel: "ઈનામ",
  jExpRewardBody: "તાત્કાલિક ઈનામો, ટિલ પર ડ્યુઅલ-QR ચકાસણી, GPS-ચકાસાયેલ દાવાઓ — પહેલા દિવસથી ઓડિટ માટે તૈયાર.",
  jExpCaptureLabel: "કેપ્ચર",
  jExpCaptureSub: "દરેક ઇન્ટરેક્શન · તમારા દ્વારા કેપ્ચર અને માલિકી",

  capEmail:   "ઈમેલ",
  capName:    "નામ",
  capAddress: "સરનામું",
  capGps:     "GPS",
  capProduct: "પ્રોડક્ટ",
  capBasket:  "બાસ્કેટ",
  capSpend:   "ખર્ચ",
  capChat:    "ચેટ",

  dashShopperLabel: "શોપર ઇન્ટેલિજન્સ",
  dashShopperShort: "શોપર",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "ઈમેલ, નામ, સરનામું, સંપર્ક પસંદગીઓ.",
  dashShopper2Title: "GPS-ચકાસાયેલ સ્થાનો",
  dashShopper2Desc: "સ્ટોર પ્રદેશ પ્રમાણે શોપર્સ ક્યાં જોડાય છે.",
  dashShopper3Title: "ખરીદી આવૃત્તિ",
  dashShopper3Desc: "પુનરાવર્તિત સ્કેન પેટર્ન, મુલાકાતની તાલ.",
  dashShopper4Title: "બાસ્કેટ રચના",
  dashShopper4Desc: "આઇટમ-સ્તરનો રસીદ ડેટા, સહ-ખરીદેલા ઉત્પાદનો.",

  dashConvLabel: "વાર્તાલાપ ઇન્ટેલિજન્સ",
  dashConvShort: "વાર્તાલાપ",
  dashConv1Title: "પ્રશ્ન શ્રેણીઓ",
  dashConv1Desc: "એલર્જન, ઘટકો, પોષણ, ઉપયોગ, રેસિપીઝ.",
  dashConv2Title: "સંવેદના વિશ્લેષણ",
  dashConv2Desc: "ઉત્પાદન અને પ્રદેશ પ્રમાણે હકારાત્મક, નકારાત્મક, તટસ્થ.",
  dashConv3Title: "મૂંઝવણના બિંદુઓ",
  dashConv3Desc: "શોપર્સ શું સમજતા નથી, વારંવાર પૂછાતા પ્રશ્નો.",
  dashConv4Title: "ખરીદી ઇરાદો",
  dashConv4Desc: "જિજ્ઞાસાથી રિટેલર વિનંતીઓ સુધીની પ્રગતિ.",
  dashConv5Title: "ભાષા પસંદગીઓ",
  dashConv5Desc: "સ્થાન પ્રમાણે શોપર્સ કઈ ભાષાઓ પસંદ કરે છે.",

  dashDistLabel: "વિતરણ ઇન્ટેલિજન્સ",
  dashDistShort: "વિતરણ",
  dashDist1Title: "સ્ટોર ઉપલબ્ધતા",
  dashDist1Desc: "શેલ્ફ પર પ્રોડક્ટ, geofence સ્કેન દ્વારા પુષ્ટિ.",
  dashDist2Title: "આઉટ-ઓફ-સ્ટોક ચેતવણી",
  dashDist2Desc: "'અહીં નથી' રિપોર્ટ્સ રીઅલ-ટાઇમમાં ફ્લેગ થાય છે.",
  dashDist3Title: "હીટમેપ",
  dashDist3Desc: "પિનકોડ, પ્રદેશ, ફેસિયા પ્રમાણે સ્કેન ઘનતા.",
  dashDist4Title: "ઘરેલું પ્રવેશ",
  dashDist4Desc: "'ગમે ત્યાં સ્કેન' દર્શાવે છે કે પ્રોડક્ટ ઘરમાં ક્યાં રહે છે.",
  dashDist5Title: "સ્ટોર સરખામણી",
  dashDist5Desc: "સ્થાન, ફેસિયા જૂથ પ્રમાણે જોડાણ દર.",

  dashCampLabel: "કેમ્પેઈન પ્રદર્શન",
  dashCampShort: "કેમ્પેઈન",
  dashCamp1Title: "સ્કેન-ટુ-જોડાણ દર",
  dashCamp1Desc: "કેટલા સ્કેન સંપૂર્ણ ઇન્ટરેક્શનમાં ફેરવાય છે.",
  dashCamp2Title: "ગેમ પૂર્ણતા દર",
  dashCamp2Desc: "પ્રતિ સત્ર સ્પિન્સ, જીત દર, ડ્રોપ-ઓફ બિંદુઓ.",
  dashCamp3Title: "રિડેમ્પશન ટ્રેકિંગ",
  dashCamp3Desc: "સ્ટોર પ્રમાણે દાવો કરેલ, ચકાસેલ, સમાપ્ત થયેલ ઈનામો.",
  dashCamp4Title: "ROI ડેશબોર્ડ",
  dashCamp4Desc: "કેમ્પેઈન ખર્ચ સામે કેપ્ચર કરેલ શોપર મૂલ્ય.",

  dashCompLabel: "અનુપાલન અને ઓડિટ",
  dashCompShort: "અનુપાલન",
  dashComp1Title: "ASA અનુપાલન સ્કોર",
  dashComp1Desc: "પ્રતિ કેમ્પેઈન, પ્રતિ વાર્તાલાપ.",
  dashComp2Title: "નિકાસયોગ્ય ઓડિટ લોગ્સ",
  dashComp2Desc: "દરેક AI પ્રતિસાદ લોગ અને પુનઃપ્રાપ્ય.",
  dashComp3Title: "ઉલ્લંઘન ચેતવણીઓ",
  dashComp3Desc: "ફ્લેગ કરેલ સામગ્રી પેટર્ન.",
  dashComp4Title: "પોષણ જોડાણ",
  dashComp4Desc: "વ્હીલ પર શોપર્સ કયા પોષક તત્વો સાથે ઇન્ટરેક્ટ કરે છે.",

  pvFormTitle: "અભિનંદન!",
  pvFormSub: "તમે જીત્યા: આગામી ખરીદી પર 20% છૂટ",
  pvFormName: "તમારું નામ",
  pvFormEmail: "તમારો ઈમેલ",
  pvFormPostcode: "પિન કોડ",
  pvFormCta: "ઈનામ લો",
  pvFormFootnote: "Privacy-first · GDPR-અનુપાલક",

  pvChatTitle: "AI એમ્બેસેડર",
  pvChatSub: "Morning O's પર તાલીમ પામેલ",
  pvChatBubble1: "શું આ ગ્લુટેન-મુક્ત છે?",
  pvChatBubble2: "હા — પ્રમાણિત ગ્લુટેન-મુક્ત અને પ્રતિ સર્વિંગ માત્ર 9g ખાંડ. શું તમે રેસિપી આઇડિયાઝ ઇચ્છો છો?",
  pvChatBubble3: "હા કૃપા કરીને",
  pvChatBubble4: "બેરીઝ સાથે ઓવરનાઈટ ઓટ્સ અજમાવો — 3 મિનિટ તૈયારી.",
  pvChatPlaceholder: "મને કંઇ પણ પૂછો…",
  pvChatFootnote: "40+ ભાષાઓ · હંમેશા ઉપલબ્ધ",

  pvMapTitle: "તમારી નજીક Morning O's શોધો",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "દિશાઓ",
  pvMapFootnote: "GPS-ચકાસાયેલ · 150m સુધી geofenced",
  pvMapOos: "સ્ટોકમાં નથી",

  pvWheelTitle: "Morning O's · ફેરવો અને જીતો",
  pvWheelCredits: "350 ક્રેડિટ્સ",
  pvWheelCta: "ફેરવો",
  pvWheelFootnote: "સ્પિન વ્હીલ · પોષણ વ્હીલ · ગાચા · ક્વિઝ · પઝલ",
  pvWheelWedgeWin: "જીત!",
  pvWheelWedgeTry: "ફરી",

  pvNutTitle: "Morning O's · પોષણ માહિતી",
  pvNutPer100g: "પ્રતિ 100g",
  pvNutPerServing: "પ્રતિ સર્વિંગ",
  pvNutCta: "ચાલુ રાખો",
  pvNutFootnote: "ASA-અનુપાલક · રમત પહેલાં ફરજિયાત",

  bookDemoSubject: "TalkPack ડેમો વિનંતી",
  bookDemoBody: "નમસ્તે ShopAI ટીમ,\n\nહું TalkPack નો ડેમો બુક કરવા માંગું છું. કૃપા કરીને સમય નક્કી કરવા માટે સંપર્ક કરો.\n\nઆભાર,",

  // ── Viz mock-data labels (GU) ──
  vizCapQuestions: "આ અઠવાડિયે શોપર્સના મુખ્ય પ્રશ્નો",
  vizQAllergens: "એલર્જન", vizQIngredients: "સામગ્રી", vizQNutrition: "પોષણ",
  vizQRecipes: "રેસિપીઝ", vizQUsage: "ઉપયોગ",
  vizCapSentiment: "બધી પ્રોડક્ટ્સના છેલ્લા 30 દિવસ",
  vizSentPositive: "સકારાત્મક", vizSentNeutral: "તટસ્થ", vizSentNegative: "નકારાત્મક",
  vizCapConfusion: "વારંવાર પૂછાતા પ્રશ્નો — સામગ્રીની ખામીઓ",
  vizConfusion1: "શું આ ગ્લુટેન-મુક્ત છે?",
  vizConfusion2: "શાકાહારીઓ માટે યોગ્ય છે?",
  vizConfusion3: "નટ્સ વિશે ચેતવણી છે?",
  vizCapFunnel: "વાર્તાલાપ દ્વારા કન્વર્ઝન",
  vizFunnelCurious: "જિજ્ઞાસુ", vizFunnelInterested: "રસ ધરાવતા", vizFunnelReady: "ખરીદવા તૈયાર",
  vizCapAvail: "જ્યાં તમે યોજના કરી હતી ત્યાં ઉત્પાદ ઉપલબ્ધ",
  vizAvailDetail: "1,326 માંથી 1,247 સ્ટોર્સ શેલ્ફ પર પુષ્ટિ",
  vizCapOos: "આજે 3 સ્ટોર્સ ફ્લેગ કર્યા · ઓટો-એલર્ટ મોકલાયો",
  vizCapHeat: "લંડન · માન્ચેસ્ટર · બર્મિંગહામ આગળ",
  vizCapHousehold: "68% સ્કેન ઘરે થાય છે — તમારી પ્રોડક્ટ ત્યાં રહે છે",
  vizCapFascia: "રિટેલર બ્રાન્ડ પ્રમાણે જોડાણ દર",
  vizCapEngrate: "સ્કેન જે પૂર્ણ ઇન્ટરેક્શનમાં બદલાય છે",
  vizEngrateCompare: "ઉદ્યોગ બેન્ચમાર્ક 42% ની તુલનામાં +36 પોઈન્ટ",
  vizCapGame: "શોપર્સ ક્યાં છોડે છે — ગેમ બેલેન્સ સેટ કરો",
  vizGameStart: "શરૂ", vizGamePlay: "રમો", vizGameWin: "જીત", vizGameClaim: "દાવો",
  vizCapRdm: "96% ચકાસણી દર · ઓછી સમાપ્તિ",
  vizRdmClaimed: "દાવો કરેલ", vizRdmValidated: "ચકાસેલ", vizRdmExpired: "સમાપ્ત",
  vizCapRoi: "કેમ્પેઈન ખર્ચ વિરુદ્ધ શોપર લાઇફટાઇમ વેલ્યૂ",
  vizRoiSpend: "ખર્ચ", vizRoiValueLabel: "મેળવેલ મૂલ્ય", vizRoiReturn: "3.4× રિટર્ન",
  vizCapComp: "પ્રતિ કેમ્પેઈન · પ્રતિ વાર્તાલાપ · ઓડિટ માટે તૈયાર",
  vizCapAudit: "દરેક AI પ્રતિસાદ લોગ થયો · માગણી પર ઉપલબ્ધ",
  vizCapViolation: "આ મહિને ઉલ્લંઘનો · AI આઉટપુટ સતત ચકાસાય છે",
  vizCapNut: "વ્હીલ પર શોપર્સ જે પોષક તત્વો સાથે સંપર્ક કરે છે",
  vizNutSugar: "ખાંડ", vizNutFat: "ચરબી", vizNutSalt: "મીઠું", vizNutSatFat: "સંતૃપ્ત",
  vizCapLang: "શોપર ભાષા સ્વતઃ શોધાય છે",
  vizLangOther: "અન્ય",
  vizCapCrm: "મેળવેલ શોપર્સ",
  vizCapMap: "આ અઠવાડિયે 247 સ્થાનો",
  vizCapWeek: "સરેરાશ 2.3 મુલાકાત દર અઠવાડિયે",
  vizDayMon: "સોમ", vizDayTue: "મંગળ", vizDayWed: "બુધ",
  vizDayThu: "ગુરુ", vizDayFri: "શુક્ર", vizDaySat: "શનિ", vizDaySun: "રવિ",
  vizCapBasket: "સરેરાશ બાસ્કેટ: £8.40 · 6.2 વસ્તુઓ",
  vizBasketDairy: "ડેરી", vizBasketBakery: "બેકરી", vizBasketSnacks: "નાસ્તો", vizBasketOther: "અન્ય",
};

// ────────── ਪੰਜਾਬੀ ──────────
const pa: LandingCopy = {
  ...en,
  navPlatform: "ਪਲੇਟਫਾਰਮ",
  navModules: "ਮੋਡਿਊਲ",
  navPortal: "ਪੋਰਟਲ ਲੌਗਇਨ",
  navLangButton: "PA",
  navRegister: "ਰਜਿਸਟਰ ਕਰੋ",
  navBookDemo: "ਡੈਮੋ ਬੁੱਕ ਕਰੋ",
  navLogin: "ਲੌਗ ਇਨ",

  trustLeft: "ਕੋਈ ਐਪ ਨਹੀਂ · ਕੋਈ ਏਕੀਕਰਣ ਨਹੀਂ · ਸਟੋਰ 'ਤੇ ਨਿਰਭਰਤਾ ਨਹੀਂ",
  trustRight: "2024 ਤੋਂ · ਯੂਕੇ · The Wholesale Group · Caterers Choice · Passot (ਜਾਪਾਨ) · Talysis UK · Jakavarna Group (ਥਾਈਲੈਂਡ) ਨਾਲ ਉਸਾਰੀ ਅਧੀਨ",

  heroEyebrow: "ਕਾਮਰਸ ਲਈ ਕੋ-ਪਾਇਲਟ",
  heroL1: "ਹਰ ਉਤਪਾਦ. ਇੱਕ ਗੱਲਬਾਤ.",
  heroL2: "ਹਰ ਸਕੈਨ. ਅਸਲ ਡਾਟਾ.",
  heroSub: "TalkPack ਕਿਸੇ ਵੀ ਉਤਪਾਦ ਨੂੰ ਜੀਵੰਤ ਗੱਲਬਾਤ ਵਿੱਚ ਬਦਲ ਦਿੰਦਾ ਹੈ। ਖਰੀਦਦਾਰ ਸਕੈਨ ਕਰਦੇ ਹਨ, ਸਿੱਖਦੇ ਹਨ, ਖੇਡਦੇ ਹਨ, ਪੁੱਛਦੇ ਹਨ, ਜਿੱਤਦੇ ਹਨ, ਅੰਕ ਕਮਾਉਂਦੇ ਹਨ। ਤੁਹਾਨੂੰ ਫਰਸਟ-ਪਾਰਟੀ ਡਾਟਾ, HFSS ਪਾਲਣਾ, ਅਤੇ ਭੌਤਿਕ ਰਿਟੇਲ ਵਿੱਚ ਖਰੀਦਦਾਰਾਂ ਨਾਲ ਸਿੱਧਾ ਸਬੰਧ ਮਿਲਦਾ ਹੈ। ਕੋਈ ਐਪ ਨਹੀਂ। ਕੋਈ ਏਕੀਕਰਣ ਨਹੀਂ। ਸਟੋਰ 'ਤੇ ਨਿਰਭਰਤਾ ਨਹੀਂ।",
  heroCtaPrimary: "ਡੈਮੋ ਬੁੱਕ ਕਰੋ",
  heroCtaGhost: "ਵੇਖੋ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
  heroPillAllergens: "ਐਲਰਜਨ",
  heroPillHfss: "HFSS",
  heroPillIngredients: "ਸਮੱਗਰੀ",
  heroVerified: "ਪੁਸ਼ਟੀ ਕੀਤੀ",

  statStores: "ਸਟੋਰ · ਯੂਕੇ · ਆਲਮੀ ਵਿਸਤਾਰ",
  statLanguages: "ਭਾਸ਼ਾਵਾਂ · ਆਟੋ-ਡਿਟੈਕਟ",
  statGames: "ਬ੍ਰੀਫ਼ ਤੋਂ ਲਾਈਵ ਮੁਹਿੰਮ ਤੱਕ",
  statModules: "ਨਿਊਟ੍ਰੀਸ਼ਨ ਵ੍ਹੀਲ ਅੰਦਰ",
  statSteps: "ਤੁਹਾਡਾ ਬ੍ਰਾਂਡ. ਤੁਹਾਡਾ ਡਾਟਾ. ਤੁਹਾਡਾ AI.",
  statDownloads: "ਐਪ ਡਾਊਨਲੋਡ · ਏਕੀਕਰਣ · ਰਗੜ",
  statGeofence: "ਭੌਤਿਕ ਰਿਟੇਲ ਤੋਂ ਖਰੀਦਦਾਰ ਡਾਟਾ",
  statScans: "ਸ਼ੈਲਫ ਤੋਂ ਖਰੀਦਦਾਰ CRM ਤੱਕ",

  fullJourneyEyebrow: "ਪੂਰਾ ਸਫ਼ਰ",
  fullJourneyHeading: "ਦਾਖ਼ਲੇ ਤੋਂ ਜਾਣਕਾਰੀ ਤੱਕ. ਇੱਕ ਪਲੇਟਫਾਰਮ.",
  fullJourneySub: "ਹਰ ਚੈਨਲ. ਹਰ ਮੋਡਿਊਲ. ਹਰ ਡਾਟਾ ਪੁਆਇੰਟ. ਇੰਜ TalkPack ਆਰਕੀਟੈਕਚਰ ਪੂਰੀ ਤਰ੍ਹਾਂ ਜੁੜਦਾ ਹੈ।",
  fullJourneyCaption: "ਪੂਰਾ ਪ੍ਰਵਾਹ. ਖਰੀਦਦਾਰ ਦਾਖ਼ਲੇ ਤੋਂ ਬ੍ਰਾਂਡ ਜਾਣਕਾਰੀ ਤੱਕ. ਡੈਸ਼ਬੋਰਡ, AI ਚੈਟ ਜਾਂ SmartMail ਪੁੱਛਗਿੱਛਾਂ ਰਾਹੀਂ ਨਿਰਯਾਤਯੋਗ।",

  picEyebrow: "Pic-n-Mix",
  picHeading: "ਇੱਕ ਪਲੇਟਫਾਰਮ. ਖਰੀਦਦਾਰ ਨਾਲ ਜੁੜਨ ਦੇ ਪੰਜ ਤਰੀਕੇ.",
  picSub: "ਬ੍ਰਾਂਡ ਆਪਣੀ ਲੋੜ ਅਨੁਸਾਰ ਮੋਡਿਊਲ ਚੁਣਦੇ ਹਨ। ਖਰੀਦਦਾਰਾਂ ਨੂੰ ਅਨੁਭਵਾਂ ਦਾ ਮਿਸ਼ਰਣ ਮਿਲਦਾ ਹੈ। ਹਰ ਮੋਡਿਊਲ ਉਸੇ ਸਕੈਨ, ਉਸੇ ਗੱਲਬਾਤ, ਉਸੇ ਡਾਟਾ ਲੇਅਰ ਉੱਤੇ ਚੱਲਦਾ ਹੈ।",
  learnDesc: "ਉਤਪਾਦ ਸਿੱਖਿਆ, ਸਮੱਗਰੀ ਪਾਰਦਰਸ਼ਿਤਾ, HFSS ਟ੍ਰੈਫਿਕ ਲਾਈਟਾਂ.",
  learnF1: "ਨਿਊਟ੍ਰੀਸ਼ਨ ਵ੍ਹੀਲ", learnF2: "ਐਲਰਜਨ ਸਕੈਨ", learnF3: "ਮੂਲ ਡਾਟਾ",
  playDesc: "ਪੰਜ ਖੇਡ ਕਿਸਮਾਂ ਜੋ ਠਹਿਰਾਅ ਅਤੇ ਮੁੜ-ਜੁੜਾਅ ਵਧਾਉਂਦੀਆਂ ਹਨ.",
  playF1: "ਸਪਿਨ ਵ੍ਹੀਲ", playF2: "ਗਾਚਾ", playF3: "ਪਜ਼ਲ", playF4: "ਕਵਿਜ਼", playF5: "ਨਿਊਟ੍ਰੀਸ਼ਨ ਸਪਿਨ",
  sayDesc: "ਗੱਲਬਾਤ ਆਧਾਰਿਤ AI ਜੋ ਖਰੀਦਦਾਰ ਦੇ ਸਵਾਲਾਂ ਦਾ ਉਨ੍ਹਾਂ ਦੀ ਆਪਣੀ ਭਾਸ਼ਾ ਵਿੱਚ ਜਵਾਬ ਦਿੰਦੀ ਹੈ.",
  sayF1: "40+ ਭਾਸ਼ਾਵਾਂ", sayF2: "ਸਮੱਗਰੀ Q&A", sayF3: "ਵਿਅੰਜਨ ਸੁਝਾਅ",
  winDesc: "ਇਨਾਮ, ਡਾਇਰੈਕਟਡ ਫੁੱਟਫਾਲ, ਟਿਲ 'ਤੇ ਡੁਅਲ-QR ਪੁਸ਼ਟੀ, ਪੈਕਿੰਗ ਸਲਿੱਪ.",
  winF1: "ਤੁਰੰਤ ਇਨਾਮ", winF2: "ਜਿਓਫੈਂਸਡ ਇਨਾਮ", winF3: "ਘਰ ਡਿਲੀਵਰੀ",
  earnDesc: "ਅੰਕ, ਰਸੀਦ ਸਕੈਨ, ਲੌਇਲਟੀ ਪ੍ਰੋਗਰਾਮ, ਬਾਸਕਟ ਜਾਣਕਾਰੀ.",
  earnF1: "ਰਸੀਦ OCR", earnF2: "ਕਰੌਸ-ਬ੍ਰਾਂਡ ਪ੍ਰੋਫਾਈਲ", earnF3: "ਇਨਾਮ ਸੂਚੀ",
  moduleSeeMore: "ਮੋਡਿਊਲ ਵੇਖੋ",

  oeEyebrow: "ਇੱਕ ਇੰਜਣ · ਕਈ ਗਤੀਸ਼ੀਲਤਾਵਾਂ",
  oeHeading: "TalkPack ਇੱਕ ਵੱਡੇ ਪਲੇਟਫਾਰਮ ਦਾ ਹਿੱਸਾ ਹੈ.",
  oeSub: "ShopAI ਬ੍ਰਾਂਡ, ਰਿਟੇਲਰ ਅਤੇ ਹੋਲਸੇਲਰ ਨੂੰ ਇੱਕ ਡਾਟਾ ਲੇਅਰ ਦਿੰਦਾ ਹੈ। ਪੂਰੀ ਤਰ੍ਹਾਂ ਗੱਲਬਾਤ ਆਧਾਰਿਤ ਬਿਜ਼ਨਸ ਜਾਣਕਾਰੀ। TalkPack ਉਹ ਤਰੀਕਾ ਹੈ ਜਿਸ ਨਾਲ ਖਰੀਦਦਾਰ ਗੱਲਬਾਤ ਵਿੱਚ ਸ਼ਾਮਲ ਹੁੰਦਾ ਹੈ।",
  oe1Strap: "ਭੌਤਿਕ ਰਿਟੇਲ ਤੋਂ ਖਰੀਦਦਾਰ ਜੁੜਾਅ",
  oe2Strap: "ਕਨਵੀਨੀਅੰਸ ਖੇਤਰ ਲਈ AI",
  oe3Strap: "ਫੀਲਡ ਆਡਿਟ ਜਾਣਕਾਰੀ",
  oe4Strap: "ਅੰਕ, ਰਸੀਦਾਂ, ਬਾਸਕਟ ਡਾਟਾ",
  oeActiveLabel: "ਮੌਜੂਦਾ",

  ucHeading: "ਵੱਖ-ਵੱਖ ਭੂਮਿਕਾਵਾਂ. ਇੱਕੋ ਪਲੇਟਫਾਰਮ.",
  ucBrandTitle: "ਬ੍ਰਾਂਡ ਮਾਰਕੀਟਰ",
  ucBrand1: "ਮਿਸ਼ਰਿਤ ਰਿਟੇਲ ਵਿੱਚ ਰਾਸ਼ਟਰੀ ਉਤਪਾਦ ਲਾਂਚ",
  ucBrand2: "ਭਾਗੀਦਾਰ ਸਟੋਰਾਂ ਵੱਲ ਡਾਇਰੈਕਟਡ ਫੁੱਟਫਾਲ",
  ucBrand3: "HFSS-ਅਨੁਪਾਲਕ ਆਨ-ਪੈਕ ਜੁੜਾਅ",
  ucBrand4: "ਕਰੌਸ-ਬ੍ਰਾਂਡ ਖਰੀਦਦਾਰ ਪ੍ਰੋਫਾਈਲਿੰਗ",
  ucBrand5: "ਨਿਯਮਿਤ ਸ਼੍ਰੇਣੀਆਂ (ਫਾਰਮਾ, ਅਲਕੋਹਲ) ਆਡਿਟ-ਲੌਗ AI ਜਵਾਬਾਂ ਨਾਲ",
  ucRetailerTitle: "ਰਿਟੇਲਰ / ਹੋਲਸੇਲਰ",
  ucRetailer1: "ਰਿਟੇਲਰ-ਫੰਡਡ ਖਾਸ ਪ੍ਰੋਮੋਸ਼ਨ",
  ucRetailer2: "ਸਿੰਬਲ ਗਰੁੱਪ ਐਕਟੀਵੇਸ਼ਨ",
  ucRetailer3: "ਸ਼ੈਲਫ ਅਨੁਪਾਲਨ ਆਡਿਟ",
  ucRetailer4: "ਰਿਟੇਲਰ-ਖਾਸ ਲੌਇਲਟੀ",
  ucAgencyTitle: "ਏਜੰਸੀ / ਰੀਸੇਲਰ",
  ucAgency1: "ਕਲਾਇੰਟ ਬ੍ਰਾਂਡਾਂ ਲਈ ਵ੍ਹਾਈਟ-ਲੇਬਲ ਮੁਹਿੰਮਾਂ",
  ucAgency2: "ਕਈ-ਕਲਾਇੰਟ ਪੋਰਟਲ ਪਹੁੰਚ",
  ucAgency3: "ਟ੍ਰੇਡ ਮਾਰਕੀਟਿੰਗ PDF ਰਿਪੋਰਟਾਂ",
  ucAgency4: "ਆਟੋ-ਉਤਪੰਨ ਸੋਸ਼ਲ ਸਮੱਗਰੀ",

  hfssEyebrow: "HFSS ਲਈ ਬਣਾਇਆ",
  hfssHeading: "HFSS ਅਨੁਪਾਲਨ, ਅੰਦਰ-ਨਿਰਮਿਤ.",
  hfssSub: "ਖਰੀਦਦਾਰ ਖੇਡਣ ਤੋਂ ਪਹਿਲਾਂ ਪੋਸ਼ਣ ਪ੍ਰੋਫਾਈਲ ਵੇਖਦਾ ਹੈ। ਚਰਬੀ, ਚੀਨੀ, ਨਮਕ, ਸੰਤ੍ਰਿਪਤ ਚਰਬੀ — ਰੋਜ਼ਾਨਾ ਸੰਦਰਭ ਖਪਤ ਨਾਲ ਟ੍ਰੈਫਿਕ ਲਾਈਟ। ਜ਼ਰੂਰੀ. ਆਡਿਟ-ਤਿਆਰ. ਨਿਯਾਮਕ ਰੱਖਿਆ ਦੀਆਂ ਚਾਰ ਪਰਤਾਂ.",
  hfss1Title: "ਜ਼ਰੂਰੀ ਨਿਊਟ੍ਰੀਸ਼ਨ ਵ੍ਹੀਲ.",
  hfss1Body: "ਹਰ HFSS ਉਤਪਾਦ ਖਰੀਦਦਾਰ ਅੱਗੇ ਵਧਣ ਤੋਂ ਪਹਿਲਾਂ ਚਰਬੀ, ਚੀਨੀ, ਨਮਕ ਅਤੇ ਸੰਤ੍ਰਿਪਤ ਚਰਬੀ ਦੀਆਂ ਟ੍ਰੈਫਿਕ ਲਾਈਟਾਂ ਰੋਜ਼ਾਨਾ ਸੰਦਰਭ ਖਪਤ ਨਾਲ ਦਿਖਾਉਂਦਾ ਹੈ.",
  hfss2Title: "ਸਿੱਖਿਆ-ਪਹਿਲਾਂ ਫ੍ਰੇਮਿੰਗ.",
  hfss2Body: "ਪਲੇਟਫਾਰਮ ਪੋਸ਼ਣ ਪਾਰਦਰਸ਼ਿਤਾ ਨੂੰ ਉਤਸ਼ਾਹਤ ਕਰਦਾ ਹੈ। ਖਰੀਦਦਾਰ ਜੁੜਾਅ ਨੂੰ ਜਾਣੂ ਚੋਣ ਵਜੋਂ ਪੇਸ਼ ਕਰਦਾ ਹੈ, ਨਾ ਕਿ ਪ੍ਰੋਮੋਸ਼ਨਲ ਦਬਾਅ ਵਜੋਂ.",
  hfss3Title: "ਆਉਟਪੁੱਟ ਪੁਸ਼ਟੀ.",
  hfss3Body: "ਗੱਲਬਾਤ ਆਧਾਰਿਤ AI ਨੂੰ ਨਿਯਾਮਕ ਪੈਟਰਨਾਂ ਲਈ ਨਿਗਰਾਨ ਕੀਤਾ ਜਾਂਦਾ ਹੈ। ਹਰ ਜਵਾਬ ਲੌਗ, ਫਿਲਟਰ ਅਤੇ ਆਡਿਟ ਯੋਗ ਹੈ.",
  hfss4Title: "ਨਿਰਯਾਤਯੋਗ ਆਡਿਟ ਲੌਗ.",
  hfss4Body: "ASA ਅਨੁਪਾਲਨ ਸਕੋਰ, ਹਰ ਜਵਾਬ ਲੌਗ ਅਤੇ ਮੁੜ ਪ੍ਰਾਪਤ ਕਰਨ ਯੋਗ, ਫਲੈਗ ਕੀਤੀ ਸਮੱਗਰੀ ਲਈ ਉਲੰਘਣਾ ਅਲਰਟ.",
  hfssBadge: "ASA ਅਨੁਪਾਲਨ: 98.4%",

  wlEyebrow: "ਤੁਹਾਡਾ ਬ੍ਰਾਂਡ, ਕਿਸੇ ਵੀ ਭਾਸ਼ਾ ਵਿੱਚ",
  wlHeading: "ਤੁਹਾਡਾ ਡਾਟਾ. ਤੁਹਾਡਾ AI. ਤੁਹਾਡੇ ਤਰੀਕੇ ਨਾਲ.",
  wlLangCaption: "40+ ਭਾਸ਼ਾਵਾਂ. ਆਟੋ-ਡਿਟੈਕਟ ਜਾਂ ਪ੍ਰਤੀ ਮੁਹਿੰਮ ਦਸਤੀ ਸੈੱਟ.",
  wlFeat1Title: "ਤੁਹਾਡੀ ਬ੍ਰਾਂਡ ਪਛਾਣ",
  wlFeat1Body: "ਲੋਗੋ, ਰੰਗ, ਫੌਂਟ ਹਰ ਖਰੀਦਦਾਰ ਸਕ੍ਰੀਨ ਅਤੇ ਪੈਕਿੰਗ ਸਲਿੱਪ 'ਤੇ ਲਾਗੂ.",
  wlFeat2Title: "ਤੁਹਾਡਾ ਡਾਟਾ, ਤੁਹਾਡਾ AI",
  wlFeat2Body: "ਡਾਟਾ ਤੁਹਾਡੇ ਖਾਤੇ 'ਤੇ ਸਟੋਰ. AI ਤੁਹਾਡੇ ਉਤਪਾਦਾਂ 'ਤੇ ਸਿਖਲਾਈ. ਸਬੰਧ ਤੁਹਾਡਾ.",
  wlFeat3Title: "ਤੁਹਾਡੇ ਸਟੋਰ, ਤੁਹਾਡੀ ਜਿਓਫੈਂਸ",
  wlFeat3Body: "ਯੂਨੀਵਰਸਲ ਮੋਡ ਜਾਂ ਸਟੋਰ-ਖਾਸ. ਡਿਫਾਲਟ 150 ਮੀਟਰ ਜਿਓਫੈਂਸ, ਪ੍ਰਤੀ ਮੁਹਿੰਮ ਕੌਨਫਿਗਰ ਯੋਗ.",
  wlFeat4Title: "ਤੁਹਾਡੀ ਟੀਮ, ਤੁਹਾਡੀ ਪਹੁੰਚ",
  wlFeat4Body: "ਭੂਮਿਕਾ-ਅਧਾਰਿਤ ਅਨੁਮਤੀਆਂ. ਬ੍ਰਾਂਡ, ਏਜੰਸੀ, ਫੀਲਡ, ਫਾਇਨਾਂਸ — ਹਰ ਇੱਕ ਨੂੰ ਸਹੀ ਦ੍ਰਿਸ਼.",

  proofEyebrow: "ਤੈਨਾਤੀ ਵਿੱਚ",
  proofHeading: "ਪਹਿਲਾਂ ਹੀ ਲਾਈਵ. ਪਹਿਲਾਂ ਹੀ ਕੰਮ ਕਰ ਰਿਹਾ.",
  proofStat1Label: "ਮੁਹਿੰਮ ਡਾਟਾਬੇਸ ਵਿੱਚ ਯੂਕੇ ਸਟੋਰ",
  proofStat2Label: "ਸਮਰਥਿਤ ਭਾਸ਼ਾਵਾਂ",
  proofStat3Label: "ਯੂਕੇ ਵਿੱਚ ਸਥਾਪਿਤ",
  proofQuotePlaceholder: "ਗਾਹਕ ਕਹਾਣੀਆਂ ਜਲਦੀ ਹੀ.",

  faqHeading: "ਬ੍ਰਾਂਡ ਸਾਨੂੰ ਪੁੱਛਦੇ ਸਵਾਲ.",
  faq1Q: "ਕੀ ਖਰੀਦਦਾਰਾਂ ਨੂੰ ਐਪ ਡਾਊਨਲੋਡ ਕਰਨੀ ਪਵੇਗੀ?",
  faq1A: "ਨਹੀਂ. TalkPack ਬ੍ਰਾਊਜ਼ਰ ਵਿੱਚ ਚੱਲਦਾ ਹੈ. ਇੱਕ ਸਕੈਨ ਅਨੁਭਵ ਖੋਲ੍ਹਦਾ ਹੈ.",
  faq2Q: "ਕੀ TalkPack ਸਾਡੇ EPOS ਜਾਂ CRM ਨਾਲ ਏਕੀਕਰਣ ਹੁੰਦਾ ਹੈ?",
  faq2A: "ਕੋਈ ਏਕੀਕਰਣ ਜ਼ਰੂਰੀ ਨਹੀਂ. ਪ੍ਰਾਪਤ ਡਾਟਾ ਫਰਸਟ-ਪਾਰਟੀ ਹੈ ਅਤੇ PDF, CSV ਜਾਂ API ਰਾਹੀਂ ਨਿਰਯਾਤ ਯੋਗ ਹੈ.",
  faq3Q: "HFSS ਉਤਪਾਦਾਂ ਨਾਲ ਕੀ ਹੁੰਦਾ ਹੈ?",
  faq3A: "ਹਰ HFSS ਉਤਪਾਦ ਖੇਡ ਤੋਂ ਪਹਿਲਾਂ ਜ਼ਰੂਰੀ ਨਿਊਟ੍ਰੀਸ਼ਨ ਵ੍ਹੀਲ ਦਿਖਾਉਂਦਾ ਹੈ. ਅਨੁਪਾਲਨ ਅੰਦਰ-ਨਿਰਮਿਤ ਹੈ, ਬਾਅਦ ਵਿੱਚ ਜੋੜਿਆ ਨਹੀਂ.",
  faq4Q: "ਤੁਸੀਂ ਕਿਹੜੀਆਂ ਭਾਸ਼ਾਵਾਂ ਦਾ ਸਮਰਥਨ ਕਰਦੇ ਹੋ?",
  faq4A: "40+. ਪਲੇਟਫਾਰਮ ਆਟੋ-ਡਿਟੈਕਟ ਕਰਦਾ ਹੈ ਜਾਂ ਤੁਸੀਂ ਪ੍ਰਤੀ ਮੁਹਿੰਮ ਸੈੱਟ ਕਰਦੇ ਹੋ.",
  faq5Q: "ਕੀ ਅਸੀਂ ਮੁਹਿੰਮ ਸਿਰਫ਼ ਖਾਸ ਰਿਟੇਲਰਾਂ ਵਿੱਚ ਚਲਾ ਸਕਦੇ ਹਾਂ?",
  faq5A: "ਹਾਂ. ਸਟੋਰ-ਖਾਸ ਮੋਡ ਚੁਣੇ ਸਟੋਰਾਂ 'ਤੇ ਜਿਓਫੈਂਸ ਕਰਦਾ ਹੈ. ਯੂਨੀਵਰਸਲ ਮੋਡ ਕਿਸੇ ਵੀ ਖਰੀਦਦਾਰ ਨੂੰ ਕਿਤੇ ਵੀ ਖੇਡਣ ਦਿੰਦਾ ਹੈ.",
  faq6Q: "ਮੁਹਿੰਮ ਬਣਾਉਣ ਵਿੱਚ ਕਿੰਨਾ ਸਮਾਂ ਲੱਗਦਾ ਹੈ?",
  faq6A: "ਮਿਆਰੀ ਪ੍ਰੋਮੋਸ਼ਨ ਲਈ ਲਗਭਗ 10 ਮਿੰਟ. ਪੋਰਟਲ ਵਿੱਚ ਸਟੈਪ-ਬਾਇ-ਸਟੈਪ ਵਿਜ਼ਾਰਡ.",
  faq7Q: "ਕੀ ਅਸੀਂ ਅਨੁਭਵ ਨੂੰ ਵ੍ਹਾਈਟ-ਲੇਬਲ ਕਰ ਸਕਦੇ ਹਾਂ?",
  faq7A: "ਹਾਂ. ਤੁਹਾਡਾ ਲੋਗੋ, ਤੁਹਾਡੇ ਰੰਗ, ਤੁਹਾਡੀ ਭਾਸ਼ਾ. ਖਰੀਦਦਾਰ TalkPack ਬ੍ਰਾਂਡਿੰਗ ਨਹੀਂ ਵੇਖਣਗੇ ਜਦੋਂ ਤੱਕ ਤੁਸੀਂ ਨਾ ਚਾਹੋ.",

  footerColPlatform: "ਪਲੇਟਫਾਰਮ",
  footerColModules: "ਮੋਡਿਊਲ",
  footerColCompany: "ਕੰਪਨੀ",
  footerColResources: "ਸਰੋਤ",
  footerCompany1: "ਬਾਰੇ",
  footerCompany2: "ਸੰਪਰਕ",
  footerResources3: "ਪ੍ਰਾਈਵੇਸੀ",
  footerResources4: "ਨਿਯਮ",
  footerNewsHeading: "ਤਿਮਾਹੀ ਅੱਪਡੇਟ. ਕੋਈ ਸਪੈਮ ਨਹੀਂ.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "ਸਬਸਕ੍ਰਾਈਬ",
  footerRegistration: "ShopAI Ltd · ਕੰਪਨੀ ਨੰ. 15877093 · ਇੰਗਲੈਂਡ ਵਿੱਚ ਰਜਿਸਟਰਡ",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (PA) ──
  heroAlt: "ਸੁਪਰਮਾਰਕੀਟ ਦੀ ਗਲੀ ਵਿੱਚ ਖਰੀਦਦਾਰ — TalkPack ਅਨੁਭਵ ਦਾ ਹਰ ਟੱਚਪੌਇੰਟ ਉਸ ਦੇ ਆਲੇ-ਦੁਆਲੇ ਘੁੰਮ ਰਿਹਾ ਹੈ",

  stepsTitle: "ਤਿੰਨ ਕਦਮ. ਇੱਕ ਪਲੇਟਫਾਰਮ.",
  stepsSub: "ਜਿਸ ਪਲ ਸ਼ੌਪਰ ਪ੍ਰਵੇਸ਼ ਕਰਦਾ ਹੈ ਉਸ ਪਲ ਤੋਂ ਲੈ ਕੇ ਜਦੋਂ ਤੁਸੀਂ ਉਸ ਬਾਰੇ ਕੁਝ ਸਿੱਖਦੇ ਹੋ. ਚਲਾਉਣ ਵਿੱਚ ਸਰਲ. ਡੇਟਾ ਨਾਲ ਅਮੀਰ.",
  step1Title: "ਪ੍ਰਵੇਸ਼",
  step1Desc: "ਸ਼ੌਪਰ ਕਿਸੇ ਵੀ ਚੈਨਲ ਤੋਂ ਟੈਪ ਜਾਂ ਸਕੈਨ ਕਰਦਾ ਹੈ. ਇੱਕ ਪ੍ਰਵੇਸ਼, ਇੱਕ ਬ੍ਰਾਂਡਡ ਅਨੁਭਵ.",
  step1PillRow: "ਵੈੱਬਸਾਈਟ · ਸੋਸ਼ਲ · ਈਮੇਲ · QR · OOH · ਸਟੋਰ ਵਿੱਚ",
  step2Title: "ਜੁੜਨਾ",
  step2Desc: "ਪੰਜ Pic-n-Mix ਮੋਡੀਊਲ ਉਨ੍ਹਾਂ ਦੀ ਭਾਸ਼ਾ ਵਿੱਚ, ਤੁਹਾਡੇ ਬ੍ਰਾਂਡ ਵਿੱਚ, AI ਦੁਆਰਾ ਪ੍ਰਮਾਣਿਤ ਚਲਦੇ ਹਨ.",
  step2PillRow: "ਸਿੱਖੋ · ਖੇਡੋ · ਪੁੱਛੋ · ਜਿੱਤੋ · ਕਮਾਓ",
  step3Title: "ਸਿੱਖੋ",
  step3Desc: "ਹਰ ਇੰਟਰੈਕਸ਼ਨ first-party ਡੇਟਾ ਬਣ ਜਾਂਦਾ ਹੈ, ਅਸਲ-ਸਮੇਂ ਵਿੱਚ ਤੁਹਾਡੇ ਡੈਸ਼ਬੋਰਡ 'ਤੇ.",
  step3PillRow: "ਸ਼ੌਪਰ · ਗੱਲਬਾਤ · ਸਥਾਨ · ਜੁੜਾਅ · ਅਨੁਪਾਲਨ",

  chipWebsite: "ਵੈੱਬਸਾਈਟ",
  chipSocial:  "ਸੋਸ਼ਲ",
  chipEmail:   "ਈਮੇਲ",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "ਸਟੋਰ ਵਿੱਚ",
  chipLearn:   "ਸਿੱਖੋ",
  chipPlay:    "ਖੇਡੋ",
  chipSay:     "ਪੁੱਛੋ",
  chipWin:     "ਜਿੱਤੋ",
  chipEarn:    "ਕਮਾਓ",
  chipShoppers:     "ਸ਼ੌਪਰ",
  chipConversation: "ਗੱਲਬਾਤ",
  chipLocation:     "ਸਥਾਨ",
  chipEngagement:   "ਜੁੜਾਅ",
  chipCompliance:   "ਅਨੁਪਾਲਨ",
  chipAriaEntry:  "ਪ੍ਰਵੇਸ਼ ਚੈਨਲ",
  chipAriaModule: "Pic-n-Mix ਮੋਡੀਊਲ",
  chipAriaIntel:  "ਇੰਟੈਲੀਜੈਂਸ ਸ਼੍ਰੇਣੀ",

  intgTitle: "ਗੰਭੀਰ ਇਨਫਰਾਸਟਰਕਚਰ. ਸਾਬਤ ਤਕਨਾਲੋਜੀ.",
  intgSub: "TalkPack ਉਨ੍ਹਾਂ ਹੀ ਕਲਾਊਡ, AI ਅਤੇ ਅਨੁਪਾਲਨ ਆਧਾਰਾਂ 'ਤੇ ਚਲਦਾ ਹੈ ਜਿਨ੍ਹਾਂ 'ਤੇ ਦੁਨੀਆ ਦੀਆਂ ਸਭ ਤੋਂ ਵੱਡੀਆਂ ਕੰਪਨੀਆਂ ਭਰੋਸਾ ਕਰਦੀਆਂ ਹਨ.",
  intgCap1: "ਮੁੱਖ ਪਲੇਟਫਾਰਮ",
  intgCap2: "ਕਲਾਊਡ ਇਨਫਰਾਸਟਰਕਚਰ",
  intgCap3: "ਗੱਲਬਾਤ AI",
  intgCap4: "ਭਾਸ਼ਾ ਮਾਡਲ",
  intgCap5: "AI ਅਤੇ ਮੈਪਿੰਗ",
  intgCap6: "ਪੋਸ਼ਣ ਡੇਟਾ",
  intgCap7: "ਭੁਗਤਾਨ",
  intgFootnote: "ISO 27001 ਪ੍ਰਮਾਣਿਤ ਪ੍ਰਦਾਤਾ · GDPR-ਅਨੁਪਾਲਕ · ਮਲਟੀ-ਰੀਜਨ ਹੋਸਟਿੰਗ · ਪਹਿਲੇ ਦਿਨ ਤੋਂ ਐਂਟਰਪ੍ਰਾਈਜ਼-ਗ੍ਰੇਡ ਸੁਰੱਖਿਆ",

  jColEntry: "ਪ੍ਰਵੇਸ਼",
  jColExperience: "ਅਨੁਭਵ",
  jColDashboard: "ਡੈਸ਼ਬੋਰਡ ਇੰਟੈਲੀਜੈਂਸ",
  jEntryDigitalTitle: "ਡਿਜੀਟਲ",
  jEntryDigitalDesc: "ਵੈੱਬਸਾਈਟ, ਐਪ ਸਟੋਰ, ਖੋਜ, ਸਿੱਧੇ ਲਿੰਕ — ਕਿਸੇ ਵੀ ਡਿਜੀਟਲ ਸਤਹ ਤੋਂ ਸ਼ੌਪਰ ਅਨੁਭਵ ਵਿੱਚ ਇੱਕ ਟੈਪ.",
  jEntrySocialTitle: "ਸੋਸ਼ਲ",
  jEntrySocialDesc: "ਸਪਾਂਸਰਡ ਪੋਸਟ, ਇਨਫਲੂਐਂਸਰ ਸਮੱਗਰੀ, DM — ਫੀਡਾਂ ਪਲੇਟਫਾਰਮ ਛੱਡੇ ਬਿਨਾਂ ਸਿੱਧੇ TalkPack ਵਿੱਚ ਵਹਿੰਦੀਆਂ ਹਨ.",
  jEntryPhysicalTitle: "ਭੌਤਿਕ",
  jEntryPhysicalDesc: "ਪੈਕ 'ਤੇ, ਸ਼ੈਲਫ 'ਤੇ, POS 'ਤੇ — ਅਸਲ ਦੁਨੀਆ ਦੇ ਪ੍ਰਵੇਸ਼ ਬਿੰਦੂ ਜਿੱਥੇ TalkPack ਇੱਕ ਸਕੈਨ ਨੂੰ ਗੱਲਬਾਤ ਵਿੱਚ ਬਦਲਦਾ ਹੈ.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "ਸਟੋਰ POS",
  jEntryPhysPill3: "ਪੈਕੇਜਿੰਗ",
  jExpVerifyLabel: "ਪ੍ਰਮਾਣਿਤ ਕਰੋ",
  jExpVerifyText: "AI ਉਤਪਾਦ ਪਛਾਣ — SKU ਅਸਲ-ਸਮੇਂ ਵਿੱਚ ਪ੍ਰਮਾਣਿਤ.",
  jExpLearnLabel: "ਸਿੱਖੋ",
  jExpLearnBody: "ਪੋਸ਼ਣ ਪਹੀਆ, ਐਲਰਜੀ, ਸਮੱਗਰੀ, ਮੁੱਢ — ਸ਼ੌਪਰ ਦੀ ਭਾਸ਼ਾ ਵਿੱਚ ਦਿਖਾਏ ਜਾਂਦੇ, ਬ੍ਰਾਂਡ ਦੁਆਰਾ ਪ੍ਰਮਾਣਿਤ.",
  jExpEngageLabel: "ਜੁੜੋ",
  jExpEngagePlayBody: "ਸਪਿਨ, ਗਾਚਾ, ਪਜ਼ਲ, ਕੁਇਜ਼ — ਰੁਕਣ ਦੇ ਸਮੇਂ ਲਈ ਪੰਜ ਖੇਡ ਕਿਸਮਾਂ.",
  jExpEngageEarnBody: "ਪੁਆਇੰਟ, ਰਸੀਦਾਂ, ਲੌਯਲਟੀ — ਬਾਸਕੇਟ-ਪੱਧਰ ਦੀ ਇੰਟੈਲੀਜੈਂਸ.",
  jExpEngageSayBody: "ਗੱਲਬਾਤ AI 40+ ਭਾਸ਼ਾਵਾਂ ਵਿੱਚ ਜਵਾਬ ਦਿੰਦਾ ਹੈ.",
  jExpRewardLabel: "ਇਨਾਮ",
  jExpRewardBody: "ਤੁਰੰਤ ਇਨਾਮ, ਟਿਲ 'ਤੇ ਡੁਅਲ-QR ਪ੍ਰਮਾਣੀਕਰਨ, GPS-ਪ੍ਰਮਾਣਿਤ ਦਾਅਵੇ — ਪਹਿਲੇ ਦਿਨ ਤੋਂ ਆਡਿਟ ਲਈ ਤਿਆਰ.",
  jExpCaptureLabel: "ਕੈਪਚਰ",
  jExpCaptureSub: "ਹਰ ਇੰਟਰੈਕਸ਼ਨ · ਤੁਹਾਡੁ ਦੁਆਰਾ ਕੈਪਚਰ ਅਤੇ ਮਾਲਕੀ",

  capEmail:   "ਈਮੇਲ",
  capName:    "ਨਾਮ",
  capAddress: "ਪਤਾ",
  capGps:     "GPS",
  capProduct: "ਉਤਪਾਦ",
  capBasket:  "ਬਾਸਕੇਟ",
  capSpend:   "ਖਰਚ",
  capChat:    "ਚੈਟ",

  dashShopperLabel: "ਸ਼ੌਪਰ ਇੰਟੈਲੀਜੈਂਸ",
  dashShopperShort: "ਸ਼ੌਪਰ",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "ਈਮੇਲ, ਨਾਮ, ਪਤਾ, ਸੰਪਰਕ ਤਰਜੀਹਾਂ.",
  dashShopper2Title: "GPS-ਪ੍ਰਮਾਣਿਤ ਸਥਾਨ",
  dashShopper2Desc: "ਸਟੋਰ ਖੇਤਰਾਂ ਅਨੁਸਾਰ ਸ਼ੌਪਰ ਕਿੱਥੇ ਜੁੜਦੇ ਹਨ.",
  dashShopper3Title: "ਖਰੀਦ ਆਵਿਰਤੀ",
  dashShopper3Desc: "ਦੁਹਰਾਏ ਜਾਣ ਵਾਲੇ ਸਕੈਨ ਪੈਟਰਨ, ਯਾਤਰਾ ਦੀ ਲੈਅ.",
  dashShopper4Title: "ਬਾਸਕੇਟ ਰਚਨਾ",
  dashShopper4Desc: "ਆਈਟਮ-ਪੱਧਰ ਦਾ ਰਸੀਦ ਡੇਟਾ, ਇਕੱਠੇ ਖਰੀਦੇ ਉਤਪਾਦ.",

  dashConvLabel: "ਗੱਲਬਾਤ ਇੰਟੈਲੀਜੈਂਸ",
  dashConvShort: "ਗੱਲਬਾਤ",
  dashConv1Title: "ਪ੍ਰਸ਼ਨ ਸ਼੍ਰੇਣੀਆਂ",
  dashConv1Desc: "ਐਲਰਜੀ, ਸਮੱਗਰੀ, ਪੋਸ਼ਣ, ਵਰਤੋਂ, ਰੈਸਿਪੀਆਂ.",
  dashConv2Title: "ਭਾਵਨਾ ਵਿਸ਼ਲੇਸ਼ਣ",
  dashConv2Desc: "ਉਤਪਾਦ ਅਤੇ ਖੇਤਰ ਅਨੁਸਾਰ ਸਕਾਰਾਤਮਕ, ਨਕਾਰਾਤਮਕ, ਨਿਰਪੱਖ.",
  dashConv3Title: "ਉਲਝਣ ਦੇ ਬਿੰਦੂ",
  dashConv3Desc: "ਸ਼ੌਪਰ ਕੀ ਨਹੀਂ ਸਮਝਦੇ, ਵਾਰ-ਵਾਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਸਵਾਲ.",
  dashConv4Title: "ਖਰੀਦ ਇਰਾਦਾ",
  dashConv4Desc: "ਉਤਸੁਕਤਾ ਤੋਂ ਰਿਟੇਲਰ ਬੇਨਤੀਆਂ ਤੱਕ ਦੀ ਪ੍ਰਗਤੀ.",
  dashConv5Title: "ਭਾਸ਼ਾ ਤਰਜੀਹਾਂ",
  dashConv5Desc: "ਸਥਾਨ ਅਨੁਸਾਰ ਸ਼ੌਪਰ ਕਿਹੜੀਆਂ ਭਾਸ਼ਾਵਾਂ ਚੁਣਦੇ ਹਨ.",

  dashDistLabel: "ਵੰਡ ਇੰਟੈਲੀਜੈਂਸ",
  dashDistShort: "ਵੰਡ",
  dashDist1Title: "ਸਟੋਰ ਉਪਲਬਧਤਾ",
  dashDist1Desc: "ਸ਼ੈਲਫ 'ਤੇ ਉਤਪਾਦ, geofence ਸਕੈਨ ਨਾਲ ਪੁਸ਼ਟੀ.",
  dashDist2Title: "ਆਊਟ-ਆਫ-ਸਟਾਕ ਚੇਤਾਵਨੀਆਂ",
  dashDist2Desc: "'ਇੱਥੇ ਨਹੀਂ' ਰਿਪੋਰਟਾਂ ਅਸਲ-ਸਮੇਂ ਵਿੱਚ ਫਲੈਗ ਕੀਤੀਆਂ ਜਾਂਦੀਆਂ.",
  dashDist3Title: "ਹੀਟਮੈਪ",
  dashDist3Desc: "ਪਿਨ ਕੋਡ, ਖੇਤਰ, ਫੇਸੀਆ ਅਨੁਸਾਰ ਸਕੈਨ ਘਣਤਾ.",
  dashDist4Title: "ਘਰੇਲੂ ਪਹੁੰਚ",
  dashDist4Desc: "'ਕਿਤੇ ਵੀ ਸਕੈਨ' ਦਿਖਾਉਂਦਾ ਹੈ ਕਿ ਉਤਪਾਦ ਘਰ ਵਿੱਚ ਕਿੱਥੇ ਰਹਿੰਦਾ ਹੈ.",
  dashDist5Title: "ਸਟੋਰ ਤੁਲਨਾ",
  dashDist5Desc: "ਸਥਾਨ, ਫੇਸੀਆ ਸਮੂਹ ਅਨੁਸਾਰ ਜੁੜਾਅ ਦਰਾਂ.",

  dashCampLabel: "ਮੁਹਿੰਮ ਪ੍ਰਦਰਸ਼ਨ",
  dashCampShort: "ਮੁਹਿੰਮ",
  dashCamp1Title: "ਸਕੈਨ-ਤੋਂ-ਜੁੜਾਅ ਦਰ",
  dashCamp1Desc: "ਕਿੰਨੇ ਸਕੈਨ ਪੂਰੀ ਇੰਟਰੈਕਸ਼ਨ ਵਿੱਚ ਬਦਲਦੇ ਹਨ.",
  dashCamp2Title: "ਖੇਡ ਪੂਰਨਤਾ ਦਰਾਂ",
  dashCamp2Desc: "ਪ੍ਰਤੀ ਸੈਸ਼ਨ ਸਪਿਨ, ਜਿੱਤ ਦਰਾਂ, ਡ੍ਰੌਪ-ਆਫ ਬਿੰਦੂ.",
  dashCamp3Title: "ਰਿਡੈਮਪਸ਼ਨ ਟਰੈਕਿੰਗ",
  dashCamp3Desc: "ਸਟੋਰ ਅਨੁਸਾਰ ਦਾਅਵਾ ਕੀਤੇ, ਪ੍ਰਮਾਣਿਤ, ਮਿਆਦ ਪੁੱਗ ਚੁੱਕੇ ਇਨਾਮ.",
  dashCamp4Title: "ROI ਡੈਸ਼ਬੋਰਡ",
  dashCamp4Desc: "ਮੁਹਿੰਮ ਖਰਚ ਬਨਾਮ ਕੈਪਚਰ ਕੀਤਾ ਸ਼ੌਪਰ ਮੁੱਲ.",

  dashCompLabel: "ਅਨੁਪਾਲਨ ਅਤੇ ਆਡਿਟ",
  dashCompShort: "ਅਨੁਪਾਲਨ",
  dashComp1Title: "ASA ਅਨੁਪਾਲਨ ਸਕੋਰ",
  dashComp1Desc: "ਪ੍ਰਤੀ ਮੁਹਿੰਮ, ਪ੍ਰਤੀ ਗੱਲਬਾਤ.",
  dashComp2Title: "ਨਿਰਯਾਤ ਯੋਗ ਆਡਿਟ ਲੌਗ",
  dashComp2Desc: "ਹਰ AI ਜਵਾਬ ਲੌਗ ਅਤੇ ਪੁਨਰ ਪ੍ਰਾਪਤ ਯੋਗ.",
  dashComp3Title: "ਉਲੰਘਣਾ ਚੇਤਾਵਨੀਆਂ",
  dashComp3Desc: "ਫਲੈਗ ਕੀਤੇ ਸਮੱਗਰੀ ਪੈਟਰਨ.",
  dashComp4Title: "ਪੋਸ਼ਣ ਜੁੜਾਅ",
  dashComp4Desc: "ਪਹੀਏ 'ਤੇ ਸ਼ੌਪਰ ਕਿਹੜੇ ਪੋਸ਼ਕ ਤੱਤਾਂ ਨਾਲ ਇੰਟਰੈਕਟ ਕਰਦੇ ਹਨ.",

  pvFormTitle: "ਵਧਾਈਆਂ!",
  pvFormSub: "ਤੁਸੀਂ ਜਿੱਤ ਗਏ: ਅਗਲੀ ਖਰੀਦ 'ਤੇ 20% ਛੂਟ",
  pvFormName: "ਤੁਹਾਡਾ ਨਾਮ",
  pvFormEmail: "ਤੁਹਾਡੀ ਈਮੇਲ",
  pvFormPostcode: "ਪਿਨ ਕੋਡ",
  pvFormCta: "ਇਨਾਮ ਲਓ",
  pvFormFootnote: "Privacy-first · GDPR-ਅਨੁਪਾਲਕ",

  pvChatTitle: "AI ਐਂਬੈਸਡਰ",
  pvChatSub: "Morning O's 'ਤੇ ਸਿੱਖਿਅਤ",
  pvChatBubble1: "ਕੀ ਇਹ ਗਲੁਟਨ-ਮੁਕਤ ਹੈ?",
  pvChatBubble2: "ਹਾਂ — ਪ੍ਰਮਾਣਿਤ ਗਲੁਟਨ-ਮੁਕਤ ਅਤੇ ਪ੍ਰਤੀ ਸਰਵਿੰਗ ਸਿਰਫ 9g ਖੰਡ. ਕੀ ਤੁਸੀਂ ਰੈਸਿਪੀ ਆਈਡੀਆਜ਼ ਚਾਹੋਗੇ?",
  pvChatBubble3: "ਹਾਂ ਕਿਰਪਾ ਕਰਕੇ",
  pvChatBubble4: "ਬੇਰੀਆਂ ਨਾਲ ਓਵਰਨਾਈਟ ਓਟਸ ਅਜ਼ਮਾਓ — 3 ਮਿੰਟ ਤਿਆਰੀ.",
  pvChatPlaceholder: "ਮੈਨੂੰ ਕੁਝ ਵੀ ਪੁੱਛੋ…",
  pvChatFootnote: "40+ ਭਾਸ਼ਾਵਾਂ · ਹਮੇਸ਼ਾ ਉਪਲਬਧ",

  pvMapTitle: "ਆਪਣੇ ਨੇੜੇ Morning O's ਲੱਭੋ",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "ਦਿਸ਼ਾਵਾਂ",
  pvMapFootnote: "GPS-ਪ੍ਰਮਾਣਿਤ · 150m ਤੱਕ geofenced",
  pvMapOos: "ਸਟਾਕ ਵਿੱਚ ਨਹੀਂ",

  pvWheelTitle: "Morning O's · ਘੁਮਾਓ ਅਤੇ ਜਿੱਤੋ",
  pvWheelCredits: "350 ਕ੍ਰੈਡਿਟ",
  pvWheelCta: "ਘੁਮਾਓ",
  pvWheelFootnote: "ਸਪਿਨ ਪਹੀਆ · ਪੋਸ਼ਣ ਪਹੀਆ · ਗਾਚਾ · ਕੁਇਜ਼ · ਪਜ਼ਲ",
  pvWheelWedgeWin: "ਜਿੱਤ!",
  pvWheelWedgeTry: "ਫਿਰ",

  pvNutTitle: "Morning O's · ਪੋਸ਼ਣ ਜਾਣਕਾਰੀ",
  pvNutPer100g: "ਪ੍ਰਤੀ 100g",
  pvNutPerServing: "ਪ੍ਰਤੀ ਸਰਵਿੰਗ",
  pvNutCta: "ਜਾਰੀ ਰੱਖੋ",
  pvNutFootnote: "ASA-ਅਨੁਪਾਲਕ · ਖੇਡ ਤੋਂ ਪਹਿਲਾਂ ਲਾਜ਼ਮੀ",

  bookDemoSubject: "TalkPack ਡੈਮੋ ਬੇਨਤੀ",
  bookDemoBody: "ਨਮਸਕਾਰ ShopAI ਟੀਮ,\n\nਮੈਂ TalkPack ਦਾ ਇੱਕ ਡੈਮੋ ਬੁੱਕ ਕਰਨਾ ਚਾਹੁੰਦਾ/ਚਾਹੁੰਦੀ ਹਾਂ. ਕਿਰਪਾ ਕਰਕੇ ਸਮਾਂ ਨਿਰਧਾਰਿਤ ਕਰਨ ਲਈ ਸੰਪਰਕ ਕਰੋ.\n\nਧੰਨਵਾਦ,",

  // ── Viz mock-data labels (PA) ──
  vizCapQuestions: "ਇਸ ਹਫ਼ਤੇ ਸ਼ੌਪਰ ਦੇ ਮੁੱਖ ਸਵਾਲ",
  vizQAllergens: "ਐਲਰਜੀ", vizQIngredients: "ਸਮੱਗਰੀ", vizQNutrition: "ਪੋਸ਼ਣ",
  vizQRecipes: "ਰੈਸਿਪੀਆਂ", vizQUsage: "ਵਰਤੋਂ",
  vizCapSentiment: "ਸਾਰੇ ਉਤਪਾਦਾਂ ਦੇ ਪਿਛਲੇ 30 ਦਿਨ",
  vizSentPositive: "ਸਕਾਰਾਤਮਕ", vizSentNeutral: "ਨਿਰਪੱਖ", vizSentNegative: "ਨਕਾਰਾਤਮਕ",
  vizCapConfusion: "ਵਾਰ-ਵਾਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਸਵਾਲ — ਸਮੱਗਰੀ ਵਿੱਚ ਖਾਮੀਆਂ",
  vizConfusion1: "ਕੀ ਇਹ ਗਲੁਟਨ-ਮੁਕਤ ਹੈ?",
  vizConfusion2: "ਸ਼ਾਕਾਹਾਰੀਆਂ ਲਈ ਢੁਕਵਾਂ?",
  vizConfusion3: "ਨਟਸ ਦੀ ਚੇਤਾਵਨੀ ਹੈ?",
  vizCapFunnel: "ਗੱਲਬਾਤ ਰਾਹੀਂ ਕਨਵਰਜ਼ਨ",
  vizFunnelCurious: "ਉਤਸੁਕ", vizFunnelInterested: "ਦਿਲਚਸਪੀ ਵਾਲਾ", vizFunnelReady: "ਖਰੀਦਣ ਲਈ ਤਿਆਰ",
  vizCapAvail: "ਜਿੱਥੇ ਤੁਸੀਂ ਯੋਜਨਾ ਬਣਾਈ ਉਥੇ ਉਤਪਾਦ ਉਪਲਬਧ",
  vizAvailDetail: "1,326 ਵਿੱਚੋਂ 1,247 ਸਟੋਰਾਂ ਵਿੱਚ ਸ਼ੈਲਫ ਉੱਤੇ ਪੁਸ਼ਟੀ",
  vizCapOos: "ਅੱਜ 3 ਸਟੋਰ ਫਲੈਗ ਕੀਤੇ · ਆਟੋ-ਅਲਰਟ ਭੇਜਿਆ",
  vizCapHeat: "ਲੰਡਨ · ਮੈਨਚੈਸਟਰ · ਬਰਮਿੰਘਮ ਮੋਹਰੀ",
  vizCapHousehold: "68% ਸਕੈਨ ਘਰ ਵਿੱਚ ਹੁੰਦੇ ਹਨ — ਤੁਹਾਡਾ ਉਤਪਾਦ ਉਥੇ ਰਹਿੰਦਾ ਹੈ",
  vizCapFascia: "ਰਿਟੇਲਰ ਫੇਸੀਆ ਅਨੁਸਾਰ ਜੁੜਾਅ ਦਰ",
  vizCapEngrate: "ਸਕੈਨ ਜੋ ਪੂਰੀ ਇੰਟਰੈਕਸ਼ਨ ਵਿੱਚ ਬਦਲਦੇ ਹਨ",
  vizEngrateCompare: "ਉਦਯੋਗ ਬੈਂਚਮਾਰਕ 42% ਦੇ ਮੁਕਾਬਲੇ +36 ਅੰਕ",
  vizCapGame: "ਸ਼ੌਪਰ ਕਿੱਥੇ ਛੱਡਦੇ ਹਨ — ਖੇਡ ਦਾ ਸੰਤੁਲਨ ਠੀਕ ਕਰੋ",
  vizGameStart: "ਸ਼ੁਰੂ", vizGamePlay: "ਖੇਡੋ", vizGameWin: "ਜਿੱਤ", vizGameClaim: "ਦਾਅਵਾ",
  vizCapRdm: "96% ਪੁਸ਼ਟੀ ਦਰ · ਘੱਟ ਮਿਆਦ-ਪੁੱਗਣਾ",
  vizRdmClaimed: "ਦਾਅਵਾ ਕੀਤੇ", vizRdmValidated: "ਪ੍ਰਮਾਣਿਤ", vizRdmExpired: "ਮਿਆਦ ਪੁੱਗ ਚੁੱਕੇ",
  vizCapRoi: "ਮੁਹਿੰਮ ਖਰਚ ਬਨਾਮ ਸ਼ੌਪਰ ਜੀਵਨ ਮੁੱਲ",
  vizRoiSpend: "ਖਰਚ", vizRoiValueLabel: "ਪ੍ਰਾਪਤ ਮੁੱਲ", vizRoiReturn: "3.4× ਵਾਪਸੀ",
  vizCapComp: "ਪ੍ਰਤੀ ਮੁਹਿੰਮ · ਪ੍ਰਤੀ ਗੱਲਬਾਤ · ਆਡਿਟ ਲਈ ਤਿਆਰ",
  vizCapAudit: "ਹਰ AI ਜਵਾਬ ਲੌਗ ਹੋਇਆ · ਮੰਗ 'ਤੇ ਉਪਲਬਧ",
  vizCapViolation: "ਇਸ ਮਹੀਨੇ ਉਲੰਘਣਾ · AI ਆਉਟਪੁੱਟ ਲਗਾਤਾਰ ਪ੍ਰਮਾਣਿਤ",
  vizCapNut: "ਪਹੀਏ 'ਤੇ ਸ਼ੌਪਰ ਜਿਨ੍ਹਾਂ ਪੋਸ਼ਕ ਤੱਤਾਂ ਨਾਲ ਇੰਟਰੈਕਟ ਕਰਦੇ ਹਨ",
  vizNutSugar: "ਖੰਡ", vizNutFat: "ਚਰਬੀ", vizNutSalt: "ਨਮਕ", vizNutSatFat: "ਸੰਤ੍ਰਿਪਤ",
  vizCapLang: "ਸ਼ੌਪਰ ਦੀ ਭਾਸ਼ਾ ਆਪਣੇ ਆਪ ਪਛਾਣੀ ਜਾਂਦੀ ਹੈ",
  vizLangOther: "ਹੋਰ",
  vizCapCrm: "ਪ੍ਰਾਪਤ ਸ਼ੌਪਰ",
  vizCapMap: "ਇਸ ਹਫ਼ਤੇ 247 ਥਾਵਾਂ",
  vizCapWeek: "ਔਸਤ 2.3 ਦੌਰੇ ਪ੍ਰਤੀ ਹਫ਼ਤਾ",
  vizDayMon: "ਸੋਮ", vizDayTue: "ਮੰਗਲ", vizDayWed: "ਬੁਧ",
  vizDayThu: "ਵੀਰ", vizDayFri: "ਸ਼ੁੱਕਰ", vizDaySat: "ਸ਼ਨਿ", vizDaySun: "ਐਤ",
  vizCapBasket: "ਔਸਤ ਬਾਸਕੇਟ: £8.40 · 6.2 ਆਈਟਮ",
  vizBasketDairy: "ਡੇਅਰੀ", vizBasketBakery: "ਬੇਕਰੀ", vizBasketSnacks: "ਸਨੈਕਸ", vizBasketOther: "ਹੋਰ",
};

// ────────── বাংলা ──────────
const bn: LandingCopy = {
  ...en,
  navPlatform: "প্ল্যাটফর্ম",
  navModules: "মডিউল",
  navPortal: "পোর্টাল লগইন",
  navLangButton: "BN",
  navRegister: "নিবন্ধন করুন",
  navBookDemo: "ডেমো বুক করুন",
  navLogin: "লগ ইন",

  trustLeft: "কোনো অ্যাপ নেই · কোনো ইন্টিগ্রেশন নেই · দোকানের উপর নির্ভরশীলতা নেই",
  trustRight: "২০২৪ থেকে · যুক্তরাজ্য · The Wholesale Group · Caterers Choice · Passot (জাপান) · Talysis UK · Jakavarna Group (থাইল্যান্ড) এর সঙ্গে নির্মাণ চলছে",

  heroEyebrow: "বাণিজ্যের জন্য কো-পাইলট",
  heroL1: "প্রতিটি পণ্য. একটি কথোপকথন.",
  heroL2: "প্রতিটি স্ক্যান. বাস্তব ডেটা.",
  heroSub: "TalkPack যেকোনো পণ্যকে লাইভ কথোপকথনে রূপান্তর করে। শপাররা স্ক্যান করে, শেখে, খেলে, প্রশ্ন করে, জিতে, পয়েন্ট জমায়। আপনি ফার্স্ট-পার্টি ডেটা, HFSS সম্মতি এবং বাস্তব রিটেইলে শপারদের সঙ্গে সরাসরি সম্পর্ক পান। কোনো অ্যাপ নেই। কোনো ইন্টিগ্রেশন নেই। দোকানের উপর নির্ভরশীলতা নেই।",
  heroCtaPrimary: "ডেমো বুক করুন",
  heroCtaGhost: "কীভাবে কাজ করে দেখুন",
  heroPillAllergens: "অ্যালার্জেন",
  heroPillHfss: "HFSS",
  heroPillIngredients: "উপাদান",
  heroVerified: "যাচাইকৃত",

  statStores: "দোকান · যুক্তরাজ্য · বিশ্বব্যাপী সম্প্রসারণ",
  statLanguages: "ভাষা · স্বয়ংক্রিয় শনাক্ত",
  statGames: "ব্রিফ থেকে লাইভ ক্যাম্পেইনে",
  statModules: "নিউট্রিশন হুইল অন্তর্ভুক্ত",
  statSteps: "আপনার ব্র্যান্ড. আপনার ডেটা. আপনার AI.",
  statDownloads: "অ্যাপ ডাউনলোড · ইন্টিগ্রেশন · ঘর্ষণ",
  statGeofence: "বাস্তব রিটেইল থেকে শপার ডেটা",
  statScans: "শেলফ থেকে শপার CRM-এ",

  fullJourneyEyebrow: "পূর্ণ যাত্রা",
  fullJourneyHeading: "প্রবেশ থেকে ইন্টেলিজেন্স. একটি প্ল্যাটফর্ম.",
  fullJourneySub: "প্রতিটি চ্যানেল. প্রতিটি মডিউল. প্রতিটি ডেটা পয়েন্ট. এভাবেই TalkPack আর্কিটেকচার পুরোপুরি যুক্ত হয়।",
  fullJourneyCaption: "পূর্ণ প্রবাহ. শপার প্রবেশ থেকে ব্র্যান্ড ইন্টেলিজেন্স. ড্যাশবোর্ড, AI চ্যাট বা SmartMail ক্যোয়েরির মাধ্যমে এক্সপোর্ট করা যায়।",

  picEyebrow: "Pic-n-Mix",
  picHeading: "একটি প্ল্যাটফর্ম. শপারের সঙ্গে যুক্ত হওয়ার পাঁচটি উপায়.",
  picSub: "ব্র্যান্ডগুলো প্রয়োজনীয় মডিউল বেছে নেয়। শপাররা অভিজ্ঞতার মিশ্রণ পায়। প্রতিটি মডিউল একই স্ক্যান, একই কথোপকথন, একই ডেটা স্তরের উপর চলে।",
  learnDesc: "পণ্য শিক্ষা, উপাদানে স্বচ্ছতা, HFSS ট্র্যাফিক লাইট.",
  learnF1: "নিউট্রিশন হুইল", learnF2: "অ্যালার্জেন স্ক্যান", learnF3: "উৎপত্তি ডেটা",
  playDesc: "পাঁচ ধরনের গেম যা থাকার সময় ও পুনঃযুক্ততা বাড়ায়.",
  playF1: "স্পিন হুইল", playF2: "গাচা", playF3: "পাজল", playF4: "কুইজ", playF5: "নিউট্রিশন স্পিন",
  sayDesc: "কথোপকথন-ভিত্তিক AI যা শপারের প্রশ্নের উত্তর তাদের ভাষায় দেয়.",
  sayF1: "৪০+ ভাষা", sayF2: "উপাদান Q&A", sayF3: "রেসিপি প্রস্তাব",
  winDesc: "পুরস্কার, ডিরেক্টেড ফুটফল, ক্যাশিয়ারে ডুয়াল-QR যাচাই, প্যাকিং স্লিপ.",
  winF1: "তাৎক্ষণিক পুরস্কার", winF2: "জিওফেন্সড পুরস্কার", winF3: "বাড়িতে ডেলিভারি",
  earnDesc: "পয়েন্ট, রসিদ স্ক্যান, লয়্যালটি প্রোগ্রাম, বাস্কেট ইন্টেলিজেন্স.",
  earnF1: "রসিদ OCR", earnF2: "ক্রস-ব্র্যান্ড প্রোফাইল", earnF3: "পুরস্কার ক্যাটালগ",
  moduleSeeMore: "মডিউল দেখুন",

  oeEyebrow: "একটি ইঞ্জিন · বহু গতিশীলতা",
  oeHeading: "TalkPack একটি বৃহত্তর প্ল্যাটফর্মের অংশ.",
  oeSub: "ShopAI ব্র্যান্ড, রিটেইলার এবং পাইকারিদের একটি ডেটা স্তর প্রদান করে। সম্পূর্ণ কথোপকথন-ভিত্তিক বিজনেস ইন্টেলিজেন্স। TalkPack হলো শপার যেভাবে কথোপকথনে যোগ দেয়।",
  oe1Strap: "বাস্তব রিটেইল থেকে শপার এনগেজমেন্ট",
  oe2Strap: "কনভেনিয়েন্স খাতের জন্য AI",
  oe3Strap: "ফিল্ড অডিট ইন্টেলিজেন্স",
  oe4Strap: "পয়েন্ট, রসিদ, বাস্কেট ডেটা",
  oeActiveLabel: "বর্তমান",

  ucHeading: "ভিন্ন ভূমিকা. একই প্ল্যাটফর্ম.",
  ucBrandTitle: "ব্র্যান্ড মার্কেটার",
  ucBrand1: "মিশ্র রিটেইলে জাতীয় পণ্য লঞ্চ",
  ucBrand2: "অংশগ্রহণকারী দোকানে ডিরেক্টেড ফুটফল",
  ucBrand3: "HFSS-সম্মত অন-প্যাক এনগেজমেন্ট",
  ucBrand4: "ক্রস-ব্র্যান্ড শপার প্রোফাইলিং",
  ucBrand5: "নিয়ন্ত্রিত ক্যাটাগরি (ফার্মা, অ্যালকোহল) অডিট-লগড AI প্রতিক্রিয়া সহ",
  ucRetailerTitle: "রিটেইলার / পাইকারি",
  ucRetailer1: "রিটেইলার-অর্থায়িত একচেটিয়া প্রমোশন",
  ucRetailer2: "সিম্বল গ্রুপ অ্যাক্টিভেশন",
  ucRetailer3: "শেলফ সম্মতি অডিট",
  ucRetailer4: "রিটেইলার-নির্দিষ্ট লয়্যালটি",
  ucAgencyTitle: "এজেন্সি / রিসেলার",
  ucAgency1: "ক্লায়েন্ট ব্র্যান্ডের জন্য হোয়াইট-লেবেল ক্যাম্পেইন",
  ucAgency2: "মাল্টি-ক্লায়েন্ট পোর্টাল অ্যাক্সেস",
  ucAgency3: "ট্রেড মার্কেটিং PDF রিপোর্ট",
  ucAgency4: "স্বয়ংক্রিয়ভাবে তৈরি সোশ্যাল কনটেন্ট",

  hfssEyebrow: "HFSS-এর জন্য নির্মিত",
  hfssHeading: "HFSS সম্মতি, অন্তর্ভুক্ত.",
  hfssSub: "শপার খেলার আগে পুষ্টি প্রোফাইল দেখে। চর্বি, চিনি, লবণ, স্যাচুরেটেড ফ্যাট — দৈনিক রেফারেন্স গ্রহণের বিরুদ্ধে ট্র্যাফিক লাইট। বাধ্যতামূলক. অডিট-প্রস্তুত. নিয়ন্ত্রক প্রতিরক্ষার চারটি স্তর.",
  hfss1Title: "বাধ্যতামূলক নিউট্রিশন হুইল.",
  hfss1Body: "প্রতিটি HFSS পণ্য শপার এগিয়ে যাওয়ার আগে দৈনিক রেফারেন্স গ্রহণের বিরুদ্ধে চর্বি, চিনি, লবণ ও স্যাচুরেটেড ফ্যাটের ট্র্যাফিক লাইট দেখায়.",
  hfss2Title: "শিক্ষা-প্রথম ফ্রেমিং.",
  hfss2Body: "প্ল্যাটফর্ম পুষ্টির স্বচ্ছতাকে উৎসাহিত করে। শপার যোগাযোগকে অবগত পছন্দ হিসেবে স্থাপন করে, প্রচারমূলক চাপ হিসেবে নয়.",
  hfss3Title: "আউটপুট যাচাই.",
  hfss3Body: "কথোপকথন-ভিত্তিক AI নিয়ন্ত্রক প্যাটার্নের জন্য পর্যবেক্ষণ করা হয়। প্রতিটি প্রতিক্রিয়া লগ, ফিল্টার এবং অডিটযোগ্য.",
  hfss4Title: "এক্সপোর্টযোগ্য অডিট লগ.",
  hfss4Body: "ASA সম্মতি স্কোর, প্রতিটি প্রতিক্রিয়া লগড ও পুনরুদ্ধারযোগ্য, ফ্ল্যাগ করা সামগ্রীর জন্য লঙ্ঘন সতর্কতা.",
  hfssBadge: "ASA সম্মতি: ৯৮.৪%",

  wlEyebrow: "আপনার ব্র্যান্ড, যেকোনো ভাষায়",
  wlHeading: "আপনার ডেটা. আপনার AI. আপনার পথে.",
  wlLangCaption: "৪০+ ভাষা. স্বয়ংক্রিয় শনাক্ত বা প্রতি ক্যাম্পেইনে ম্যানুয়ালি সেট.",
  wlFeat1Title: "আপনার ব্র্যান্ড পরিচয়",
  wlFeat1Body: "লোগো, রঙ, ফন্ট প্রতিটি শপার স্ক্রিন ও প্যাকিং স্লিপে প্রয়োগ.",
  wlFeat2Title: "আপনার ডেটা, আপনার AI",
  wlFeat2Body: "ডেটা আপনার অ্যাকাউন্টের অধীনে সংরক্ষিত। AI আপনার পণ্যে প্রশিক্ষিত। সম্পর্ক আপনার.",
  wlFeat3Title: "আপনার দোকান, আপনার জিওফেন্স",
  wlFeat3Body: "সর্বজনীন মোড বা দোকান-নির্দিষ্ট। ডিফল্ট ১৫০ মিটার জিওফেন্স, প্রতি ক্যাম্পেইনে কনফিগারযোগ্য.",
  wlFeat4Title: "আপনার দল, আপনার অ্যাক্সেস",
  wlFeat4Body: "ভূমিকা-ভিত্তিক অনুমতি। ব্র্যান্ড, এজেন্সি, ফিল্ড, ফিন্যান্স — প্রত্যেকের জন্য সঠিক ভিউ.",

  proofEyebrow: "মোতায়েনে",
  proofHeading: "ইতিমধ্যে লাইভ. ইতিমধ্যে কাজ করছে.",
  proofStat1Label: "ক্যাম্পেইন ডেটাবেসে যুক্তরাজ্যের দোকান",
  proofStat2Label: "সমর্থিত ভাষা",
  proofStat3Label: "যুক্তরাজ্যে প্রতিষ্ঠিত",
  proofQuotePlaceholder: "গ্রাহকের গল্প শীঘ্রই আসছে.",

  faqHeading: "ব্র্যান্ডরা আমাদের যে প্রশ্নগুলো করে.",
  faq1Q: "শপারদের কি অ্যাপ ডাউনলোড করতে হবে?",
  faq1A: "না. TalkPack ব্রাউজারে চলে. একটি স্ক্যান অভিজ্ঞতা খোলে.",
  faq2Q: "TalkPack কি আমাদের EPOS বা CRM-এর সাথে ইন্টিগ্রেট হয়?",
  faq2A: "কোনো ইন্টিগ্রেশন প্রয়োজন নেই. সংগৃহীত ডেটা ফার্স্ট-পার্টি এবং PDF, CSV বা API-এর মাধ্যমে এক্সপোর্টযোগ্য.",
  faq3Q: "HFSS পণ্যের ক্ষেত্রে কী হয়?",
  faq3A: "প্রতিটি HFSS পণ্য খেলার আগে বাধ্যতামূলক নিউট্রিশন হুইল দেখায়. সম্মতি অন্তর্ভুক্ত, পরে যুক্ত নয়.",
  faq4Q: "আপনি কোন ভাষাগুলো সমর্থন করেন?",
  faq4A: "৪০+. প্ল্যাটফর্ম স্বয়ংক্রিয়ভাবে শনাক্ত করে অথবা আপনি প্রতি ক্যাম্পেইনে সেট করেন.",
  faq5Q: "আমরা কি শুধু নির্দিষ্ট রিটেইলারে ক্যাম্পেইন চালাতে পারি?",
  faq5A: "হ্যাঁ. দোকান-নির্দিষ্ট মোড নির্বাচিত দোকানে জিওফেন্স করে. সর্বজনীন মোডে যেকোনো শপার যেকোনো জায়গায় খেলতে পারে.",
  faq6Q: "ক্যাম্পেইন তৈরিতে কত সময় লাগে?",
  faq6A: "মানক প্রমোশনের জন্য প্রায় ১০ মিনিট. পোর্টালে ধাপে ধাপে উইজার্ড.",
  faq7Q: "আমরা কি অভিজ্ঞতাকে হোয়াইট-লেবেল করতে পারি?",
  faq7A: "হ্যাঁ. আপনার লোগো, আপনার রঙ, আপনার ভাষা. শপাররা TalkPack ব্র্যান্ডিং দেখবে না যতক্ষণ না আপনি চান.",

  footerColPlatform: "প্ল্যাটফর্ম",
  footerColModules: "মডিউল",
  footerColCompany: "কোম্পানি",
  footerColResources: "রিসোর্স",
  footerCompany1: "সম্পর্কে",
  footerCompany2: "যোগাযোগ",
  footerResources3: "গোপনীয়তা",
  footerResources4: "শর্তাবলি",
  footerNewsHeading: "ত্রৈমাসিক আপডেট. কোনো স্প্যাম নেই.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "সাবস্ক্রাইব",
  footerRegistration: "ShopAI Ltd · কোম্পানি নং 15877093 · ইংল্যান্ডে নিবন্ধিত",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (BN) ──
  heroAlt: "সুপারমার্কেটের গলিতে ক্রেতা — TalkPack অভিজ্ঞতার প্রতিটি টাচপয়েন্ট তাকে ঘিরে আবর্তিত হচ্ছে",

  stepsTitle: "তিনটি ধাপ. একটি প্ল্যাটফর্ম.",
  stepsSub: "যে মুহূর্তে শপার প্রবেশ করেন সেই মুহূর্ত থেকে যখন আপনি তাঁর সম্পর্কে কিছু শেখেন. পরিচালনায় সহজ. তথ্যে সমৃদ্ধ.",
  step1Title: "প্রবেশ",
  step1Desc: "শপার যেকোনো চ্যানেল থেকে ট্যাপ বা স্ক্যান করেন. একটি প্রবেশপথ, একটি ব্র্যান্ডেড অভিজ্ঞতা.",
  step1PillRow: "ওয়েবসাইট · সোশ্যাল · ইমেল · QR · OOH · দোকানে",
  step2Title: "যুক্ত হন",
  step2Desc: "পাঁচটি Pic-n-Mix মডিউল তাঁদের ভাষায়, আপনার ব্র্যান্ডে, AI দ্বারা যাচাই করা হয়ে চলে.",
  step2PillRow: "শিখুন · খেলুন · জিজ্ঞাসা · জিতুন · উপার্জন",
  step3Title: "শিখুন",
  step3Desc: "প্রতিটি ইন্টারঅ্যাকশন first-party তথ্য হয়ে ওঠে, রিয়েল-টাইমে আপনার ড্যাশবোর্ডে.",
  step3PillRow: "শপার · কথোপকথন · অবস্থান · যুক্ততা · সম্মতি",

  chipWebsite: "ওয়েবসাইট",
  chipSocial:  "সোশ্যাল",
  chipEmail:   "ইমেল",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "দোকানে",
  chipLearn:   "শিখুন",
  chipPlay:    "খেলুন",
  chipSay:     "জিজ্ঞাসা",
  chipWin:     "জিতুন",
  chipEarn:    "উপার্জন",
  chipShoppers:     "শপার",
  chipConversation: "কথোপকথন",
  chipLocation:     "অবস্থান",
  chipEngagement:   "যুক্ততা",
  chipCompliance:   "সম্মতি",
  chipAriaEntry:  "প্রবেশ চ্যানেল",
  chipAriaModule: "Pic-n-Mix মডিউল",
  chipAriaIntel:  "ইন্টেলিজেন্স বিভাগ",

  intgTitle: "গুরুতর অবকাঠামো. প্রমাণিত প্রযুক্তি.",
  intgSub: "TalkPack বিশ্বের বৃহত্তম কোম্পানিগুলো যেগুলিতে নির্ভর করে সেই একই ক্লাউড, AI এবং সম্মতির ভিত্তিতে চলে.",
  intgCap1: "মূল প্ল্যাটফর্ম",
  intgCap2: "ক্লাউড অবকাঠামো",
  intgCap3: "কথোপকথন AI",
  intgCap4: "ভাষা মডেল",
  intgCap5: "AI এবং ম্যাপিং",
  intgCap6: "পুষ্টি তথ্য",
  intgCap7: "পেমেন্ট",
  intgFootnote: "ISO 27001 প্রত্যয়িত প্রদানকারী · GDPR-সম্মত · বহু-অঞ্চল হোস্টিং · প্রথম দিন থেকে এন্টারপ্রাইজ-গ্রেড নিরাপত্তা",

  jColEntry: "প্রবেশ",
  jColExperience: "অভিজ্ঞতা",
  jColDashboard: "ড্যাশবোর্ড ইন্টেলিজেন্স",
  jEntryDigitalTitle: "ডিজিটাল",
  jEntryDigitalDesc: "ওয়েবসাইট, অ্যাপ স্টোর, অনুসন্ধান, সরাসরি লিঙ্ক — যেকোনো ডিজিটাল পৃষ্ঠ থেকে শপার অভিজ্ঞতায় এক ট্যাপ.",
  jEntrySocialTitle: "সোশ্যাল",
  jEntrySocialDesc: "স্পন্সরড পোস্ট, ইনফ্লুয়েন্সার কনটেন্ট, DM — ফিডগুলি প্ল্যাটফর্ম না ছেড়ে সরাসরি TalkPack-এ প্রবাহিত হয়.",
  jEntryPhysicalTitle: "ভৌত",
  jEntryPhysicalDesc: "প্যাকে, শেলফে, POS-এ — বাস্তব দুনিয়ার প্রবেশ বিন্দু যেখানে TalkPack একটি স্ক্যানকে কথোপকথনে রূপান্তরিত করে.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "দোকান POS",
  jEntryPhysPill3: "প্যাকেজিং",
  jExpVerifyLabel: "যাচাই",
  jExpVerifyText: "AI পণ্য সনাক্তকরণ — SKU রিয়েল-টাইমে যাচাই.",
  jExpLearnLabel: "শিখুন",
  jExpLearnBody: "পুষ্টি চাকা, অ্যালার্জেন, উপাদান, উৎস — শপারের ভাষায় দেখানো হয়, ব্র্যান্ড দ্বারা যাচাই.",
  jExpEngageLabel: "যুক্ত হন",
  jExpEngagePlayBody: "স্পিন, গাচা, ধাঁধা, কুইজ — থাকার সময়ের জন্য পাঁচটি গেম ধরন.",
  jExpEngageEarnBody: "পয়েন্ট, রসিদ, লয়্যালটি — বাস্কেট-স্তরের ইন্টেলিজেন্স.",
  jExpEngageSayBody: "কথোপকথন AI 40+ ভাষায় উত্তর দেয়.",
  jExpRewardLabel: "পুরস্কার",
  jExpRewardBody: "তাত্ক্ষণিক পুরস্কার, টিল-এ ডুয়াল-QR যাচাই, GPS-যাচাইকৃত দাবি — প্রথম দিন থেকে অডিটের জন্য প্রস্তুত.",
  jExpCaptureLabel: "ক্যাপচার",
  jExpCaptureSub: "প্রতিটি ইন্টারঅ্যাকশন · আপনার দ্বারা ক্যাপচার এবং মালিকানাধীন",

  capEmail:   "ইমেল",
  capName:    "নাম",
  capAddress: "ঠিকানা",
  capGps:     "GPS",
  capProduct: "পণ্য",
  capBasket:  "বাস্কেট",
  capSpend:   "খরচ",
  capChat:    "চ্যাট",

  dashShopperLabel: "শপার ইন্টেলিজেন্স",
  dashShopperShort: "শপার",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "ইমেল, নাম, ঠিকানা, যোগাযোগ পছন্দ.",
  dashShopper2Title: "GPS-যাচাইকৃত অবস্থান",
  dashShopper2Desc: "দোকান অঞ্চল অনুযায়ী শপাররা কোথায় যুক্ত হন.",
  dashShopper3Title: "ক্রয় ফ্রিকোয়েন্সি",
  dashShopper3Desc: "পুনরাবৃত্ত স্ক্যান প্যাটার্ন, পরিদর্শনের ছন্দ.",
  dashShopper4Title: "বাস্কেট গঠন",
  dashShopper4Desc: "আইটেম-পর্যায়ের রসিদ তথ্য, সহ-ক্রয়কৃত পণ্য.",

  dashConvLabel: "কথোপকথন ইন্টেলিজেন্স",
  dashConvShort: "কথোপকথন",
  dashConv1Title: "প্রশ্ন বিভাগ",
  dashConv1Desc: "অ্যালার্জেন, উপাদান, পুষ্টি, ব্যবহার, রেসিপি.",
  dashConv2Title: "অনুভূতি বিশ্লেষণ",
  dashConv2Desc: "পণ্য এবং অঞ্চল অনুযায়ী ইতিবাচক, নেতিবাচক, নিরপেক্ষ.",
  dashConv3Title: "বিভ্রান্তির পয়েন্ট",
  dashConv3Desc: "শপাররা যা বোঝেন না, বারবার করা প্রশ্ন.",
  dashConv4Title: "ক্রয়ের অভিপ্রায়",
  dashConv4Desc: "কৌতূহল থেকে রিটেলার অনুরোধ পর্যন্ত অগ্রগতি.",
  dashConv5Title: "ভাষার পছন্দ",
  dashConv5Desc: "অবস্থান অনুযায়ী শপাররা কোন ভাষা বেছে নেন.",

  dashDistLabel: "বিতরণ ইন্টেলিজেন্স",
  dashDistShort: "বিতরণ",
  dashDist1Title: "দোকান প্রাপ্যতা",
  dashDist1Desc: "শেলফে পণ্য, geofence স্ক্যান দ্বারা নিশ্চিত.",
  dashDist2Title: "স্টক-আউট সতর্কতা",
  dashDist2Desc: "'এখানে নেই' রিপোর্ট রিয়েল-টাইমে চিহ্নিত.",
  dashDist3Title: "হিটম্যাপ",
  dashDist3Desc: "পিন কোড, অঞ্চল, ফেসিয়া অনুযায়ী স্ক্যান ঘনত্ব.",
  dashDist4Title: "গৃহস্থালি অনুপ্রবেশ",
  dashDist4Desc: "'যেখানে সেখানে স্ক্যান' দেখায় পণ্য বাড়িতে কোথায় থাকে.",
  dashDist5Title: "দোকান তুলনা",
  dashDist5Desc: "অবস্থান, ফেসিয়া গোষ্ঠী অনুযায়ী যুক্ততা হার.",

  dashCampLabel: "ক্যাম্পেইন কর্মক্ষমতা",
  dashCampShort: "ক্যাম্পেইন",
  dashCamp1Title: "স্ক্যান-থেকে-যুক্ততা হার",
  dashCamp1Desc: "কতগুলি স্ক্যান সম্পূর্ণ ইন্টারঅ্যাকশনে রূপান্তরিত হয়.",
  dashCamp2Title: "গেম সমাপ্তির হার",
  dashCamp2Desc: "প্রতি সেশনে স্পিন, জয়ের হার, ড্রপ-অফ পয়েন্ট.",
  dashCamp3Title: "রিডেমপশন ট্র্যাকিং",
  dashCamp3Desc: "দোকান অনুযায়ী দাবিকৃত, যাচাইকৃত, মেয়াদোত্তীর্ণ পুরস্কার.",
  dashCamp4Title: "ROI ড্যাশবোর্ড",
  dashCamp4Desc: "ক্যাম্পেইন খরচ বনাম ক্যাপচার করা শপার মান.",

  dashCompLabel: "সম্মতি ও অডিট",
  dashCompShort: "সম্মতি",
  dashComp1Title: "ASA সম্মতি স্কোর",
  dashComp1Desc: "প্রতি ক্যাম্পেইন, প্রতি কথোপকথন.",
  dashComp2Title: "রপ্তানিযোগ্য অডিট লগ",
  dashComp2Desc: "প্রতিটি AI প্রতিক্রিয়া লগ এবং পুনরুদ্ধারযোগ্য.",
  dashComp3Title: "লঙ্ঘন সতর্কতা",
  dashComp3Desc: "চিহ্নিত কনটেন্ট প্যাটার্ন.",
  dashComp4Title: "পুষ্টি যুক্ততা",
  dashComp4Desc: "চাকায় শপাররা কোন পুষ্টির সাথে ইন্টারঅ্যাক্ট করেন.",

  pvFormTitle: "অভিনন্দন!",
  pvFormSub: "আপনি জিতেছেন: পরবর্তী কেনাকাটায় 20% ছাড়",
  pvFormName: "আপনার নাম",
  pvFormEmail: "আপনার ইমেল",
  pvFormPostcode: "পিন কোড",
  pvFormCta: "পুরস্কার নিন",
  pvFormFootnote: "Privacy-first · GDPR-সম্মত",

  pvChatTitle: "AI অ্যাম্বাসাডর",
  pvChatSub: "Morning O's-এ প্রশিক্ষিত",
  pvChatBubble1: "এটা কি গ্লুটেন-মুক্ত?",
  pvChatBubble2: "হ্যাঁ — প্রত্যয়িত গ্লুটেন-মুক্ত এবং প্রতি পরিবেশনায় মাত্র 9g চিনি. রেসিপির আইডিয়া কি দেখতে চান?",
  pvChatBubble3: "হ্যাঁ অনুগ্রহ করে",
  pvChatBubble4: "বেরিসহ ওভারনাইট ওটস চেষ্টা করুন — 3 মিনিট প্রস্তুতি.",
  pvChatPlaceholder: "আমাকে যেকোনো কিছু জিজ্ঞাসা করুন…",
  pvChatFootnote: "40+ ভাষা · সর্বদা সক্রিয়",

  pvMapTitle: "আপনার কাছাকাছি Morning O's খুঁজুন",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "দিকনির্দেশনা",
  pvMapFootnote: "GPS-যাচাইকৃত · 150m-এর মধ্যে geofenced",
  pvMapOos: "স্টকে নেই",

  pvWheelTitle: "Morning O's · ঘুরিয়ে জিতুন",
  pvWheelCredits: "350 ক্রেডিট",
  pvWheelCta: "ঘুরান",
  pvWheelFootnote: "স্পিন চাকা · পুষ্টি চাকা · গাচা · কুইজ · ধাঁধা",
  pvWheelWedgeWin: "জিত!",
  pvWheelWedgeTry: "আবার",

  pvNutTitle: "Morning O's · পুষ্টি তথ্য",
  pvNutPer100g: "প্রতি 100g",
  pvNutPerServing: "প্রতি পরিবেশনায়",
  pvNutCta: "চালিয়ে যান",
  pvNutFootnote: "ASA-সম্মত · খেলার আগে বাধ্যতামূলক",

  bookDemoSubject: "TalkPack ডেমো অনুরোধ",
  bookDemoBody: "নমস্কার ShopAI দল,\n\nআমি TalkPack-এর একটি ডেমো বুক করতে চাই. সময় নির্ধারণ করতে অনুগ্রহ করে যোগাযোগ করুন.\n\nধন্যবাদ,",

  // ── Viz mock-data labels (BN) ──
  vizCapQuestions: "এই সপ্তাহের শপারদের প্রধান প্রশ্ন",
  vizQAllergens: "অ্যালার্জেন", vizQIngredients: "উপাদান", vizQNutrition: "পুষ্টি",
  vizQRecipes: "রেসিপি", vizQUsage: "ব্যবহার",
  vizCapSentiment: "সমস্ত পণ্যের গত 30 দিন",
  vizSentPositive: "ইতিবাচক", vizSentNeutral: "নিরপেক্ষ", vizSentNegative: "নেতিবাচক",
  vizCapConfusion: "বারবার করা প্রশ্ন — কনটেন্টে ফাঁক",
  vizConfusion1: "এটা কি গ্লুটেন-মুক্ত?",
  vizConfusion2: "ভেগানদের জন্য উপযুক্ত?",
  vizConfusion3: "বাদাম সম্পর্কে সতর্কতা?",
  vizCapFunnel: "কথোপকথনের মাধ্যমে কনভার্সন",
  vizFunnelCurious: "কৌতূহলী", vizFunnelInterested: "আগ্রহী", vizFunnelReady: "কিনতে প্রস্তুত",
  vizCapAvail: "যেখানে আপনি পরিকল্পনা করেছেন সেখানে পণ্য উপলব্ধ",
  vizAvailDetail: "1,326-এর মধ্যে 1,247 দোকানে শেলফে নিশ্চিত",
  vizCapOos: "আজ 3টি দোকান চিহ্নিত · অটো-অ্যালার্ট পাঠানো হয়েছে",
  vizCapHeat: "লন্ডন · ম্যানচেস্টার · বার্মিংহাম এগিয়ে",
  vizCapHousehold: "68% স্ক্যান বাড়িতে হয় — আপনার পণ্য সেখানে বাস করে",
  vizCapFascia: "রিটেলার ফেসিয়া অনুযায়ী যুক্ততা হার",
  vizCapEngrate: "যে স্ক্যানগুলি পূর্ণ ইন্টারঅ্যাকশনে রূপান্তরিত হয়",
  vizEngrateCompare: "শিল্প বেঞ্চমার্ক 42%-এর তুলনায় +36 পয়েন্ট",
  vizCapGame: "শপাররা কোথায় ছেড়ে যায় — গেম ব্যালেন্স টিউন করুন",
  vizGameStart: "শুরু", vizGamePlay: "খেলুন", vizGameWin: "জিতুন", vizGameClaim: "দাবি",
  vizCapRdm: "96% যাচাই হার · কম মেয়াদোত্তীর্ণ",
  vizRdmClaimed: "দাবি করা", vizRdmValidated: "যাচাই করা", vizRdmExpired: "মেয়াদোত্তীর্ণ",
  vizCapRoi: "ক্যাম্পেইন খরচ বনাম শপার লাইফটাইম মান",
  vizRoiSpend: "খরচ", vizRoiValueLabel: "ক্যাপচার করা মান", vizRoiReturn: "3.4× রিটার্ন",
  vizCapComp: "প্রতি ক্যাম্পেইন · প্রতি কথোপকথন · অডিটের জন্য প্রস্তুত",
  vizCapAudit: "প্রতিটি AI প্রতিক্রিয়া লগ · চাহিদা মতো পুনরুদ্ধারযোগ্য",
  vizCapViolation: "এ মাসে লঙ্ঘন · AI আউটপুট ক্রমাগত যাচাই হচ্ছে",
  vizCapNut: "চাকায় শপাররা যে পুষ্টির সাথে ইন্টারঅ্যাক্ট করেন",
  vizNutSugar: "চিনি", vizNutFat: "ফ্যাট", vizNutSalt: "লবণ", vizNutSatFat: "সম্পৃক্ত",
  vizCapLang: "শপারের ভাষা স্বয়ংক্রিয়ভাবে শনাক্ত",
  vizLangOther: "অন্যান্য",
  vizCapCrm: "ক্যাপচার করা শপার",
  vizCapMap: "এ সপ্তাহে 247টি অবস্থান",
  vizCapWeek: "প্রতি সপ্তাহে গড়ে 2.3টি ভিজিট",
  vizDayMon: "সোম", vizDayTue: "মঙ্গল", vizDayWed: "বুধ",
  vizDayThu: "বৃহঃ", vizDayFri: "শুক্র", vizDaySat: "শনি", vizDaySun: "রবি",
  vizCapBasket: "গড় বাস্কেট: £8.40 · 6.2টি আইটেম",
  vizBasketDairy: "দুগ্ধ", vizBasketBakery: "বেকারি", vizBasketSnacks: "স্ন্যাকস", vizBasketOther: "অন্যান্য",
};

// ────────── தமிழ் ──────────
const ta: LandingCopy = {
  ...en,
  navPlatform: "தளம்",
  navModules: "தொகுதிகள்",
  navPortal: "போர்ட்டல் உள்நுழைவு",
  navLangButton: "TA",
  navRegister: "பதிவு செய்க",
  navBookDemo: "டெமோ முன்பதிவு",
  navLogin: "உள்நுழைய",

  trustLeft: "ஆப் இல்லை · ஒருங்கிணைப்பு இல்லை · கடை சார்பு இல்லை",
  trustRight: "2024 முதல் · ஐக்கிய இராச்சியம் · The Wholesale Group · Caterers Choice · Passot (ஜப்பான்) · Talysis UK · Jakavarna Group (தாய்லாந்து) உடன் கட்டமைப்பு",

  heroEyebrow: "வணிகத்திற்கான இணை-ஓட்டுநர்",
  heroL1: "ஒவ்வொரு பொருள். ஒரு உரையாடல்.",
  heroL2: "ஒவ்வொரு ஸ்கேன். உண்மையான தரவு.",
  heroSub: "TalkPack எந்தவொரு பொருளையும் நேரடி உரையாடலாக மாற்றுகிறது. ஷாப்பர்கள் ஸ்கேன் செய்கிறார்கள், கற்றுக்கொள்கிறார்கள், விளையாடுகிறார்கள், கேட்கிறார்கள், வெல்கிறார்கள், சம்பாதிக்கிறார்கள். உங்களுக்கு first-party தரவு, HFSS இணக்கம், மற்றும் இயற்பியல் சில்லறை விற்பனையில் ஷாப்பர்களுடன் நேரடி உறவு கிடைக்கும். ஆப் இல்லை. ஒருங்கிணைப்பு இல்லை. கடை சார்பு இல்லை.",
  heroCtaPrimary: "டெமோ முன்பதிவு",
  heroCtaGhost: "எவ்வாறு செயல்படுகிறது",
  heroPillAllergens: "ஒவ்வாமைகள்",
  heroPillHfss: "HFSS",
  heroPillIngredients: "மூலப்பொருட்கள்",
  heroVerified: "சரிபார்க்கப்பட்டது",

  statStores: "கடைகள் · UK · உலகளாவிய விரிவாக்கம்",
  statLanguages: "மொழிகள் · தானாக கண்டறிதல்",
  statGames: "சுருக்கத்திலிருந்து நேரடி பிரச்சாரம் வரை",
  statModules: "ஊட்டச்சத்து சக்கரம் உள்ளமைந்துள்ளது",
  statSteps: "உங்கள் பிராண்ட். உங்கள் தரவு. உங்கள் AI.",
  statDownloads: "ஆப் பதிவிறக்கங்கள் · ஒருங்கிணைப்புகள் · உராய்வு",
  statGeofence: "இயற்பியல் சில்லறை விற்பனையிலிருந்து ஷாப்பர் தரவு",
  statScans: "அலமாரியிலிருந்து ஷாப்பர் CRM வரை",

  fullJourneyEyebrow: "முழு பயணம்",
  fullJourneyHeading: "நுழைவிலிருந்து புத்திசாலித்தனத்திற்கு. ஒரு தளம்.",
  fullJourneySub: "ஒவ்வொரு சேனலும். ஒவ்வொரு தொகுதியும். ஒவ்வொரு தரவு புள்ளியும். TalkPack கட்டமைப்பு முழுவதும் இப்படியே இணைகிறது.",
  fullJourneyCaption: "முழு ஓட்டம். ஷாப்பர் நுழைவிலிருந்து பிராண்ட் புத்திசாலித்தனம் வரை. டாஷ்போர்டு, AI அரட்டை அல்லது SmartMail வினவல்கள் வழியாக ஏற்றுமதி.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "ஒரு தளம். ஷாப்பருடன் ஈடுபடுவதற்கு ஐந்து வழிகள்.",
  picSub: "பிராண்டுகள் தங்களுக்குத் தேவையான தொகுதிகளைத் தேர்ந்தெடுக்கின்றன. ஷாப்பர்கள் அனுபவங்களின் கலவையைப் பெறுகிறார்கள். ஒவ்வொரு தொகுதியும் அதே ஸ்கேன், அதே உரையாடல், அதே தரவு அடுக்கின் வழியாகச் செல்கிறது.",
  learnDesc: "தயாரிப்பு கல்வி, மூலப்பொருள் வெளிப்படைத்தன்மை, HFSS டிராஃபிக் விளக்குகள்.",
  learnF1: "ஊட்டச்சத்து சக்கரம்", learnF2: "ஒவ்வாமை ஸ்கேன்", learnF3: "வம்சாவளி தரவு",
  playDesc: "தங்கி இருக்கும் நேரத்தையும் மீண்டும் ஈடுபாட்டையும் அதிகரிக்கும் ஐந்து வகை விளையாட்டுகள்.",
  playF1: "ஸ்பின் வீல்", playF2: "காச்சா", playF3: "புதிர்", playF4: "வினாடி வினா", playF5: "ஊட்டச்சத்து ஸ்பின்",
  sayDesc: "ஷாப்பர் கேள்விகளை அவர்களின் சொந்த மொழியில் பதிலளிக்கும் உரையாடல் AI.",
  sayF1: "40+ மொழிகள்", sayF2: "மூலப்பொருள் கேள்வி-பதில்", sayF3: "சமையல் பரிந்துரைகள்",
  winDesc: "பரிசுகள், கடைக்கு வழிநடத்தல், கேஷியரில் இரட்டை-QR சரிபார்ப்பு, பேக்கிங் சீட்டுகள்.",
  winF1: "உடனடி வெகுமதி", winF2: "புவிவேலி பரிசுகள்", winF3: "வீட்டுக்கு டெலிவரி",
  earnDesc: "புள்ளிகள், ரசீது ஸ்கேன், விசுவாசத் திட்டம், கூடை புத்திசாலித்தனம்.",
  earnF1: "ரசீது OCR", earnF2: "குறுக்கு-பிராண்ட் சுயவிவரம்", earnF3: "வெகுமதி பட்டியல்",
  moduleSeeMore: "தொகுதியைக் காண்க",

  oeEyebrow: "ஒரு இயந்திரம் · பல இயக்கங்கள்",
  oeHeading: "TalkPack ஒரு பெரிய தளத்தின் ஒரு பகுதியாகும்.",
  oeSub: "ShopAI பிராண்டுகள், சில்லறை வியாபாரிகள் மற்றும் மொத்த விற்பனையாளர்களுக்கு ஒரே தரவு அடுக்கை வழங்குகிறது. முழுமையான உரையாடல் வணிக புத்திசாலித்தனம். TalkPack ஷாப்பர் உரையாடலில் சேரும் வழியாகும்.",
  oe1Strap: "இயற்பியல் சில்லறை விற்பனையிலிருந்து ஷாப்பர் ஈடுபாடு",
  oe2Strap: "வசதியான துறைக்கான AI",
  oe3Strap: "களம் ஆடிட் புத்திசாலித்தனம்",
  oe4Strap: "புள்ளிகள், ரசீதுகள், கூடை தரவு",
  oeActiveLabel: "தற்போதைய",

  ucHeading: "வெவ்வேறு பாத்திரங்கள். அதே தளம்.",
  ucBrandTitle: "பிராண்ட் சந்தைப்படுத்துநர்",
  ucBrand1: "கலப்பு சில்லறை விற்பனையில் தேசிய தயாரிப்பு வெளியீடு",
  ucBrand2: "பங்குபெறும் கடைகளுக்கு வழிநடத்தப்பட்ட நடமாட்டம்",
  ucBrand3: "HFSS-இணக்கமான ஆன்-பேக் ஈடுபாடு",
  ucBrand4: "குறுக்கு-பிராண்ட் ஷாப்பர் சுயவிவரம்",
  ucBrand5: "ஒழுங்குபடுத்தப்பட்ட வகைகள் (மருந்து, மது) ஆடிட்-பதிவு AI பதில்களுடன்",
  ucRetailerTitle: "சில்லறை விற்பனையாளர் / மொத்தம்",
  ucRetailer1: "சில்லறை விற்பனையாளர் நிதியளிக்கப்பட்ட பிரத்யேக விளம்பரங்கள்",
  ucRetailer2: "சிம்பல் குழு செயல்படுத்தல்",
  ucRetailer3: "அலமாரி இணக்க ஆடிட்",
  ucRetailer4: "சில்லறை விற்பனையாளர்-குறிப்பிட்ட விசுவாசம்",
  ucAgencyTitle: "ஏஜென்சி / மறுவிற்பனையாளர்",
  ucAgency1: "வாடிக்கையாளர் பிராண்டுகளுக்கான வொய்ட்-லேபிள் பிரச்சாரங்கள்",
  ucAgency2: "பல-வாடிக்கையாளர் போர்ட்டல் அணுகல்",
  ucAgency3: "வர்த்தக சந்தைப்படுத்தல் PDF அறிக்கைகள்",
  ucAgency4: "தானாக உருவாக்கப்பட்ட சமூக உள்ளடக்கம்",

  hfssEyebrow: "HFSS-க்காக கட்டமைக்கப்பட்டது",
  hfssHeading: "HFSS இணக்கம், உள்ளமைந்தது.",
  hfssSub: "ஷாப்பர் விளையாடுவதற்கு முன்பு ஊட்டச்சத்து சுயவிவரத்தைப் பார்க்கிறார். கொழுப்பு, சர்க்கரை, உப்பு, நிறைவுற்ற கொழுப்பு — தினசரி குறிப்பு உட்கொள்ளலுக்கு எதிராக டிராஃபிக் விளக்கு. கட்டாயம். ஆடிட்-தயார். ஒழுங்குமுறை பாதுகாப்பின் நான்கு அடுக்குகள்.",
  hfss1Title: "கட்டாய ஊட்டச்சத்து சக்கரம்.",
  hfss1Body: "ஒவ்வொரு HFSS தயாரிப்பும் ஷாப்பர் தொடர்வதற்கு முன்பு கொழுப்பு, சர்க்கரை, உப்பு மற்றும் நிறைவுற்ற கொழுப்பு டிராஃபிக் விளக்குகளை தினசரி உட்கொள்ளலுக்கு எதிராக காட்டுகிறது.",
  hfss2Title: "கல்வி-முதல் வடிவமைப்பு.",
  hfss2Body: "தளம் ஊட்டச்சத்து வெளிப்படைத்தன்மையை ஊக்குவிக்கிறது. ஷாப்பர் தொடர்புகளை விளம்பரத் தள்ளலாக அல்ல, தகவலறிந்த தேர்வாக நிலைநிறுத்துகிறது.",
  hfss3Title: "வெளியீடு சரிபார்ப்பு.",
  hfss3Body: "உரையாடல் AI ஒழுங்குமுறை வடிவங்களுக்காக கண்காணிக்கப்படுகிறது. ஒவ்வொரு பதிலும் பதிவு செய்யப்படுகிறது, வடிகட்டப்படுகிறது மற்றும் ஆடிட் செய்யக்கூடியது.",
  hfss4Title: "ஏற்றுமதி செய்யக்கூடிய ஆடிட் பதிவுகள்.",
  hfss4Body: "ASA இணக்க மதிப்பெண், ஒவ்வொரு பதிலும் பதிவு செய்யப்பட்டது மற்றும் மீட்டெடுக்கக்கூடியது, கொடியிடப்பட்ட உள்ளடக்கத்திற்கான மீறல் எச்சரிக்கைகள்.",
  hfssBadge: "ASA இணக்கம்: 98.4%",

  wlEyebrow: "உங்கள் பிராண்ட், எந்த மொழியிலும்",
  wlHeading: "உங்கள் தரவு. உங்கள் AI. உங்கள் வழியில்.",
  wlLangCaption: "40+ மொழிகள். தானாக கண்டறிதல் அல்லது பிரச்சாரத்திற்கு கைமுறையாக அமைத்தல்.",
  wlFeat1Title: "உங்கள் பிராண்ட் அடையாளம்",
  wlFeat1Body: "லோகோ, வண்ணங்கள், எழுத்துருக்கள் ஒவ்வொரு ஷாப்பர் திரையிலும் பேக்கிங் சீட்டிலும் பயன்படுத்தப்படுகின்றன.",
  wlFeat2Title: "உங்கள் தரவு, உங்கள் AI",
  wlFeat2Body: "உங்கள் கணக்கில் தரவு சேமிக்கப்படுகிறது. உங்கள் தயாரிப்புகளில் AI பயிற்றுவிக்கப்படுகிறது. உறவு உங்களுடையது.",
  wlFeat3Title: "உங்கள் கடைகள், உங்கள் புவிவேலி",
  wlFeat3Body: "உலகளாவிய பயன்முறை அல்லது கடை-குறிப்பிட்ட. இயல்புநிலை 150 மீ புவிவேலி, பிரச்சாரத்திற்கு உள்ளமைக்கக்கூடியது.",
  wlFeat4Title: "உங்கள் குழு, உங்கள் அணுகல்",
  wlFeat4Body: "பாத்திர-அடிப்படையிலான அனுமதிகள். பிராண்ட், ஏஜென்சி, களம், நிதி — ஒவ்வொருவருக்கும் சரியான பார்வை.",

  proofEyebrow: "செயல்பாட்டில்",
  proofHeading: "ஏற்கனவே நேரடியாக. ஏற்கனவே செயலில்.",
  proofStat1Label: "பிரச்சார தரவுத்தளத்தில் UK கடைகள்",
  proofStat2Label: "ஆதரிக்கப்படும் மொழிகள்",
  proofStat3Label: "UK-யில் நிறுவப்பட்டது",
  proofQuotePlaceholder: "வாடிக்கையாளர் கதைகள் விரைவில்.",

  faqHeading: "பிராண்டுகள் எங்களிடம் கேட்கும் கேள்விகள்.",
  faq1Q: "ஷாப்பர்கள் ஆப் பதிவிறக்க வேண்டுமா?",
  faq1A: "இல்லை. TalkPack உலாவியில் இயங்குகிறது. ஒரு ஸ்கேன் அனுபவத்தைத் திறக்கிறது.",
  faq2Q: "TalkPack எங்கள் EPOS அல்லது CRM உடன் ஒருங்கிணைகிறதா?",
  faq2A: "ஒருங்கிணைப்பு தேவையில்லை. சேகரிக்கப்பட்ட தரவு first-party மற்றும் PDF, CSV அல்லது API மூலம் ஏற்றுமதி செய்யக்கூடியது.",
  faq3Q: "HFSS தயாரிப்புகளுக்கு என்ன நடக்கும்?",
  faq3A: "ஒவ்வொரு HFSS தயாரிப்பும் விளையாட்டுக்கு முன்பு கட்டாய ஊட்டச்சத்து சக்கரத்தைக் காட்டுகிறது. இணக்கம் உள்ளமைந்துள்ளது, பின்னர் சேர்க்கப்படவில்லை.",
  faq4Q: "எந்த மொழிகளை நீங்கள் ஆதரிக்கிறீர்கள்?",
  faq4A: "40+. தளம் தானாக கண்டறிகிறது அல்லது நீங்கள் பிரச்சாரத்திற்கு அமைக்கிறீர்கள்.",
  faq5Q: "குறிப்பிட்ட சில்லறை விற்பனையாளர்களில் மட்டும் பிரச்சாரத்தை இயக்க முடியுமா?",
  faq5A: "ஆம். கடை-குறிப்பிட்ட பயன்முறை தேர்ந்தெடுக்கப்பட்ட கடைகளுக்கு பிரச்சாரத்தை புவிவேலி செய்கிறது. உலகளாவிய பயன்முறை எந்த ஷாப்பரும் எங்கும் விளையாட அனுமதிக்கிறது.",
  faq6Q: "பிரச்சாரத்தைக் கட்டியெழுப்ப எவ்வளவு நேரம்?",
  faq6A: "தர விளம்பரத்திற்கு சுமார் 10 நிமிடங்கள். போர்ட்டலில் படிப்படியான வழிகாட்டி.",
  faq7Q: "அனுபவத்தை வொய்ட்-லேபிள் செய்ய முடியுமா?",
  faq7A: "ஆம். உங்கள் லோகோ, உங்கள் வண்ணங்கள், உங்கள் மொழி. நீங்கள் விரும்பும்வரை ஷாப்பர்கள் TalkPack பிராண்டிங்கைப் பார்க்க மாட்டார்கள்.",

  footerColPlatform: "தளம்",
  footerColModules: "தொகுதிகள்",
  footerColCompany: "நிறுவனம்",
  footerColResources: "வளங்கள்",
  footerCompany1: "எங்களைப் பற்றி",
  footerCompany2: "தொடர்பு",
  footerResources3: "தனியுரிமை",
  footerResources4: "நிபந்தனைகள்",
  footerNewsHeading: "காலாண்டு புதுப்பிப்புகள். ஸ்பேம் இல்லை.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "சந்தாதாரர்",
  footerRegistration: "ShopAI Ltd · நிறுவன எண் 15877093 · இங்கிலாந்தில் பதிவு செய்யப்பட்டது",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (TA) ──
  heroAlt: "சூப்பர்மார்க்கெட் வழியில் வாங்குபவர் — TalkPack அனுபவத்தின் ஒவ்வொரு தொடுதிறமும் அவளைச் சுற்றி வருகிறது",

  stepsTitle: "மூன்று படிகள். ஒரு தளம்.",
  stepsSub: "வாங்குபவர் நுழையும் தருணத்திலிருந்து நீங்கள் அவரைப் பற்றி ஏதாவது கற்றுக்கொள்ளும் தருணம் வரை. இயக்க எளிமை. தரவில் செழுமை.",
  step1Title: "நுழைய",
  step1Desc: "வாங்குபவர் எந்த சேனலிலிருந்தும் தட்டுகிறார் அல்லது ஸ்கேன் செய்கிறார். ஒரு நுழைவு, ஒரு பிராண்டட் அனுபவம்.",
  step1PillRow: "வலைதளம் · சமூகம் · மின்னஞ்சல் · QR · OOH · கடையில்",
  step2Title: "ஈடுபட",
  step2Desc: "ஐந்து Pic-n-Mix தொகுதிகள் அவர்களின் மொழியில், உங்கள் பிராண்டில், AI-ஆல் சரிபார்க்கப்பட்டு இயங்குகின்றன.",
  step2PillRow: "கற்க · விளையாட · கேள்வி · வெல்ல · சம்பாதிக்க",
  step3Title: "கற்க",
  step3Desc: "ஒவ்வொரு தொடர்பும் first-party தரவாக மாறி, நிகழ்நேரத்தில் உங்கள் டாஷ்போர்டுக்கு வருகிறது.",
  step3PillRow: "வாங்குபவர்கள் · உரையாடல் · இடம் · ஈடுபாடு · இணக்கம்",

  chipWebsite: "வலைதளம்",
  chipSocial:  "சமூகம்",
  chipEmail:   "மின்னஞ்சல்",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "கடையில்",
  chipLearn:   "கற்க",
  chipPlay:    "விளையாட",
  chipSay:     "கேள்வி",
  chipWin:     "வெல்ல",
  chipEarn:    "சம்பாதிக்க",
  chipShoppers:     "வாங்குபவர்கள்",
  chipConversation: "உரையாடல்",
  chipLocation:     "இடம்",
  chipEngagement:   "ஈடுபாடு",
  chipCompliance:   "இணக்கம்",
  chipAriaEntry:  "நுழைவு சேனல்",
  chipAriaModule: "Pic-n-Mix தொகுதி",
  chipAriaIntel:  "நுண்ணறிவு வகை",

  intgTitle: "தீவிரமான கட்டமைப்பு. நிரூபிக்கப்பட்ட தொழில்நுட்பம்.",
  intgSub: "உலகின் மிகப்பெரிய நிறுவனங்கள் நம்பும் அதே கிளவுட், AI மற்றும் இணக்க அடித்தளங்களில் TalkPack இயங்குகிறது.",
  intgCap1: "தாய் தளம்",
  intgCap2: "கிளவுட் கட்டமைப்பு",
  intgCap3: "உரையாடல் AI",
  intgCap4: "மொழி மாதிரிகள்",
  intgCap5: "AI மற்றும் வரைபடம்",
  intgCap6: "ஊட்டச்சத்து தரவு",
  intgCap7: "கட்டணங்கள்",
  intgFootnote: "ISO 27001 சான்றளிக்கப்பட்ட வழங்குநர்கள் · GDPR-இணக்கமான · பல-பகுதி ஹோஸ்டிங் · முதல் நாளிலிருந்தே நிறுவன நிலை பாதுகாப்பு",

  jColEntry: "நுழைவு",
  jColExperience: "அனுபவம்",
  jColDashboard: "டாஷ்போர்டு நுண்ணறிவு",
  jEntryDigitalTitle: "டிஜிட்டல்",
  jEntryDigitalDesc: "வலைதளம், ஆப் ஸ்டோர்கள், தேடல், நேரடி இணைப்புகள் — எந்த டிஜிட்டல் மேற்பரப்பிலிருந்தும் வாங்குபவர் அனுபவத்திற்கு ஒரே தட்டில்.",
  jEntrySocialTitle: "சமூகம்",
  jEntrySocialDesc: "ஸ்பான்சர் செய்யப்பட்ட இடுகைகள், செல்வாக்கு செலுத்துநர்களின் உள்ளடக்கம், DM-கள் — ஊட்டங்கள் தளத்தை விட்டு வெளியேற வேண்டாமே நேரடியாக TalkPack-க்கு பாய்கின்றன.",
  jEntryPhysicalTitle: "உடல்",
  jEntryPhysicalDesc: "பேக்கில், அலமாரியில், POS-இல் — TalkPack ஒரு ஸ்கேனை உரையாடலாக மாற்றும் உண்மை உலக நுழைவு புள்ளிகள்.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "கடை POS",
  jEntryPhysPill3: "பொதி",
  jExpVerifyLabel: "சரிபார்",
  jExpVerifyText: "AI தயாரிப்பு அடையாளம் — SKU நிகழ்நேரத்தில் சரிபார்க்கப்பட்டது.",
  jExpLearnLabel: "கற்க",
  jExpLearnBody: "ஊட்டச்சத்து சக்கரம், ஒவ்வாமைகள், மூலப்பொருட்கள், மூலப்பெறுமதி — வாங்குபவரின் மொழியில் காட்டப்பட்டு, பிராண்டால் சரிபார்க்கப்படுகிறது.",
  jExpEngageLabel: "ஈடுபட",
  jExpEngagePlayBody: "ஸ்பின், காச்சா, புதிர், வினாடி வினா — தங்கும் நேரத்திற்கான ஐந்து விளையாட்டு வகைகள்.",
  jExpEngageEarnBody: "புள்ளிகள், ரசீதுகள், விசுவாசத் திட்டம் — கூடை-நிலை நுண்ணறிவு.",
  jExpEngageSayBody: "உரையாடல் AI 40+ மொழிகளில் பதிலளிக்கிறது.",
  jExpRewardLabel: "வெகுமதி",
  jExpRewardBody: "உடனடி பரிசுகள், டில்-இல் இரட்டை-QR சரிபார்ப்பு, GPS-சரிபார்க்கப்பட்ட கோரிக்கைகள் — முதல் நாளிலிருந்தே தணிக்கைக்கு தயார்.",
  jExpCaptureLabel: "சேமி",
  jExpCaptureSub: "ஒவ்வொரு தொடர்பும் · உங்களால் சேமிக்கப்பட்டு உரிமையாக்கப்படுகிறது",

  capEmail:   "மின்னஞ்சல்",
  capName:    "பெயர்",
  capAddress: "முகவரி",
  capGps:     "GPS",
  capProduct: "தயாரிப்பு",
  capBasket:  "கூடை",
  capSpend:   "செலவு",
  capChat:    "அரட்டை",

  dashShopperLabel: "வாங்குபவர் நுண்ணறிவு",
  dashShopperShort: "வாங்குபவர்",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "மின்னஞ்சல், பெயர், முகவரி, தொடர்பு விருப்பத்தேர்வுகள்.",
  dashShopper2Title: "GPS-சரிபார்க்கப்பட்ட இடங்கள்",
  dashShopper2Desc: "கடை பகுதிகளின் படி வாங்குபவர்கள் எங்கு ஈடுபடுகிறார்கள்.",
  dashShopper3Title: "வாங்குதல் அதிர்வெண்",
  dashShopper3Desc: "மீண்டும் வரும் ஸ்கேன் முறைகள், வருகை தாளம்.",
  dashShopper4Title: "கூடை அமைப்பு",
  dashShopper4Desc: "உருப்படி நிலை ரசீது தரவு, ஒன்றாக வாங்கப்படும் தயாரிப்புகள்.",

  dashConvLabel: "உரையாடல் நுண்ணறிவு",
  dashConvShort: "உரையாடல்",
  dashConv1Title: "கேள்வி வகைகள்",
  dashConv1Desc: "ஒவ்வாமைகள், மூலப்பொருட்கள், ஊட்டச்சத்து, பயன்பாடு, செய்முறைகள்.",
  dashConv2Title: "உணர்வு பகுப்பாய்வு",
  dashConv2Desc: "தயாரிப்பு மற்றும் பகுதியின் படி நேர்மறை, எதிர்மறை, நடுநிலை.",
  dashConv3Title: "குழப்ப புள்ளிகள்",
  dashConv3Desc: "வாங்குபவர்கள் புரிந்து கொள்ளாதது, மீண்டும் கேட்கப்படும் கேள்விகள்.",
  dashConv4Title: "வாங்குதல் நோக்கம்",
  dashConv4Desc: "ஆர்வத்திலிருந்து சில்லறை விற்பனையாளர் கோரிக்கைகள் வரை முன்னேற்றம்.",
  dashConv5Title: "மொழி விருப்பத்தேர்வுகள்",
  dashConv5Desc: "இடத்தின் படி வாங்குபவர்கள் எந்த மொழிகளைத் தேர்வு செய்கிறார்கள்.",

  dashDistLabel: "விநியோக நுண்ணறிவு",
  dashDistShort: "விநியோகம்",
  dashDist1Title: "கடை கிடைக்கும் தன்மை",
  dashDist1Desc: "அலமாரியில் தயாரிப்பு, geofence ஸ்கேன்களால் உறுதிப்படுத்தப்பட்டது.",
  dashDist2Title: "சரக்கு இல்லை எச்சரிக்கைகள்",
  dashDist2Desc: "'இங்கு இல்லை' அறிக்கைகள் நிகழ்நேரத்தில் கொடியிடப்படுகின்றன.",
  dashDist3Title: "வெப்ப வரைபடங்கள்",
  dashDist3Desc: "PIN குறியீடு, பகுதி, fascia படி ஸ்கேன் அடர்த்தி.",
  dashDist4Title: "வீட்டு ஊடுருவல்",
  dashDist4Desc: "'எங்கேயும் ஸ்கேன்' தயாரிப்பு வீட்டில் எங்கு வாழ்கிறது என்பதைக் காட்டுகிறது.",
  dashDist5Title: "கடை ஒப்பீடு",
  dashDist5Desc: "இடம், fascia குழுவின் படி ஈடுபாடு விகிதங்கள்.",

  dashCampLabel: "பிரச்சார செயல்திறன்",
  dashCampShort: "பிரச்சாரம்",
  dashCamp1Title: "ஸ்கேன்-முதல்-ஈடுபாடு விகிதம்",
  dashCamp1Desc: "எத்தனை ஸ்கேன்கள் முழு தொடர்புகளாக மாறுகின்றன.",
  dashCamp2Title: "விளையாட்டு நிறைவு விகிதங்கள்",
  dashCamp2Desc: "ஒரு அமர்வுக்கு ஸ்பின்கள், வெற்றி விகிதங்கள், விட்டுச்செல்லும் புள்ளிகள்.",
  dashCamp3Title: "மீட்பு கண்காணிப்பு",
  dashCamp3Desc: "கடையின் படி கோரப்பட்ட, சரிபார்க்கப்பட்ட, காலாவதியான பரிசுகள்.",
  dashCamp4Title: "ROI டாஷ்போர்டு",
  dashCamp4Desc: "பிரச்சார செலவு vs கைப்பற்றப்பட்ட வாங்குபவர் மதிப்பு.",

  dashCompLabel: "இணக்கம் & தணிக்கை",
  dashCompShort: "இணக்கம்",
  dashComp1Title: "ASA இணக்க மதிப்பெண்",
  dashComp1Desc: "ஒவ்வொரு பிரச்சாரத்திற்கும், ஒவ்வொரு உரையாடலுக்கும்.",
  dashComp2Title: "ஏற்றுமதி செய்யக்கூடிய தணிக்கை பதிவுகள்",
  dashComp2Desc: "ஒவ்வொரு AI பதிலும் பதிவு செய்யப்பட்டு மீட்டெடுக்கக்கூடியது.",
  dashComp3Title: "மீறல் எச்சரிக்கைகள்",
  dashComp3Desc: "கொடியிடப்பட்ட உள்ளடக்க முறைகள்.",
  dashComp4Title: "ஊட்டச்சத்து ஈடுபாடு",
  dashComp4Desc: "சக்கரத்தில் வாங்குபவர்கள் எந்த ஊட்டச்சத்துக்களுடன் தொடர்பு கொள்கிறார்கள்.",

  pvFormTitle: "வாழ்த்துக்கள்!",
  pvFormSub: "நீங்கள் வென்றுவிட்டீர்கள்: அடுத்த வாங்குதலில் 20% தள்ளுபடி",
  pvFormName: "உங்கள் பெயர்",
  pvFormEmail: "உங்கள் மின்னஞ்சல்",
  pvFormPostcode: "PIN குறியீடு",
  pvFormCta: "பரிசை பெறு",
  pvFormFootnote: "Privacy-first · GDPR-இணக்கமான",

  pvChatTitle: "AI தூதர்",
  pvChatSub: "Morning O's-இல் பயிற்றுவிக்கப்பட்டது",
  pvChatBubble1: "இது gluten இல்லாதது தானா?",
  pvChatBubble2: "ஆம் — சான்றளிக்கப்பட்ட gluten இல்லாதது மற்றும் ஒரு பரிமாறலுக்கு வெறும் 9g சர்க்கரை. சமையல் யோசனைகள் வேண்டுமா?",
  pvChatBubble3: "ஆமாம், தயவு செய்து",
  pvChatBubble4: "பழங்களுடன் இரவு ஓட்ஸை முயற்சி செய்யுங்கள் — 3 நிமிட தயாரிப்பு.",
  pvChatPlaceholder: "என்னிடம் எதையும் கேளுங்கள்…",
  pvChatFootnote: "40+ மொழிகள் · எப்போதும் இயக்கத்தில்",

  pvMapTitle: "உங்கள் அருகில் Morning O's கண்டறிக",
  pvMapPin: "Nisa · SE1 · 0.3 mi",
  pvMapCta: "திசைகள்",
  pvMapFootnote: "GPS-சரிபார்க்கப்பட்டது · 150m இல் geofenced",
  pvMapOos: "சரக்கு இல்லை",

  pvWheelTitle: "Morning O's · சுழற்றி வெல்லுங்கள்",
  pvWheelCredits: "350 கடன்கள்",
  pvWheelCta: "சுழற்று",
  pvWheelFootnote: "ஸ்பின் சக்கரம் · ஊட்டச்சத்து சக்கரம் · காச்சா · வினாடி வினா · புதிர்",
  pvWheelWedgeWin: "வெற்றி!",
  pvWheelWedgeTry: "மீண்டும்",

  pvNutTitle: "Morning O's · ஊட்டச்சத்து தகவல்",
  pvNutPer100g: "100g-க்கு",
  pvNutPerServing: "ஒரு பரிமாறலுக்கு",
  pvNutCta: "தொடர",
  pvNutFootnote: "ASA-இணக்கமான · விளையாட்டுக்கு முன் கட்டாயம்",

  bookDemoSubject: "TalkPack டெமோ கோரிக்கை",
  bookDemoBody: "வணக்கம் ShopAI குழுவே,\n\nநான் TalkPack டெமோவை முன்பதிவு செய்ய விரும்புகிறேன். நேரம் ஒதுக்க தயவு செய்து தொடர்பு கொள்ளுங்கள்.\n\nநன்றி,",

  // ── Viz mock-data labels (TA) ──
  vizCapQuestions: "இந்த வாரத்தின் வாங்குபவரின் முக்கிய கேள்விகள்",
  vizQAllergens: "ஒவ்வாமை", vizQIngredients: "மூலப்பொருட்கள்", vizQNutrition: "ஊட்டச்சத்து",
  vizQRecipes: "சமையல்", vizQUsage: "பயன்பாடு",
  vizCapSentiment: "அனைத்து தயாரிப்புகளின் கடந்த 30 நாட்கள்",
  vizSentPositive: "நேர்மறை", vizSentNeutral: "நடுநிலை", vizSentNegative: "எதிர்மறை",
  vizCapConfusion: "மீண்டும் கேட்கப்படும் கேள்விகள் — உள்ளடக்க இடைவெளிகள்",
  vizConfusion1: "இது gluten இல்லாததா?",
  vizConfusion2: "சைவத்திற்கு பொருத்தமா?",
  vizConfusion3: "கொட்டை எச்சரிக்கைகள் உள்ளனவா?",
  vizCapFunnel: "உரையாடல் வழியாக மாற்றம்",
  vizFunnelCurious: "ஆர்வமுள்ள", vizFunnelInterested: "ஆர்வம் கொண்ட", vizFunnelReady: "வாங்கத் தயார்",
  vizCapAvail: "நீங்கள் திட்டமிட்ட இடங்களில் தயாரிப்பு கிடைக்கும்",
  vizAvailDetail: "1,326 கடைகளில் 1,247 அலமாரியில் உறுதிப்படுத்தப்பட்டது",
  vizCapOos: "இன்று 3 கடைகள் கொடியிடப்பட்டன · தானியங்கி எச்சரிக்கை அனுப்பப்பட்டது",
  vizCapHeat: "லண்டன் · மான்செஸ்டர் · பர்மிங்காம் முன்னணியில்",
  vizCapHousehold: "68% ஸ்கேன்கள் வீட்டில் நிகழ்கின்றன — உங்கள் தயாரிப்பு அங்கு வாழ்கிறது",
  vizCapFascia: "சில்லறை விற்பனை வடிவம் வாரியான ஈடுபாட்டு விகிதம்",
  vizCapEngrate: "முழு தொடர்புகளாக மாறும் ஸ்கேன்கள்",
  vizEngrateCompare: "தொழில் அளவீடான 42%-உடன் ஒப்பிடுகையில் +36 புள்ளிகள்",
  vizCapGame: "வாங்குபவர்கள் எங்கு விட்டுச் செல்கிறார்கள் — விளையாட்டு சமநிலையை சரி செய்யுங்கள்",
  vizGameStart: "தொடங்கு", vizGamePlay: "விளையாடு", vizGameWin: "வெற்றி", vizGameClaim: "உரிமைகோர்",
  vizCapRdm: "96% சரிபார்ப்பு விகிதம் · குறைந்த காலாவதி",
  vizRdmClaimed: "கோரப்பட்டது", vizRdmValidated: "சரிபார்க்கப்பட்டது", vizRdmExpired: "காலாவதி",
  vizCapRoi: "பிரச்சார செலவு vs வாங்குபவர் வாழ்நாள் மதிப்பு",
  vizRoiSpend: "செலவு", vizRoiValueLabel: "கைப்பற்றப்பட்ட மதிப்பு", vizRoiReturn: "3.4× திரும்பப்பெறு",
  vizCapComp: "ஒரு பிரச்சாரத்திற்கு · ஒரு உரையாடலுக்கு · தணிக்கைக்கு தயார்",
  vizCapAudit: "ஒவ்வொரு AI பதிலும் பதிவு செய்யப்பட்டது · தேவைக்கேற்ப மீட்டெடுக்கக்கூடியது",
  vizCapViolation: "இந்த மாதம் மீறல்கள் · AI வெளியீடு தொடர்ந்து சரிபார்க்கப்படுகிறது",
  vizCapNut: "சக்கரத்தில் வாங்குபவர்கள் தொடர்பு கொள்ளும் ஊட்டச்சத்துகள்",
  vizNutSugar: "சர்க்கரை", vizNutFat: "கொழுப்பு", vizNutSalt: "உப்பு", vizNutSatFat: "நிறைவுற்ற",
  vizCapLang: "வாங்குபவரின் மொழி தானாக கண்டறியப்படுகிறது",
  vizLangOther: "மற்றவை",
  vizCapCrm: "கைப்பற்றப்பட்ட வாங்குபவர்கள்",
  vizCapMap: "இந்த வாரம் 247 இடங்கள்",
  vizCapWeek: "வாரத்திற்கு சராசரி 2.3 வருகைகள்",
  vizDayMon: "திங்.", vizDayTue: "செவ்.", vizDayWed: "புத.",
  vizDayThu: "வியா.", vizDayFri: "வெள்.", vizDaySat: "சனி", vizDaySun: "ஞாயி.",
  vizCapBasket: "சராசரி கூடை: £8.40 · 6.2 பொருட்கள்",
  vizBasketDairy: "பால் பொருட்கள்", vizBasketBakery: "பேக்கரி", vizBasketSnacks: "சிற்றுண்டி", vizBasketOther: "மற்றவை",
};

// ────────── العربية ──────────
const ar: LandingCopy = {
  ...en,
  navPlatform: "المنصة",
  navModules: "الوحدات",
  navPortal: "تسجيل دخول البوابة",
  navLangButton: "AR",
  navRegister: "تسجيل",
  navBookDemo: "احجز عرضاً تجريبياً",
  navLogin: "تسجيل الدخول",

  trustLeft: "بدون تطبيق · بدون تكامل · بدون اعتماد على المتجر",
  trustRight: "منذ 2024 · المملكة المتحدة · بناء مع The Wholesale Group · Caterers Choice · Passot (اليابان) · Talysis UK · Jakavarna Group (تايلاند)",

  heroEyebrow: "مساعد طيار للتجارة",
  heroL1: "كل منتج. محادثة.",
  heroL2: "كل مسح. بيانات حقيقية.",
  heroSub: "يحوّل TalkPack أي منتج إلى محادثة حية. يمسح المتسوقون، يتعلمون، يلعبون، يسألون، يربحون، يجمعون النقاط. تحصل على بيانات first-party، والامتثال لـ HFSS، وعلاقة مباشرة مع المتسوقين في التجزئة الفعلية. بدون تطبيق. بدون تكامل. بدون اعتماد على المتجر.",
  heroCtaPrimary: "احجز عرضاً تجريبياً",
  heroCtaGhost: "اطلع على طريقة العمل",
  heroPillAllergens: "مسببات الحساسية",
  heroPillHfss: "HFSS",
  heroPillIngredients: "المكونات",
  heroVerified: "موثّق",

  statStores: "متجر · المملكة المتحدة · توسع عالمي",
  statLanguages: "لغة · اكتشاف تلقائي",
  statGames: "من الموجز إلى حملة حية",
  statModules: "عجلة التغذية مدمجة",
  statSteps: "علامتك التجارية. بياناتك. ذكاؤك الاصطناعي.",
  statDownloads: "تنزيلات التطبيق · عمليات التكامل · الاحتكاك",
  statGeofence: "بيانات المتسوق من التجزئة الفعلية",
  statScans: "من الرف إلى CRM المتسوق",

  fullJourneyEyebrow: "الرحلة الكاملة",
  fullJourneyHeading: "من الدخول إلى الذكاء. منصة واحدة.",
  fullJourneySub: "كل قناة. كل وحدة. كل نقطة بيانات. هكذا تتصل بنية TalkPack من البداية إلى النهاية.",
  fullJourneyCaption: "تدفق كامل. من دخول المتسوق إلى ذكاء العلامة التجارية. قابل للتصدير عبر لوحة التحكم أو محادثة الذكاء الاصطناعي أو استعلامات SmartMail.",

  picEyebrow: "Pic-n-Mix",
  picHeading: "منصة واحدة. خمس طرق للتفاعل مع المتسوق.",
  picSub: "تختار العلامات التجارية الوحدات التي تحتاجها. يحصل المتسوقون على مزيج من التجارب. كل وحدة تمر عبر نفس المسح، ونفس المحادثة، ونفس طبقة البيانات.",
  learnDesc: "تثقيف بالمنتج، شفافية المكونات، إشارات HFSS الضوئية.",
  learnF1: "عجلة التغذية", learnF2: "مسح مسببات الحساسية", learnF3: "بيانات المنشأ",
  playDesc: "خمسة أنواع من الألعاب تعزز وقت البقاء وإعادة المشاركة.",
  playF1: "عجلة الحظ", playF2: "غاتشا", playF3: "أحجية", playF4: "اختبار", playF5: "عجلة التغذية",
  sayDesc: "ذكاء اصطناعي حواري يجيب على أسئلة المتسوق بلغته.",
  sayF1: "أكثر من 40 لغة", sayF2: "أسئلة عن المكونات", sayF3: "اقتراحات وصفات",
  winDesc: "جوائز، توجيه إلى المتاجر، تحقق ثنائي QR في الكاشير، إيصالات تعبئة.",
  winF1: "مكافأة فورية", winF2: "جوائز محدّدة جغرافياً", winF3: "توصيل منزلي",
  earnDesc: "نقاط، مسح إيصالات، برنامج ولاء، ذكاء سلة المشتريات.",
  earnF1: "OCR للإيصال", earnF2: "ملف عبر العلامات", earnF3: "كتالوج المكافآت",
  moduleSeeMore: "عرض الوحدة",

  oeEyebrow: "محرّك واحد · ديناميكيات متعددة",
  oeHeading: "TalkPack جزء من منصة أوسع.",
  oeSub: "يوفّر ShopAI للعلامات التجارية وتجار التجزئة وتجار الجملة طبقة بيانات موحّدة. ذكاء أعمال حواري شامل. TalkPack هو الطريقة التي ينضم بها المتسوق إلى المحادثة.",
  oe1Strap: "مشاركة المتسوق من التجزئة الفعلية",
  oe2Strap: "ذكاء اصطناعي لقطاع السوبرماركت الصغير",
  oe3Strap: "ذكاء التدقيق الميداني",
  oe4Strap: "نقاط، إيصالات، بيانات السلة",
  oeActiveLabel: "الحالي",

  ucHeading: "أدوار مختلفة. منصة واحدة.",
  ucBrandTitle: "مسوّق العلامة التجارية",
  ucBrand1: "إطلاق منتج وطني عبر تجزئة مختلطة",
  ucBrand2: "توجيه حركة المرور إلى المتاجر المشاركة",
  ucBrand3: "تفاعل على العبوة متوافق مع HFSS",
  ucBrand4: "تنميط متسوق عبر العلامات",
  ucBrand5: "فئات منظَّمة (دواء، كحول) مع ردود AI مدققة",
  ucRetailerTitle: "تاجر تجزئة / جملة",
  ucRetailer1: "عروض حصرية بتمويل تاجر التجزئة",
  ucRetailer2: "تفعيل مجموعة الرموز",
  ucRetailer3: "تدقيق الامتثال على الرف",
  ucRetailer4: "ولاء خاص بتاجر التجزئة",
  ucAgencyTitle: "وكالة / موزّع",
  ucAgency1: "حملات بعلامة بيضاء لعملاء العلامة التجارية",
  ucAgency2: "وصول بوابة متعددة العملاء",
  ucAgency3: "تقارير تسويق تجاري بصيغة PDF",
  ucAgency4: "محتوى اجتماعي يُولَّد تلقائياً",

  hfssEyebrow: "مصمَّم لـ HFSS",
  hfssHeading: "الامتثال لـ HFSS، مدمَج.",
  hfssSub: "يرى المتسوق الملف الغذائي قبل اللعب. الدهون، السكر، الملح، الدهون المشبعة — إشارات ضوئية مقابل المتطلبات اليومية المرجعية. إلزامي. جاهز للتدقيق. أربع طبقات من الدفاع التنظيمي.",
  hfss1Title: "عجلة تغذية إلزامية.",
  hfss1Body: "كل منتج HFSS يعرض إشارات ضوئية للدهون والسكر والملح والدهون المشبعة مقابل المتطلبات اليومية قبل أن يستطيع المتسوق المتابعة.",
  hfss2Title: "إطار تعليمي أولاً.",
  hfss2Body: "تعزّز المنصة الشفافية الغذائية. تضع تفاعلات المتسوق كاختيار مستنير، وليس ضغطاً ترويجياً.",
  hfss3Title: "التحقق من المخرجات.",
  hfss3Body: "يتم رصد الذكاء الاصطناعي الحواري وفق أنماط تنظيمية. تُسجَّل كل استجابة وتُصفّى وتكون قابلة للتدقيق.",
  hfss4Title: "سجلات تدقيق قابلة للتصدير.",
  hfss4Body: "نقاط امتثال ASA، كل استجابة مسجَّلة وقابلة للاسترجاع، تنبيهات انتهاك للمحتوى المُعلَّم.",
  hfssBadge: "امتثال ASA: 98.4%",

  wlEyebrow: "علامتك التجارية، بأي لغة",
  wlHeading: "بياناتك. ذكاؤك الاصطناعي. بطريقتك.",
  wlLangCaption: "أكثر من 40 لغة. اكتشاف تلقائي أو ضبط يدوي لكل حملة.",
  wlFeat1Title: "هوية علامتك التجارية",
  wlFeat1Body: "الشعار، الألوان، الخطوط مطبَّقة على كل شاشة متسوق وكل إيصال تعبئة.",
  wlFeat2Title: "بياناتك، ذكاؤك الاصطناعي",
  wlFeat2Body: "البيانات محفوظة في حسابك. الذكاء الاصطناعي مدرَّب على منتجاتك. العلاقة لك.",
  wlFeat3Title: "متاجرك، حدودك الجغرافية",
  wlFeat3Body: "وضع شامل أو خاص بمتجر. سياج جغرافي افتراضي 150 م، قابل للضبط لكل حملة.",
  wlFeat4Title: "فريقك، صلاحياتك",
  wlFeat4Body: "صلاحيات بحسب الدور. علامة، وكالة، ميدان، مالية — كل دور يحصل على العرض المناسب.",

  proofEyebrow: "قيد التشغيل",
  proofHeading: "تعمل بالفعل. تعمل بنجاح.",
  proofStat1Label: "متاجر بريطانية في قاعدة بيانات الحملات",
  proofStat2Label: "لغة مدعومة",
  proofStat3Label: "تأسست في المملكة المتحدة",
  proofQuotePlaceholder: "قصص العملاء قريباً.",

  faqHeading: "الأسئلة التي تطرحها العلامات التجارية علينا.",
  faq1Q: "هل يحتاج المتسوقون إلى تنزيل تطبيق؟",
  faq1A: "لا. يعمل TalkPack في المتصفح. مسح واحد يفتح التجربة.",
  faq2Q: "هل يتكامل TalkPack مع EPOS أو CRM لدينا؟",
  faq2A: "لا حاجة للتكامل. البيانات الملتقطة هي first-party وقابلة للتصدير عبر PDF أو CSV أو API.",
  faq3Q: "ماذا يحدث لمنتجات HFSS؟",
  faq3A: "كل منتج HFSS يعرض عجلة تغذية إلزامية قبل اللعب. الامتثال مدمج، وليس مضافاً لاحقاً.",
  faq4Q: "ما اللغات التي تدعمها؟",
  faq4A: "أكثر من 40. تكتشف المنصة تلقائياً أو تضبطها لكل حملة.",
  faq5Q: "هل يمكننا تشغيل حملة في تجار تجزئة محددين فقط؟",
  faq5A: "نعم. الوضع الخاص بالمتجر يضع سياجاً جغرافياً للحملة على المتاجر المختارة. الوضع الشامل يسمح لأي متسوق باللعب في أي مكان.",
  faq6Q: "كم يستغرق بناء حملة؟",
  faq6A: "حوالي 10 دقائق لعرض ترويجي قياسي. معالج خطوة بخطوة في البوابة.",
  faq7Q: "هل يمكننا جعل التجربة بعلامة بيضاء؟",
  faq7A: "نعم. شعارك، ألوانك، لغتك. لن يرى المتسوقون علامة TalkPack إلا إذا أردت ذلك.",

  footerColPlatform: "المنصة",
  footerColModules: "الوحدات",
  footerColCompany: "الشركة",
  footerColResources: "الموارد",
  footerCompany1: "نبذة",
  footerCompany2: "تواصل",
  footerResources3: "الخصوصية",
  footerResources4: "الشروط",
  footerNewsHeading: "تحديثات ربع سنوية. بدون رسائل مزعجة.",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "اشترك",
  footerRegistration: "ShopAI Ltd · رقم الشركة 15877093 · مسجلة في إنجلترا",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (AR) ──
  heroAlt: "متسوقة في ممر سوبر ماركت — كل نقطة اتصال في تجربة TalkPack تدور حولها",

  stepsTitle: "ثلاث خطوات. منصة واحدة.",
  stepsSub: "من اللحظة التي يدخل فيها المتسوق إلى اللحظة التي تتعلم فيها شيئاً عنه. سهل التشغيل. غني بالبيانات.",
  step1Title: "ادخل",
  step1Desc: "يضغط المتسوق أو يمسح من أي قناة. مدخل واحد، تجربة واحدة بعلامتك التجارية.",
  step1PillRow: "موقع · سوشيال · بريد · QR · OOH · داخل المتجر",
  step2Title: "تفاعل",
  step2Desc: "خمس وحدات Pic-n-Mix بلغته، بعلامتك التجارية، تم التحقق منها بالذكاء الاصطناعي.",
  step2PillRow: "تعلّم · العب · اسأل · افز · اجمع",
  step3Title: "تعلّم",
  step3Desc: "كل تفاعل يصبح بيانات first-party، تتدفق في الزمن الحقيقي إلى لوحة المعلومات الخاصة بك.",
  step3PillRow: "متسوقون · محادثة · موقع · تفاعل · امتثال",

  chipWebsite: "موقع",
  chipSocial:  "سوشيال",
  chipEmail:   "بريد",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "داخل المتجر",
  chipLearn:   "تعلّم",
  chipPlay:    "العب",
  chipSay:     "اسأل",
  chipWin:     "افز",
  chipEarn:    "اجمع",
  chipShoppers:     "متسوقون",
  chipConversation: "محادثة",
  chipLocation:     "موقع",
  chipEngagement:   "تفاعل",
  chipCompliance:   "امتثال",
  chipAriaEntry:  "قناة الدخول",
  chipAriaModule: "وحدة Pic-n-Mix",
  chipAriaIntel:  "فئة الذكاء",

  intgTitle: "بنية تحتية جادة. تكنولوجيا مثبتة.",
  intgSub: "يعمل TalkPack على نفس أسس السحابة والذكاء الاصطناعي والامتثال التي تثق بها أكبر الشركات في العالم.",
  intgCap1: "المنصة الأم",
  intgCap2: "البنية التحتية السحابية",
  intgCap3: "الذكاء الاصطناعي للمحادثة",
  intgCap4: "نماذج اللغة",
  intgCap5: "الذكاء الاصطناعي والخرائط",
  intgCap6: "بيانات التغذية",
  intgCap7: "المدفوعات",
  intgFootnote: "مزودون معتمدون ISO 27001 · متوافقون مع GDPR · استضافة متعددة المناطق · أمان على مستوى المؤسسات منذ اليوم الأول",

  jColEntry: "الدخول",
  jColExperience: "التجربة",
  jColDashboard: "ذكاء لوحة المعلومات",
  jEntryDigitalTitle: "رقمي",
  jEntryDigitalDesc: "الموقع الإلكتروني، متاجر التطبيقات، البحث، الروابط المباشرة — نقرة واحدة من أي سطح رقمي إلى تجربة المتسوق.",
  jEntrySocialTitle: "سوشيال",
  jEntrySocialDesc: "المنشورات الممولة، محتوى المؤثرين، الرسائل المباشرة — تتدفق المحتويات مباشرة إلى TalkPack دون مغادرة المنصة.",
  jEntryPhysicalTitle: "مادي",
  jEntryPhysicalDesc: "على العبوة، على الرف، عند نقطة البيع — نقاط الدخول الواقعية حيث يحول TalkPack المسح إلى محادثة.",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "نقطة بيع داخل المتجر",
  jEntryPhysPill3: "العبوة",
  jExpVerifyLabel: "التحقق",
  jExpVerifyText: "تعرف المنتج بالذكاء الاصطناعي — التحقق من SKU في الزمن الحقيقي.",
  jExpLearnLabel: "تعلّم",
  jExpLearnBody: "عجلة التغذية، مسببات الحساسية، المكونات، المنشأ — معروضة بلغة المتسوق، ومعتمدة من العلامة التجارية.",
  jExpEngageLabel: "تفاعل",
  jExpEngagePlayBody: "عجلة، جاتشا، أحجية، اختبار — خمسة أنواع من الألعاب لزيادة وقت البقاء.",
  jExpEngageEarnBody: "نقاط، إيصالات، ولاء — ذكاء على مستوى السلة.",
  jExpEngageSayBody: "ذكاء اصطناعي محادث يجيب بأكثر من 40 لغة.",
  jExpRewardLabel: "مكافأة",
  jExpRewardBody: "جوائز فورية، تحقق ثنائي QR عند نقطة الدفع، مطالبات تم التحقق منها بـ GPS — جاهزة للتدقيق منذ اليوم الأول.",
  jExpCaptureLabel: "التقاط",
  jExpCaptureSub: "كل تفاعل · تلتقطه وتمتلكه أنت",

  capEmail:   "بريد",
  capName:    "الاسم",
  capAddress: "العنوان",
  capGps:     "GPS",
  capProduct: "المنتج",
  capBasket:  "السلة",
  capSpend:   "الإنفاق",
  capChat:    "محادثة",

  dashShopperLabel: "ذكاء المتسوق",
  dashShopperShort: "متسوق",
  dashShopper1Title: "CRM من طرف أول",
  dashShopper1Desc: "البريد، الاسم، العنوان، تفضيلات الاتصال.",
  dashShopper2Title: "مواقع تم التحقق منها بـ GPS",
  dashShopper2Desc: "أين يتفاعل المتسوقون حسب مناطق المتجر.",
  dashShopper3Title: "تكرار الشراء",
  dashShopper3Desc: "أنماط المسح المتكررة، إيقاع الزيارات.",
  dashShopper4Title: "تركيب السلة",
  dashShopper4Desc: "بيانات الإيصالات على مستوى الصنف، المنتجات المشتراة معاً.",

  dashConvLabel: "ذكاء المحادثة",
  dashConvShort: "محادثة",
  dashConv1Title: "فئات الأسئلة",
  dashConv1Desc: "مسببات الحساسية، المكونات، التغذية، الاستخدام، الوصفات.",
  dashConv2Title: "تحليل المشاعر",
  dashConv2Desc: "إيجابي، سلبي، محايد حسب المنتج والمنطقة.",
  dashConv3Title: "نقاط الالتباس",
  dashConv3Desc: "ما لا يفهمه المتسوقون، الأسئلة المتكررة.",
  dashConv4Title: "نية الشراء",
  dashConv4Desc: "التقدم من الفضول إلى طلبات تاجر التجزئة.",
  dashConv5Title: "تفضيلات اللغة",
  dashConv5Desc: "أي لغات يختارها المتسوقون حسب الموقع.",

  dashDistLabel: "ذكاء التوزيع",
  dashDistShort: "توزيع",
  dashDist1Title: "توفر المتجر",
  dashDist1Desc: "المنتج على الرف، تم تأكيده عبر مسح بـ geofence.",
  dashDist2Title: "تنبيهات نفاد المخزون",
  dashDist2Desc: "بلاغات 'غير متوفر هنا' يتم تمييزها في الزمن الحقيقي.",
  dashDist3Title: "خرائط حرارية",
  dashDist3Desc: "كثافة المسح حسب الرمز البريدي، المنطقة، fascia.",
  dashDist4Title: "اختراق الأسر",
  dashDist4Desc: "'امسح في أي مكان' يُظهر أين يعيش المنتج في المنزل.",
  dashDist5Title: "مقارنة المتاجر",
  dashDist5Desc: "معدلات التفاعل حسب الموقع، حسب مجموعة fascia.",

  dashCampLabel: "أداء الحملة",
  dashCampShort: "حملة",
  dashCamp1Title: "معدل المسح إلى التفاعل",
  dashCamp1Desc: "كم عدد المسوحات التي تتحول إلى تفاعلات كاملة.",
  dashCamp2Title: "معدلات إكمال اللعبة",
  dashCamp2Desc: "الدورات لكل جلسة، معدلات الفوز، نقاط الانسحاب.",
  dashCamp3Title: "تتبع الاسترداد",
  dashCamp3Desc: "الجوائز المُطالب بها والمعتمدة والمنتهية الصلاحية حسب المتجر.",
  dashCamp4Title: "لوحة معلومات ROI",
  dashCamp4Desc: "إنفاق الحملة مقابل قيمة المتسوق المُلتقطة.",

  dashCompLabel: "الامتثال والتدقيق",
  dashCompShort: "امتثال",
  dashComp1Title: "درجة امتثال ASA",
  dashComp1Desc: "لكل حملة، لكل محادثة.",
  dashComp2Title: "سجلات تدقيق قابلة للتصدير",
  dashComp2Desc: "كل رد للذكاء الاصطناعي مُسجل وقابل للاسترجاع.",
  dashComp3Title: "تنبيهات الانتهاكات",
  dashComp3Desc: "أنماط محتوى تم تمييزها.",
  dashComp4Title: "التفاعل الغذائي",
  dashComp4Desc: "ما هي العناصر الغذائية التي يتفاعل معها المتسوقون على العجلة.",

  pvFormTitle: "تهانينا!",
  pvFormSub: "ربحت: خصم 20% على عملية الشراء التالية",
  pvFormName: "اسمك",
  pvFormEmail: "بريدك الإلكتروني",
  pvFormPostcode: "الرمز البريدي",
  pvFormCta: "احصل على الجائزة",
  pvFormFootnote: "Privacy-first · متوافق مع GDPR",

  pvChatTitle: "سفير الذكاء الاصطناعي",
  pvChatSub: "تم تدريبه على Morning O's",
  pvChatBubble1: "هل هو خالٍ من الجلوتين؟",
  pvChatBubble2: "نعم — معتمد كخالٍ من الجلوتين ويحتوي على 9 جرام فقط من السكر لكل حصة. هل تريد أفكار وصفات؟",
  pvChatBubble3: "نعم من فضلك",
  pvChatBubble4: "جرب شوفان الليلة السابقة بالتوت — تحضير 3 دقائق.",
  pvChatPlaceholder: "اسألني أي شيء…",
  pvChatFootnote: "40+ لغة · متاح دائماً",

  pvMapTitle: "ابحث عن Morning O's بالقرب منك",
  pvMapPin: "Nisa · SE1 · 0.3 ميل",
  pvMapCta: "الاتجاهات",
  pvMapFootnote: "تم التحقق منه بـ GPS · geofence ضمن 150م",
  pvMapOos: "غير متوفر",

  pvWheelTitle: "Morning O's · أدر واربح",
  pvWheelCredits: "350 رصيداً",
  pvWheelCta: "أدر",
  pvWheelFootnote: "عجلة · عجلة التغذية · جاتشا · اختبار · أحجية",
  pvWheelWedgeWin: "فوز!",
  pvWheelWedgeTry: "حاول",

  pvNutTitle: "Morning O's · المعلومات الغذائية",
  pvNutPer100g: "لكل 100 جرام",
  pvNutPerServing: "لكل حصة",
  pvNutCta: "متابعة",
  pvNutFootnote: "متوافق مع ASA · إلزامي قبل اللعب",

  bookDemoSubject: "طلب عرض TalkPack",
  bookDemoBody: "مرحباً بفريق ShopAI،\n\nأود حجز عرض توضيحي لـ TalkPack. يرجى التواصل معي لتحديد موعد.\n\nشكراً،",

  // ── Viz mock-data labels (AR) ──
  vizCapQuestions: "أبرز أسئلة المتسوقين هذا الأسبوع",
  vizQAllergens: "مسببات الحساسية", vizQIngredients: "المكونات", vizQNutrition: "التغذية",
  vizQRecipes: "وصفات", vizQUsage: "الاستخدام",
  vizCapSentiment: "آخر 30 يوماً عبر جميع المنتجات",
  vizSentPositive: "إيجابي", vizSentNeutral: "محايد", vizSentNegative: "سلبي",
  vizCapConfusion: "أسئلة متكررة — فجوات في المحتوى",
  vizConfusion1: "هل هو خالٍ من الجلوتين؟",
  vizConfusion2: "هل يناسب النباتيين؟",
  vizConfusion3: "هل توجد تحذيرات بشأن المكسرات؟",
  vizCapFunnel: "التحويل عبر المحادثة",
  vizFunnelCurious: "فضولي", vizFunnelInterested: "مهتم", vizFunnelReady: "جاهز للشراء",
  vizCapAvail: "المنتج متوفر حيث خططتَ",
  vizAvailDetail: "1,247 من 1,326 متجراً مؤكدة على الرف",
  vizCapOos: "تم تمييز 3 متاجر اليوم · أُرسل تنبيه تلقائي",
  vizCapHeat: "لندن · مانشستر · برمنغهام في الصدارة",
  vizCapHousehold: "68% من عمليات المسح تحدث في المنزل — منتجك يعيش هناك",
  vizCapFascia: "معدل التفاعل حسب علامة المتجر",
  vizCapEngrate: "عمليات المسح التي تتحول إلى تفاعلات كاملة",
  vizEngrateCompare: "+36 نقطة مقابل 42% معيار الصناعة",
  vizCapGame: "أين ينسحب المتسوقون — اضبط توازن اللعبة",
  vizGameStart: "البدء", vizGamePlay: "العب", vizGameWin: "اربح", vizGameClaim: "المطالبة",
  vizCapRdm: "معدل تحقق 96% · انتهاء صلاحية منخفض",
  vizRdmClaimed: "مُطالب به", vizRdmValidated: "مُتحقق منه", vizRdmExpired: "منتهي الصلاحية",
  vizCapRoi: "إنفاق الحملة مقابل القيمة الدائمة للمتسوق",
  vizRoiSpend: "الإنفاق", vizRoiValueLabel: "القيمة المُلتقطة", vizRoiReturn: "3.4× عائد",
  vizCapComp: "لكل حملة · لكل محادثة · جاهز للتدقيق",
  vizCapAudit: "كل رد للذكاء الاصطناعي مُسجل · يمكن استرجاعه عند الطلب",
  vizCapViolation: "الانتهاكات هذا الشهر · يتم التحقق من مخرجات الذكاء الاصطناعي بشكل مستمر",
  vizCapNut: "العناصر الغذائية التي يتفاعل معها المتسوقون على العجلة",
  vizNutSugar: "سكر", vizNutFat: "دهون", vizNutSalt: "ملح", vizNutSatFat: "مشبعة",
  vizCapLang: "يتم اكتشاف لغة المتسوق تلقائياً",
  vizLangOther: "أخرى",
  vizCapCrm: "متسوقون مُلتقطون",
  vizCapMap: "247 موقعاً هذا الأسبوع",
  vizCapWeek: "بمتوسط 2.3 زيارة في الأسبوع",
  vizDayMon: "إث", vizDayTue: "ثل", vizDayWed: "أر",
  vizDayThu: "خم", vizDayFri: "جم", vizDaySat: "سب", vizDaySun: "أح",
  vizCapBasket: "متوسط السلة: £8.40 · 6.2 عنصراً",
  vizBasketDairy: "ألبان", vizBasketBakery: "مخبوزات", vizBasketSnacks: "وجبات خفيفة", vizBasketOther: "أخرى",
};

// ────────── اردو ──────────
const ur: LandingCopy = {
  ...en,
  navPlatform: "پلیٹ فارم",
  navModules: "ماڈیولز",
  navPortal: "پورٹل لاگ ان",
  navLangButton: "UR",
  navRegister: "رجسٹر کریں",
  navBookDemo: "ڈیمو بک کریں",
  navLogin: "لاگ ان",

  trustLeft: "کوئی ایپ نہیں · کوئی انٹیگریشن نہیں · سٹور پر انحصار نہیں",
  trustRight: "2024 سے · برطانیہ · The Wholesale Group · Caterers Choice · Passot (جاپان) · Talysis UK · Jakavarna Group (تھائی لینڈ) کے ساتھ تعمیر",

  heroEyebrow: "تجارت کے لیے کو پائلٹ",
  heroL1: "ہر مصنوعہ. ایک گفتگو.",
  heroL2: "ہر اسکین. حقیقی ڈیٹا.",
  heroSub: "TalkPack کسی بھی مصنوعہ کو لائیو گفتگو میں بدل دیتا ہے۔ شاپرز اسکین کرتے ہیں، سیکھتے ہیں، کھیلتے ہیں، پوچھتے ہیں، جیتتے ہیں، پوائنٹس کماتے ہیں۔ آپ کو first-party ڈیٹا، HFSS تعمیل، اور فزیکل ریٹیل میں شاپرز کے ساتھ براہِ راست تعلق ملتا ہے۔ کوئی ایپ نہیں۔ کوئی انٹیگریشن نہیں۔ سٹور پر انحصار نہیں۔",
  heroCtaPrimary: "ڈیمو بک کریں",
  heroCtaGhost: "کام کرنے کا طریقہ دیکھیں",
  heroPillAllergens: "الرجن",
  heroPillHfss: "HFSS",
  heroPillIngredients: "اجزاء",
  heroVerified: "تصدیق شدہ",

  statStores: "اسٹورز · برطانیہ · عالمی توسیع",
  statLanguages: "زبانیں · خودکار شناخت",
  statGames: "بریف سے لائیو مہم تک",
  statModules: "نیوٹریشن وہیل بلٹ ان",
  statSteps: "آپ کا برانڈ۔ آپ کا ڈیٹا۔ آپ کا AI۔",
  statDownloads: "ایپ ڈاؤن لوڈز · انٹیگریشنز · رگڑ",
  statGeofence: "فزیکل ریٹیل سے شاپر ڈیٹا",
  statScans: "شیلف سے شاپر CRM تک",

  fullJourneyEyebrow: "مکمل سفر",
  fullJourneyHeading: "داخلے سے ذہانت تک. ایک پلیٹ فارم.",
  fullJourneySub: "ہر چینل۔ ہر ماڈیول۔ ہر ڈیٹا پوائنٹ۔ یوں TalkPack کا ڈھانچہ سرا تا سرا جُڑتا ہے۔",
  fullJourneyCaption: "مکمل بہاؤ۔ شاپر داخلے سے برانڈ ذہانت تک۔ ڈیش بورڈ، AI چیٹ یا SmartMail سوالات کے ذریعے ایکسپورٹ۔",

  picEyebrow: "Pic-n-Mix",
  picHeading: "ایک پلیٹ فارم. شاپر سے رابطے کے پانچ طریقے.",
  picSub: "برانڈز اپنی ضرورت کے ماڈیولز چنتے ہیں۔ شاپرز کو تجربات کا مرکب ملتا ہے۔ ہر ماڈیول اسی اسکین، اسی گفتگو، اسی ڈیٹا پرت سے گزرتا ہے۔",
  learnDesc: "مصنوعہ کی تعلیم، اجزاء میں شفافیت، HFSS ٹریفک لائٹس۔",
  learnF1: "نیوٹریشن وہیل", learnF2: "الرجن اسکین", learnF3: "ماخذ ڈیٹا",
  playDesc: "پانچ کھیل اقسام جو ٹھہراؤ اور دوبارہ شمولیت بڑھاتی ہیں۔",
  playF1: "اسپن وہیل", playF2: "گاچا", playF3: "پزل", playF4: "کوئز", playF5: "نیوٹریشن اسپن",
  sayDesc: "گفتگو پر مبنی AI جو شاپر کے سوالات کا ان کی زبان میں جواب دیتی ہے۔",
  sayF1: "40+ زبانیں", sayF2: "اجزاء پر سوال و جواب", sayF3: "ترکیب تجاویز",
  winDesc: "انعامات، ڈائریکٹڈ فٹ فال، کاؤنٹر پر ڈوئل-QR تصدیق، پیکنگ سلپس۔",
  winF1: "فوری انعام", winF2: "جیو فینسڈ انعامات", winF3: "گھر تک ترسیل",
  earnDesc: "پوائنٹس، رسید اسکین، وفاداری پروگرام، باسکٹ ذہانت۔",
  earnF1: "رسید OCR", earnF2: "کراس برانڈ پروفائل", earnF3: "انعام کیٹلاگ",
  moduleSeeMore: "ماڈیول دیکھیں",

  oeEyebrow: "ایک انجن · کئی حرکیات",
  oeHeading: "TalkPack ایک بڑے پلیٹ فارم کا حصہ ہے۔",
  oeSub: "ShopAI برانڈز، ریٹیلرز اور ہول سیلرز کو ایک ڈیٹا پرت دیتا ہے۔ ابتدا تا انتہا گفتگو پر مبنی بزنس انٹیلیجنس۔ TalkPack وہ طریقہ ہے جس سے شاپر گفتگو میں شامل ہوتا ہے۔",
  oe1Strap: "فزیکل ریٹیل سے شاپر مشغولیت",
  oe2Strap: "کنوینینس سیکٹر کے لیے AI",
  oe3Strap: "فیلڈ آڈٹ ذہانت",
  oe4Strap: "پوائنٹس، رسیدیں، باسکٹ ڈیٹا",
  oeActiveLabel: "موجودہ",

  ucHeading: "مختلف کردار. ایک ہی پلیٹ فارم.",
  ucBrandTitle: "برانڈ مارکیٹر",
  ucBrand1: "ملا جلا ریٹیل میں قومی پراڈکٹ لانچ",
  ucBrand2: "شامل اسٹورز کی طرف ڈائریکٹڈ فٹ فال",
  ucBrand3: "HFSS موافق آن پیک مشغولیت",
  ucBrand4: "کراس برانڈ شاپر پروفائلنگ",
  ucBrand5: "ضابطہ بند کیٹیگریز (فارما، الکحل) آڈٹ شدہ AI جوابات کے ساتھ",
  ucRetailerTitle: "ریٹیلر / ہول سیلر",
  ucRetailer1: "ریٹیلر کے فنڈڈ خصوصی پروموشن",
  ucRetailer2: "سمبل گروپ ایکٹیویشن",
  ucRetailer3: "شیلف کمپلائنس آڈٹ",
  ucRetailer4: "ریٹیلر مخصوص لائلٹی",
  ucAgencyTitle: "ایجنسی / ری سیلر",
  ucAgency1: "کلائنٹ برانڈز کے لیے وائٹ لیبل مہمات",
  ucAgency2: "ملٹی کلائنٹ پورٹل رسائی",
  ucAgency3: "ٹریڈ مارکیٹنگ PDF رپورٹس",
  ucAgency4: "خودکار طور پر تخلیق شدہ سوشل مواد",

  hfssEyebrow: "HFSS کے لیے بنایا گیا",
  hfssHeading: "HFSS تعمیل، بلٹ ان۔",
  hfssSub: "شاپر کھیلنے سے پہلے غذائی پروفائل دیکھتا ہے۔ چربی، چینی، نمک، سیر شدہ چربی — یومیہ حوالہ خوراک کے مقابلے میں ٹریفک لائٹس۔ لازمی۔ آڈٹ کے لیے تیار۔ ضابطہ دفاع کی چار پرتیں۔",
  hfss1Title: "لازمی نیوٹریشن وہیل۔",
  hfss1Body: "ہر HFSS مصنوعہ آگے بڑھنے سے پہلے یومیہ خوراک کے مقابلے میں چربی، چینی، نمک اور سیر شدہ چربی کی ٹریفک لائٹس دکھاتا ہے۔",
  hfss2Title: "تعلیم پہلے فریمنگ۔",
  hfss2Body: "پلیٹ فارم غذائی شفافیت کو فروغ دیتا ہے۔ شاپر روابط کو پروموشنل دباؤ کے بجائے باخبر انتخاب کے طور پر رکھتا ہے۔",
  hfss3Title: "آؤٹ پٹ تصدیق۔",
  hfss3Body: "گفتگو پر مبنی AI ضابطہ نمونوں کے لیے زیر نگرانی ہے۔ ہر جواب لاگ، فلٹر اور آڈٹ ہو سکتا ہے۔",
  hfss4Title: "ایکسپورٹ ہونے والے آڈٹ لاگز۔",
  hfss4Body: "ASA کمپلائنس اسکور، ہر جواب لاگ اور دوبارہ حاصل کرنے کے قابل، فلیگ شدہ مواد کے لیے خلاف ورزی کے انتباہ۔",
  hfssBadge: "ASA کمپلائنس: 98.4%",

  wlEyebrow: "آپ کا برانڈ، کسی بھی زبان میں",
  wlHeading: "آپ کا ڈیٹا۔ آپ کا AI۔ آپ کے انداز میں۔",
  wlLangCaption: "40+ زبانیں۔ خودکار شناخت یا ہر مہم کے لیے دستی سیٹ۔",
  wlFeat1Title: "آپ کی برانڈ شناخت",
  wlFeat1Body: "لوگو، رنگ، فونٹس ہر شاپر اسکرین اور پیکنگ سلپ پر لاگو۔",
  wlFeat2Title: "آپ کا ڈیٹا، آپ کا AI",
  wlFeat2Body: "ڈیٹا آپ کے اکاؤنٹ پر محفوظ۔ AI آپ کی مصنوعات پر تربیت یافتہ۔ تعلق آپ کا ہے۔",
  wlFeat3Title: "آپ کے اسٹورز، آپ کا جیو فینس",
  wlFeat3Body: "یونیورسل موڈ یا اسٹور مخصوص۔ ڈیفالٹ 150 میٹر جیو فینس، ہر مہم کے لیے قابل ترتیب۔",
  wlFeat4Title: "آپ کی ٹیم، آپ کی رسائی",
  wlFeat4Body: "کردار پر مبنی اجازتیں۔ برانڈ، ایجنسی، فیلڈ، فنانس — ہر ایک کو درست منظر۔",

  proofEyebrow: "تعیناتی میں",
  proofHeading: "پہلے ہی لائیو۔ پہلے ہی فعال۔",
  proofStat1Label: "مہم ڈیٹا بیس میں برطانیہ کے اسٹورز",
  proofStat2Label: "حمایت یافتہ زبانیں",
  proofStat3Label: "برطانیہ میں قائم",
  proofQuotePlaceholder: "گاہک کی کہانیاں جلد آرہی ہیں۔",

  faqHeading: "برانڈز جو سوالات ہم سے پوچھتے ہیں۔",
  faq1Q: "کیا شاپرز کو ایپ ڈاؤن لوڈ کرنی پڑتی ہے؟",
  faq1A: "نہیں۔ TalkPack براؤزر میں چلتا ہے۔ ایک اسکین تجربہ کھولتا ہے۔",
  faq2Q: "کیا TalkPack ہمارے EPOS یا CRM کے ساتھ مربوط ہوتا ہے؟",
  faq2A: "کوئی انٹیگریشن درکار نہیں۔ حاصل ڈیٹا first-party ہے اور PDF، CSV یا API کے ذریعے ایکسپورٹ ہو سکتا ہے۔",
  faq3Q: "HFSS مصنوعات کا کیا ہوتا ہے؟",
  faq3A: "ہر HFSS مصنوعہ کھیل سے پہلے لازمی نیوٹریشن وہیل دکھاتا ہے۔ تعمیل بلٹ ان ہے، بعد میں شامل نہیں۔",
  faq4Q: "آپ کن زبانوں کی حمایت کرتے ہیں؟",
  faq4A: "40+۔ پلیٹ فارم خودکار شناخت کرتا ہے یا آپ ہر مہم کے لیے سیٹ کرتے ہیں۔",
  faq5Q: "کیا ہم مہم صرف مخصوص ریٹیلرز میں چلا سکتے ہیں؟",
  faq5A: "ہاں۔ اسٹور مخصوص موڈ منتخب اسٹورز پر مہم کو جیو فینس کرتا ہے۔ یونیورسل موڈ کسی بھی شاپر کو کہیں بھی کھیلنے دیتا ہے۔",
  faq6Q: "مہم بنانے میں کتنا وقت لگتا ہے؟",
  faq6A: "معیاری پروموشن کے لیے تقریباً 10 منٹ۔ پورٹل میں قدم بہ قدم وزرڈ۔",
  faq7Q: "کیا ہم تجربہ کو وائٹ لیبل کر سکتے ہیں؟",
  faq7A: "ہاں۔ آپ کا لوگو، آپ کے رنگ، آپ کی زبان۔ شاپرز TalkPack برانڈنگ نہیں دیکھیں گے جب تک آپ نہ چاہیں۔",

  footerColPlatform: "پلیٹ فارم",
  footerColModules: "ماڈیولز",
  footerColCompany: "کمپنی",
  footerColResources: "وسائل",
  footerCompany1: "تعارف",
  footerCompany2: "رابطہ",
  footerResources3: "رازداری",
  footerResources4: "شرائط",
  footerNewsHeading: "سہ ماہی اپڈیٹس۔ کوئی اسپام نہیں۔",
  footerNewsPlaceholder: "your@email.com",
  footerNewsSubmit: "سبسکرائب",
  footerRegistration: "ShopAI Ltd · کمپنی نمبر 15877093 · انگلینڈ میں رجسٹرڈ",
  footerCopyright: "© 2026 ShopAI Ltd",

  // ── Tier 2 (UR) ──
  heroAlt: "سپر مارکیٹ کے گلیارے میں خریدار خاتون — TalkPack تجربے کا ہر ٹچ پوائنٹ ان کے گرد گھوم رہا ہے",

  stepsTitle: "تین قدم۔ ایک پلیٹ فارم۔",
  stepsSub: "اس لمحے سے جب شاپر داخل ہوتا ہے، اس لمحے تک جب آپ ان کے بارے میں کچھ سیکھتے ہیں۔ چلانے میں آسان۔ ڈیٹا میں امیر۔",
  step1Title: "داخل ہوں",
  step1Desc: "شاپر کسی بھی چینل سے ٹیپ یا اسکین کرتا ہے۔ ایک داخلہ، ایک برانڈڈ تجربہ۔",
  step1PillRow: "ویب · سوشل · ای میل · QR · OOH · سٹور میں",
  step2Title: "مشغول کریں",
  step2Desc: "پانچ Pic-n-Mix ماڈیولز اپنی زبان میں، آپ کے برانڈ میں، AI کے ذریعے تصدیق شدہ چلتے ہیں۔",
  step2PillRow: "سیکھیں · کھیلیں · پوچھیں · جیتیں · کمائیں",
  step3Title: "سیکھیں",
  step3Desc: "ہر تعامل first-party ڈیٹا بن جاتا ہے، جو آپ کے ڈیش بورڈ پر حقیقی وقت میں آتا ہے۔",
  step3PillRow: "شاپرز · گفتگو · مقام · مشغولیت · تعمیل",

  chipWebsite: "ویب",
  chipSocial:  "سوشل",
  chipEmail:   "ای میل",
  chipQr:      "QR",
  chipOoh:     "OOH",
  chipInStore: "سٹور میں",
  chipLearn:   "سیکھیں",
  chipPlay:    "کھیلیں",
  chipSay:     "پوچھیں",
  chipWin:     "جیتیں",
  chipEarn:    "کمائیں",
  chipShoppers:     "شاپرز",
  chipConversation: "گفتگو",
  chipLocation:     "مقام",
  chipEngagement:   "مشغولیت",
  chipCompliance:   "تعمیل",
  chipAriaEntry:  "داخلہ چینل",
  chipAriaModule: "Pic-n-Mix ماڈیول",
  chipAriaIntel:  "انٹیلی جنس زمرہ",

  intgTitle: "سنجیدہ بنیادی ڈھانچہ۔ ثابت شدہ ٹیکنالوجی۔",
  intgSub: "TalkPack انہی کلاؤڈ، AI اور تعمیل کی بنیادوں پر چلتا ہے جن پر دنیا کی سب سے بڑی کمپنیاں اعتماد کرتی ہیں۔",
  intgCap1: "والدین پلیٹ فارم",
  intgCap2: "کلاؤڈ بنیادی ڈھانچہ",
  intgCap3: "گفتگو AI",
  intgCap4: "زبان کے ماڈلز",
  intgCap5: "AI اور نقشہ سازی",
  intgCap6: "غذائی ڈیٹا",
  intgCap7: "ادائیگیاں",
  intgFootnote: "ISO 27001 سرٹیفائیڈ فراہم کنندگان · GDPR-تعمیل · ملٹی ریجن ہوسٹنگ · پہلے دن سے انٹرپرائز سطح کی سیکیورٹی",

  jColEntry: "داخلہ",
  jColExperience: "تجربہ",
  jColDashboard: "ڈیش بورڈ انٹیلی جنس",
  jEntryDigitalTitle: "ڈیجیٹل",
  jEntryDigitalDesc: "ویب سائٹ، ایپ سٹورز، تلاش، براہ راست لنک — کسی بھی ڈیجیٹل سطح سے شاپر کے تجربے میں ایک ٹیپ۔",
  jEntrySocialTitle: "سوشل",
  jEntrySocialDesc: "سپانسرڈ پوسٹس، انفلوئنسر مواد، DMs — فیڈز پلیٹ فارم چھوڑے بغیر سیدھا TalkPack میں آتی ہیں۔",
  jEntryPhysicalTitle: "جسمانی",
  jEntryPhysicalDesc: "پیک پر، شیلف پر، POS پر — حقیقی دنیا کے داخلہ پوائنٹس جہاں TalkPack ایک اسکین کو گفتگو میں بدل دیتا ہے۔",
  jEntryPhysPill1: "OOH",
  jEntryPhysPill2: "سٹور POS",
  jEntryPhysPill3: "پیکیجنگ",
  jExpVerifyLabel: "تصدیق",
  jExpVerifyText: "AI پروڈکٹ شناخت — SKU حقیقی وقت میں تصدیق شدہ۔",
  jExpLearnLabel: "سیکھیں",
  jExpLearnBody: "غذائی پہیہ، الرجن، اجزاء، اصل — شاپر کی زبان میں دکھائے جاتے ہیں، برانڈ کے ذریعے تصدیق شدہ۔",
  jExpEngageLabel: "مشغول",
  jExpEngagePlayBody: "اسپن، گاچا، پزل، کوئز — ٹھہرنے کے وقت کے لیے پانچ گیم اقسام۔",
  jExpEngageEarnBody: "پوائنٹس، رسیدیں، وفاداری — باسکٹ سطح کی انٹیلی جنس۔",
  jExpEngageSayBody: "گفتگو AI 40+ زبانوں میں جواب دیتی ہے۔",
  jExpRewardLabel: "انعام",
  jExpRewardBody: "فوری انعامات، ٹل پر ڈوئل QR تصدیق، GPS-تصدیق شدہ دعوے — پہلے دن سے آڈٹ کے لیے تیار۔",
  jExpCaptureLabel: "حاصل",
  jExpCaptureSub: "ہر تعامل · آپ کی طرف سے حاصل اور ملکیت",

  capEmail:   "ای میل",
  capName:    "نام",
  capAddress: "پتہ",
  capGps:     "GPS",
  capProduct: "مصنوعہ",
  capBasket:  "باسکٹ",
  capSpend:   "اخراجات",
  capChat:    "چیٹ",

  dashShopperLabel: "شاپر انٹیلی جنس",
  dashShopperShort: "شاپر",
  dashShopper1Title: "First-party CRM",
  dashShopper1Desc: "ای میل، نام، پتہ، رابطہ ترجیحات۔",
  dashShopper2Title: "GPS-تصدیق شدہ مقامات",
  dashShopper2Desc: "سٹور علاقوں کے مطابق شاپرز کہاں مشغول ہوتے ہیں۔",
  dashShopper3Title: "خریداری کی فریکوئنسی",
  dashShopper3Desc: "بار بار اسکین پیٹرن، دورے کی تال۔",
  dashShopper4Title: "باسکٹ ساخت",
  dashShopper4Desc: "آئٹم سطح کا رسید ڈیٹا، ایک ساتھ خریدی گئی مصنوعات۔",

  dashConvLabel: "گفتگو انٹیلی جنس",
  dashConvShort: "گفتگو",
  dashConv1Title: "سوال زمرے",
  dashConv1Desc: "الرجن، اجزاء، غذائیت، استعمال، ترکیبیں۔",
  dashConv2Title: "جذبات کا تجزیہ",
  dashConv2Desc: "مصنوعہ اور علاقے کے مطابق مثبت، منفی، غیر جانبدار۔",
  dashConv3Title: "الجھن کے نکات",
  dashConv3Desc: "شاپرز کیا نہیں سمجھتے، بار بار پوچھے جانے والے سوالات۔",
  dashConv4Title: "خریداری کا ارادہ",
  dashConv4Desc: "تجسس سے ریٹیلر درخواستوں تک پیش رفت۔",
  dashConv5Title: "زبان کی ترجیحات",
  dashConv5Desc: "مقام کے مطابق شاپرز کون سی زبانیں منتخب کرتے ہیں۔",

  dashDistLabel: "تقسیم انٹیلی جنس",
  dashDistShort: "تقسیم",
  dashDist1Title: "سٹور دستیابی",
  dashDist1Desc: "شیلف پر مصنوعہ، geofence اسکینوں سے تصدیق شدہ۔",
  dashDist2Title: "اسٹاک ختم ہونے کی الرٹ",
  dashDist2Desc: "'یہاں نہیں' رپورٹس حقیقی وقت میں نشان زد۔",
  dashDist3Title: "ہیٹ میپ",
  dashDist3Desc: "پوسٹ کوڈ، علاقہ، fascia کے مطابق اسکین کثافت۔",
  dashDist4Title: "گھریلو رسائی",
  dashDist4Desc: "'کہیں بھی اسکین' دکھاتا ہے کہ مصنوعہ گھر میں کہاں رہتا ہے۔",
  dashDist5Title: "سٹور موازنہ",
  dashDist5Desc: "مقام، fascia گروپ کے مطابق مشغولیت کی شرحیں۔",

  dashCampLabel: "مہم کی کارکردگی",
  dashCampShort: "مہم",
  dashCamp1Title: "اسکین تا مشغولیت کی شرح",
  dashCamp1Desc: "کتنے اسکین مکمل تعاملات میں تبدیل ہوتے ہیں۔",
  dashCamp2Title: "گیم تکمیل کی شرح",
  dashCamp2Desc: "فی سیشن اسپن، جیت کی شرح، چھوڑنے کے نکات۔",
  dashCamp3Title: "ریڈیمپشن ٹریکنگ",
  dashCamp3Desc: "سٹور کے مطابق دعویدار، تصدیق شدہ، ختم ہو چکے انعامات۔",
  dashCamp4Title: "ROI ڈیش بورڈ",
  dashCamp4Desc: "مہم کے اخراجات بمقابلہ حاصل کردہ شاپر کی قیمت۔",

  dashCompLabel: "تعمیل اور آڈٹ",
  dashCompShort: "تعمیل",
  dashComp1Title: "ASA تعمیل سکور",
  dashComp1Desc: "فی مہم، فی گفتگو۔",
  dashComp2Title: "برآمد ہونے والے آڈٹ لاگز",
  dashComp2Desc: "ہر AI جواب لاگ اور بازیافت کے قابل۔",
  dashComp3Title: "خلاف ورزی کی الرٹ",
  dashComp3Desc: "نشان زد مواد کے پیٹرن۔",
  dashComp4Title: "غذائی مشغولیت",
  dashComp4Desc: "پہیے پر شاپرز کن غذائی اجزاء کے ساتھ تعامل کرتے ہیں۔",

  pvFormTitle: "مبارک ہو!",
  pvFormSub: "آپ نے جیتا: اگلی خریداری پر 20% رعایت",
  pvFormName: "آپ کا نام",
  pvFormEmail: "آپ کا ای میل",
  pvFormPostcode: "پوسٹ کوڈ",
  pvFormCta: "انعام لیں",
  pvFormFootnote: "Privacy-first · GDPR-تعمیل",

  pvChatTitle: "AI سفیر",
  pvChatSub: "Morning O's پر تربیت یافتہ",
  pvChatBubble1: "کیا یہ گلوٹن سے پاک ہے؟",
  pvChatBubble2: "ہاں — تصدیق شدہ گلوٹن سے پاک اور فی سرونگ صرف 9 گرام چینی۔ کیا آپ ترکیب کے خیالات چاہیں گے؟",
  pvChatBubble3: "ہاں براہ کرم",
  pvChatBubble4: "بیریز کے ساتھ راتوں رات اوٹس آزمائیں — 3 منٹ کی تیاری۔",
  pvChatPlaceholder: "مجھ سے کچھ بھی پوچھیں…",
  pvChatFootnote: "40+ زبانیں · ہمیشہ فعال",

  pvMapTitle: "اپنے قریب Morning O's تلاش کریں",
  pvMapPin: "Nisa · SE1 · 0.3 میل",
  pvMapCta: "ہدایات",
  pvMapFootnote: "GPS-تصدیق شدہ · 150m تک geofenced",
  pvMapOos: "اسٹاک میں نہیں",

  pvWheelTitle: "Morning O's · گھمائیں اور جیتیں",
  pvWheelCredits: "350 کریڈٹ",
  pvWheelCta: "گھمائیں",
  pvWheelFootnote: "اسپن وہیل · غذائی وہیل · گاچا · کوئز · پزل",
  pvWheelWedgeWin: "جیت!",
  pvWheelWedgeTry: "دوبارہ",

  pvNutTitle: "Morning O's · غذائی معلومات",
  pvNutPer100g: "فی 100 گرام",
  pvNutPerServing: "فی سرونگ",
  pvNutCta: "جاری رکھیں",
  pvNutFootnote: "ASA-تعمیل · کھیل سے پہلے لازمی",

  bookDemoSubject: "TalkPack ڈیمو کی درخواست",
  bookDemoBody: "ہیلو ShopAI ٹیم،\n\nمیں TalkPack کا ایک ڈیمو بک کرنا چاہتا/چاہتی ہوں۔ براہ کرم وقت طے کرنے کے لیے رابطہ کریں۔\n\nشکریہ،",

  // ── Viz mock-data labels (UR) ──
  vizCapQuestions: "اس ہفتے شاپرز کے سرفہرست سوالات",
  vizQAllergens: "الرجن", vizQIngredients: "اجزاء", vizQNutrition: "غذائیت",
  vizQRecipes: "ترکیبیں", vizQUsage: "استعمال",
  vizCapSentiment: "تمام مصنوعات کے گزشتہ 30 دن",
  vizSentPositive: "مثبت", vizSentNeutral: "غیر جانبدار", vizSentNegative: "منفی",
  vizCapConfusion: "بار بار پوچھے جانے والے سوالات — مواد میں کمی",
  vizConfusion1: "کیا یہ گلوٹن سے پاک ہے؟",
  vizConfusion2: "کیا ویگنز کے لیے موزوں ہے؟",
  vizConfusion3: "گری دار میوہ جات کی انتباہ؟",
  vizCapFunnel: "گفتگو کے ذریعے تبدیلی",
  vizFunnelCurious: "متجسس", vizFunnelInterested: "دلچسپی رکھنے والا", vizFunnelReady: "خریدنے کے لیے تیار",
  vizCapAvail: "جہاں آپ نے منصوبہ بندی کی وہاں مصنوعہ دستیاب",
  vizAvailDetail: "1,326 میں سے 1,247 سٹورز شیلف پر تصدیق شدہ",
  vizCapOos: "آج 3 سٹورز کو نشان زد کیا گیا · خودکار الرٹ بھیجا گیا",
  vizCapHeat: "لندن · مانچیسٹر · برمنگھم آگے",
  vizCapHousehold: "68% اسکین گھر پر ہوتے ہیں — آپ کا مصنوعہ وہاں رہتا ہے",
  vizCapFascia: "ریٹیلر برانڈ کے مطابق مشغولیت کی شرح",
  vizCapEngrate: "اسکین جو مکمل تعامل میں بدلتے ہیں",
  vizEngrateCompare: "صنعت کے بینچ مارک 42% کے مقابلے +36 پوائنٹس",
  vizCapGame: "شاپرز کہاں چھوڑتے ہیں — کھیل کا توازن ٹیون کریں",
  vizGameStart: "شروع", vizGamePlay: "کھیلیں", vizGameWin: "جیت", vizGameClaim: "حاصل",
  vizCapRdm: "96% تصدیق کی شرح · کم میعاد ختم",
  vizRdmClaimed: "حاصل کیے", vizRdmValidated: "تصدیق شدہ", vizRdmExpired: "میعاد ختم",
  vizCapRoi: "مہم کا خرچ بمقابلہ شاپر لائف ٹائم ویلیو",
  vizRoiSpend: "خرچ", vizRoiValueLabel: "حاصل قیمت", vizRoiReturn: "3.4× واپسی",
  vizCapComp: "فی مہم · فی گفتگو · آڈٹ کے لیے تیار",
  vizCapAudit: "ہر AI جواب لاگ شدہ · مانگ پر دستیاب",
  vizCapViolation: "اس مہینے خلاف ورزیاں · AI آؤٹ پٹ مسلسل تصدیق شدہ",
  vizCapNut: "وہیل پر شاپرز جن غذائی اجزاء کے ساتھ تعامل کرتے ہیں",
  vizNutSugar: "چینی", vizNutFat: "چربی", vizNutSalt: "نمک", vizNutSatFat: "سیر شدہ",
  vizCapLang: "شاپر کی زبان خودکار طور پر شناخت ہوتی ہے",
  vizLangOther: "دیگر",
  vizCapCrm: "حاصل کیے گئے شاپرز",
  vizCapMap: "اس ہفتے 247 مقامات",
  vizCapWeek: "اوسط 2.3 ملاقات فی ہفتہ",
  vizDayMon: "پیر", vizDayTue: "منگل", vizDayWed: "بدھ",
  vizDayThu: "جمعرات", vizDayFri: "جمعہ", vizDaySat: "ہفتہ", vizDaySun: "اتوار",
  vizCapBasket: "اوسط باسکٹ: £8.40 · 6.2 آئٹم",
  vizBasketDairy: "ڈیری", vizBasketBakery: "بیکری", vizBasketSnacks: "اسنیکس", vizBasketOther: "دیگر",
};

// All translations now populated for Tier 2 keys (LLM-generated 2026-05-05).
// Pre-existing keys (nav, hero, stats, FAQ, footer) were translated earlier.
// The switcher still works; missing keys fall back to EN via `...en` spread.
export const COPY: Record<LandingLanguage, LandingCopy> = {
  en,
  "en-us": enUs,
  fr,
  es,
  de,
  it,
  pl,
  tr,
  ja,
  zh,
  th,
  hi,
  gu,
  pa,
  bn,
  ta,
  ar,
  ur,
};

// Stats ticker values (language-neutral numerics + tokens)
export const TICKER_STATS: { value: string; labelKey: keyof LandingCopy }[] = [
  { value: "40+",          labelKey: "statLanguages" },
  { value: "22,000+",      labelKey: "statStores" },
  { value: "One scan",     labelKey: "statScans" },
  { value: "HFSS-ready",   labelKey: "statModules" },
  { value: "White-label",  labelKey: "statSteps" },
  { value: "Zero",         labelKey: "statDownloads" },
  { value: "10 minutes",   labelKey: "statGames" },
  { value: "First-party",  labelKey: "statGeofence" },
];
